import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import {
 getFirestore,
 collection,
 onSnapshot,
 doc,
 getDoc,
 getDocs,
 setDoc,
 updateDoc,
 deleteDoc,
 writeBatch,
 query,
 where,
 limit,
 serverTimestamp
} from 'firebase/firestore';
import {
 getAuth,
 onAuthStateChanged,
 signInWithEmailAndPassword,
 createUserWithEmailAndPassword,

 sendPasswordResetEmail,
 signInWithPopup,
 signInWithRedirect,
 GoogleAuthProvider,
 signOut,
 updatePassword,
 reauthenticateWithCredential,
 reauthenticateWithPopup,
 EmailAuthProvider,
 confirmPasswordReset,
 verifyPasswordResetCode,
 fetchSignInMethodsForEmail,
 multiFactor,
 TotpMultiFactorGenerator,
 getMultiFactorResolver,
 User as FirebaseUser
} from 'firebase/auth';
import QRCode from 'qrcode';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
 LayoutDashboard,
 Database,
 RefreshCw,
 AlertCircle,
 CheckCircle,
 History,
 Activity,
 Menu,
 X,
 Settings,
 Trash2,
 Mail as MailIcon,
 Zap as ZapIcon,
 DollarSign,
 Save,
 ChevronRight,
 ChevronLeft,
 Eye,
 Send,
 Building,
 BarChart3,
 TrendingUp,
 PieChart as PieChartIcon,
 Bell,
 LogOut,
 AlertTriangle,
 UserX,
 Users,
 UserPlus,
 Calendar as CalendarIcon,
 Clock3,
 ArrowRight,
 ChevronDown,
 Globe,
 Download,
 Search,
 Filter,
 ArrowUpDown,
 Info,
 Link as LinkIcon,
 UserCog,
 Camera,
 Key,
 ShieldCheck,
 ToggleRight,
 ToggleLeft,
 Sparkles,
 Target,
 MapPin,
 Shield,
 FileText,
 CheckCircle2,
  MessageSquare,
  LifeBuoy,
  Moon,
  Sun,
  Clock,
  PauseCircle,
  Zap,
  Plus
} from 'lucide-react';

import {
 BarChart,
 Bar,
 LineChart,
 Line,
 XAxis,
 YAxis,
 CartesianGrid,
 Tooltip,
 Legend,
 ResponsiveContainer,
 PieChart,
 Pie,
 Cell
} from 'recharts';

// --- Interfaces ---
interface DunningTier {
 id: string;
 name: string;
 daysPastDue: number;
 subject: string;
 body: string;
 enabled: boolean;
 attachPdf?: boolean;
 isDefault?: boolean;
 complianceReview?: {
  compliant: boolean;
  riskLevel: 'low' | 'medium' | 'high';
  issues?: Array<{
   category: string;
   severity: 'low' | 'medium' | 'high';
   phrase?: string;
   explanation: string;
  }>;
  explanation?: string;
  suggestedSubject?: string;
  suggestedBody?: string;
  model?: string;
  reviewedAt?: string;
  usage?: {
   promptTokens: number;
   completionTokens: number;
   totalTokens: number;
   estimatedCostUsd: number;
  };
 };
}

interface ToneRewriteResult {
 suggestedSubject: string;
 suggestedBody: string;
 toneSummary: string;
 notes: string;
 model: string;
 rewrittenAt: string;
 usage: {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
 };
}

interface Invoice {
 id: string;
 invoiceId: string;
 customerName: string;
 customerEmail?: string;
 balance: number;
 totalAmount?: number;
 dueDate: string;
 status: string;
 linkedBy: string;
 realmId: string;
 docNumber?: string;
 invoiceDate?: string;
 paidAt?: any;
}

interface AgingSnapshot {
 weekKey: string;
 snapshotDate: string;
 current: number;
 days1_30: number;
 days31_60: number;
 days61_90: number;
 days90plus: number;
 totalAR: number;
 invoiceCount: number;
}

interface CollectionNote {
 id: string;
 linkedBy: string;
 realmId: string;
 invoiceId: string;
 customerEmail?: string | null;
 customerName?: string | null;
 note: string;
 ptpDate?: string | null;
 createdAt?: any;
 createdBy?: string;
}

interface SnoozeOverride {
 id: string;
 type: 'snooze';
 realmId: string;
 invoiceId?: string;
 snoozedUntil?: any;
 active?: boolean;
}

interface CustomerPauseOverride {
 id: string;
 type: 'customer_pause';
 realmId: string;
 customerEmail?: string;
 pausedAt?: any;
 active?: boolean;
}

interface CustomerMetaOverride {
 id: string;
 type: 'customer_meta';
 realmId: string;
 customerEmail?: string;
 notes?: string;
 tags?: string[];
 emailOverride?: string;
 excluded?: boolean;
}

interface InvoiceControlsOverride {
 id: string;
 type: 'invoice_controls';
 realmId: string;
 invoiceId?: string;
 disabled?: boolean;
 markedCollected?: boolean;
 ccEmail?: string;
 customMessage?: string;
}

type DunningOverride = SnoozeOverride | CustomerPauseOverride | CustomerMetaOverride | InvoiceControlsOverride;

interface AuditEvent {
 id: string;
 linkedBy: string;
 actor: string;
 action: string;
 details: string;
 metadata?: Record<string, any> | null;
 createdAt?: any;
}

interface ApiKey {
 id: string;
 name: string;
 keyPrefix: string;
 scopes: string[];
 active: boolean;
 createdAt?: any;
 lastUsedAt?: any;
 callCount?: number;
 linkedBy: string;
}

interface TeamMember {
 id: string;
 email: string;
 displayName: string | null;
 memberUid: string | null;
 role: 'admin' | 'member' | 'viewer';
 status: 'pending' | 'active';
 invitedAt?: any;
 acceptedAt?: any;
 linkedBy: string;
 connectionIds?: string[];
}

interface WorkspaceMembership {
 id: string; // ownerUid
 ownerUid: string;
 ownerEmail: string;
 ownerName: string;
 role: 'admin' | 'member' | 'viewer';
 status: 'active';
 joinedAt?: any;
 connectionIds?: string[];
 ownerPlan?: 'starter' | 'pro' | 'cpa';
 ownerTrialEndsAt?: any;
 ownerSubscriptionStatus?: string | null;
 ownerActiveProducts?: string[];
 ownerHasPaidSubscription?: boolean;
}

interface TeamInviteInfo {
 ownerName: string;
 ownerEmail: string;
 role: string;
 targetEmail: string;
 expiresAt: string;
}

interface Webhook {
 id: string;
 url: string;
 description: string;
 events: string[];
 active: boolean;
 createdAt?: any;
 updatedAt?: any;
}

interface WebhookDelivery {
 id: string;
 webhookId: string;
 event: string;
 url: string;
 success: boolean;
 statusCode: number | null;
 error: string | null;
 deliveredAt?: any;
}

interface DunningLog {
 id: string;
 invoiceId: string;
 docNumber: string;
 customerName: string;
 templateSent: string;
 deliveryStatus: string;
 errorDetail?: string;
 blockedReasonCode?: string;
 createdAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 queuedAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 sentAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 deliveredAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 openedAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 failedAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 lastEventAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 blockedAt?: { seconds?: number; toDate?: () => Date } | string | Date | null;
 lastEventType?: string;
 senderUsed?: string;
 recipient?: string;
 sgMessageId?: string;
 isFirstContact?: boolean;
 invoiceAmount?: number;
 includedPaymentLink?: boolean;
 paymentLinkHost?: string | null;
 includedInvoiceAttachment?: boolean;
 attachmentFilename?: string | null;
 invoicePdfMode?: 'branded' | 'fallback' | 'none' | string;
 safeguardSnapshot?: {
  recipientPolicy?: string;
  optOutCheck?: string;
  disputeCheck?: string;
  recentSendCheck?: string;
  frequencyCapCheck?: string;
  mailingAddressCheck?: string;
  templateComplianceCheck?: string;
  senderVerificationCheck?: string;
 };
 realmId: string;
 linkedBy: string;
}

interface Connection {
 id: string;
 companyName: string;
 realmId: string;
 status: string;
 lastUpdated: string;
 linkedBy: string;
 syncError?: boolean;
 lastHandshakeError?: string;
 tokenStatus?: string;
 tokenFailedAt?: string;
 lastTokenRefreshAt?: string;
 lastCycleStatus?: string;
 lastCycleAt?: string;
 lastCycleError?: string;
}

interface AppNotification {
 id: number;
 type: 'success' | 'error' | 'info';
 message: string;
}

interface NotificationPrefs {
 payment_received: boolean;
 dispute_filed: boolean;
 sync_failed: boolean;
 trial_expiring: boolean;
 subscription_payment_failed: boolean;
 weekly_digest: boolean;
}

interface UserProfile {
 displayName?: string;
 photoURL?: string;
 senderEmail?: string;
 senderAddress?: string;
 senderCity?: string;
 senderZip?: string;
 useCustomSender: boolean;
 notificationsEnabled: boolean;
 notificationPrefs?: NotificationPrefs;
 plan?: 'starter' | 'pro' | 'cpa';
 planStartedAt?: any;
 trialEndsAt?: any;
 subscriptionStatus?: string;
 paddleSubscriptionId?: string;
 paddleCustomerId?: string;
 paddleCancellationEffectiveDate?: string;
 activeProducts?: string[];
 tosVersion?: string;
 tosAcceptedAt?: any;
 privacyVersion?: string;
 privacyAcceptedAt?: any;
 ssoEnabled?: boolean;
 ssoRequiredDomain?: string;
 slackWebhookUrl?: string;
}

interface BillingPortalData {
 url?: string | null;
 updateUrl?: string | null;
 cancelUrl?: string | null;
 status?: string | null;
 nextBilledAt?: string | null;
}

interface SupportCitation {
 label: string;
 value: string;
}

interface SupportThread {
 id: string;
 linkedBy: string;
 customerUid: string;
 customerEmail?: string;
 customerDisplayName?: string;
 status: string;
 priority: string;
 supportTier?: 'standard' | 'priority' | 'white_glove' | string;
 targetResponseSlaHours?: number;
 issueType: string;
 subject?: string;
 lastMessagePreview?: string;
 lastHandoffId?: string | null;
 lastHandoffSummary?: string | null;
 lastTicketId?: string | null;
 firstResponseAt?: any;
 escalationAt?: any;
 owner?: string | null;
 lastHumanUpdateAt?: any;
 waitingOnCustomer?: boolean;
 lastResponseState?: 'ready' | 'fallback' | 'escalated' | string;
 updatedAt?: any;
 createdAt?: any;
}

interface SupportMessage {
 id: string;
 threadId: string;
 linkedBy: string;
 senderType: 'customer' | 'ai' | 'human_support' | 'system' | string;
 senderId?: string;
 message: string;
 messageFormat?: string;
 answerSource?: 'openai' | 'deterministic' | 'fallback' | string;
 citations?: SupportCitation[];
 actions?: Array<{ type: string; label: string; requiresApproval?: boolean }>;
 createdAt?: any;
}

interface SupportHandoff {
 id: string;
 threadId: string;
 linkedBy: string;
 summary: string;
 riskReason: string;
 recommendedNextStep: string;
 createdAt?: any;
}

interface SupportAnalytics {
 periodDays: number;
 newThreads: number;
 escalationCount: number;
 escalationRate: number;
 unresolvedOver24h: number;
 averageFirstResponseMinutes: number | null;
 supportHelpfulRate: number | null;
 helpHelpfulRate: number | null;
 topIssueTypes: Array<{ issueType: string; count: number }>;
 recurringIssues: Array<{
  issueType: string;
  count: number;
  escalated: number;
  unresolved: number;
  escalationRate: number;
  sampleSubject: string;
  recommendation: string;
 }>;
}

interface HelpArticle {
 id: string;
 category: 'getting_started' | 'quickbooks' | 'delivery' | 'sender' | 'compliance' | 'billing' | 'tabs';
 title: string;
 summary: string;
 bullets: string[];
 howTo?: string[];
 details?: string[];
 faqs?: Array<{ question: string; answer: string }>;
}

interface MaintenanceNotice {
 enabled?: boolean;
 phase?: 'scheduled' | 'in_progress' | 'resolved' | string;
 title?: string | null;
 message?: string | null;
 startsAt?: any;
 endsAt?: any;
 nextUpdateAt?: any;
 retryAfterSeconds?: number | null;
 affectedAreas?: string[];
 supportEmail?: string | null;
 updatedAt?: any;
}

const SUPPORT_ISSUE_LABELS: Record<string, string> = {
 billing: 'Billing',
 auth: 'Auth',
 quickbooks: 'QuickBooks',
 deliverability: 'Deliverability',
 sender: 'Sender',
 compliance: 'Compliance',
 invoice: 'Invoice',
 unknown: 'General'
};

const getSupportStatusBadge = (thread: SupportThread | null, supportSending: boolean) => {
 if (supportSending) {
  return { label: 'Checking', className: 'bg-cyan-50 text-cyan-700 border-cyan-200' };
 }
 if (!thread) return null;
 if (thread.lastResponseState === 'fallback') {
  return { label: 'Needs Review', className: 'bg-rose-50 text-rose-700 border-rose-200' };
 }
 if (thread.status === 'escalated') {
  return { label: 'Escalated', className: 'bg-amber-50 text-amber-700 border-amber-200' };
 }
 if (thread.waitingOnCustomer) {
  return { label: 'Waiting On You', className: 'bg-slate-100 text-slate-700 border-slate-200' };
 }
 if (thread.firstResponseAt) {
  return { label: 'Answered', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
 }
 return { label: String(thread.status || 'open').replace(/_/g, ' '), className: 'bg-slate-100 text-slate-700 border-slate-200' };
};

const SUPPORT_PRIORITY_LABELS: Record<string, string> = {
 low: 'Low',
 normal: 'Normal',
 high: 'High',
 urgent: 'Urgent'
};

const SUPPORT_TIER_LABELS: Record<string, string> = {
 standard: 'Standard',
 priority: 'Priority',
 white_glove: 'White-Glove'
};

const getMaintenanceBadge = (notice: MaintenanceNotice) => {
 switch (String(notice.phase || '').toLowerCase()) {
  case 'scheduled':
   return { label: 'Scheduled Maintenance', className: 'bg-amber-50 text-amber-700 border-amber-200' };
  case 'resolved':
   return { label: 'Maintenance Complete', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  case 'in_progress':
  default:
   return { label: 'Maintenance In Progress', className: 'bg-rose-50 text-rose-700 border-rose-200' };
 }
};

const PROHIBITED_PHRASES = [
 'arrest', 'jail', 'prison', 'criminal', 'garnish your wages', 'seize your property',
 'seize your assets', 'report to credit bureau', 'destroy your credit', 'ruin your credit',
 'legal action will be taken', 'we will sue', 'file a lawsuit', 'warrant', 'law enforcement',
 'police', 'wage garnishment', 'bank levy', 'lien on your'
];

const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_WARNING_TIMEOUT_MS = 25 * 60 * 1000;
const SESSION_COUNTDOWN_SECONDS = Math.floor((SESSION_IDLE_TIMEOUT_MS - SESSION_WARNING_TIMEOUT_MS) / 1000);

// --- Plan Configuration ---
type PlanId = 'starter' | 'pro' | 'cpa';

interface PlanConfig {
 id: PlanId;
 name: string;
 price: number;
 maxConnections: number;
 maxTemplates: number;
 maxTeamMembers: number;
 customSmtp: boolean;
 analytics: boolean;
 compliance: boolean;
 csvExport: boolean;
 supportTier: 'standard' | 'priority' | 'white_glove';
 supportSlaLabel: string;
 description: string;
}

const PLANS: Record<PlanId, PlanConfig> = {
 starter: {
 id: 'starter', name: 'Starter', price: 49,
  maxConnections: 1, maxTemplates: 3, maxTeamMembers: 0,
  customSmtp: false, analytics: true, compliance: false, csvExport: false,
  supportTier: 'standard', supportSlaLabel: 'AI support + standard human queue',
  description: '1 QBO connection, system email sender, up to 3 templates'
 },
 pro: {
  id: 'pro', name: 'Pro', price: 99,
  maxConnections: 3, maxTemplates: 99, maxTeamMembers: 3,
  customSmtp: true, analytics: true, compliance: true, csvExport: true,
  supportTier: 'priority', supportSlaLabel: 'AI support + priority human queue',
  description: 'Up to 3 QBO connections, custom SMTP, unlimited templates'
 },
 cpa: {
  id: 'cpa', name: 'CPA', price: 199,
  maxConnections: 10, maxTemplates: 99, maxTeamMembers: 10,
  customSmtp: true, analytics: true, compliance: true, csvExport: true,
  supportTier: 'white_glove', supportSlaLabel: 'AI support + white-glove human queue',
  description: 'Up to 10 QBO connections for accountants & agencies'
 }
};

// --- Configuration ---
const PRIMARY_BLUE = "#51e2f5";
const COLORS = [PRIMARY_BLUE, '#8b5cf6', '#ec4899', '#f43f5e'];
const MARKETING_SITE_URL = (process.env.REACT_APP_MARKETING_SITE_URL || window.location.origin).trim();
const APP_SITE_URL = (process.env.REACT_APP_APP_SITE_URL || window.location.origin).trim();
const PRIVACY_PAGE_URL = `${MARKETING_SITE_URL}/privacy.html`;
const TERMS_PAGE_URL = `${MARKETING_SITE_URL}/terms.html`;
const STATUS_PAGE_URL = `${MARKETING_SITE_URL}/status`;
// Bump these date strings whenever the respective document materially changes — all existing users will be prompted to re-accept.
const CURRENT_TOS_VERSION = '2026-04-15';
const CURRENT_PRIVACY_VERSION = '2026-04-15';
const SUPPORT_EMAIL = (process.env.REACT_APP_SUPPORT_EMAIL || 'support@soar-one.com').trim();

const HELP_ARTICLES: HelpArticle[] = [
 {
  id: 'help_getting_started_1',
  category: 'getting_started',
  title: 'Getting started with SoarOne',
  summary: 'Use this guide to get SoarOne production-ready, from QuickBooks connection through sender setup, templates, and first validation.',
  bullets: [
   'Connect at least one QuickBooks company before expecting invoice data or reminders.',
   'Complete your mailing address in Account because it is part of your sender identity and compliance footer.',
   'Choose whether you will use the system sender or a verified business-domain sender.',
   'Review every reminder template before enabling automation.',
   'Use History and Support together during first-time QA.'
  ],
  howTo: [
   'Open Main and connect your first QuickBooks company.',
   'Open Account and complete company name, mailing address, city, and zip.',
   'Open SMTP if you want a custom sender like billing@yourdomain.com.',
   'Open Templates and review each reminder stage for timing, tone, and compliance.',
   'Run Manual Sync from Main.',
   'Open History and confirm invoices, reminder rows, and delivery states are flowing normally.',
   'Only move to live use after you test blocked, queued, sent, and retry behavior.'
  ],
  details: [
   'The fastest path to a working account is: connect QuickBooks, complete Account, verify sender, review templates, then test History.',
   'If you skip sender setup or mailing address setup, reminders may be blocked even if QuickBooks sync is healthy.',
   'If you are on Starter, the app uses the shared system sender. Pro and CPA unlock custom sender setup.'
  ],
  faqs: [
   { question: 'What should I configure first?', answer: 'Start with QuickBooks and Account. Without those two, the rest of the workflow will be unreliable.' },
   { question: 'Can I use SoarOne without custom SMTP?', answer: 'Yes. Starter can use the system sender, but custom sender setup is better for branding and deliverability.' },
   { question: 'How do I know setup is complete?', answer: 'You should see an active QuickBooks connection, a complete mailing address, reviewed templates, and working History events.' }
  ]
 },
 {
  id: 'help_add_company',
  category: 'quickbooks',
  title: 'How to add a QuickBooks company',
  summary: 'Add and authorize a QuickBooks company from Main so SoarOne can sync invoices, customer balances, and reminder activity.',
  bullets: [
   'A QuickBooks connection is required for live invoice and reminder data.',
   'Each connected company counts against your plan limit.',
   'Manual Sync is the fastest way to validate a fresh connection.'
  ],
  howTo: [
   'Open Main.',
   'Select Integrate QuickBooks.',
   'Sign in to QuickBooks and choose the company you want to authorize.',
   'Return to Main and confirm the connection shows as active.',
   'Run Manual Sync.',
   'Open History and verify invoices and reminder-related rows are loading.'
  ],
  details: [
   'If you manage multiple companies, connect them one at a time and verify each connection after sync.',
   'If the connection is present but invoice data looks stale, run Manual Sync first before assuming the connection is broken.',
   'If a company is disconnected, reminder decisions based on invoice state may become stale or incomplete.'
  ],
  faqs: [
   { question: 'Why do I not see new invoices right after connecting?', answer: 'Run Manual Sync first. The initial connection does not always mean data has already been refreshed into the app view.' },
   { question: 'Can I connect more than one company?', answer: 'Yes, if your plan allows it. Starter supports 1, Pro supports up to 3, and CPA supports up to 10.' },
   { question: 'What if authorization fails?', answer: 'Reconnect from Main, confirm you picked the correct QuickBooks company, and then ask Support to review connection health if it still fails.' }
  ]
 },
 {
  id: 'help_quickbooks_1',
  category: 'quickbooks',
  title: 'QuickBooks sync troubleshooting',
  summary: 'If invoices are missing, stale, or incorrect, start with connection health, sync behavior, and recent account activity before assuming reminders are broken.',
  bullets: [
   'Main is your source of truth for connection state.',
   'History helps confirm whether invoice and reminder events are actually moving.',
   'Support can inspect account-level sync context if the problem is not obvious from the UI.'
  ],
  howTo: [
   'Open Main and review the QuickBooks connection card.',
   'If the connection is inactive or disconnected, reconnect authorization first.',
   'If the connection is active, run Manual Sync.',
   'Open History and check whether invoice and reminder rows are updating.',
   'If you still see stale data, ask Support to review connection health and recent account activity.'
  ],
  details: [
   'A sync issue can look like a reminder issue because reminders depend on current invoice state.',
   'If History is not updating after a manual sync, the issue is usually upstream of reminder generation.',
   'If only one company looks stale, focus on that specific connection rather than the whole account.'
  ],
  faqs: [
   { question: 'Why is History empty even though QuickBooks is connected?', answer: 'Usually because the first sync has not run yet, or the connection is active but not healthy. Run Manual Sync and re-check.' },
   { question: 'Should I reconnect or sync first?', answer: 'If the connection shows active, sync first. If it shows inactive or disconnected, reconnect first.' },
   { question: 'Can Support fix sync problems?', answer: 'Support can diagnose sync state and recommend the next step. Some connection issues still require reauthorization in QuickBooks.' }
  ]
 },
 {
  id: 'help_sender_1',
  category: 'sender',
  title: 'How to configure custom SMTP / sender identity',
  summary: 'Use a verified business-domain sender so reminders look trustworthy, meet policy expectations, and deliver more reliably than a personal mailbox.',
  bullets: [
   'Custom sender works best with a business-domain address that your team monitors.',
   'Role-based addresses like billing@ or ar@ are usually better than personal inboxes.',
   'Verification must be completed before you rely on the sender for live reminders.'
  ],
  howTo: [
   'Open SMTP.',
   'Enable custom sender if your plan allows it.',
   'Enter the sender email you want customers to see.',
   'Confirm your mailing address is complete in Account.',
   'Use Verify Sender to register and send the verification email.',
   'Open the mailbox for that sender and complete the verification step.',
   'Run a test and check History for sender-related issues.'
  ],
  details: [
   'The sender email and mailing address work together. A missing address can still block reminders even if the mailbox itself is valid.',
   'If you use Gmail or another personal mailbox domain, delivery and trust are usually weaker than a verified domain sender.',
   'If the sender is not verified, SoarOne may block reminders before send rather than risk misleading delivery behavior.'
  ],
  faqs: [
   { question: 'What is the best sender name to use?', answer: 'A role-based business address like billing@yourdomain.com is usually best because it feels official, stable, and team-managed.' },
   { question: 'Does SMTP mean I need to run my own mail server?', answer: 'Not in practice for this workflow. In SoarOne, it mainly means configuring and verifying the sender identity used for outbound reminders.' },
   { question: 'Why is a verified sender important?', answer: 'Because it improves deliverability, customer trust, and operational clarity when you need to troubleshoot reminder issues.' }
  ]
 },
 {
  id: 'help_sender_2',
  category: 'sender',
  title: 'Why sender setup matters',
  summary: 'Sender identity is not just branding. It directly affects delivery reliability, customer trust, and whether a reminder can safely leave the system.',
  bullets: [
   'A verified business sender improves deliverability.',
   'Your mailing address is required for compliant reminder sending.',
   'Consumer mailbox domains are more likely to create delivery problems.',
   'History and Support are the best places to diagnose sender-related issues.'
  ],
  details: [
   'Customers are more likely to trust reminders that come from a clear business sender than from a personal mailbox.',
   'A sender problem can show up as blocked, queued, or failed behavior depending on where the process breaks.',
   'If you are testing, always check both the sender address setup and the provider evidence shown in History.'
  ],
  faqs: [
   { question: 'Can I stay on the system sender forever?', answer: 'Yes on Starter, but a custom verified sender is usually better once you want stronger branding and more predictable delivery.' },
   { question: 'What causes sender-related blocks?', answer: 'Common causes are missing sender email, unverified sender identity, or incomplete mailing address details.' }
  ]
 },
 {
  id: 'help_delivery_1',
  category: 'delivery',
  title: 'Why reminders get blocked',
  summary: 'Blocked reminders are stopped intentionally before send. This is usually a safeguard decision, not a provider delivery event.',
  bullets: [
   'Common causes are recipient allowlist, opt-out, dispute hold, missing sender, or missing mailing address.',
   'Open History and check the blocked reason plus the safeguard snapshot.',
   'Fix the root cause first, then retry from History.',
   'Ask Support if you want a plain-language explanation of a specific blocked reminder.'
  ],
  details: [
   'Blocked is different from failed. Blocked means SoarOne stopped the reminder before handoff. Failed usually means it got handed off and then broke later.',
   'The safeguard snapshot in History is the best operational evidence for why a reminder was blocked.',
   'Retrying without fixing the root cause usually just reproduces the same blocked result.'
  ],
  faqs: [
   { question: 'What is the most common block reason in testing?', answer: 'In dev and QA, the recipient allowlist is often the most common reason because the environment is intentionally restricted.' },
   { question: 'Can I retry a blocked reminder immediately?', answer: 'Yes, but only after the root cause is fixed. Otherwise the retry is likely to be blocked again.' },
   { question: 'Where do I see the exact reason?', answer: 'Open History, choose the reminder row, and review the blocked reason and safeguard snapshot.' }
  ]
 },
 {
  id: 'help_delivery_2',
  category: 'delivery',
  title: 'Queued, sent, delivered, and failed statuses explained',
  summary: 'History shows where a reminder is in the pipeline, from internal acceptance through provider handoff and downstream delivery evidence.',
  bullets: [
   'Queued means SoarOne accepted the reminder and is waiting for provider confirmation.',
   'Sent means the provider accepted the message for delivery.',
   'Delivered means the provider reported delivery.',
   'Failed usually means a provider or delivery-path issue that needs review.',
   'Blocked means SoarOne intentionally stopped the reminder before send.'
  ],
  details: [
   'Queued does not necessarily mean something is wrong. It often means the system is waiting for the provider event pipeline to catch up.',
   'Sent and Delivered are different. Sent means accepted by the provider; Delivered means the provider says it reached the destination mailbox.',
   'Failed usually needs a closer look at sender state, provider detail, and webhook evidence.'
  ],
  faqs: [
   { question: 'Is queued bad?', answer: 'Not always. Queued is often temporary. It becomes more suspicious only if it stays queued too long without provider updates.' },
   { question: 'Why do I have sent but not delivered?', answer: 'Because the provider accepted the reminder, but delivery confirmation has not arrived or may never arrive depending on the provider event path.' },
   { question: 'What should I do with failed reminders?', answer: 'Inspect History, review provider detail, check sender configuration, and retry only after the likely cause is understood.' }
  ]
 },
 {
  id: 'help_delivery_3',
  category: 'delivery',
  title: 'How to troubleshoot reminder delivery step by step',
  summary: 'Use this workflow when a customer says reminders are not going out or you see inconsistent send states in History.',
  bullets: [
   'Start in History, not Templates.',
   'Separate blocked issues from failed issues before deciding on the next step.',
   'Use Support when the issue spans multiple reminders or companies.'
  ],
  howTo: [
   'Open History and locate the affected reminder.',
   'Check the status first: blocked, queued, sent, delivered, or failed.',
   'If blocked, read the blocked reason and safeguard snapshot.',
   'If failed, review the provider detail and sender setup.',
   'If queued, give the provider path a short time and then re-check.',
   'If the same issue appears repeatedly, ask Support to summarize the main delivery risks across the account.'
  ],
  faqs: [
   { question: 'Where should I start when a reminder looks wrong?', answer: 'Start with History because it shows the real status, timeline, and evidence.' },
   { question: 'When should I escalate to human support?', answer: 'Escalate when the same issue repeats, the cause is ambiguous, or a provider-side problem needs manual review.' }
  ]
 },
 {
  id: 'help_compliance_1',
  category: 'compliance',
  title: 'Template compliance basics',
  summary: 'Templates can be blocked or flagged if they sound threatening, misleading, coercive, or otherwise unsafe for collections-style communication.',
  bullets: [
   'Run compliance review before enabling templates.',
   'Remove threatening or coercive wording.',
   'Keep sender address complete.',
   'Respect disputes and opt-outs before retrying reminders.'
  ],
  details: [
   'Compliance is not just about wording. It also includes whether you are respecting disputes, opt-outs, and sender identity requirements.',
   'A good template is clear, factual, and professional without sounding threatening or deceptive.',
   'If a template is flagged, fix the risky language before enabling it live.'
  ],
  faqs: [
   { question: 'What language usually gets flagged?', answer: 'Threats, pressure tactics, misleading urgency, or language that overstates consequences are common triggers.' },
   { question: 'Does compliance review replace human judgment?', answer: 'No. It helps reduce risk, but final responsibility still sits with your team.' }
  ]
 },
 {
  id: 'help_billing_1',
  category: 'billing',
  title: 'How to change plan',
  summary: 'Your plan controls connection limits, support tier, sender options, and some advanced operational features like compliance tooling and export access.',
  bullets: [
   'Open Account.',
   'Review your current subscription state and plan limits.',
   'Use the plan and billing controls to change plan.',
   'Starter uses the system sender, while Pro and CPA unlock custom sender and stronger support tiers.'
  ],
  details: [
   'Starter is best for single-company usage with the system sender.',
   'Pro is usually the right step when you want custom sender setup, more connections, and stronger support handling.',
   'CPA is designed for higher-volume or multi-client workflows.'
  ],
  faqs: [
   { question: 'Where do I change billing details?', answer: 'Open Account and use the billing and subscription controls there.' },
   { question: 'Which plan unlocks custom sender?', answer: 'Pro and CPA unlock custom sender functionality.' },
   { question: 'Will changing plan affect existing history?', answer: 'No, plan changes affect limits and features going forward, not the historical reminder record.' }
  ]
 },
 {
  id: 'help_billing_2',
  category: 'billing',
  title: 'Billing and subscription FAQ',
  summary: 'Answers to the most common plan and subscription questions in one place.',
  bullets: [
   'Use this article when the question is about plan limits, renewals, or feature access rather than reminder troubleshooting.'
  ],
  faqs: [
   { question: 'How do I know which plan I am on?', answer: 'Open Account and review the subscription section. It shows your current plan and billing state.' },
   { question: 'Why can’t I add another company?', answer: 'You may be at your current plan limit. Check the plan description and connection count in Main and Account.' },
   { question: 'Why can’t I use custom SMTP?', answer: 'Custom sender setup is not available on Starter. Upgrade to Pro or CPA first.' },
   { question: 'Does support tier change by plan?', answer: 'Yes. Higher plans get stronger support routing and faster escalation handling.' }
  ]
 },
 {
  id: 'help_tabs_1',
  category: 'tabs',
  title: 'What each tab does',
  summary: 'Each tab has a clear operational role. This guide explains what to use each area for and where to go first when something looks wrong.',
  bullets: [
   'Main: QuickBooks connections and sync status.',
   'Insights: AR trends and portfolio analytics.',
   'History: reminder statuses, retries, and delivery evidence.',
   'Support: Help Center and support chat.',
   'Templates: reminder templates and sequencing.',
   'SMTP: sender verification and sender settings.',
   'Compliance: disputes, opt-outs, and template safety.',
   'Account: profile, billing, and subscription settings.'
  ],
  details: [
   'Main is for connection health and sync actions.',
   'History is for operational truth: reminder state, timeline, retry behavior, and evidence.',
   'Support is for either self-service articles or an account-aware support conversation.',
   'Account is where you manage identity, mailing address, and billing.',
   'If you are unsure where to start, use History for delivery questions and Main for QuickBooks questions.'
  ],
  faqs: [
   { question: 'Where do I troubleshoot blocked reminders?', answer: 'History first, then Support if you need a plain-language explanation or account-level summary.' },
   { question: 'Where do I change sender details?', answer: 'Use SMTP for sender identity and Account for mailing address details.' },
   { question: 'Where do I change plan?', answer: 'Open Account.' }
  ]
 },
 {
  id: 'help_faq_1',
  category: 'getting_started',
  title: 'General FAQ',
  summary: 'Quick answers to the most common “how does this work?” questions about SoarOne.',
  bullets: [
   'This article is a fast reference when you need a short answer instead of a full walkthrough.'
  ],
  faqs: [
   { question: 'What does SoarOne do?', answer: 'SoarOne helps manage AR reminder workflows by connecting to QuickBooks, applying safeguards, and tracking reminder outcomes in History.' },
   { question: 'What is the difference between Help and Support?', answer: 'Help is self-service documentation. Support is an account-aware assistant that can explain live issues and escalate to a human when needed.' },
   { question: 'What should I check first when something looks wrong?', answer: 'Check History for reminder evidence and Main for QuickBooks connection health.' },
   { question: 'Can Support see my account context?', answer: 'Yes. The support assistant is designed to answer using your live account context, recent reminder history, and relevant help content.' }
  ]
 }
];

// --- Paddle Configuration ---
// Frontend uses public Paddle values via CRA env vars.
// Set these in hosting/client/.env.local (or your CI environment).
const PADDLE_CLIENT_TOKEN = (process.env.REACT_APP_PADDLE_CLIENT_TOKEN || '').trim();
const PADDLE_ENVIRONMENT = ((process.env.REACT_APP_PADDLE_ENVIRONMENT || 'sandbox').toLowerCase() === 'production'
 ? 'production'
 : 'sandbox') as 'sandbox' | 'production';

const PADDLE_PRICE_INSIGHTS = (process.env.REACT_APP_PADDLE_PRICE_INSIGHTS || '').trim();
const PADDLE_PRICE_IDS: Record<PlanId, string> = {
 starter: (process.env.REACT_APP_PADDLE_PRICE_STARTER || '').trim(),
 pro: (process.env.REACT_APP_PADDLE_PRICE_PRO || '').trim(),
 cpa: (process.env.REACT_APP_PADDLE_PRICE_CPA || '').trim(),
};

const isValidPaddleToken = (value: string) => /^(test_|live_|Test_|Live_)/.test(value);
const isValidPaddlePriceId = (value: string) => /^pri_[A-Za-z0-9_]+$/.test(value);
const getPaddleTokenIssues = (): string[] => {
 const issues: string[] = [];
 if (!isValidPaddleToken(PADDLE_CLIENT_TOKEN)) issues.push('REACT_APP_PADDLE_CLIENT_TOKEN');
 return issues;
};
const getPlanPaddleConfigIssues = (): string[] => {
 const issues = getPaddleTokenIssues();
 if (!isValidPaddlePriceId(PADDLE_PRICE_IDS.starter)) issues.push('REACT_APP_PADDLE_PRICE_STARTER');
 if (!isValidPaddlePriceId(PADDLE_PRICE_IDS.pro)) issues.push('REACT_APP_PADDLE_PRICE_PRO');
 if (!isValidPaddlePriceId(PADDLE_PRICE_IDS.cpa)) issues.push('REACT_APP_PADDLE_PRICE_CPA');
 return issues;
};
const getInsightsPaddleConfigIssues = (): string[] => {
 const issues = getPaddleTokenIssues();
 if (!isValidPaddlePriceId(PADDLE_PRICE_INSIGHTS)) issues.push('REACT_APP_PADDLE_PRICE_INSIGHTS');
 return issues;
};
const isPaddleLoaderConfigured = () => getPaddleTokenIssues().length === 0;
const isPlanPaddleConfigured = () => getPlanPaddleConfigIssues().length === 0;

// --- Typography System ---
const FONT_STYLE = document.createElement('style');
FONT_STYLE.textContent = `
 @font-face {
  font-family: 'Geist Sans';
  src: url('/fonts/Geist-Variable.woff2') format('woff2');
  font-style: normal;
  font-weight: 100 900;
  font-display: swap;
 }

 @font-face {
  font-family: 'Geist Sans';
  src: url('/fonts/Geist-Italic-Variable.woff2') format('woff2');
  font-style: italic;
  font-weight: 100 900;
  font-display: swap;
 }
 :root {
  --font-display: 'Geist Sans', 'DM Sans', system-ui, sans-serif;
  --font-body: 'DM Sans', sans-serif;
 }
 
 body, html {
  font-family: var(--font-body);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  letter-spacing: -0.01em;
 }
 
 h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-display);
 }
`;
if (!document.getElementById('soarone-fonts')) {
 FONT_STYLE.id = 'soarone-fonts';
 document.head.appendChild(FONT_STYLE);
}

// --- Paddle.js Initialization ---
let paddleInitPromise: Promise<any> | null = null;
let paddleInitialized = false;

const initializePaddle = (): Promise<any> => {
 if (paddleInitialized && (window as any).Paddle) {
  return Promise.resolve((window as any).Paddle);
 }
 if (paddleInitPromise) return paddleInitPromise;
 if (!isPaddleLoaderConfigured()) {
  const issues = getPaddleTokenIssues().join(', ');
  console.warn('[Paddle] Missing config:', issues);
  return Promise.reject(new Error(`Missing Paddle config: ${issues}`));
 }

 paddleInitPromise = new Promise((resolve, reject) => {
  const finishInit = () => {
   const Paddle = (window as any).Paddle;
   if (!Paddle) {
    paddleInitPromise = null;
    reject(new Error('Paddle.js loaded, but window.Paddle is unavailable.'));
    return;
   }
   if (!paddleInitialized) {
    if (PADDLE_ENVIRONMENT === 'sandbox') {
     Paddle.Environment.set('sandbox');
    }
    Paddle.Initialize({
     token: PADDLE_CLIENT_TOKEN,
     eventCallback: (event: any) => {
      if (event.name === 'checkout.completed') {
       console.log('[Paddle] Checkout completed successfully');
       window.location.href = window.location.origin + window.location.pathname + '?checkout=success';
      }
      if (event.name === 'checkout.closed') {
       console.log('[Paddle] Checkout closed by user');
      }
      if (event.name === 'checkout.error') {
       const details =
        event?.data?.error?.detail ||
        event?.data?.error?.message ||
        event?.data?.detail ||
        event?.data?.message ||
        'Unknown checkout error';
       console.error('[Paddle] Checkout error:', { details, event });
      }
     }
    });
    paddleInitialized = true;
    console.log(`[Paddle] Initialized in ${PADDLE_ENVIRONMENT} mode`);
   }
   resolve(Paddle);
  };

  const existingScript = document.getElementById('paddle-js') as HTMLScriptElement | null;
  if ((window as any).Paddle) {
   finishInit();
   return;
  }
  if (existingScript) {
   existingScript.addEventListener('load', finishInit, { once: true });
   existingScript.addEventListener('error', () => {
    paddleInitPromise = null;
    reject(new Error('Failed to load Paddle.js script.'));
   }, { once: true });
   window.setTimeout(() => {
    if (!(window as any).Paddle) {
     paddleInitPromise = null;
     reject(new Error('Timed out waiting for Paddle.js to become available.'));
    }
   }, 8000);
   return;
  }

  const script = document.createElement('script');
  script.id = 'paddle-js';
  script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
  script.async = true;
  script.onload = finishInit;
  script.onerror = () => {
   paddleInitPromise = null;
   console.error('[Paddle] Failed to load Paddle.js script');
   reject(new Error('Failed to load Paddle.js script.'));
  };
  document.head.appendChild(script);
 });

 return paddleInitPromise;
};
void initializePaddle().catch((err) => {
 console.error('[Paddle] Initialization failed:', err);
});

const envFirebaseConfig = {
 apiKey: (process.env.REACT_APP_FIREBASE_API_KEY || '').trim(),
 authDomain: (process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '').trim(),
 projectId: (process.env.REACT_APP_FIREBASE_PROJECT_ID || '').trim(),
 storageBucket: (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '').trim(),
 messagingSenderId: (process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '').trim(),
 appId: (process.env.REACT_APP_FIREBASE_APP_ID || '').trim(),
 measurementId: (process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '').trim()
};

interface OnboardingStep {
 id: string;
 title: string;
 description: string;
 complete: boolean;
 actionLabel: string;
 actionKey: 'account' | 'quickbooks' | 'settings' | 'connections';
}

const getPreferredAuthDomain = () => {
 try {
  const currentHost = window.location.hostname;
  const appHost = APP_SITE_URL ? new URL(APP_SITE_URL).hostname : '';
  const marketingHost = MARKETING_SITE_URL ? new URL(MARKETING_SITE_URL).hostname : '';

  if (!currentHost) return envFirebaseConfig.authDomain;

  // Keep auth pinned to the configured environment domain. Switching to the
  // current browser hostname causes popup auth failures whenever that host
  // is not explicitly whitelisted in Firebase Auth.
  if (envFirebaseConfig.authDomain) {
   return envFirebaseConfig.authDomain;
  }

  // If code ever renders on the marketing host, keep auth pinned to the app host.
  if (currentHost === marketingHost && appHost) {
   return appHost;
  }
 } catch (e) {
  // fall through to the configured auth domain
 }

 return envFirebaseConfig.authDomain;
};

const getFirebaseConfig = () => {
 const fallbackConfig = {
  ...envFirebaseConfig,
  authDomain: getPreferredAuthDomain()
 };
 try {
  const raw = (window as any).__firebase_config;
  const runtimeConfig = raw ? JSON.parse(raw) : {};
  return {
   ...runtimeConfig,
   ...Object.fromEntries(
    Object.entries(fallbackConfig).filter(([, value]) => Boolean(value))
   )
  };
 } catch (e) {
  return fallbackConfig;
 }
};

const getAppId = () => getFirebaseConfig().projectId || '';

const firebaseApp = initializeApp(getFirebaseConfig());
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp, 'asia-east2');
const aiFunctions = getFunctions(firebaseApp, 'us-central1');
const supportFunctions = getFunctions(firebaseApp, 'us-central1');
const currentAppId = getAppId();
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const PUBLIC_DATA_PATH = ['artifacts', currentAppId, 'public', 'data'] as const;
const QBO_AUTH_INIT_URL = (process.env.REACT_APP_QBO_AUTH_INIT_URL || '').trim();

// --- Sub-components ---
const CompanyPill: React.FC<{ realmId: string, connections: Connection[] }> = ({ realmId, connections }) => {
 const conn = connections.find(c => c.realmId === realmId);
 return (
  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border border-slate-100 bg-slate-50 text-slate-600 shadow-none">
   <Building size={10} className="opacity-70" />
   <span className="text-xs font-bold uppercase tracking-tight truncate max-w-[120px]">
    {conn?.companyName || 'Unknown Entity'}
   </span>
  </div>
 );
};

const StatusBadge: React.FC<{ status: string, error?: boolean }> = ({ status, error }) => {
 const isActive = status === 'active';
 return (
  <span className={`px-2.5 py-1 rounded-full text-xs font-medium uppercase tracking-wide flex items-center gap-1.5 border shadow-none ${
   isActive ? (error ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-emerald-50 text-emerald-600 border-emerald-100') : 'bg-rose-50 text-rose-700 border-rose-100'
  }`}>
   {isActive ? (error ? <AlertTriangle size={10} /> : <CheckCircle size={10} />) : <UserX size={10} />}
   {error ? 'SYNC ALERT' : status || 'UNKNOWN'}
  </span>
 );
};

const DeliveryBadge: React.FC<{ status: string }> = ({ status }) => {
 switch (status?.toLowerCase()) {
  case 'queued':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 text-amber-700 border border-amber-100 font-semibold text-[10px] uppercase tracking-wide"><Clock3 size={12} /> Queued</span>;
  case 'sent':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 text-slate-700 border border-slate-200 font-semibold text-[10px] uppercase tracking-wide"><Send size={12} /> Sent</span>;
  case 'opened':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100 font-semibold text-[10px] uppercase tracking-wide"><Eye size={12} /> Opened</span>;
  case 'delivered':
   return <span style={{ color: PRIMARY_BLUE }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-50 border border-cyan-100 font-semibold text-[10px] uppercase tracking-wide"><CheckCircle size={12} /> Delivered</span>;
  case 'blocked':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-50 text-amber-800 border border-amber-100 font-semibold text-[10px] uppercase tracking-wide"><AlertTriangle size={12} /> Blocked</span>;
  case 'failed':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-rose-50 text-rose-700 border border-rose-100 font-semibold text-[10px] uppercase tracking-wide"><AlertCircle size={12} /> Failed</span>;
  case 'opted_out':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-violet-50 text-violet-700 border border-violet-100 font-semibold text-[10px] uppercase tracking-wide"><MailIcon size={12} /> Opted Out</span>;
  case 'disputed':
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-orange-50 text-orange-700 border border-orange-100 font-semibold text-[10px] uppercase tracking-wide"><Shield size={12} /> Disputed</span>;
  default:
   return <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 text-slate-500 border border-slate-100 font-semibold text-[10px] uppercase tracking-wide"><Info size={12} /> Unknown</span>;
 }
};

const Toast: React.FC<AppNotification & { onClose: () => void }> = ({ type, message, onClose }) => {
 const isSuccess = type === 'success';
 const isError = type === 'error';
 return (
  <div className={`flex items-center gap-4 p-4 rounded-xl border animate-in slide-in-from-right-full duration-300 mb-3 min-w-[280px] bg-white shadow-none ${
   isSuccess ? 'border-emerald-100' : isError ? 'border-rose-100' : 'border-slate-100'
  }`}>
   <div className={`p-2 rounded-lg shrink-0 ${
    isSuccess ? 'bg-emerald-50 text-emerald-600' : isError ? 'bg-rose-100 text-rose-600' : 'bg-slate-50'
   }`}>
    {isSuccess ? <CheckCircle size={18} /> : isError ? <AlertCircle size={18} /> : <Bell size={18} />}
   </div>
   <p className={`text-xs sm:text-xs font-medium flex-1 ${isError ? 'text-rose-900' : 'text-slate-700'}`}>{message}</p>
   <button onClick={onClose} className="text-slate-300 hover:text-slate-500 transition-colors p-1.5"><X size={14} /></button>
  </div>
 );
};

// --- Debounce Hook ---
// FIX: Used to prevent Firestore writes on every keystroke in tier editing
function useDebounce<T>(value: T, delay: number): T {
 const [debouncedValue, setDebouncedValue] = useState<T>(value);
 useEffect(() => {
  const handler = setTimeout(() => setDebouncedValue(value), delay);
  return () => clearTimeout(handler);
 }, [value, delay]);
 return debouncedValue;
}

const getTimestampMs = (value: DunningLog['sentAt']): number => {
 if (!value) return 0;
 if (value instanceof Date) return value.getTime();
 if (typeof value === 'string') {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
 }
 if (typeof value === 'object') {
  if (typeof value.toDate === 'function') {
   const parsed = value.toDate().getTime();
   return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value.seconds === 'number') {
   return value.seconds * 1000;
  }
 }
 return 0;
};

const formatTimestamp = (value: DunningLog['sentAt']): string => {
 const timestampMs = getTimestampMs(value);
 return timestampMs ? new Date(timestampMs).toLocaleString() : '—';
};

const formatCurrency = (value?: number | null): string => {
 if (typeof value !== 'number' || Number.isNaN(value)) return '—';
 return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const getHistoryStatusNarrative = (log: DunningLog): string => {
 switch ((log.deliveryStatus || '').toLowerCase()) {
  case 'queued':
   return 'SoarOne accepted this reminder and is waiting for SendGrid to confirm provider processing.';
  case 'sent':
   return 'SendGrid confirmed provider processing. The reminder was handed off for delivery.';
  case 'delivered':
   return 'The provider reported this reminder as delivered to the recipient mailbox.';
  case 'opened':
   return 'The recipient engaged with the reminder after delivery.';
  case 'failed':
   return log.errorDetail || 'The provider reported that this reminder could not be delivered.';
  case 'blocked':
   return getBlockedReasonLabel(log.blockedReasonCode);
  case 'opted_out':
   return log.errorDetail || 'The recipient unsubscribed from automated payment reminders.';
  case 'disputed':
   return log.errorDetail || 'A dispute was filed for this invoice and future reminders should pause until it is resolved.';
  default:
   return 'Status evidence is limited for this reminder.';
 }
};

const getPdfModeLabel = (mode?: DunningLog['invoicePdfMode']): string => {
 switch ((mode || '').toLowerCase()) {
  case 'branded':
   return 'Branded invoice PDF';
  case 'fallback':
   return 'Fallback invoice PDF';
  case 'none':
   return 'No invoice PDF';
  default:
   return 'Unknown';
 }
};

const getSafeguardSnapshotLabel = (key: string, value?: string): string => {
 const normalized = (value || '').toLowerCase();
 switch (key) {
  case 'recipientPolicy':
   return normalized === 'allowed' ? 'Allowed by recipient policy' : normalized === 'blocked' ? 'Blocked by recipient policy' : 'Recipient policy not recorded';
  case 'optOutCheck':
   return normalized === 'clear' ? 'Recipient not opted out' : normalized === 'blocked' ? 'Recipient has opted out' : 'Opt-out check not recorded';
  case 'disputeCheck':
   return normalized === 'clear' ? 'No active dispute found' : normalized === 'blocked' ? 'Active dispute blocked send' : 'Dispute check not recorded';
  case 'recentSendCheck':
   return normalized === 'clear' ? 'No recent duplicate reminder' : normalized === 'blocked' ? 'Recent-send protection blocked this stage' : 'Recent-send check not recorded';
  case 'frequencyCapCheck':
   return normalized === 'clear' ? 'Recipient below frequency cap' : normalized === 'blocked' ? 'Recipient hit the frequency cap' : 'Frequency cap check not recorded';
  case 'mailingAddressCheck':
   return normalized === 'complete' ? 'Mailing address present' : normalized === 'missing' ? 'Mailing address missing' : 'Mailing address check not recorded';
  case 'templateComplianceCheck':
   return normalized === 'passed'
    ? 'Template passed current compliance checks'
    : normalized === 'deterministic_blocked'
     ? 'Template blocked by deterministic compliance rules'
     : normalized === 'ai_flagged'
      ? 'Template flagged by AI compliance review'
      : normalized === 'not_reviewed'
       ? 'Template not yet AI-reviewed'
       : 'Template compliance state not recorded';
  case 'senderVerificationCheck':
   return normalized === 'system_sender'
    ? 'Using system sender'
    : normalized === 'verified'
     ? 'Custom sender verified'
     : normalized === 'blocked'
      ? 'Custom sender not verified'
      : normalized === 'check_failed'
       ? 'Custom sender verification could not be confirmed'
       : normalized === 'missing'
        ? 'Sender email missing'
        : 'Sender verification state not recorded';
  default:
   return value || 'Not recorded';
 }
};

const getBlockedReasonLabel = (reasonCode?: string): string => {
 switch ((reasonCode || '').toLowerCase()) {
  case 'recipient_whitelist':
   return 'Blocked by environment recipient allowlist';
  case 'opt_out':
   return 'Blocked because the recipient unsubscribed';
  case 'dispute_hold':
   return 'Blocked because the invoice is under dispute review';
  case 'recent_send':
   return 'Blocked by recent-send protection';
  case 'frequency_cap':
   return 'Blocked by recipient frequency cap';
  case 'missing_sender':
   return 'Blocked because no sender email is configured';
  case 'missing_mailing_address':
   return 'Blocked because the mailing address is incomplete';
  case 'template_validation':
   return 'Blocked by deterministic compliance rules';
  case 'template_review_flagged':
   return 'Blocked because AI compliance review flagged the template';
  case 'custom_sender_unverified':
   return 'Blocked because the custom sender is not verified';
  case 'custom_sender_check_failed':
   return 'Blocked because custom sender verification could not be confirmed';
  case 'missing_recipient':
   return 'Blocked because the customer email is missing';
  default:
   return 'Blocked';
 }
};

const getBlockedReasonNextStep = (reasonCode?: string): string => {
 switch ((reasonCode || '').toLowerCase()) {
  case 'recipient_whitelist':
   return 'Add the recipient to SENDGRID_ALLOWED_RECIPIENTS, then retry the reminder.';
  case 'opt_out':
   return 'Keep this reminder blocked unless the recipient explicitly asks to be resubscribed.';
  case 'dispute_hold':
   return 'Resolve the dispute first, then retry only if payment reminders should resume.';
  case 'recent_send':
   return 'Wait for the recent-send window to pass before retrying this reminder stage.';
  case 'frequency_cap':
   return 'Wait until the recipient drops below the frequency cap, then retry.';
  case 'missing_sender':
   return 'Configure a sender email in Account or enable a verified custom sender, then retry.';
  case 'missing_mailing_address':
   return 'Update the mailing address in Account, save changes, and retry.';
  case 'template_validation':
   return 'Remove the prohibited language from the template, save it, and retry.';
  case 'template_review_flagged':
   return 'Revise the template and run AI compliance review again before retrying.';
  case 'custom_sender_unverified':
   return 'Verify the custom sender identity, then retry.';
  case 'custom_sender_check_failed':
   return 'Confirm sender verification in SendGrid and retry once verification can be checked.';
  case 'missing_recipient':
   return 'Add a valid customer email address to the invoice record, then retry.';
  default:
   return 'Review the safeguard details above, fix the underlying issue, and retry when ready.';
 }
};

const getHistoryDateValue = (log: DunningLog): DunningLog['sentAt'] => {
 switch ((log.deliveryStatus || '').toLowerCase()) {
  case 'opened':
   return log.openedAt || log.lastEventAt || log.deliveredAt || log.sentAt || log.queuedAt || log.createdAt || null;
  case 'delivered':
   return log.deliveredAt || log.lastEventAt || log.sentAt || log.queuedAt || log.createdAt || null;
  case 'sent':
   return log.sentAt || log.lastEventAt || log.queuedAt || log.createdAt || null;
  case 'failed':
   return log.failedAt || log.lastEventAt || log.createdAt || null;
  case 'blocked':
   return log.blockedAt || log.createdAt || null;
  case 'queued':
   return log.queuedAt || log.createdAt || null;
  case 'opted_out':
   return log.lastEventAt || log.createdAt || null;
  case 'disputed':
   return log.lastEventAt || log.createdAt || null;
  default:
   return log.lastEventAt || log.createdAt || log.sentAt || log.blockedAt || null;
 }
};

const mapAuthError = (err: any, mode: 'login' | 'register' = 'login') => {
 const code = err?.code || '';
 switch (code) {
  case 'auth/email-already-in-use':
   return 'An account already exists for that email. Try signing in instead.';
  case 'auth/invalid-email':
   return 'Enter a valid email address.';
  case 'auth/weak-password':
   return 'Use a stronger password with at least 8 characters.';
  case 'auth/invalid-credential':
  case 'auth/wrong-password':
  case 'auth/user-not-found':
   return mode === 'login'
    ? 'Email or password is incorrect.'
    : 'We could not create the account with those credentials.';
  case 'auth/popup-closed-by-user':
  case 'auth/cancelled-popup-request':
   return 'The Google sign-in window was closed before finishing.';
  case 'auth/too-many-requests':
   return 'Too many attempts. Please wait a moment and try again.';
  case 'auth/requires-recent-login':
   return 'This action requires a recent sign-in. Please sign out and sign back in.';
  case 'auth/email-not-verified':
   return 'Please verify your email address before continuing.';
  case 'auth/account-exists-with-different-credential':
   return 'An account already exists with a different sign-in method for that email.';
  case 'auth/network-request-failed':
   return 'Network error. Check your connection and try again.';
  case 'auth/user-disabled':
   return 'This account has been disabled. Contact support for help.';
  case 'auth/expired-action-code':
   return 'This link has expired. Please request a new one.';
  case 'auth/invalid-action-code':
   return 'This link is invalid or has already been used.';
  case 'auth/operation-not-allowed':
   return 'This sign-in method is not enabled. Contact support.';
  case 'auth/multi-factor-auth-required':
   return 'Two-factor authentication is required. Please complete the 2FA step.';
  default:
   return err?.message || 'We could not complete that request. Please try again.';
 }
};

// --- ITEM 25: GLOBAL ERROR BOUNDARY ---
interface ErrorBoundaryState { hasError: boolean; errorMessage: string; }
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
 constructor(props: any) {
  super(props);
  this.state = { hasError: false, errorMessage: '' };
 }
 static getDerivedStateFromError(error: Error) {
  return { hasError: true, errorMessage: error?.message || 'Unknown error' };
 }
 componentDidCatch(error: Error, info: React.ErrorInfo) {
  console.error('[ErrorBoundary]', error, info.componentStack);
  try {
   const { getFunctions: getF, httpsCallable: hc } = require('firebase/functions');
   const { getApp } = require('firebase/app');
   const fns = getF(getApp(), 'asia-east2');
   hc(fns, 'reportClientError')({ message: error?.message, componentStack: info.componentStack?.slice(0, 2000), url: window.location.href, appVersion: '1.0' }).catch(() => {});
  } catch { /* ignore */ }
 }
 render() {
  if (!this.state.hasError) return this.props.children;
  return (
   <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', background: '#f8fafc', padding: '32px' }}>
    <div style={{ maxWidth: 480, textAlign: 'center' }}>
     <div style={{ width: 64, height: 64, background: '#fef2f2', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
     </div>
     <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Something went wrong</h2>
     <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24, lineHeight: 1.6 }}>An unexpected error occurred. Our team has been notified. Try refreshing the page — if the problem persists, contact support.</p>
     <p style={{ fontSize: 11, color: '#94a3b8', background: '#f1f5f9', padding: '8px 12px', borderRadius: 8, marginBottom: 24, fontFamily: 'monospace', wordBreak: 'break-all' }}>{this.state.errorMessage}</p>
     <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
      <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', background: '#51e2f5', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Refresh</button>
      <a href="mailto:support@soar-one.com" style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, color: '#475569', textDecoration: 'none', display: 'inline-block' }}>Contact Support</a>
     </div>
    </div>
   </div>
  );
 }
}

// --- ADMIN PANEL (Secret Route: #admin) ---
// Requires Firebase Auth + admin custom claim (no more static password)

const AdminPanel: React.FC = () => {
 const [authed, setAuthed] = useState(false);
 const [viewerIsOwner, setViewerIsOwner] = useState(false);
 const [authChecking, setAuthChecking] = useState(true);
 const [loading, setLoading] = useState(false);
 const [tab, setTab] = useState<'users' | 'connections' | 'disputes' | 'support' | 'admins' | 'system'>('users');
 const [users, setUsers] = useState<any[]>([]);
 const [allConnections, setAllConnections] = useState<any[]>([]);
 const [stats, setStats] = useState<any>(null);
 const [disputes, setDisputes] = useState<any[]>([]);
 const [optOuts, setOptOuts] = useState<any[]>([]);
 const [killSwitchEnabled, setKillSwitchEnabled] = useState(false);
 const [killSwitchLoading, setKillSwitchLoading] = useState(false);
 const [backupConfig, setBackupConfig] = useState<any>(null);
 const [backupConfigLoading, setBackupConfigLoading] = useState(false);
 const [backupSaving, setBackupSaving] = useState(false);
 const [backupRunning, setBackupRunning] = useState(false);
 const [backupEnabled, setBackupEnabled] = useState(false);
 const [backupInterval, setBackupInterval] = useState(24);
 const [incidentTitle, setIncidentTitle] = useState('');
 const [incidentBody, setIncidentBody] = useState('');
 const [incidentStatus, setIncidentStatus] = useState<'investigating' | 'monitoring' | 'resolved' | 'scheduled'>('investigating');
 const [incidentSaving, setIncidentSaving] = useState(false);
 const [supportThreads, setSupportThreads] = useState<SupportThread[]>([]);
 const [supportAnalytics, setSupportAnalytics] = useState<SupportAnalytics | null>(null);
 const [selectedSupportCustomerUid, setSelectedSupportCustomerUid] = useState<string | null>(null);
 const [selectedAdminSupportThreadId, setSelectedAdminSupportThreadId] = useState<string | null>(null);
 const [adminSupportMessages, setAdminSupportMessages] = useState<SupportMessage[]>([]);
 const [adminSupportHandoffs, setAdminSupportHandoffs] = useState<SupportHandoff[]>([]);
 const [adminSupportInput, setAdminSupportInput] = useState('');
 const [adminSupportSearch, setAdminSupportSearch] = useState('');
 const [adminSupportStatusFilter, setAdminSupportStatusFilter] = useState('all');
 const [adminSupportIssueFilter, setAdminSupportIssueFilter] = useState('all');
 const [adminSupportTierFilter, setAdminSupportTierFilter] = useState('all');
 const [adminSupportAgeFilter, setAdminSupportAgeFilter] = useState('all');
 const [adminSupportEscalatedFilter, setAdminSupportEscalatedFilter] = useState('all');
 const [adminSupportWaitingFilter, setAdminSupportWaitingFilter] = useState('all');
 const [supportLoading, setSupportLoading] = useState(false);
 const [msg, setMsg] = useState('');
 const [userSearch, setUserSearch] = useState('');

 const [adminMfaResolver, setAdminMfaResolver] = useState<any>(null);
 const [adminMfaCode, setAdminMfaCode] = useState('');
 const [adminMfaLoading, setAdminMfaLoading] = useState(false);

 const firebaseConfig = useMemo(() => getFirebaseConfig(), []);
 const firebaseApp = useMemo(() => { try { return initializeApp(firebaseConfig, 'admin'); } catch { return initializeApp(firebaseConfig); } }, []);
 const adminAuth = useMemo(() => getAuth(firebaseApp), [firebaseApp]);
 const functions = useMemo(() => getFunctions(firebaseApp, 'asia-east2'), [firebaseApp]);
 const supportFunctions = useMemo(() => getFunctions(firebaseApp, 'us-central1'), [firebaseApp]);

 // Check admin claim on the signed-in user's token
 useEffect(() => {
  const unsub = onAuthStateChanged(adminAuth, async (user) => {
   if (user) {
    try {
     const tokenResult = await user.getIdTokenResult(true);
     const hasAdminAccess = tokenResult.claims.admin === true || tokenResult.claims.owner === true;
     setViewerIsOwner(tokenResult.claims.owner === true);
     if (hasAdminAccess) {
      setAuthed(true);
     } else {
      setMsg('Access denied. Your account does not have admin or owner privileges.');
      setAuthed(false);
     }
    } catch (e) {
     setMsg('Failed to verify admin credentials.');
     setAuthed(false);
    }
   } else {
    setAuthed(false);
    setViewerIsOwner(false);
   }
   setAuthChecking(false);
  });
  return () => unsub();
 }, [adminAuth]);

 const call = useCallback(async (fn: string, data: any = {}) => {
  const supportCallableNames = new Set([
   'supportChat',
   'supportExecuteSafeAction',
   'adminListSupportThreads',
   'adminGetSupportThread',
   'adminGetSupportAnalytics',
   'submitSupportFeedback'
  ]);
  const targetFunctions = supportCallableNames.has(fn) ? supportFunctions : functions;
  return httpsCallable(targetFunctions, fn)(data);
 }, [functions, supportFunctions]);

 const refresh = useCallback(async () => {
  setLoading(true);
  try {
   const [usersRes, statsRes] = await Promise.all([
    call('adminListUsers'),
    call('adminGetStats')
   ]);
   const ud = (usersRes as any).data;
   const sd = (statsRes as any).data;
   setUsers(ud.users || []);
   setAllConnections(ud.connections || []);
   setStats(sd);
   setDisputes(sd.disputesList || []);
   setOptOuts(sd.optOutsList || []);
  } catch (e: any) { setMsg('Error: ' + e.message); }
  setLoading(false);
 }, [call]);

 const loadSupportThreads = useCallback(async () => {
  try {
   const result: any = await call('adminListSupportThreads');
   setSupportThreads(result.data?.threads || []);
  } catch (e: any) {
   setMsg('Support load failed: ' + e.message);
  }
 }, [call]);

 const loadSupportAnalytics = useCallback(async () => {
  try {
   const result: any = await call('adminGetSupportAnalytics');
   setSupportAnalytics(result.data || null);
  } catch (e: any) {
   setMsg('Support analytics load failed: ' + e.message);
  }
 }, [call]);

 const loadBackupConfig = useCallback(async () => {
  setBackupConfigLoading(true);
  try {
   const result: any = await call('adminGetBackupConfig');
   const cfg = result.data || {};
   setBackupConfig(cfg);
   setBackupEnabled(cfg.enabled ?? false);
   setBackupInterval(cfg.minIntervalHours ?? 24);
  } catch (e: any) {
   setMsg('Backup config load failed: ' + e.message);
  } finally { setBackupConfigLoading(false); }
 }, [call]);

 const handleSaveBackupConfig = async () => {
  setBackupSaving(true);
  try {
   await call('adminSetBackupConfig', { enabled: backupEnabled, minIntervalHours: backupInterval });
   setMsg('Backup settings saved.');
   await loadBackupConfig();
  } catch (e: any) { setMsg('Save failed: ' + e.message); }
  finally { setBackupSaving(false); }
 };

 const handleRunBackupNow = async () => {
  if (!window.confirm('Start a manual Firestore backup now?')) return;
  setBackupRunning(true);
  try {
   const result: any = await call('adminTriggerBackupNow');
   setMsg(`Backup started → ${result.data?.destination || 'check Cloud Storage'}`);
   await loadBackupConfig();
  } catch (e: any) { setMsg('Backup failed: ' + e.message); }
  finally { setBackupRunning(false); }
 };

 const handleCreateIncident = async () => {
  if (!incidentTitle.trim()) return setMsg('Incident title is required.');
  setIncidentSaving(true);
  try {
   await call('adminCreateIncident', { title: incidentTitle.trim(), body: incidentBody.trim(), status: incidentStatus });
   setMsg(`Incident "${incidentTitle}" posted to status page.`);
   setIncidentTitle('');
   setIncidentBody('');
   setIncidentStatus('investigating');
  } catch (e: any) { setMsg('Failed to create incident: ' + e.message); }
  finally { setIncidentSaving(false); }
 };

 const loadSupportThreadDetail = useCallback(async (threadId: string) => {
  setSupportLoading(true);
  try {
   const result: any = await call('adminGetSupportThread', { threadId });
   setAdminSupportMessages(result.data?.messages || []);
   setAdminSupportHandoffs(result.data?.handoffs || []);
   setSelectedAdminSupportThreadId(threadId);
   const thread = result.data?.thread;
   if (thread?.customerUid) setSelectedSupportCustomerUid(thread.customerUid);
  } catch (e: any) {
   setMsg('Support thread load failed: ' + e.message);
  } finally {
   setSupportLoading(false);
  }
 }, [call]);

 const handleAdminSupportSend = useCallback(async () => {
  const message = adminSupportInput.trim();
  if (!message || !selectedSupportCustomerUid) return;
  setSupportLoading(true);
  try {
   const result: any = await call('supportChat', {
    threadId: selectedAdminSupportThreadId,
    message,
    mode: 'internal',
    selectedCustomerUid: selectedSupportCustomerUid
   });
   const nextThreadId = result.data?.threadId;
   setAdminSupportInput('');
   await loadSupportThreads();
   await loadSupportAnalytics();
   if (nextThreadId) {
    await loadSupportThreadDetail(nextThreadId);
   }
  } catch (e: any) {
   setMsg('Support reply failed: ' + e.message);
  } finally {
   setSupportLoading(false);
  }
 }, [adminSupportInput, selectedSupportCustomerUid, selectedAdminSupportThreadId, call, loadSupportThreads, loadSupportAnalytics, loadSupportThreadDetail]);

 const filteredAdminSupportThreads = useMemo(() => {
  const q = adminSupportSearch.trim().toLowerCase();
  const now = Date.now();
  return supportThreads
   .filter(thread => !selectedSupportCustomerUid || thread.customerUid === selectedSupportCustomerUid)
   .filter(thread => adminSupportStatusFilter === 'all' || String(thread.status || '').toLowerCase() === adminSupportStatusFilter)
   .filter(thread => adminSupportIssueFilter === 'all' || String(thread.issueType || '').toLowerCase() === adminSupportIssueFilter)
   .filter(thread => adminSupportTierFilter === 'all' || String(thread.supportTier || '').toLowerCase() === adminSupportTierFilter)
   .filter(thread => {
    if (adminSupportEscalatedFilter !== 'only') return true;
    return String(thread.status || '').toLowerCase() === 'escalated' || !!thread.escalationAt || !!thread.lastHandoffId;
   })
   .filter(thread => {
    if (adminSupportWaitingFilter !== 'only') return true;
    return thread.waitingOnCustomer === true;
   })
   .filter(thread => {
    if (adminSupportAgeFilter === 'all') return true;
    const ageMs = now - getTimestampMs(thread.updatedAt || thread.createdAt);
    if (adminSupportAgeFilter === '24h') return ageMs >= 24 * 60 * 60 * 1000;
    if (adminSupportAgeFilter === '72h') return ageMs >= 72 * 60 * 60 * 1000;
    return true;
   })
   .filter(thread => {
    if (!q) return true;
    return [
     thread.subject || '',
     thread.lastMessagePreview || '',
     thread.customerDisplayName || '',
     thread.customerEmail || '',
     thread.issueType || ''
    ].some(value => value.toLowerCase().includes(q));
   });
 }, [supportThreads, selectedSupportCustomerUid, adminSupportSearch, adminSupportStatusFilter, adminSupportIssueFilter, adminSupportTierFilter, adminSupportAgeFilter, adminSupportEscalatedFilter, adminSupportWaitingFilter]);

 useEffect(() => {
  if (!authed) return;
  refresh();
  loadSupportThreads();
  loadSupportAnalytics();
  loadBackupConfig();
  // Load kill switch state
  const appId = firebaseConfig.projectId || '';
  const db2 = getFirestore(firebaseApp);
  getDoc(doc(db2, 'artifacts', appId, 'system_config', 'kill_switch'))
   .then(snap => setKillSwitchEnabled(snap.exists() && snap.data()?.enabled === true))
   .catch(() => {});
 }, [authed, refresh, loadSupportThreads, loadSupportAnalytics, loadBackupConfig, firebaseApp, firebaseConfig]);

 const handleSetPlan = async (uid: string, plan: string) => {
  try { await call('adminSetUserPlan', { uid, plan }); setMsg(`Plan set to ${plan}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleExtendTrial = async (uid: string, plan: PlanId = 'starter') => {
  try { await call('adminSetUserPlan', { uid, plan, extendTrial: true }); setMsg(`${plan.toUpperCase()} trial extended 14 days`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleDeleteConn = async (realmId: string) => {
  if (!window.confirm(`Delete connection ${realmId}?`)) return;
  try { await call('adminDeleteConnection', { realmId }); setMsg('Connection deleted'); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleBulk = async (action: string) => {
  if (!window.confirm(`Confirm: ${action}?`)) return;
  try { const r = (await call('adminBulkAction', { action }) as any).data; setMsg(`Done. Affected: ${r.affected}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleDisableUser = async (uid: string, disable: boolean) => {
  if (!window.confirm(`${disable ? 'Disable' : 'Enable'} user ${uid}?`)) return;
  try { await call('adminToggleUser', { uid, disable }); setMsg(`User ${disable ? 'disabled' : 'enabled'}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleDeleteUser = async (uid: string, email: string) => {
  if (!window.confirm(`PERMANENTLY DELETE user ${email || uid}? This removes their Auth account, profile, settings, and all connections. This cannot be undone.`)) return;
  try { await call('adminDeleteUser', { uid }); setMsg(`User ${email} deleted`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleGrantAdmin = async (uid: string, email: string) => {
  if (!window.confirm(`Grant ADMIN privileges to ${email || uid}? They will be able to manage all users, connections, and system settings.`)) return;
  try { const r = (await call('adminGrantAdmin', { uid }) as any).data; setMsg(r.message || `Admin granted to ${email}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleRevokeAdmin = async (uid: string, email: string) => {
  if (!window.confirm(`Revoke admin privileges from ${email || uid}?`)) return;
  try { const r = (await call('adminRevokeAdmin', { uid }) as any).data; setMsg(r.message || `Admin revoked from ${email}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
 const handleSetSubscriptionStatus = async (uid: string, status: string) => {
  try { await call('adminUpdateUserSubscription', { uid, subscriptionStatus: status }); setMsg(`Subscription status set to ${status}`); refresh(); } catch (e: any) { setMsg(e.message); }
 };
const handleClearBilling = async (uid: string, email: string) => {
 if (!window.confirm(`Clear stored billing IDs for ${email || uid}? This does not cancel the actual Paddle subscription.`)) return;
 try { await call('adminUpdateUserSubscription', { uid, clearBilling: true }); setMsg(`Billing IDs cleared for ${email || uid}`); refresh(); } catch (e: any) { setMsg(e.message); }
};
const handleSetInsights = async (uid: string, email: string, enable: boolean) => {
 if (!window.confirm(`${enable ? 'Grant' : 'Revoke'} AR Insights for ${email || uid}?`)) return;
 try { await call('adminSetUserProducts', { uid, grant: enable, product: 'insights' }); setMsg(`AR Insights ${enable ? 'granted to' : 'revoked from'} ${email || uid}`); refresh(); } catch (e: any) { setMsg(e.message); }
};

 if (!authed) return (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center font-['DM_Sans',sans-serif]">
   <div className="bg-slate-900 p-10 rounded-2xl border border-slate-800 w-full max-w-sm">
    <a href={MARKETING_SITE_URL} className="inline-block mb-2">
     <img src="/brand-wordmark.png" alt="SoarOne" className="h-12 w-auto" />
    </a>
    <p className="text-xs text-slate-500 mb-8">System management console</p>
    {authChecking ? (
     <p className="text-sm text-slate-400 text-center py-4">Verifying admin credentials...</p>
    ) : adminMfaResolver ? (
     <>
      <p className="text-xs text-slate-400 mb-1">Two-factor authentication required.</p>
      <p className="text-xs text-slate-500 mb-4">Enter the code from your authenticator app.</p>
      <input
       type="text" inputMode="numeric" autoComplete="one-time-code"
       placeholder="6-digit code"
       value={adminMfaCode}
       onChange={e => setAdminMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
       onKeyDown={e => { if (e.key === 'Enter') {
        setAdminMfaLoading(true);
        const hint = adminMfaResolver.hints.find((h: any) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (!hint) { setMsg('No TOTP factor found.'); setAdminMfaLoading(false); return; }
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, adminMfaCode.replace(/\s/g, ''));
        adminMfaResolver.resolveSignIn(assertion)
         .then(() => { setAdminMfaResolver(null); setAdminMfaCode(''); })
         .catch(() => setMsg('Invalid code — check your authenticator app.'))
         .finally(() => setAdminMfaLoading(false));
       }}}
       className="w-full bg-slate-800 border border-slate-700 text-white text-center text-lg tracking-widest rounded-xl px-4 py-3 mb-3 outline-none focus:border-cyan-500"
      />
      <button
       disabled={adminMfaCode.length !== 6 || adminMfaLoading}
       onClick={() => {
        setAdminMfaLoading(true);
        const hint = adminMfaResolver.hints.find((h: any) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (!hint) { setMsg('No TOTP factor found.'); setAdminMfaLoading(false); return; }
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, adminMfaCode.replace(/\s/g, ''));
        adminMfaResolver.resolveSignIn(assertion)
         .then(() => { setAdminMfaResolver(null); setAdminMfaCode(''); })
         .catch(() => setMsg('Invalid code — check your authenticator app.'))
         .finally(() => setAdminMfaLoading(false));
       }}
       className="w-full bg-cyan-500 text-slate-900 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wide hover:bg-cyan-400 transition-all mb-3 disabled:opacity-40 disabled:cursor-not-allowed">
       {adminMfaLoading ? 'Verifying...' : 'Verify Code'}
      </button>
      {msg && <p className="text-xs text-rose-400 mt-3 text-center">{msg}</p>}
      <button onClick={() => { setAdminMfaResolver(null); setAdminMfaCode(''); setMsg(''); }}
       className="w-full text-slate-500 py-2 text-xs hover:text-slate-300 transition-colors mt-2">← Back</button>
     </>
    ) : (
     <>
      <p className="text-xs text-slate-400 mb-4">Admin access requires a Firebase account with the <code className="text-cyan-400">admin: true</code> or <code className="text-cyan-400">owner: true</code> custom claim. Sign in with your privileged account below.</p>
      <button onClick={async () => {
       setMsg('');
       try {
        await signInWithPopup(adminAuth, googleProvider);
       } catch (e: any) {
        if (e.code === 'auth/multi-factor-auth-required') {
         setAdminMfaResolver(getMultiFactorResolver(adminAuth, e));
         setAdminMfaCode('');
        } else {
         setMsg(e.message);
        }
       }
      }} className="w-full bg-cyan-500 text-slate-900 py-3.5 rounded-xl text-xs font-bold uppercase tracking-wide hover:bg-cyan-400 transition-all mb-3">Sign in with Google</button>
      {msg && <p className="text-xs text-rose-400 mt-3 text-center">{msg}</p>}
      <button onClick={() => { window.location.hash = ''; window.location.reload(); }}
       className="w-full text-slate-500 py-2 text-xs hover:text-slate-300 transition-colors mt-2">← Back to app</button>
     </>
    )}
   </div>
  </div>
 );

 return (
  <div className="min-h-screen bg-slate-950 font-['DM_Sans',sans-serif] text-white">
   <div className="max-w-7xl mx-auto p-8">
    {/* Header */}
    <div className="flex items-center justify-between mb-10">
     <div>
      <a href={MARKETING_SITE_URL} className="inline-block">
       <img src="/brand-wordmark.png" alt="SoarOne" className="h-12 w-auto" />
      </a>
      <p className="text-xs text-slate-500 mt-1">System Management Console {viewerIsOwner ? '· Owner Access' : '· Admin Access'}</p>
     </div>
     <div className="flex items-center gap-3">
      <button onClick={() => { window.location.hash = ''; window.location.reload(); }} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-xs font-medium hover:bg-slate-700 text-slate-400">Exit Admin</button>
     </div>
    </div>

    {msg && <div className="mb-6 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl text-xs text-cyan-400 flex justify-between items-center">{msg} <button onClick={() => setMsg('')} className="text-cyan-600">✕</button></div>}

    {/* Stats Bar */}
    {stats && (
     <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
     {[
       { label: 'Users', value: users.length },
       { label: 'Active Conns', value: `${stats.activeConnections}/${stats.totalConnections}` },
       { label: 'Emails Sent', value: stats.sentEmails },
       { label: 'Failed', value: stats.failedEmails, color: 'text-rose-400' },
       { label: 'Disputes', value: stats.pendingDisputes, color: stats.pendingDisputes > 0 ? 'text-amber-400' : '' },
       { label: 'Total AR', value: `$${(stats.totalAR || 0).toLocaleString()}` },
       { label: 'AI Reviews', value: stats.aiUsage?.totalReviews || 0 },
       { label: 'AI Cost', value: `$${Number(stats.aiUsage?.estimatedCostUsd || 0).toFixed(2)}` },
      ].map((s, i) => (
       <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{s.label}</p>
        <p className={`text-lg font-bold ${s.color || 'text-white'}`}>{s.value}</p>
       </div>
      ))}
     </div>
    )}

    {/* Tabs */}
    <div className="flex gap-2 mb-8 border-b border-slate-800 pb-3">
     {(['users', 'connections', 'disputes', 'support', 'admins', 'system'] as const).map(t => (
      <button key={t} onClick={() => setTab(t)} className={`px-5 py-2.5 rounded-lg text-xs font-semibold uppercase tracking-wide transition-all ${tab === t ? 'bg-cyan-500 text-slate-900' : 'text-slate-500 hover:bg-slate-900'}`}>{t}</button>
     ))}
    </div>

    {/* Users Tab */}
    {tab === 'users' && (
     <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
       <p className="text-xs text-slate-500">{users.length} registered user{users.length !== 1 ? 's' : ''}</p>
       <input type="text" placeholder="Search by name or email..." value={userSearch} onChange={e => setUserSearch(e.target.value)}
        className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-xs text-white outline-none focus:border-cyan-500 w-64" />
      </div>
      {users.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">No users found.</p>}
      {users.filter((u: any) => {
       if (!userSearch.trim()) return true;
       const q = userSearch.toLowerCase();
       return (u.email || '').toLowerCase().includes(q) || (u.displayName || '').toLowerCase().includes(q) || (u.uid || '').toLowerCase().includes(q);
      }).map((u: any) => (
       <div key={u.uid} className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
         <div className="flex items-center gap-3">
          {u.photoURL ? (
           <img src={u.photoURL} className="w-10 h-10 rounded-lg object-cover" alt="" />
          ) : (
           <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-500 text-xs font-bold uppercase">{(u.email || '?')[0]}</div>
          )}
          <div>
           <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            {u.displayName || 'Unnamed User'}
            {u.isOwner && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">Owner</span>}
            {u.isAdmin && !u.isOwner && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">Admin</span>}
            {u.disabled && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-400">Disabled</span>}
            {!u.hasProfile && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700 text-slate-500">No Profile</span>}
           </h3>
           <p className="text-[11px] text-slate-400">{u.email}</p>
           <p className="text-[10px] text-slate-600 font-mono mt-0.5">{u.uid}</p>
          </div>
         </div>
         <div className="flex flex-col items-end gap-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${
           u.plan === 'cpa' ? 'bg-purple-500/20 text-purple-400' :
           u.plan === 'pro' ? 'bg-cyan-500/20 text-cyan-400' :
           u.plan === 'none' ? 'bg-slate-800 text-slate-600' :
           'bg-slate-700 text-slate-400'
          }`}>
           {u.plan || 'none'}
          </span>
          <span className="text-[10px] text-slate-600">{u.provider === 'google.com' ? 'Google' : 'Email'}</span>
         </div>
        </div>
        {/* Stats row */}
        <div className="flex gap-6 mb-4 pb-4 border-b border-slate-800">
         <div><span className="text-[10px] text-slate-600">Connections</span><p className="text-sm font-semibold text-white">{u.connectionCount}</p></div>
         <div><span className="text-[10px] text-slate-600">Templates</span><p className="text-sm font-semibold text-white">{u.templateCount}</p></div>
         <div><span className="text-[10px] text-slate-600">Emails Sent</span><p className="text-sm font-semibold text-white">{u.emailsSent}</p></div>
         <div><span className="text-[10px] text-slate-600">SMTP</span><p className="text-sm font-semibold text-white">{u.useCustomSender ? u.senderEmail || 'On' : 'Off'}</p></div>
         <div><span className="text-[10px] text-slate-600">Signed Up</span><p className="text-xs text-slate-400">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</p></div>
         <div><span className="text-[10px] text-slate-600">Last Login</span><p className="text-xs text-slate-400">{u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString() : '—'}</p></div>
        {u.trialEndsAt && (
         <div><span className="text-[10px] text-slate-600">Trial Ends</span><p className="text-xs text-amber-400">{new Date(u.trialEndsAt).toLocaleDateString()}</p></div>
        )}
       </div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
         <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-2">Account</p>
          <p className="text-xs text-slate-300">Role: <span className="text-white font-semibold">{u.isOwner ? 'Owner' : u.isAdmin ? 'Admin' : 'User'}</span></p>
          <p className="text-xs text-slate-300 mt-1">State: <span className={`font-semibold ${u.disabled ? 'text-rose-400' : 'text-emerald-400'}`}>{u.disabled ? 'Disabled' : 'Active'}</span></p>
         </div>
         <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-2">Subscription</p>
          <p className="text-xs text-slate-300">Status: <span className="text-white font-semibold">{u.subscriptionStatus || 'none'}</span></p>
          <p className="text-xs text-slate-300 mt-1">AR Insights: <span className={`font-semibold ${(Array.isArray(u.activeProducts) && u.activeProducts.includes('insights')) ? 'text-emerald-400' : 'text-slate-400'}`}>{(Array.isArray(u.activeProducts) && u.activeProducts.includes('insights')) ? 'Enabled' : 'Off'}</span></p>
          <p className="text-[10px] text-slate-500 mt-1 break-all">Paddle sub: {u.paddleSubscriptionId || 'none'}</p>
          <p className="text-[10px] text-slate-500 mt-1 break-all">Paddle customer: {u.paddleCustomerId || 'none'}</p>
         </div>
         <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
          <p className="text-[10px] text-slate-600 uppercase tracking-wide mb-2">Billing Controls</p>
          <div className="flex flex-wrap gap-2">
           {(['trialing', 'active', 'past_due', 'paused', 'canceled'] as const).map(status => (
            <button key={status} onClick={() => handleSetSubscriptionStatus(u.uid, status)}
             className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border transition-all ${u.subscriptionStatus === status ? 'border-cyan-500/40 text-cyan-400 bg-cyan-500/10' : 'border-slate-700 text-slate-500 hover:text-slate-300'}`}>
             {status}
            </button>
           ))}
           <button onClick={() => handleClearBilling(u.uid, u.email)} className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-amber-700/50 text-amber-400 hover:bg-amber-500/10 transition-all">
            Clear Billing IDs
           </button>
           <button onClick={() => handleSetInsights(u.uid, u.email, !(Array.isArray(u.activeProducts) && u.activeProducts.includes('insights')))} className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border transition-all ${(Array.isArray(u.activeProducts) && u.activeProducts.includes('insights')) ? 'border-rose-700/50 text-rose-400 hover:bg-rose-500/10' : 'border-emerald-700/50 text-emerald-400 hover:bg-emerald-500/10'}`}>
            {(Array.isArray(u.activeProducts) && u.activeProducts.includes('insights')) ? 'Revoke AR Insights' : 'Grant AR Insights'}
           </button>
          </div>
         </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
         {(['starter', 'pro', 'cpa'] as const).map(p => (
          <button key={p} onClick={() => handleSetPlan(u.uid, p)} disabled={u.plan === p}
           className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border transition-all ${u.plan === p ? 'border-cyan-500/50 text-cyan-400 bg-cyan-500/10' : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300'}`}>
           Set {p}
          </button>
         ))}
         <button onClick={() => handleExtendTrial(u.uid, 'starter')} className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-amber-700/50 text-amber-500 hover:bg-amber-500/10 transition-all">
          Starter Trial
         </button>
         <button onClick={() => handleExtendTrial(u.uid, 'pro')} className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-cyan-700/50 text-cyan-400 hover:bg-cyan-500/10 transition-all">
          Pro Trial
         </button>
         <button onClick={() => handleExtendTrial(u.uid, 'cpa')} className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-purple-700/50 text-purple-400 hover:bg-purple-500/10 transition-all">
          CPA Trial
         </button>
         <button onClick={() => handleDisableUser(u.uid, !u.disabled)} className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border transition-all ${u.disabled ? 'border-emerald-700/50 text-emerald-500 hover:bg-emerald-500/10' : 'border-rose-700/50 text-rose-500 hover:bg-rose-500/10'}`}>
          {u.disabled ? 'Enable' : 'Disable'}
         </button>
         <button onClick={() => { setSelectedSupportCustomerUid(u.uid); setSelectedAdminSupportThreadId(null); setAdminSupportMessages([]); setAdminSupportHandoffs([]); setTab('support'); }} className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-cyan-700/50 text-cyan-400 hover:bg-cyan-500/10 transition-all">
          Support
         </button>
         <button onClick={() => handleDeleteUser(u.uid, u.email)} disabled={u.isOwner} className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-rose-700/50 text-rose-500 hover:bg-rose-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          Delete
         </button>
        </div>
       </div>
      ))}
     </div>
    )}

    {/* Connections Tab */}
    {tab === 'connections' && (
     <div className="space-y-2">
      {allConnections.length === 0 && <p className="text-slate-500 text-sm py-10 text-center">No connections.</p>}
      {allConnections.map((c: any) => (
       <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center justify-between">
        <div>
         <h4 className="text-sm font-semibold text-white">{c.companyName}</h4>
         <p className="text-[10px] text-slate-500">Realm: {c.realmId} · User: <span className="font-mono">{c.linkedBy?.slice(0, 12)}...</span> · Updated: {c.lastUpdated}</p>
        </div>
        <div className="flex items-center gap-3">
         <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>{c.status}</span>
         <button onClick={() => handleDeleteConn(c.realmId)} className="p-2 text-slate-600 hover:text-rose-400 transition-colors"><Trash2 size={14} /></button>
        </div>
       </div>
      ))}
     </div>
    )}

    {/* Disputes & Opt-outs Tab */}
    {tab === 'disputes' && (
     <div className="space-y-8">
      <div>
       <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300">Active Disputes ({disputes.filter((d: any) => d.status !== 'resolved').length})</h3>
        <button onClick={() => handleBulk('resolve_all_disputes')} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-white transition-all">Resolve All</button>
       </div>
       {disputes.length === 0 ? <p className="text-slate-600 text-xs">No disputes.</p> : (
        <div className="space-y-2">
         {disputes.map((d: any) => (
          <div key={d.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between">
           <div>
            <p className="text-xs text-white font-medium">Invoice: {d.invoiceId} · {d.email}</p>
            <p className="text-[10px] text-slate-500">Realm: {d.realmId} · Status: <span className={d.status === 'resolved' ? 'text-emerald-400' : 'text-amber-400'}>{d.status}</span></p>
           </div>
          </div>
         ))}
        </div>
       )}
      </div>
      <div>
       <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300">Email Suppressions ({optOuts.length})</h3>
        <button onClick={() => handleBulk('clear_all_optouts')} className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-[10px] font-bold uppercase tracking-wide text-rose-500 hover:text-white hover:bg-rose-500/20 transition-all">Clear All</button>
       </div>
       {optOuts.length === 0 ? <p className="text-slate-600 text-xs">No suppressions.</p> : (
        <div className="space-y-2">
         {optOuts.map((o: any) => (
          <div key={o.id} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center justify-between">
           <div>
            <p className="text-xs text-white font-medium">{o.email}</p>
            <p className="text-[10px] text-slate-500">Source: {o.source || 'unsubscribe'} · Realm: {o.realmId || 'all'}</p>
           </div>
          </div>
         ))}
        </div>
       )}
      </div>
     </div>
    )}

    {tab === 'support' && (
     <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
       <div className="flex items-center justify-between mb-4">
         <div>
          <h3 className="text-sm font-semibold text-slate-300">Support Threads</h3>
          <p className="text-[10px] text-slate-600 mt-1">Internal support copilot view.</p>
        </div>
        <button onClick={() => { loadSupportThreads(); loadSupportAnalytics(); }} className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-white transition-all">
         Refresh
        </button>
       </div>
        {supportAnalytics && (
         <div className="mb-4 space-y-4">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
           <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">New Threads</p>
            <p className="mt-2 text-lg font-semibold text-white">{supportAnalytics.newThreads}</p>
            <p className="text-[10px] text-slate-600 mt-1">Last {supportAnalytics.periodDays} days</p>
           </div>
           <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Escalation Rate</p>
            <p className="mt-2 text-lg font-semibold text-white">{Math.round(supportAnalytics.escalationRate * 100)}%</p>
            <p className="text-[10px] text-slate-600 mt-1">{supportAnalytics.escalationCount} escalated</p>
           </div>
           <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Unresolved 24h+</p>
            <p className="mt-2 text-lg font-semibold text-white">{supportAnalytics.unresolvedOver24h}</p>
            <p className="text-[10px] text-slate-600 mt-1">Needs follow-up</p>
           </div>
           <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">First Response</p>
            <p className="mt-2 text-lg font-semibold text-white">{supportAnalytics.averageFirstResponseMinutes === null ? 'N/A' : `${supportAnalytics.averageFirstResponseMinutes}m`}</p>
            <p className="text-[10px] text-slate-600 mt-1">Average</p>
           </div>
          </div>
          <div className="bg-slate-800/30 border border-slate-800 rounded-xl p-4">
           <div className="flex items-center justify-between gap-4">
            <div>
             <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Recurring Issue Detector</h4>
             <p className="text-[10px] text-slate-600 mt-1">Top support patterns that should drive FAQ and product improvements.</p>
            </div>
            <div className="text-[10px] text-slate-500">
             Helpfulness: AI {supportAnalytics.supportHelpfulRate === null ? 'N/A' : `${Math.round(supportAnalytics.supportHelpfulRate * 100)}%`} · Help {supportAnalytics.helpHelpfulRate === null ? 'N/A' : `${Math.round(supportAnalytics.helpHelpfulRate * 100)}%`}
            </div>
           </div>
           <div className="mt-4 flex flex-wrap gap-2">
            {supportAnalytics.topIssueTypes.map(issue => (
             <span key={issue.issueType} className="px-2 py-1 rounded-full bg-slate-900/60 border border-slate-800 text-[10px] text-slate-400 uppercase tracking-wide">
              {(SUPPORT_ISSUE_LABELS[issue.issueType] || issue.issueType)}: {issue.count}
             </span>
            ))}
           </div>
           <div className="mt-4 space-y-3">
            {supportAnalytics.recurringIssues.length === 0 && (
             <p className="text-[11px] text-slate-500">No recurring issue clusters yet.</p>
            )}
            {supportAnalytics.recurringIssues.map((issue, index) => (
             <div key={`${issue.issueType}_${index}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
              <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
               <div>
                <p className="text-sm font-semibold text-white">{SUPPORT_ISSUE_LABELS[issue.issueType] || issue.issueType}</p>
                <p className="text-[11px] text-slate-500 mt-1">{issue.sampleSubject}</p>
               </div>
               <span className="px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-[10px] font-bold uppercase tracking-wide border border-cyan-500/20">
                {issue.recommendation}
               </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-slate-500">
               <span>{issue.count} threads</span>
               <span>{Math.round(issue.escalationRate * 100)}% escalation</span>
               <span>{issue.unresolved} unresolved</span>
              </div>
             </div>
            ))}
           </div>
          </div>
         </div>
        )}
        {selectedSupportCustomerUid && (
         <div className="mb-4 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-[11px] text-cyan-300">
          Selected customer: <span className="font-mono">{selectedSupportCustomerUid}</span>
         </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3 mb-4">
         <input
          type="text"
          placeholder="Search threads..."
          value={adminSupportSearch}
          onChange={(e) => setAdminSupportSearch(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-500"
         />
         <select value={adminSupportStatusFilter} onChange={(e) => setAdminSupportStatusFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="escalated">Escalated</option>
          <option value="resolved">Resolved</option>
          <option value="pending">Pending</option>
         </select>
         <select value={adminSupportIssueFilter} onChange={(e) => setAdminSupportIssueFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
          <option value="all">All Issues</option>
          <option value="billing">Billing</option>
          <option value="auth">Auth</option>
          <option value="quickbooks">QuickBooks</option>
          <option value="deliverability">Deliverability</option>
          <option value="sender">Sender</option>
          <option value="compliance">Compliance</option>
          <option value="invoice">Invoice</option>
          <option value="unknown">General</option>
         </select>
         <select value={adminSupportTierFilter} onChange={(e) => setAdminSupportTierFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
          <option value="all">All Tiers</option>
          <option value="standard">Standard</option>
          <option value="priority">Priority</option>
          <option value="white_glove">White-Glove</option>
         </select>
         <select value={adminSupportAgeFilter} onChange={(e) => setAdminSupportAgeFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
          <option value="all">All Ages</option>
          <option value="24h">24h+</option>
          <option value="72h">72h+</option>
         </select>
         <div className="grid grid-cols-2 gap-3">
          <select value={adminSupportEscalatedFilter} onChange={(e) => setAdminSupportEscalatedFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
           <option value="all">All Queue</option>
           <option value="only">Escalated</option>
          </select>
          <select value={adminSupportWaitingFilter} onChange={(e) => setAdminSupportWaitingFilter(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-xs text-white">
           <option value="all">All Follow-up</option>
           <option value="only">Waiting On Customer</option>
          </select>
         </div>
        </div>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
        {filteredAdminSupportThreads.map(thread => (
          <button
           key={thread.id}
           onClick={() => loadSupportThreadDetail(thread.id)}
           className={`w-full text-left rounded-xl border p-4 transition-all ${selectedAdminSupportThreadId === thread.id ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-slate-800/40 border-slate-800'}`}
          >
           <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold text-white">{thread.customerDisplayName || thread.customerEmail || thread.customerUid}</p>
            <span className="text-[10px] uppercase tracking-wide text-slate-500">{SUPPORT_PRIORITY_LABELS[String(thread.priority || 'normal').toLowerCase()] || 'Normal'}</span>
           </div>
           <p className="mt-2 text-[11px] text-slate-400">{thread.subject || 'Support request'}</p>
           <div className="mt-2 flex items-center gap-2 flex-wrap">
           <span className="px-2 py-1 rounded-full bg-slate-800 text-slate-400 text-[10px] font-bold uppercase tracking-wide">
             {SUPPORT_ISSUE_LABELS[String(thread.issueType || 'unknown').toLowerCase()] || 'General'}
            </span>
            <span className="px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-[10px] font-bold uppercase tracking-wide border border-cyan-500/20">
             {SUPPORT_TIER_LABELS[String(thread.supportTier || 'standard').toLowerCase()] || 'Standard'}
            </span>
            <span className="px-2 py-1 rounded-full bg-slate-800 text-slate-500 text-[10px] font-bold uppercase tracking-wide">
             {thread.status || 'open'}
            </span>
            {thread.waitingOnCustomer && (
             <span className="px-2 py-1 rounded-full bg-amber-500/10 text-amber-300 text-[10px] font-bold uppercase tracking-wide border border-amber-500/20">
              Waiting On Customer
             </span>
            )}
           </div>
           <p className="mt-2 text-[10px] text-slate-600">{thread.lastMessagePreview || 'No preview yet.'}</p>
           <p className="mt-2 text-[10px] text-slate-600">
            Updated {formatTimestamp(thread.updatedAt || thread.createdAt)}
            {thread.targetResponseSlaHours ? ` · SLA ${thread.targetResponseSlaHours}h` : ''}
            {thread.owner ? ` · Owner ${thread.owner.slice(0, 8)}...` : ''}
           </p>
          </button>
         ))}
        {filteredAdminSupportThreads.length === 0 && (
         <p className="text-xs text-slate-600">No support threads for the current selection yet.</p>
        )}
       </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
       <div className="flex items-start justify-between gap-4 mb-4">
        <div>
         <h3 className="text-lg font-semibold text-white">{selectedAdminSupportThreadId ? 'Support Transcript' : 'Internal Support'}</h3>
         <p className="text-[11px] text-slate-500 mt-1">Admin messages are sent through the same secure support orchestrator, but with internal access checks.</p>
        </div>
        {adminSupportHandoffs[0] && (
         <span className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-500/10 text-amber-300 border border-amber-500/20">Escalated</span>
        )}
       </div>
       {adminSupportHandoffs[0] && (
        <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
         <p className="font-semibold">{adminSupportHandoffs[0].summary}</p>
         <p className="mt-2 text-xs text-amber-300">Next step: {adminSupportHandoffs[0].recommendedNextStep}</p>
        </div>
       )}
       <div className="space-y-4 max-h-[52vh] overflow-y-auto pr-1 mb-4">
        {adminSupportMessages.length === 0 && (
         <div className="rounded-xl border border-slate-800 bg-slate-800/30 p-4 text-sm text-slate-400">
          Pick a support thread or select a user from the Users tab and open Support to start a new internal conversation.
         </div>
        )}
        {adminSupportMessages.map(message => (
         <div key={message.id} className={`max-w-3xl ${message.senderType === 'human_support' ? 'ml-auto' : ''}`}>
          <div className={`rounded-xl border p-4 ${message.senderType === 'human_support' ? 'bg-cyan-500 text-slate-900 border-cyan-500' : 'bg-slate-800/40 border-slate-800 text-white'}`}>
           <p className="text-[10px] font-bold uppercase tracking-wide opacity-70 mb-2">{message.senderType === 'human_support' ? 'Admin' : message.senderType === 'ai' ? 'Support AI' : message.senderType}</p>
           <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message}</p>
           {message.citations && message.citations.length > 0 && (
            <div className={`mt-3 rounded-lg p-3 ${message.senderType === 'human_support' ? 'bg-white/15 border border-white/20' : 'bg-slate-900/40 border border-slate-700'}`}>
             {message.citations.map((citation, index) => (
              <p key={`${message.id}_${index}`} className="text-[11px] mt-1 first:mt-0"><span className="font-semibold">{citation.label}:</span> {citation.value}</p>
             ))}
            </div>
           )}
          </div>
         </div>
        ))}
       </div>
       <div className="space-y-3">
        <textarea
         value={adminSupportInput}
         onChange={(e) => setAdminSupportInput(e.target.value)}
         placeholder={selectedSupportCustomerUid ? 'Ask the support AI to diagnose or summarize this customer account.' : 'Select a customer from Users or a support thread first.'}
         disabled={!selectedSupportCustomerUid}
         className="w-full min-h-[120px] bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-500 disabled:opacity-40"
        />
        <div className="flex items-center justify-between gap-4">
         <p className="text-[11px] text-slate-500">Internal support can inspect tenant-scoped context and generate escalation packets, but Phase 1 does not allow risky writes.</p>
         <button onClick={handleAdminSupportSend} disabled={supportLoading || !adminSupportInput.trim() || !selectedSupportCustomerUid} className="px-5 py-3 bg-cyan-500 text-slate-900 rounded-xl text-[10px] font-bold uppercase tracking-wide disabled:opacity-40">
          {supportLoading ? 'Working...' : 'Send'}
         </button>
        </div>
       </div>
      </div>
     </div>
    )}

    {/* System Tab */}
    {tab === 'admins' && (
     <div className="space-y-6">
      {/* Current Admins */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <div className="flex items-center justify-between mb-6">
        <div>
         <h3 className="text-sm font-semibold text-slate-300">Privileged Accounts</h3>
         <p className="text-[10px] text-slate-600 mt-1">Owners can grant and revoke admins. Admins can still manage accounts and subscriptions.</p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-400">
         {users.filter((u: any) => u.isOwner).length} owner{users.filter((u: any) => u.isOwner).length !== 1 ? 's' : ''} · {users.filter((u: any) => u.isAdmin && !u.isOwner).length} admin{users.filter((u: any) => u.isAdmin && !u.isOwner).length !== 1 ? 's' : ''}
        </span>
       </div>
       {users.filter((u: any) => u.isAdmin).length === 0 ? (
        <div className="text-center py-8">
         <p className="text-slate-500 text-xs mb-2">No admin users found.</p>
         <p className="text-slate-600 text-[10px]">Grant the first owner locally, then use this console for normal admin promotions:</p>
         <code className="text-[10px] text-cyan-400 bg-slate-800 px-3 py-1.5 rounded-lg mt-2 inline-block font-mono">
          npm.cmd run owner:grant -- UID
         </code>
        </div>
       ) : (
        <div className="space-y-2">
         {users.filter((u: any) => u.isAdmin).map((u: any) => (
          <div key={u.uid} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
           <div className="flex items-center gap-3">
            {u.photoURL ? (
             <img src={u.photoURL} className="w-9 h-9 rounded-lg object-cover" alt="" />
            ) : (
             <div className="w-9 h-9 rounded-lg bg-slate-700 flex items-center justify-center text-cyan-400 text-xs font-bold uppercase">{(u.email || '?')[0]}</div>
            )}
            <div>
             <p className="text-sm font-semibold text-white flex items-center gap-2">
              {u.displayName || u.email}
              {u.isOwner ? (
               <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">Owner</span>
              ) : (
               <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">Admin</span>
              )}
             </p>
             <p className="text-[10px] text-slate-500">{u.email} · <span className="font-mono">{u.uid.slice(0, 12)}...</span></p>
             <p className="text-[10px] text-slate-600">Last login: {u.lastSignIn ? new Date(u.lastSignIn).toLocaleDateString() : 'Never'}</p>
            </div>
           </div>
           {!u.isOwner && (
            <button
             onClick={() => handleRevokeAdmin(u.uid, u.email)}
             disabled={!viewerIsOwner}
             className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-rose-700/50 text-rose-400 hover:bg-rose-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
             Revoke Admin
            </button>
           )}
          </div>
         ))}
        </div>
       )}
      </div>

      {/* Grant Admin */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-2">Grant Admin Privileges</h3>
       <p className="text-[10px] text-slate-600 mb-6">Select a non-admin user to promote. They will need to sign out and back in for changes to take effect.</p>
       {!viewerIsOwner && <p className="text-[10px] text-amber-400 mb-4">Only owner accounts can grant or revoke admin access.</p>}
       <div className="space-y-2 max-h-64 overflow-y-auto">
        {users.filter((u: any) => !u.isAdmin && !u.disabled).length === 0 ? (
         <p className="text-slate-600 text-xs text-center py-4">All active users are already admins.</p>
        ) : (
         users.filter((u: any) => !u.isAdmin && !u.disabled).map((u: any) => (
          <div key={u.uid} className="bg-slate-800/30 border border-slate-800 rounded-xl p-3 flex items-center justify-between">
           <div className="flex items-center gap-3">
            {u.photoURL ? (
             <img src={u.photoURL} className="w-8 h-8 rounded-lg object-cover" alt="" />
            ) : (
             <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-500 text-[10px] font-bold uppercase">{(u.email || '?')[0]}</div>
            )}
            <div>
             <p className="text-xs font-medium text-white">{u.displayName || u.email}</p>
             <p className="text-[10px] text-slate-500">{u.email}</p>
            </div>
           </div>
           <button
            onClick={() => handleGrantAdmin(u.uid, u.email)}
            disabled={!viewerIsOwner}
            className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-cyan-700/50 text-cyan-400 hover:bg-cyan-500/10 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
           >
            Grant Admin
           </button>
          </div>
         ))
        )}
       </div>
      </div>

      {/* Admin Security Info */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-4">Security Notes</h3>
       <div className="space-y-3 text-[11px] text-slate-400 leading-relaxed">
        <p>Privileged access is stored as <strong className="text-slate-300">Firebase Auth custom claims</strong>, not in Firestore. Client-side code cannot mint elevated roles for itself.</p>
        <p><strong className="text-slate-300">Owner</strong> accounts manage admin access. Regular <strong className="text-slate-300">admin</strong> accounts can still manage users, plans, and subscription state.</p>
        <p>After granting or revoking admin, the affected user must <strong className="text-slate-300">sign out and sign back in</strong> (or wait up to 1 hour) for the change to propagate to their auth token.</p>
        <p><strong className="text-slate-300">Self-demotion protection:</strong> You cannot revoke your own admin privileges. This prevents accidental lockout.</p>
        <p className="text-slate-600">Bootstrap the first owner locally with <code className="text-cyan-400/70 bg-slate-800 px-2 py-0.5 rounded font-mono">npm.cmd run owner:grant -- UID</code>, then use this console for admin management.</p>
       </div>
      </div>
     </div>
    )}

    {/* System Tab (original) */}
    {tab === 'system' && (
     <div className="space-y-6">

      {/* Kill Switch */}
      <div className={`border rounded-xl p-6 ${killSwitchEnabled ? 'bg-rose-950/60 border-rose-700' : 'bg-slate-900 border-slate-800'}`}>
       <div className="flex items-center justify-between gap-4">
        <div>
         <p className={`text-sm font-bold ${killSwitchEnabled ? 'text-rose-300' : 'text-slate-300'}`}>
          {killSwitchEnabled ? '⛔ System Kill Switch — ON' : '✅ System Kill Switch — OFF'}
         </p>
         <p className="text-xs text-slate-500 mt-1">
          When ON: all scheduled jobs (dunning, token refresh) halt immediately. AI calls are blocked. Use during runaway loops or unexpected billing spikes.
         </p>
        </div>
        <button
         disabled={killSwitchLoading}
         onClick={async () => {
          setKillSwitchLoading(true);
          try {
           const appId = firebaseConfig.projectId || '';
           const db2 = getFirestore(firebaseApp);
           await setDoc(doc(db2, 'artifacts', appId, 'system_config', 'kill_switch'), { enabled: !killSwitchEnabled });
           setKillSwitchEnabled(v => !v);
          } catch (e: any) { setMsg('Kill switch error: ' + e.message); }
          setKillSwitchLoading(false);
         }}
         className={`whitespace-nowrap px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50 ${killSwitchEnabled ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-slate-700 hover:bg-slate-600 text-slate-200'}`}
        >
         {killSwitchLoading ? 'Saving…' : killSwitchEnabled ? 'Disable Kill Switch' : 'Enable Kill Switch'}
        </button>
       </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-4">System Information</h3>
       <div className="grid grid-cols-2 gap-4 text-xs">
        <div><span className="text-slate-500">Project ID:</span> <span className="text-white font-mono">{firebaseConfig.projectId || '(missing)'}</span></div>
        <div><span className="text-slate-500">Region:</span> <span className="text-white font-mono">asia-east2</span></div>
        <div><span className="text-slate-500">App ID:</span> <span className="text-white font-mono">{firebaseConfig.appId || '(missing)'}</span></div>
        <div><span className="text-slate-500">Admin version:</span> <span className="text-white font-mono">1.0.0</span></div>
       </div>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-4">AI Compliance Usage</h3>
       <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
        <div><span className="text-slate-500">Reviews:</span> <span className="text-white font-mono">{stats?.aiUsage?.totalReviews || 0}</span></div>
        <div><span className="text-slate-500">Flagged:</span> <span className="text-amber-400 font-mono">{stats?.aiUsage?.flaggedReviews || 0}</span></div>
        <div><span className="text-slate-500">Tokens:</span> <span className="text-white font-mono">{(stats?.aiUsage?.totalTokens || 0).toLocaleString()}</span></div>
        <div><span className="text-slate-500">Estimated cost:</span> <span className="text-white font-mono">${Number(stats?.aiUsage?.estimatedCostUsd || 0).toFixed(4)}</span></div>
       </div>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-semibold text-slate-300">Firestore Backup</h3>
        {backupConfig?.lastBackupAt && (
         <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-lg ${backupConfig?.lastBackupStatus === 'failed' ? 'bg-rose-900/40 text-rose-400' : 'bg-emerald-900/40 text-emerald-400'}`}>
          {backupConfig?.lastBackupStatus === 'failed' ? '✗ Last failed' : '✓ Last ok'} · {new Date(backupConfig.lastBackupAt).toLocaleDateString()}
         </span>
        )}
       </div>
       {backupConfigLoading ? (
        <p className="text-xs text-slate-500 animate-pulse">Loading…</p>
       ) : (
        <div className="space-y-5">
         <div className="flex items-center justify-between">
          <div>
           <p className="text-xs font-semibold text-slate-300">Automated backups</p>
           <p className="text-[11px] text-slate-500 mt-0.5">Export all Firestore data to Cloud Storage on a schedule.</p>
          </div>
          <button
           onClick={() => setBackupEnabled(v => !v)}
           className={`relative w-12 h-6 rounded-full transition-colors ${backupEnabled ? 'bg-cyan-500' : 'bg-slate-700'}`}
          >
           <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${backupEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
         </div>
         <div>
          <label className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide block mb-2">Backup frequency</label>
          <select
           value={backupInterval}
           onChange={e => setBackupInterval(Number(e.target.value))}
           disabled={!backupEnabled}
           className="w-full bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-3 py-2.5 disabled:opacity-40"
          >
           <option value={6}>Every 6 hours</option>
           <option value={12}>Every 12 hours</option>
           <option value={24}>Daily (every 24 hours)</option>
           <option value={48}>Every 2 days</option>
           <option value={168}>Weekly</option>
          </select>
         </div>
         {backupConfig?.lastBackupDestination && (
          <div>
           <p className="text-[11px] text-slate-500 font-mono truncate">Last destination: {backupConfig.lastBackupDestination}</p>
          </div>
         )}
         <div className="flex gap-3 pt-2">
          <button
           onClick={handleSaveBackupConfig}
           disabled={backupSaving}
           className="px-5 py-2.5 bg-cyan-500 text-slate-900 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-cyan-400 transition-colors disabled:opacity-50"
          >
           {backupSaving ? 'Saving…' : 'Save Settings'}
          </button>
          <button
           onClick={handleRunBackupNow}
           disabled={backupRunning}
           className="px-5 py-2.5 bg-slate-800 border border-slate-700 text-slate-300 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:border-cyan-700 hover:text-cyan-400 transition-all disabled:opacity-50"
          >
           {backupRunning ? 'Starting…' : 'Run Backup Now'}
          </button>
         </div>
         <p className="text-[10px] text-slate-600">Estimated cost: &lt; $1.50/mo (varies by DB size). Bucket: <span className="font-mono">{`${firebaseConfig.projectId}-firestore-backups`}</span></p>
        </div>
       )}
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-1">Status Page — Post Incident</h3>
       <p className="text-[11px] text-slate-500 mb-5">Creates a new entry on <a href="https://soar-one.com/status" target="_blank" rel="noreferrer" className="text-cyan-500 hover:underline">soar-one.com/status</a> visible to all users.</p>
       <div className="space-y-3">
        <input
         type="text"
         placeholder="Incident title (e.g. Email delivery degraded)"
         value={incidentTitle}
         onChange={(e) => setIncidentTitle(e.target.value)}
         className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-3 py-2.5 placeholder:text-slate-600 outline-none focus:border-cyan-700"
        />
        <textarea
         rows={3}
         placeholder="Description / update body (optional)"
         value={incidentBody}
         onChange={(e) => setIncidentBody(e.target.value)}
         className="w-full bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-lg px-3 py-2.5 placeholder:text-slate-600 outline-none focus:border-cyan-700 resize-none"
        />
        <div className="flex gap-3 items-center">
         <select
          value={incidentStatus}
          onChange={(e) => setIncidentStatus(e.target.value as any)}
          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-3 py-2.5"
         >
          <option value="investigating">Investigating</option>
          <option value="monitoring">Monitoring</option>
          <option value="resolved">Resolved</option>
          <option value="scheduled">Scheduled Maintenance</option>
         </select>
         <button
          onClick={handleCreateIncident}
          disabled={incidentSaving || !incidentTitle.trim()}
          className="px-5 py-2.5 bg-cyan-500 text-slate-900 rounded-lg text-[10px] font-bold uppercase tracking-wide hover:bg-cyan-400 transition-colors disabled:opacity-50"
         >
          {incidentSaving ? 'Posting…' : 'Post to Status Page'}
         </button>
        </div>
       </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-4">Plan Limits Reference</h3>
       <table className="w-full text-xs">
        <thead><tr className="text-slate-500 text-left"><th className="pb-2">Plan</th><th className="pb-2">Price</th><th className="pb-2">Connections</th><th className="pb-2">Templates</th><th className="pb-2">SMTP</th><th className="pb-2">Compliance</th></tr></thead>
        <tbody className="text-white">
         <tr className="border-t border-slate-800"><td className="py-2">Starter</td><td>$49/mo</td><td>1</td><td>3</td><td className="text-slate-600">No</td><td className="text-slate-600">No</td></tr>
         <tr className="border-t border-slate-800"><td className="py-2">Pro</td><td>$99/mo</td><td>3</td><td>∞</td><td className="text-cyan-400">Yes</td><td className="text-cyan-400">Yes</td></tr>
         <tr className="border-t border-slate-800"><td className="py-2">CPA</td><td>$199/mo</td><td>10</td><td>∞</td><td className="text-cyan-400">Yes</td><td className="text-cyan-400">Yes</td></tr>
        </tbody>
       </table>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
       <h3 className="text-sm font-semibold text-slate-300 mb-4">Quick Actions</h3>
       <div className="flex flex-wrap gap-3">
        <button onClick={() => handleBulk('resolve_all_disputes')} className="px-5 py-3 bg-slate-800 border border-slate-700 rounded-xl text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-amber-400 hover:border-amber-500/30 transition-all">Resolve All Disputes</button>
        <button onClick={() => handleBulk('clear_all_optouts')} className="px-5 py-3 bg-slate-800 border border-slate-700 rounded-xl text-[10px] font-bold uppercase tracking-wide text-slate-400 hover:text-rose-400 hover:border-rose-500/30 transition-all">Clear All Opt-Outs</button>
       </div>
      </div>
     </div>
    )}
   </div>
  </div>
 );
};

// ─── Debtor Self-Service Portal ───────────────────────────────────────────────
function DebtorPortalView() {
 const params = new URLSearchParams(window.location.search);
 const e = params.get('e') || '';
 const r = params.get('r') || '';
 const u = params.get('u') || '';
 const t = params.get('t') || '';
 const ts = params.get('ts') || '';

 const [data, setData] = React.useState<any>(null);
 const [error, setError] = React.useState('');
 const [loading, setLoading] = React.useState(true);
 const [expandedHistory, setExpandedHistory] = React.useState<string | null>(null);
 const [activePlanInvoice, setActivePlanInvoice] = React.useState<string | null>(null);
 const [contactName, setContactName] = React.useState('');
 const [proposal, setProposal] = React.useState('');
 const [submitting, setSubmitting] = React.useState(false);
 const [submittedIds, setSubmittedIds] = React.useState<Set<string>>(new Set());

 React.useEffect(() => {
  if (!e || !r || !u || !t) { setError('Invalid portal link. Please use the link from your reminder email.'); setLoading(false); return; }
  fetch(`/api/portal-data?e=${encodeURIComponent(e)}&r=${encodeURIComponent(r)}&u=${encodeURIComponent(u)}&t=${encodeURIComponent(t)}&ts=${encodeURIComponent(ts)}`)
   .then(res => res.json())
   .then(json => { if (json.error) setError(json.error); else setData(json); })
   .catch(() => setError('Failed to load portal data. Please try again.'))
   .finally(() => setLoading(false));
 }, []);

 const handleSubmitPlan = async (invoiceId: string, docNumber: string) => {
  if (!proposal.trim() || proposal.trim().length < 10) return;
  setSubmitting(true);
  try {
   const fullProposal = `Re: Invoice #${docNumber}\n\n${proposal.trim()}`;
   const res = await fetch('/api/portal-submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ e, r, u, t, ts, proposal: fullProposal, contactName: contactName.trim() }),
   });
   const json = await res.json();
   if (json.error) { alert(json.error); return; }
   setSubmittedIds(prev => new Set(prev).add(invoiceId));
   setActivePlanInvoice(null);
   setProposal('');
  } catch { alert('Failed to submit. Please try again.'); }
  finally { setSubmitting(false); }
 };

 const fmt = (v: any) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v)) : '—';
 const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
 const deliveryColor = (s: string) => s === 'opened' ? 'text-blue-600' : s === 'delivered' ? 'text-emerald-600' : s === 'sent' ? 'text-slate-500' : 'text-rose-500';
 const deliveryLabel = (s: string) => s === 'opened' ? 'Opened' : s === 'delivered' ? 'Delivered' : s === 'sent' ? 'Sent' : s || 'Unknown';

 // Build invoice-centric data: group logs and disputes by invoiceId
 const enrichedInvoices = React.useMemo(() => {
  if (!data) return [];
  const logsByInv: Record<string, any[]> = {};
  (data.logs || []).forEach((log: any) => {
   if (!logsByInv[log.invoiceId]) logsByInv[log.invoiceId] = [];
   logsByInv[log.invoiceId].push(log);
  });
  const disputesByInv: Record<string, any> = {};
  (data.disputes || []).forEach((d: any) => { disputesByInv[d.invoiceId] = d; });

  // Merge: known invoices first, then any log-only invoice ids
  const knownIds = new Set((data.invoices || []).map((i: any) => i.id));
  const logOnlyIds = Object.keys(logsByInv).filter(id => !knownIds.has(id));

  const base = [
   ...(data.invoices || []),
   ...logOnlyIds.map((id: string) => {
    const logs = logsByInv[id] || [];
    const first = logs[0] || {};
    return { id, docNumber: first.docNumber || id, balance: first.invoiceAmount, totalAmt: first.invoiceAmount, dueDate: null, status: 'Open' };
   }),
  ];

  return base.map((inv: any) => {
   const logs = (logsByInv[inv.id] || []).sort((a: any, b: any) =>
    new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
   );
   const lastLog = logs[0] || null;
   return { ...inv, logs, lastLog, dispute: disputesByInv[inv.id] || null };
  });
 }, [data]);

 const tierUrgency = (name: string) => {
  const n = (name || '').toLowerCase();
  if (n.includes('final') || n.includes('urgent') || n.includes('overdue')) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (n.includes('firm') || n.includes('second') || n.includes('follow')) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-sky-100 text-sky-700 border-sky-200';
 };

 return (
  <div className="min-h-screen bg-slate-50 font-['DM_Sans',sans-serif] text-slate-900">
   <div className="max-w-2xl mx-auto px-4 py-10">
    {/* Header */}
    <div className="flex items-center gap-3 mb-8">
     <img src="/brand-wordmark.png" alt="SoarOne" className="h-8 w-auto" />
     {data?.companyName && <><span className="text-slate-300">·</span><span className="text-sm text-slate-500 font-medium">{data.companyName}</span></>}
    </div>

    {loading && <div className="text-center py-20 text-slate-400 text-sm">Loading your account…</div>}
    {error && <div className="p-6 rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 text-sm text-center">{error}</div>}

    {data && (
     <div className="space-y-6">
      <div>
       <h1 className="text-2xl font-bold tracking-tight mb-1">Your Invoices</h1>
       <p className="text-sm text-slate-400">{data.customerEmail}</p>
      </div>

      {enrichedInvoices.length === 0 && (
       <div className="bg-white rounded-2xl border border-slate-200 px-6 py-10 text-center text-sm text-slate-400">No invoices found for this account.</div>
      )}

      {/* Invoice cards */}
      {enrichedInvoices.map((inv: any) => {
       const isExpanded = expandedHistory === inv.id;
       const isPlanOpen = activePlanInvoice === inv.id;
       const isPlanSubmitted = submittedIds.has(inv.id);
       const isPaid = inv.status === 'Paid' || parseFloat(inv.balance) === 0;
       const hasDispute = !!inv.dispute;

       return (
        <div key={inv.id} className={`bg-white rounded-2xl border overflow-hidden transition-all ${isPaid ? 'border-slate-100' : inv.dispute?.status === 'pending' ? 'border-amber-200' : 'border-slate-200'}`}>
         {/* Card header */}
         <div className="px-6 pt-5 pb-4">
          <div className="flex items-start justify-between gap-3">
           <div>
            <p className="text-base font-bold text-slate-900">Invoice #{inv.docNumber}</p>
            <p className="text-xs text-slate-400 mt-0.5">Due {fmtDate(inv.dueDate)}</p>
           </div>
           <div className="text-right shrink-0">
            <p className={`text-xl font-bold ${isPaid ? 'text-emerald-600' : 'text-slate-900'}`}>{fmt(inv.balance)}</p>
            {inv.totalAmt && parseFloat(inv.totalAmt) !== parseFloat(inv.balance) && (
             <p className="text-[11px] text-slate-400 mt-0.5">of {fmt(inv.totalAmt)} total</p>
            )}
           </div>
          </div>

          {/* Status + tier badges */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
           <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${isPaid ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {isPaid ? '✓ Paid' : 'Outstanding'}
           </span>
           {inv.lastLog?.templateSent && !isPaid && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${tierUrgency(inv.lastLog.templateSent)}`}>
             {inv.lastLog.templateSent}
            </span>
           )}
           {hasDispute && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${inv.dispute.status === 'resolved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-violet-50 text-violet-700 border-violet-200'}`}>
             Dispute: {inv.dispute.status === 'resolved' ? 'Resolved' : 'Under Review'}
            </span>
           )}
          </div>

          {/* Last reminder line */}
          {inv.lastLog && (
           <p className="text-[11px] text-slate-400 mt-2">
            Last reminder {fmtDate(inv.lastLog.createdAt)}
            {' · '}
            <span className={`font-medium ${deliveryColor(inv.lastLog.deliveryStatus)}`}>{deliveryLabel(inv.lastLog.deliveryStatus)}</span>
           </p>
          )}
         </div>

         {/* History toggle */}
         {inv.logs.length > 0 && (
          <div className="border-t border-slate-100">
           <button
            onClick={() => setExpandedHistory(isExpanded ? null : inv.id)}
            className="w-full flex items-center justify-between px-6 py-3 text-[11px] font-semibold text-slate-500 hover:bg-slate-50 transition-colors text-left"
           >
            <span>{isExpanded ? 'Hide' : 'View'} reminder history ({inv.logs.length})</span>
            <span className="text-slate-300">{isExpanded ? '▲' : '▼'}</span>
           </button>
           {isExpanded && (
            <div className="px-6 pb-4 space-y-2">
             {inv.logs.map((log: any) => (
              <div key={log.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
               <div>
                <p className="text-xs font-semibold text-slate-700">{log.templateSent}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{fmtDate(log.createdAt)}</p>
               </div>
               <span className={`text-[11px] font-semibold capitalize ${deliveryColor(log.deliveryStatus)}`}>{deliveryLabel(log.deliveryStatus)}</span>
              </div>
             ))}
            </div>
           )}
          </div>
         )}

         {/* Actions — only for open invoices */}
         {!isPaid && (
          <div className="border-t border-slate-100 px-6 py-4">
           {isPlanSubmitted ? (
            <p className="text-sm text-emerald-600 font-semibold">✓ Payment arrangement request sent — {data.companyName} will be in touch.</p>
           ) : isPlanOpen ? (
            <div className="space-y-3">
             <p className="text-xs font-semibold text-slate-600">Request a payment arrangement for Invoice #{inv.docNumber}</p>
             <input
              value={contactName}
              onChange={ev => setContactName(ev.target.value)}
              placeholder="Your name (optional)"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-slate-300"
             />
             <textarea
              value={proposal}
              onChange={ev => setProposal(ev.target.value)}
              rows={3}
              placeholder="Describe your proposed payment schedule or arrangement…"
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-slate-300 resize-none"
             />
             <p className="text-[11px] text-slate-400">{proposal.length}/1000</p>
             <div className="flex gap-2">
              <button
               onClick={() => handleSubmitPlan(inv.id, inv.docNumber)}
               disabled={submitting || proposal.trim().length < 10}
               className="px-5 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold disabled:opacity-50 transition-all"
              >
               {submitting ? 'Sending…' : 'Send Request'}
              </button>
              <button onClick={() => { setActivePlanInvoice(null); setProposal(''); }} className="px-5 py-2 border border-slate-200 text-slate-500 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all">Cancel</button>
             </div>
            </div>
           ) : (
            <div className="flex gap-2 flex-wrap">
             <button
              onClick={() => { setActivePlanInvoice(inv.id); setExpandedHistory(null); }}
              className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-semibold transition-all hover:bg-slate-700"
             >
              Arrange Payment
             </button>
             {!hasDispute && (
              <button
               onClick={() => alert(`To dispute Invoice #${inv.docNumber}, please use the dispute link in your most recent reminder email.`)}
               className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all"
              >
               Dispute Invoice
              </button>
             )}
            </div>
           )}
          </div>
         )}
        </div>
       );
      })}

      {/* Footer */}
      <div className="text-center text-[11px] text-slate-400 space-y-2 pt-2 pb-6">
       <p>This portal is provided by {data.companyName || 'your vendor'} via SoarOne AR automation.</p>
       <a href={data.unsubscribeUrl} className="underline hover:text-slate-600 transition-colors">Unsubscribe from payment reminders</a>
      </div>
     </div>
    )}
   </div>
  </div>
 );
}

// =============================================================================
// ITEM 26 — IN-APP CHANGELOG
// =============================================================================

const APP_VERSION = '1.4.0';

const CHANGELOG: Array<{ version: string; date: string; tabs: string[]; items: Array<{ label: string; desc: string }> }> = [
 {
  version: '1.4.0',
  date: '2026-04-28',
  tabs: ['invoices', 'account'],
  items: [
   { label: 'Customer Management', desc: 'Add notes, tags, and email overrides per customer directly from the Invoices tab.' },
   { label: 'Invoice Controls', desc: 'Set a CC email, custom note, disable dunning, or mark an invoice as collected from the new Controls button.' },
   { label: 'Debtor Self-Service Portal', desc: 'Debtors can now view their invoices, reminder history, and submit payment plan requests via a secure link in every reminder.' },
   { label: 'Google Workspace SSO', desc: 'Enforce a company email domain for all team member invites (Pro/CPA plans).' },
   { label: 'Customer API', desc: 'Full REST API with 5 endpoints, per-key rate limiting, and callCount analytics.' },
  ]
 },
 {
  version: '1.3.0',
  date: '2026-04-21',
  tabs: ['account'],
  items: [
   { label: 'Team Members & RBAC', desc: 'Invite team members with admin/member/viewer roles. Members get read-only access to the workspace.' },
   { label: 'Outbound Webhooks', desc: 'Subscribe to events like payment_received, reminder_sent, and sync_failed via signed webhooks.' },
   { label: 'Audit Trail', desc: 'Full audit log of all account actions with CSV export, in the Compliance tab.' },
   { label: 'Two-Factor Authentication', desc: 'Secure your account with TOTP-based 2FA via any authenticator app.' },
  ]
 },
];

const WhatsNewModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
 const latest = CHANGELOG[0];
 return (
  <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1400] flex items-center justify-center p-6" onClick={onClose}>
   <div className="bg-white w-full max-w-lg rounded-3xl border border-slate-100 shadow-none overflow-hidden" onClick={e => e.stopPropagation()}>
    <div style={{ background: 'linear-gradient(135deg, #51e2f5, #38bdf8)' }} className="h-1.5 w-full" />
    <div className="p-8">
     <div className="flex items-start justify-between mb-6">
      <div className="flex items-start gap-4">
       <div className="p-3 rounded-2xl bg-sky-50 border border-sky-100">
        <Sparkles size={22} className="text-sky-500" />
       </div>
       <div>
        <h3 style={{ fontFamily: "var(--font-display)" }} className="text-xl font-bold text-slate-900 tracking-tight">What's New</h3>
        <p className="text-xs text-slate-400 mt-0.5">Version {latest.version} · {latest.date}</p>
       </div>
      </div>
      <button onClick={onClose} className="p-2 rounded-xl bg-slate-100 text-slate-500 hover:text-slate-900"><X size={16} /></button>
     </div>
     <div className="space-y-3 max-h-72 overflow-y-auto">
      {latest.items.map((item, i) => (
       <div key={i} className="flex items-start gap-3 p-3 rounded-2xl border border-slate-100 bg-slate-50/50">
        <div className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-2 flex-shrink-0" />
        <div>
         <p className="text-sm font-semibold text-slate-800">{item.label}</p>
         <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
        </div>
       </div>
      ))}
     </div>
     {CHANGELOG.length > 1 && (
      <details className="mt-4">
       <summary className="text-xs font-semibold text-slate-400 cursor-pointer select-none hover:text-slate-600">Previous releases</summary>
       <div className="space-y-2 mt-2">
        {CHANGELOG.slice(1).map((release, ri) => (
         <div key={ri} className="p-3 rounded-xl border border-slate-100 bg-slate-50/30">
          <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wide mb-1">v{release.version} — {release.date}</p>
          <ul className="space-y-0.5">
           {release.items.map((item, ii) => (
            <li key={ii} className="text-xs text-slate-600">· {item.label}</li>
           ))}
          </ul>
         </div>
        ))}
       </div>
      </details>
     )}
     <button onClick={onClose} className="w-full mt-5 py-3 rounded-xl text-sm font-bold text-slate-900" style={{ backgroundColor: '#51e2f5' }}>Got it</button>
    </div>
   </div>
  </div>
 );
};

export default function App() {
 // Secret admin route: yourapp.com/#admin or yourapp.com?admin=true
 const isAdmin = window.location.hash.includes('admin') || new URLSearchParams(window.location.search).has('admin');

 // --- Team Invite URL handling ---
 // Store invite token from URL into localStorage, then clean the URL
 useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('invite');
  if (token) {
   localStorage.setItem('soarone_invite_token', token);
   params.delete('invite');
   const newUrl = params.toString()
    ? `${window.location.pathname}?${params.toString()}`
    : window.location.pathname;
   window.history.replaceState({}, '', newUrl);
  }
 }, []);

 // --- Dark Mode ---
 const [darkMode, setDarkMode] = useState(() => {
  const saved = localStorage.getItem('soarone-theme');
  if (saved) return saved === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
 });

 useEffect(() => {
  const root = document.documentElement;
  if (darkMode) {
   root.classList.add('dark');
   root.style.setProperty('--bg-primary', '#0B0F1A');
   root.style.setProperty('--bg-secondary', '#111827');
   root.style.setProperty('--bg-card', '#1A1F2E');
   root.style.setProperty('--bg-input', '#1E2538');
   root.style.setProperty('--bg-hover', '#252D3F');
   root.style.setProperty('--border-color', '#1F2937');
   root.style.setProperty('--border-light', '#1A2035');
   root.style.setProperty('--text-primary', '#F1F5F9');
   root.style.setProperty('--text-secondary', '#94A3B8');
   root.style.setProperty('--text-muted', '#64748B');
   root.style.setProperty('--text-inverse', '#0F172A');
   root.style.setProperty('--shadow', 'none');
  } else {
   root.classList.remove('dark');
   root.style.setProperty('--bg-primary', '#FBFBFE');
   root.style.setProperty('--bg-secondary', '#FFFFFF');
   root.style.setProperty('--bg-card', '#FFFFFF');
   root.style.setProperty('--bg-input', '#F8FAFC');
   root.style.setProperty('--bg-hover', '#F1F5F9');
   root.style.setProperty('--border-color', '#F1F5F9');
   root.style.setProperty('--border-light', '#F8FAFC');
   root.style.setProperty('--text-primary', '#0F172A');
   root.style.setProperty('--text-secondary', '#64748B');
   root.style.setProperty('--text-muted', '#94A3B8');
   root.style.setProperty('--text-inverse', '#FFFFFF');
   root.style.setProperty('--shadow', '0 1px 3px rgba(0,0,0,0.06)');
  }
  localStorage.setItem('soarone-theme', darkMode ? 'dark' : 'light');
 }, [darkMode]);

 const toggleDarkMode = () => setDarkMode(prev => !prev);

 // Theme-aware class helpers
const t = useMemo(() => ({
  bg: darkMode ? 'bg-[#0B0F1A]' : 'bg-[#FBFBFE]',
  bgCard: darkMode ? 'bg-[#1A1F2E]' : 'bg-white',
  bgSidebar: darkMode ? 'bg-[#111827]' : 'bg-white',
  bgInput: darkMode ? 'bg-[#1E2538]' : 'bg-slate-50',
  bgHover: darkMode ? 'hover:bg-[#252D3F]' : 'hover:bg-slate-50',
  bgBadge: darkMode ? 'bg-[#1E2538]' : 'bg-slate-50',
  bgModal: darkMode ? 'bg-[#111827]' : 'bg-white',
  border: darkMode ? 'border-[#1F2937]' : 'border-slate-100',
  borderInput: darkMode ? 'border-[#2D3748]' : 'border-slate-50',
  text: darkMode ? 'text-slate-100' : 'text-slate-900',
  textSec: darkMode ? 'text-slate-400' : 'text-slate-500',
  textMuted: darkMode ? 'text-slate-500' : 'text-slate-400',
  textLink: darkMode ? 'text-slate-300' : 'text-slate-700',
  ring: darkMode ? 'ring-[#2D3748]' : 'ring-slate-100',
}), [darkMode]);
 const panelClass = `${t.bgCard} rounded-2xl border ${t.border} shadow-none`;
 const inputClass = `w-full ${t.bgInput} border-2 ${t.borderInput} rounded-xl px-6 py-4 text-sm font-normal outline-none focus:ring-2 ${t.ring} ${t.text} transition-all shadow-none`;
 const subtleLabelClass = `text-xs font-medium uppercase ml-4 tracking-wide ${t.textSec}`;

 const [user, setUser] = useState<FirebaseUser | null>(null);
 const [listenersPaused, setListenersPaused] = useState(false);
 const [connections, setConnections] = useState<Connection[]>([]);
 const [logs, setLogs] = useState<DunningLog[]>([]);
 const [invoices, setInvoices] = useState<Invoice[]>([]);
 const [agingSnapshots, setAgingSnapshots] = useState<AgingSnapshot[]>([]);
 const [dunningOverrides, setDunningOverrides] = useState<DunningOverride[]>([]);
 const [auditLog, setAuditLog] = useState<AuditEvent[]>([]);
 const [auditSearch, setAuditSearch] = useState('');
 const [webhooks, setWebhooks] = useState<Webhook[]>([]);
 const [webhookDeliveries, setWebhookDeliveries] = useState<WebhookDelivery[]>([]);
 const [webhookExpandedId, setWebhookExpandedId] = useState<string | null>(null);
 const [webhookForm, setWebhookForm] = useState({ url: '', description: '', events: [] as string[] });
 const [webhookCreateLoading, setWebhookCreateLoading] = useState(false);
 const [webhookCreateError, setWebhookCreateError] = useState('');
 const [webhookNewSecret, setWebhookNewSecret] = useState<{ id: string; secret: string } | null>(null);
 const [webhookTestLoading, setWebhookTestLoading] = useState<string | null>(null);
 const [webhookTestResult, setWebhookTestResult] = useState<{ id: string; success: boolean; statusCode: number | null; error: string | null } | null>(null);
 const [webhookDeliveriesLoading, setWebhookDeliveriesLoading] = useState<string | null>(null);
 // API Keys (Item 17)
 const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
 const [apiKeyCreateLoading, setApiKeyCreateLoading] = useState(false);
 const [apiKeyCreateError, setApiKeyCreateError] = useState('');
 const [newApiKeyName, setNewApiKeyName] = useState('');
 const [newApiKeyScopes, setNewApiKeyScopes] = useState<string[]>(['invoices:read', 'logs:read', 'summary:read']);
 const [justCreatedKey, setJustCreatedKey] = useState<{ id: string; key: string } | null>(null);
 const [apiKeyRevokeLoading, setApiKeyRevokeLoading] = useState<string | null>(null);
 const [showApiKeyForm, setShowApiKeyForm] = useState(false);
 const [keyCopied, setKeyCopied] = useState(false);
 const [showApiKeyDocs, setShowApiKeyDocs] = useState(false);
 // SSO (Item 19)
 const [localSsoEnabled, setLocalSsoEnabled] = useState(false);
 const [localSsoDomain, setLocalSsoDomain] = useState('');
 const [ssoSaving, setSsoSaving] = useState(false);
 // Team Members / RBAC
 const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
 const [myWorkspaces, setMyWorkspaces] = useState<WorkspaceMembership[]>([]);
 const [activeWorkspaceOwnerId, setActiveWorkspaceOwnerId] = useState<string | null>(null);
 const [teamInviteEmail, setTeamInviteEmail] = useState('');
 const [teamInviteRole, setTeamInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
 const [teamInviteLoading, setTeamInviteLoading] = useState(false);
 const [teamInviteError, setTeamInviteError] = useState('');
 const [teamInviteSuccess, setTeamInviteSuccess] = useState('');
 const [teamRoleUpdateLoading, setTeamRoleUpdateLoading] = useState<string | null>(null);
 const [memberConnectionsLoading, setMemberConnectionsLoading] = useState<string | null>(null);
 const [localSlackUrl, setLocalSlackUrl] = useState('');
 const [slackUrlSaving, setSlackUrlSaving] = useState(false);
 const [webhookPayloadOpen, setWebhookPayloadOpen] = useState<string | null>(null);
 const [pendingInviteToken, setPendingInviteToken] = useState<string | null>(null);
 const [inviteAcceptInfo, setInviteAcceptInfo] = useState<TeamInviteInfo | null>(null);
 const [inviteAcceptLoading, setInviteAcceptLoading] = useState(false);
 const [inviteAcceptError, setInviteAcceptError] = useState('');
 const [inviteAcceptDone, setInviteAcceptDone] = useState(false);
 const [inviteInfoLoading, setInviteInfoLoading] = useState(false);
 const [invoiceSearch, setInvoiceSearch] = useState('');
 const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<'all' | 'overdue' | 'snoozed' | 'paused' | 'excluded' | 'collected'>('all');
 const [reportPeriod, setReportPeriod] = useState<30 | 60 | 90 | 365 | 0>(30);
 const [analyticsSubTab, setAnalyticsSubTab] = useState<'charts' | 'insights' | 'reports'>('charts');
 const [insightsSegmentFilter, setInsightsSegmentFilter] = useState<string>('all');
 const [snoozeModal, setSnoozeModal] = useState<{ invoiceId: string; realmId: string; customerName: string } | null>(null);
 const [snoozeDays, setSnoozeDays] = useState(7);
 const [snoozeLoading, setSnoozeLoading] = useState(false);
 const [collectionNotes, setCollectionNotes] = useState<CollectionNote[]>([]);
 const [noteModal, setNoteModal] = useState<{ invoiceId: string; realmId: string; customerName: string; docNumber: string } | null>(null);
 const [noteInput, setNoteInput] = useState('');
 const [ptpInput, setPtpInput] = useState('');
 const [noteSaving, setNoteSaving] = useState(false);
 // Item 26: What's New modal
 const [showWhatsNew, setShowWhatsNew] = useState(false);
 // Item 23: Customer meta modal
 const [customerMetaModal, setCustomerMetaModal] = useState<{ realmId: string; customerEmail: string; customerName: string } | null>(null);
 const [cmNotes, setCmNotes] = useState('');
 const [cmTags, setCmTags] = useState('');
 const [cmEmailOverride, setCmEmailOverride] = useState('');
 const [cmExcluded, setCmExcluded] = useState(false);
 const [cmSaving, setCmSaving] = useState(false);
 const [cmActiveTab, setCmActiveTab] = useState<'details' | 'history'>('details');
 // Item 24: Invoice controls modal
 const [invoiceCtrlModal, setInvoiceCtrlModal] = useState<{ invoiceId: string; realmId: string; customerName: string; docNumber: string } | null>(null);
 const [icDisabled, setIcDisabled] = useState(false);
 const [icMarkedCollected, setIcMarkedCollected] = useState(false);
 const [icCcEmail, setIcCcEmail] = useState('');
 const [icCustomMessage, setIcCustomMessage] = useState('');
 const [icSaving, setIcSaving] = useState(false);
 const [supportThreads, setSupportThreads] = useState<SupportThread[]>([]);
 const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
 const [supportHandoffs, setSupportHandoffs] = useState<SupportHandoff[]>([]);
 const [dunningTiers, setDunningTiers] = useState<DunningTier[]>([]);
 const [activeTab, setActiveTab] = useState('connections');
 const [loading, setLoading] = useState(true);
 const [isSidebarOpen, setIsSidebarOpen] = useState(false);
 const [syncingId, setSyncingId] = useState<string | null>(null);
 const [savingSettings, setSavingSettings] = useState(false);
const [editingTierId, setEditingTierId] = useState<string | null>(null);
 const [showMergePreview, setShowMergePreview] = useState<boolean>(false);
 const [showTemplateLibrary, setShowTemplateLibrary] = useState<boolean>(false);
 const [selectedRealmId, setSelectedRealmId] = useState<string>('all');
 const [selectedHistoryLog, setSelectedHistoryLog] = useState<DunningLog | null>(null);
 const [selectedSupportThreadId, setSelectedSupportThreadId] = useState<string | null>(null);
const [supportInput, setSupportInput] = useState('');
const [supportSending, setSupportSending] = useState(false);
const [supportPanelMode, setSupportPanelMode] = useState<'chat' | 'help'>('chat');
const [supportSearch, setSupportSearch] = useState('');
const [supportStatusFilter, setSupportStatusFilter] = useState('all');
const [helpSearch, setHelpSearch] = useState('');
const [expandedHelpArticles, setExpandedHelpArticles] = useState<Record<string, boolean>>({});
 const [maintenanceNotice, setMaintenanceNotice] = useState<MaintenanceNotice | null>(null);
 const [helpFeedback, setHelpFeedback] = useState<Record<string, 'yes' | 'no'>>({});
 const [supportMessageFeedback, setSupportMessageFeedback] = useState<Record<string, 'yes' | 'no'>>({});
 const [showSessionExpiryWarning, setShowSessionExpiryWarning] = useState(false);
 const [sessionCountdown, setSessionCountdown] = useState(SESSION_COUNTDOWN_SECONDS);
 const sessionWarningTimerRef = useRef<number | null>(null);
 const sessionLogoutTimerRef = useRef<number | null>(null);
 const sessionCountdownTimerRef = useRef<number | null>(null);
 const activityThrottleRef = useRef(0);
 const newConversationMode = useRef(false);

 useEffect(() => {
  if (!selectedHistoryLog) return;
  const handleEscape = (event: KeyboardEvent) => {
   if (event.key === 'Escape') setSelectedHistoryLog(null);
  };
  window.addEventListener('keydown', handleEscape);
  return () => window.removeEventListener('keydown', handleEscape);
 }, [selectedHistoryLog]);

 const [userProfile, setUserProfile] = useState<UserProfile>({ useCustomSender: false, notificationsEnabled: true });
 const [newPassword, setNewPassword] = useState('');
 const [profilePicUrl, setProfilePicUrl] = useState('');
 const [notifications, setNotifications] = useState<AppNotification[]>([]);

 // FIX: Auth form state (was missing — login form didn't work)
 const [authEmail, setAuthEmail] = useState('');
 const [authPassword, setAuthPassword] = useState('');
 const [authConfirmPassword, setAuthConfirmPassword] = useState('');
 const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
 const [authLoading, setAuthLoading] = useState(false);
 const [tosChecked, setTosChecked] = useState(false);
 const [tosLinkOpened, setTosLinkOpened] = useState(false);
 const [privacyChecked, setPrivacyChecked] = useState(false);
 const [privacyLinkOpened, setPrivacyLinkOpened] = useState(false);
 const [verificationEmailSent, setVerificationEmailSent] = useState(false);
 const [resendingVerification, setResendingVerification] = useState(false);
 const [showTosModal, setShowTosModal] = useState(false);
 const [modalTosOpened, setModalTosOpened] = useState(false);
 const [modalPrivacyOpened, setModalPrivacyOpened] = useState(false);
 const [modalTosChecked, setModalTosChecked] = useState(false);
 const [modalPrivacyChecked, setModalPrivacyChecked] = useState(false);
 const [modalAccepting, setModalAccepting] = useState(false);
 // Reset modal checkbox state every time the modal opens so stale state from a previous session never leaks
 useEffect(() => {
  if (!showTosModal) return;
  setModalTosOpened(false);
  setModalPrivacyOpened(false);
  setModalTosChecked(false);
  setModalPrivacyChecked(false);
  setModalAccepting(false);
 }, [showTosModal]);

 // Page routing for auth screens
 const [authPage, setAuthPage] = useState<'login' | 'recovery' | 'reset'>('login');

 // Recovery page state (forgot password / forgot username)
 const [recoveryTab, setRecoveryTab] = useState<'password' | 'username'>('password');
 const [recoveryEmail, setRecoveryEmail] = useState('');
 const [recoveryLoading, setRecoveryLoading] = useState(false);
 const [recoverySent, setRecoverySent] = useState(false);
 const [recoveryResult, setRecoveryResult] = useState('');

 // Password Reset Flow — detect URL params SYNCHRONOUSLY at initialization
 const resetParams = useMemo(() => {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const oobCode = params.get('oobCode');
  if (mode === 'resetPassword' && oobCode) return { oobCode };
  return null;
 }, []);

 const [resetMode, setResetMode] = useState(!!resetParams);
 const [resetOobCode] = useState(resetParams?.oobCode || '');
 const [resetNewPassword, setResetNewPassword] = useState('');
 const [resetConfirmPassword, setResetConfirmPassword] = useState('');
 const [resetEmail, setResetEmail] = useState('');
 const [resetLoading, setResetLoading] = useState(false);
 const [resetSuccess, setResetSuccess] = useState(false);

 // Settings Draft State (matches reference pattern)
 const [editingDraft, setEditingDraft] = useState<Partial<DunningTier> | null>(null);
 const [complianceReviewLoading, setComplianceReviewLoading] = useState(false);
 const [toneRewriteLoading, setToneRewriteLoading] = useState(false);
 const [toneRewritePreview, setToneRewritePreview] = useState<ToneRewriteResult | null>(null);
 const [suggestComplianceReview, setSuggestComplianceReview] = useState(false);
 const [lastToneMode, setLastToneMode] = useState<'softer' | 'balanced' | 'firmer' | 'professional' | 'shorter' | null>(null);
 const toneModeLabels: Record<'softer' | 'balanced' | 'firmer' | 'professional' | 'shorter', string> = {
  softer: 'Softer',
  balanced: 'Balanced',
  firmer: 'Firmer',
  professional: 'Professional',
  shorter: 'Shorter'
 };

 // --- Compliance State ---
 const [disputes, setDisputes] = useState<any[]>([]);
 const [optOuts, setOptOuts] = useState<any[]>([]);
 const [gdprEmail, setGdprEmail] = useState('');
 const [gdprLoading, setGdprLoading] = useState(false);
 const [reauthModalOpen, setReauthModalOpen] = useState(false);
 const [reauthPassword, setReauthPassword] = useState('');
 const [reauthError, setReauthError] = useState('');
 const [reauthLoading, setReauthLoading] = useState(false);
 const reauthCallbackRef = React.useRef<(() => Promise<void>) | null>(null);
 const currentSessionTokenRef = React.useRef<string | null>(null);
 const [signOutAllLoading, setSignOutAllLoading] = useState(false);

 const [onboardingDismissed, setOnboardingDismissed] = useState<boolean>(false);
 // Sync dismissed state once the user is known — key is scoped per UID so different accounts don't share it
 useEffect(() => {
  if (!user) return;
  setOnboardingDismissed(localStorage.getItem(`soarone_onboarding_dismissed_${user.uid}`) === '1');
 }, [user?.uid]);
 useEffect(() => {
  setLocalSsoEnabled(userProfile.ssoEnabled ?? false);
  setLocalSsoDomain(userProfile.ssoRequiredDomain ?? '');
 }, [userProfile.ssoEnabled, userProfile.ssoRequiredDomain]);
 useEffect(() => {
  setLocalSlackUrl(userProfile.slackWebhookUrl ?? '');
 }, [userProfile.slackWebhookUrl]);
 // Item 26: Show What's New modal once per version after login
 useEffect(() => {
  if (!user) return;
  const seen = localStorage.getItem('soarone_seen_version');
  if (seen !== APP_VERSION) setShowWhatsNew(true);
 }, [user?.uid]);
 const dismissOnboarding = () => {
  if (!user) return;
  localStorage.setItem(`soarone_onboarding_dismissed_${user.uid}`, '1');
  setOnboardingDismissed(true);
 };

 // --- 2FA / MFA State ---
 const [mfaSetupStep, setMfaSetupStep] = useState<null | 'qrcode' | 'verify'>(null);
 const [mfaTotpSecret, setMfaTotpSecret] = useState<any>(null);
 const [mfaQrDataUrl, setMfaQrDataUrl] = useState('');
 const [mfaVerifyCode, setMfaVerifyCode] = useState('');
 const [mfaLoading, setMfaLoading] = useState(false);
 const [mfaError, setMfaError] = useState('');
 const [mfaChallengeResolver, setMfaChallengeResolver] = useState<any>(null);
 const [mfaChallengeCode, setMfaChallengeCode] = useState('');
 const [mfaChallengeLoading, setMfaChallengeLoading] = useState(false);

 const isGoogleUser = () =>
  auth.currentUser?.providerData?.some(p => p.providerId === 'google.com') ?? false;

 const requireReauth = (callback: () => Promise<void>) => {
  if (isGoogleUser()) {
   // Google users re-authenticate via popup — no password modal needed
   reauthenticateWithPopup(auth.currentUser!, googleProvider)
    .then(() => callback())
    .catch((err: any) => {
     if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
      addNotify('info', 'Re-authentication cancelled. Please try again.');
     } else {
      addNotify('error', `Re-authentication failed: ${err.message}`);
     }
    });
   return;
  }
  reauthCallbackRef.current = callback;
  setReauthPassword('');
  setReauthError('');
  setReauthModalOpen(true);
 };

 const handleReauthSubmit = async () => {
  if (!auth.currentUser?.email || !reauthPassword) {
   setReauthError('Password is required.');
   return;
  }
  setReauthLoading(true);
  setReauthError('');
  try {
   const credential = EmailAuthProvider.credential(auth.currentUser.email, reauthPassword);
   await reauthenticateWithCredential(auth.currentUser, credential);
   setReauthModalOpen(false);
   setReauthPassword('');
   if (reauthCallbackRef.current) await reauthCallbackRef.current();
  } catch (err: any) {
   const code = err?.code || '';
   if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
    setReauthError('Incorrect password. Please try again.');
   } else if (code === 'auth/too-many-requests') {
    setReauthError('Too many attempts. Please wait a moment and try again.');
   } else {
    setReauthError('Verification failed. Please try again.');
   }
  } finally { setReauthLoading(false); }
 };
 const reviewedTemplateCount = useMemo(() => dunningTiers.filter(tier => !!tier.complianceReview?.reviewedAt).length, [dunningTiers]);
 const passingTemplateCount = useMemo(() => dunningTiers.filter(tier => tier.complianceReview?.compliant === true).length, [dunningTiers]);
 const flaggedTemplateCount = useMemo(() => dunningTiers.filter(tier => tier.complianceReview?.compliant === false).length, [dunningTiers]);
 // --- Plan Gating ---
 const currentPlan = useMemo((): PlanConfig => {
  return PLANS[userProfile.plan || 'starter'];
 }, [userProfile.plan]);

 const getParsedDate = (value: any): Date | null => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (typeof value === 'string') {
   const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
   if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
 };

 const computeTrialActive = (trialEndsAt: any, hasPaidSubscription: boolean): boolean => {
  const trialEnd = getParsedDate(trialEndsAt);
  if (!trialEnd || trialEnd <= new Date()) return false;
  return !hasPaidSubscription;
 };

 const ownHasPaidSubscription = !!userProfile.paddleSubscriptionId &&
  ['active', 'trialing', 'past_due', 'paused'].includes(userProfile.subscriptionStatus || '');

 const isTrialActive = useMemo((): boolean => (
  computeTrialActive(userProfile.trialEndsAt, ownHasPaidSubscription)
 ), [userProfile.trialEndsAt, ownHasPaidSubscription]);

 const trialDaysLeft = useMemo((): number => {
  const trialEnd = getParsedDate(userProfile.trialEndsAt);
  if (!isTrialActive || !trialEnd) return 0;
  return Math.max(0, Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
 }, [isTrialActive, userProfile.trialEndsAt]);

 // Effective plan:
 // - starter trials are upgraded to Pro for the trial period
 // - manual Pro / CPA trials inherit their assigned plan
 // Also handle Paddle subscription lifecycle states
 const effectivePlan = useMemo((): PlanConfig => {
  if (isTrialActive) {
   if ((userProfile.plan || 'starter') === 'starter') return PLANS.pro;
   return currentPlan;
  }
  // Past due: grace period — keep current plan but frontend shows warning banner
  if (userProfile.subscriptionStatus === 'past_due') return currentPlan;
  // Paused or canceled: downgrade to starter
  if (userProfile.subscriptionStatus === 'paused' || userProfile.subscriptionStatus === 'canceled') {
   return PLANS.starter;
  }
  return currentPlan;
 }, [isTrialActive, currentPlan, userProfile.subscriptionStatus, userProfile.plan]);

 const canAddConnection = useMemo(() => connections.length < effectivePlan.maxConnections, [connections.length, effectivePlan]);
 const canAddTemplate = useMemo(() => dunningTiers.length < effectivePlan.maxTemplates, [dunningTiers.length, effectivePlan]);
 const canUseSmtp = effectivePlan.customSmtp;
 const canUseCompliance = effectivePlan.compliance;
 const canUseTeam = effectivePlan.maxTeamMembers > 0;

 const activeWorkspace = myWorkspaces.find(w => w.ownerUid === activeWorkspaceOwnerId) ?? null;
 const isViewingMemberWorkspace = activeWorkspaceOwnerId !== null;
 const workspaceTrialActive = useMemo((): boolean => (
  computeTrialActive(activeWorkspace?.ownerTrialEndsAt, activeWorkspace?.ownerHasPaidSubscription === true)
 ), [activeWorkspace?.ownerTrialEndsAt, activeWorkspace?.ownerHasPaidSubscription]);
 // Insights access: free during the active trial OR when the Insights add-on is active.
 // For member workspaces, use the owner access summary mirrored into the workspace doc.
 const hasInsightsAccess = useMemo((): boolean => {
  if (isViewingMemberWorkspace) {
   if (workspaceTrialActive) return true;
   return (activeWorkspace?.ownerActiveProducts || []).includes('insights');
  }
  if (isTrialActive) return true;
  return (userProfile.activeProducts || []).includes('insights');
 }, [isViewingMemberWorkspace, workspaceTrialActive, activeWorkspace?.ownerActiveProducts, isTrialActive, userProfile.activeProducts]);
 // Per-connection access restriction: filter connections when a member workspace has connectionIds set
 const visibleConnections = useMemo(() => {
  if (!isViewingMemberWorkspace) return connections;
  const cIds = activeWorkspace?.connectionIds;
  if (!cIds || cIds.length === 0) return connections;
  return connections.filter(c => cIds.includes(c.realmId));
 }, [connections, isViewingMemberWorkspace, activeWorkspace]);
 const memberWorkspaceRole = activeWorkspace?.role ?? null;
 const templateSafetySummary = useMemo(() => {
  return dunningTiers.reduce((summary, tier) => {
   const level = getTemplateSafety(tier).level;
   summary[level] += 1;
   return summary;
  }, { ready: 0, review: 0, blocked: 0 } as Record<TemplateSafetyLevel, number>);
 }, [dunningTiers, userProfile.senderAddress, userProfile.senderCity, userProfile.useCustomSender, userProfile.senderEmail, canUseCompliance, getTemplateSafety]);
 const historyStatusSummary = useMemo(() => {
  return logs.reduce((summary, log) => {
   const normalized = String(log.deliveryStatus || 'unknown').toLowerCase();
   if (normalized in summary) {
    summary[normalized as keyof typeof summary] += 1;
   } else {
    summary.unknown += 1;
   }
   return summary;
 }, {
   queued: 0,
   sent: 0,
   delivered: 0,
   opened: 0,
   failed: 0,
   blocked: 0,
   unknown: 0
  });
 }, [logs]);
 const sortedSupportThreads = useMemo(() => {
  return [...supportThreads].sort((a, b) => getTimestampMs(b.updatedAt || b.createdAt) - getTimestampMs(a.updatedAt || a.createdAt));
 }, [supportThreads]);
 const filteredSupportThreads = useMemo(() => {
  const q = supportSearch.trim().toLowerCase();
  return sortedSupportThreads.filter(thread => {
   const matchesSearch = !q || [
    thread.subject || '',
    thread.lastMessagePreview || '',
    thread.customerDisplayName || '',
    thread.customerEmail || '',
    thread.issueType || ''
   ].some(value => value.toLowerCase().includes(q));
   const matchesStatus = supportStatusFilter === 'all' || String(thread.status || '').toLowerCase() === supportStatusFilter;
   return matchesSearch && matchesStatus;
  });
 }, [sortedSupportThreads, supportSearch, supportStatusFilter]);
const selectedSupportThread = useMemo(() => {
  if (selectedSupportThreadId === null) return null;
  return filteredSupportThreads.find(thread => thread.id === selectedSupportThreadId) || null;
 }, [filteredSupportThreads, selectedSupportThreadId]);

 const dedupeSupportTranscriptMessages = (messages: SupportMessage[]) => {
  const sorted = [...messages].sort((a, b) => getTimestampMs(a.createdAt) - getTimestampMs(b.createdAt));
  const deduped: SupportMessage[] = [];

  for (const message of sorted) {
   const prior = deduped[deduped.length - 1];
   const sameThread = prior?.threadId === message.threadId;
   const sameSender = prior?.senderType === message.senderType;
   const sameText = String(prior?.message || '').trim() === String(message.message || '').trim();
   const closeInTime = Math.abs(getTimestampMs(message.createdAt) - getTimestampMs(prior?.createdAt)) <= 15000;
   const localPair = String(prior?.id || '').startsWith('local_') || String(message.id || '').startsWith('local_');

   if (prior && sameThread && sameSender && sameText && closeInTime && localPair) {
    if (!String(message.id || '').startsWith('local_')) {
     deduped[deduped.length - 1] = message;
    }
    continue;
   }

   deduped.push(message);
  }

  return deduped;
 };

 const selectedSupportMessages = useMemo(() => {
  if (!selectedSupportThread) return [];
  return dedupeSupportTranscriptMessages(supportMessages
   .filter(message => message.threadId === selectedSupportThread.id)
   .sort((a, b) => getTimestampMs(a.createdAt) - getTimestampMs(b.createdAt)));
 }, [supportMessages, selectedSupportThread]);
 const selectedSupportHandoff = useMemo(() => {
  if (!selectedSupportThread) return null;
  return supportHandoffs
   .filter(handoff => handoff.threadId === selectedSupportThread.id)
   .sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt))[0] || null;
 }, [supportHandoffs, selectedSupportThread]);
 const selectedSupportStatusBadge = useMemo(() => getSupportStatusBadge(selectedSupportThread, supportSending), [selectedSupportThread, supportSending]);
const filteredHelpArticles = useMemo(() => {
  const q = helpSearch.trim().toLowerCase();
  const filtered = !q ? HELP_ARTICLES : HELP_ARTICLES.filter(article => {
  return [
   article.title,
   article.summary,
   article.category,
   ...article.bullets,
   ...(article.howTo || []),
   ...(article.details || []),
   ...(article.faqs || []).flatMap(faq => [faq.question, faq.answer])
  ].join(' ').toLowerCase().includes(q);
 });
 return [...filtered].sort((a, b) => {
  const aIsFaq = a.title.toLowerCase().includes('faq');
  const bIsFaq = b.title.toLowerCase().includes('faq');
  if (aIsFaq && !bIsFaq) return -1;
  if (!aIsFaq && bIsFaq) return 1;
  return 0;
 });
}, [helpSearch]);
 const effectiveSupportTier = effectivePlan.supportTier;
 const effectiveSupportSlaLabel = effectivePlan.supportSlaLabel;
 const maintenanceBanner = useMemo(() => {
  if (!maintenanceNotice) return null;
  const phase = String(maintenanceNotice.phase || '').toLowerCase();
  const isVisible = maintenanceNotice.enabled === true || phase === 'scheduled' || phase === 'resolved';
  if (!isVisible) return null;

  const badge = getMaintenanceBadge(maintenanceNotice);
  const affected = Array.isArray(maintenanceNotice.affectedAreas) ? maintenanceNotice.affectedAreas.filter(Boolean) : [];
  const timeBits = [
   maintenanceNotice.startsAt ? `Starts: ${formatTimestamp(maintenanceNotice.startsAt)}` : '',
   maintenanceNotice.endsAt ? `Ends: ${formatTimestamp(maintenanceNotice.endsAt)}` : '',
   maintenanceNotice.nextUpdateAt ? `Next update: ${formatTimestamp(maintenanceNotice.nextUpdateAt)}` : ''
  ].filter(Boolean);

  let fallbackMessage = 'A scheduled SoarOne maintenance update has been posted.';
  if (phase === 'scheduled') {
   fallbackMessage = 'Planned maintenance is scheduled. Some areas may be temporarily unavailable during the window.';
  } else if (phase === 'resolved') {
   fallbackMessage = 'Scheduled maintenance is complete and core checks have passed.';
  } else if (phase === 'in_progress') {
   fallbackMessage = 'Scheduled maintenance is in progress. Some features may be temporarily unavailable while work completes.';
  }

  return {
   badge,
   title: maintenanceNotice.title || badge.label,
   message: maintenanceNotice.message || fallbackMessage,
   affected,
   timeBits,
   retryAfterSeconds: maintenanceNotice.retryAfterSeconds,
   supportEmail: maintenanceNotice.supportEmail || null
  };
 }, [maintenanceNotice]);
 const canExportCsv = effectivePlan.csvExport;
 const onboardingSteps = useMemo<OnboardingStep[]>(() => [
  {
   id: 'profile',
   title: 'Add your company profile',
   description: 'Set the sender or company name customers will recognize in reminders.',
   complete: Boolean((userProfile.displayName || '').trim()),
   actionLabel: 'Open Account',
   actionKey: 'account'
  },
  {
   id: 'quickbooks',
   title: 'Connect QuickBooks',
   description: 'Link your accounting system so SoarOne can sync invoices and start automation.',
   complete: connections.length > 0,
   actionLabel: 'Connect QuickBooks',
   actionKey: 'quickbooks'
  },
  {
   id: 'mailing',
   title: 'Add your mailing address',
   description: 'Complete the required business address used in compliance footers and sender identity.',
   complete: Boolean((userProfile.senderAddress || '').trim() && (userProfile.senderCity || '').trim()),
   actionLabel: 'Add Address',
   actionKey: 'account'
  },
  {
   id: 'templates',
   title: 'Review your reminder templates',
   description: 'Check timing, tone, and placeholders before reminders go live.',
   complete: dunningTiers.some(t => !t.isDefault),
   actionLabel: 'Review Templates',
   actionKey: 'settings'
  },
  {
   id: 'sync',
   title: 'Run your first sync',
   description: 'Pull your overdue invoices from QuickBooks so SoarOne can start working.',
   complete: connections.some(c => c.lastUpdated != null),
   actionLabel: 'Sync Now',
   actionKey: 'connections'
  }
 ], [userProfile.displayName, userProfile.senderAddress, userProfile.senderCity, connections.length, dunningTiers.length, connections]);
 const completedOnboardingSteps = useMemo(() => onboardingSteps.filter(step => step.complete).length, [onboardingSteps]);
 const onboardingProgressPercent = useMemo(() => onboardingSteps.length > 0 ? Math.round((completedOnboardingSteps / onboardingSteps.length) * 100) : 0, [completedOnboardingSteps, onboardingSteps.length]);
 const allOnboardingDone = completedOnboardingSteps === onboardingSteps.length;
 // Never show onboarding when viewing someone else's workspace — it's irrelevant and evaluates the wrong data
 const showOnboardingChecklist = !onboardingDismissed && !allOnboardingDone && !isViewingMemberWorkspace;
 const showOnboardingComplete = !onboardingDismissed && allOnboardingDone && !isViewingMemberWorkspace;

const [showPricing, setShowPricing] = useState(false);
const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
const [insightsCheckoutLoading, setInsightsCheckoutLoading] = useState(false);
 const [billingPortalData, setBillingPortalData] = useState<BillingPortalData | null>(null);

 const handleCheckout = async (planId: string) => {
  // SECURITY: Validate planId is one of our known plans
  const validPlans: PlanId[] = ['starter', 'pro', 'cpa'];
 if (!validPlans.includes(planId as PlanId)) {
  addNotify('error', 'Invalid plan selected.');
  return;
 }

 let Paddle = (window as any).Paddle;
 if (!Paddle) {
  try {
   Paddle = await initializePaddle();
  } catch (err: any) {
   addNotify('error', err?.message || 'Payment system failed to load. Please try again.');
   return;
  }
 }
  if (!user?.email) {
   addNotify('error', 'You must be logged in to subscribe.');
   return;
  }

 const priceId = PADDLE_PRICE_IDS[planId as PlanId];
 if (!isPlanPaddleConfigured() || !isValidPaddlePriceId(priceId)) {
  addNotify('error', 'Billing is not yet configured. Please contact support.');
  console.error('[Paddle] Missing/invalid config:', getPlanPaddleConfigIssues());
  return;
 }

  setCheckoutLoading(planId);

  try {
   Paddle.Checkout.open({
    items: [{ priceId, quantity: 1 }],
    customer: {
     email: user.email,
    },
    customData: {
     firebaseUid: user.uid,
     planId: planId,
    },
    settings: {
     displayMode: 'overlay',
     theme: 'light',
     locale: 'en',
     allowLogout: false,
     successUrl: window.location.origin + window.location.pathname + '?checkout=plan_success',
    },
   });
  } catch (err: any) {
   const details =
    err?.response?.data?.error?.detail ||
    err?.response?.data?.error?.message ||
    err?.message ||
    'Failed to open checkout. Please try again.';
   addNotify('error', details);
   console.error('[Paddle] Checkout open error:', err);
  } finally {
   setCheckoutLoading(null);
  }
 };

 const handleInsightsCheckout = async () => {
  let Paddle = (window as any).Paddle;
  if (!Paddle) {
   try {
    Paddle = await initializePaddle();
   } catch (err: any) {
    addNotify('error', err?.message || 'Payment system failed to load. Please try again.');
    return;
   }
  }
  if (!user?.email) { addNotify('error', 'You must be logged in to subscribe.'); return; }
  if (getInsightsPaddleConfigIssues().length > 0) {
   addNotify('error', 'AR Insights billing is not yet configured. Please contact support.');
   console.error('[Paddle] Missing/invalid Insights config:', getInsightsPaddleConfigIssues());
   return;
  }
  setInsightsCheckoutLoading(true);
  try {
   Paddle.Checkout.open({
    items: [{ priceId: PADDLE_PRICE_INSIGHTS, quantity: 1 }],
    customer: { email: user.email },
    customData: { firebaseUid: user.uid, planId: 'insights' },
    settings: {
     displayMode: 'overlay',
     theme: 'light',
     locale: 'en',
     allowLogout: false,
     successUrl: window.location.origin + window.location.pathname + '?checkout=insights_success',
    },
   });
  } catch (err: any) {
   addNotify('error', err?.message || 'Failed to open checkout. Please try again.');
   console.error('[Paddle] Insights checkout error:', err);
  } finally {
   setInsightsCheckoutLoading(false);
  }
 };

 const loadBillingPortalData = useCallback(async () => {
  if (!functions || !user) return;
  try {
   const getPortalUrl = httpsCallable(functions, 'getPaddlePortalUrl');
   const result: any = await getPortalUrl();
   setBillingPortalData(result.data || null);
  } catch (err: any) {
   console.error('[Billing] Failed to fetch portal data:', err.message || err);
   setBillingPortalData(null);
  }
 }, [functions, user]);

 const handleManageSubscription = async (action: 'manage' | 'payment' | 'cancel' = 'manage') => {
  if (!functions || !user) return;
  setCheckoutLoading(action);
  try {
   const getPortalUrl = httpsCallable(functions, 'getPaddlePortalUrl');
   const result: any = await getPortalUrl();
   const portalData = result.data || {};
   setBillingPortalData(portalData);

   const targetUrl = action === 'payment'
    ? (portalData.updateUrl || portalData.url)
    : action === 'cancel'
     ? (portalData.cancelUrl || portalData.url)
     : (portalData.url || portalData.updateUrl || portalData.cancelUrl);

   if (targetUrl) {
    window.open(targetUrl, '_blank');
   } else {
    addNotify('error', 'Could not generate subscription management link. Please contact support.');
   }
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to open subscription portal.');
  } finally {
   setCheckoutLoading(null);
  }
 };

 useEffect(() => {
  if (user && userProfile.paddleSubscriptionId) {
   loadBillingPortalData();
  } else {
   setBillingPortalData(null);
  }
 }, [user, userProfile.paddleSubscriptionId, loadBillingPortalData]);

 const requireUpgrade = (feature: string) => {
  setShowPricing(true);
  addNotify('info', `${feature} requires a higher plan. Choose a plan to unlock.`);
 };

 // --- History/Search State ---
 const [historySearch, setHistorySearch] = useState('');
 const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
 const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>({ key: 'sentAt', direction: 'desc' });
 const [viewDate, setViewDate] = useState(new Date());
 const [selectedDate, setSelectedDate] = useState(new Date());
 const initialLoadDone = useRef(false);
 const profileFormDirty = useRef(false);
 const fileInputRef = useRef<HTMLInputElement>(null);
 const listenerSubCountRef = useRef({ count: 0, windowStart: Date.now() });

const addNotify = (type: 'success' | 'error' | 'info', message: string) => {
 const id = Date.now();
 setNotifications(prev => [...prev, { id, type, message }]);
 setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
};

const clearSessionTimers = useCallback(() => {
 if (sessionWarningTimerRef.current) {
  window.clearTimeout(sessionWarningTimerRef.current);
  sessionWarningTimerRef.current = null;
 }
 if (sessionLogoutTimerRef.current) {
  window.clearTimeout(sessionLogoutTimerRef.current);
  sessionLogoutTimerRef.current = null;
 }
 if (sessionCountdownTimerRef.current) {
  window.clearInterval(sessionCountdownTimerRef.current);
  sessionCountdownTimerRef.current = null;
 }
}, []);

const handleIdleLogout = useCallback(async () => {
 clearSessionTimers();
 setShowSessionExpiryWarning(false);
 setSessionCountdown(SESSION_COUNTDOWN_SECONDS);
 try {
  await signOut(auth);
  addNotify('info', 'You were signed out after 30 minutes of inactivity.');
 } catch {
  addNotify('error', 'Session expired, but automatic sign-out did not complete cleanly.');
 }
}, [clearSessionTimers]);

const startSessionCountdown = useCallback(() => {
 if (sessionCountdownTimerRef.current) {
  window.clearInterval(sessionCountdownTimerRef.current);
 }
 setSessionCountdown(SESSION_COUNTDOWN_SECONDS);
 sessionCountdownTimerRef.current = window.setInterval(() => {
  setSessionCountdown(prev => {
   if (prev <= 1) {
    if (sessionCountdownTimerRef.current) {
     window.clearInterval(sessionCountdownTimerRef.current);
     sessionCountdownTimerRef.current = null;
    }
    return 0;
   }
   return prev - 1;
  });
 }, 1000);
}, []);

const resetSessionTimers = useCallback(() => {
 clearSessionTimers();
 setShowSessionExpiryWarning(false);
 setSessionCountdown(SESSION_COUNTDOWN_SECONDS);
 sessionWarningTimerRef.current = window.setTimeout(() => {
  setShowSessionExpiryWarning(true);
  startSessionCountdown();
 }, SESSION_WARNING_TIMEOUT_MS);
 sessionLogoutTimerRef.current = window.setTimeout(() => {
  handleIdleLogout().catch(() => {});
 }, SESSION_IDLE_TIMEOUT_MS);
}, [clearSessionTimers, handleIdleLogout, startSessionCountdown]);

const handleExtendSession = useCallback(() => {
 resetSessionTimers();
 addNotify('success', 'Session extended. We will keep you signed in while you stay active.');
}, [resetSessionTimers]);

 // SECURITY: Strip billing/subscription fields from profile before client-side writes
 // These fields are ONLY writable by backend Cloud Functions (admin SDK bypasses Firestore rules)
 const BILLING_FIELDS = ['plan', 'planStartedAt', 'trialEndsAt', 'subscriptionStatus',
  'paddleSubscriptionId', 'paddleCustomerId', 'paddleCancellationEffectiveDate'] as const;

 const stripBillingFields = (profile: Partial<UserProfile>): Partial<UserProfile> => {
  const safe = { ...profile };
  for (const field of BILLING_FIELDS) {
   delete (safe as any)[field];
  }
  return safe;
 };

 // --- Auth Handlers ---
 // FIX: Login form now actually works
 const handleAuthSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!authEmail || !authPassword) return addNotify('error', 'Email and password are required.');
  if (authMode === 'register') {
   if (authPassword !== authConfirmPassword) return addNotify('error', 'Passwords do not match.');
   if (authPassword.length < 8) return addNotify('error', 'Use a password with at least 8 characters.');
   if (!tosChecked || !privacyChecked) return addNotify('error', 'You must read and accept both the Terms of Service and Privacy Policy to create an account.');
  }
  setAuthLoading(true);
  try {
   if (authMode === 'register') {
    await createUserWithEmailAndPassword(auth, authEmail.trim(), authPassword);
    // Seed profile first (sets plan + trialEndsAt), then record TOS acceptance
    try { await httpsCallable(getFunctions(undefined, 'asia-east2'), 'createUserProfile')(); } catch {}
    try { await httpsCallable(getFunctions(undefined, 'asia-east2'), 'acceptTos')(); } catch {}
    try {
     await httpsCallable(getFunctions(undefined, 'asia-east2'), 'sendVerificationEmail')();
     setVerificationEmailSent(true);
     addNotify('success', `We sent a verification email to ${authEmail.trim()}. Click the link to continue.`);
    } catch {
     addNotify('info', 'Account created. Use "Send verification email" on the next screen to get the link.');
    }
   } else {
    await signInWithEmailAndPassword(auth, authEmail.trim(), authPassword);
   }
  } catch (err: any) {
   if (err.code === 'auth/multi-factor-auth-required') {
    const resolver = getMultiFactorResolver(auth, err);
    setMfaChallengeResolver(resolver);
    setMfaChallengeCode('');
    setAuthLoading(false);
    return;
   }
   addNotify('error', mapAuthError(err, authMode));
  } finally {
   setAuthLoading(false);
  }
 };

 const handleForgotPassword = () => {
  setRecoveryEmail(authEmail || '');
  setRecoveryTab('password');
  setRecoverySent(false);
  setRecoveryResult('');
  setAuthPage('recovery');
 };

const handleGoogleAuth = async () => {
  try {
   await signInWithPopup(auth, googleProvider);
  } catch (err: any) {
   if (err.code === 'auth/multi-factor-auth-required') {
    const resolver = getMultiFactorResolver(auth, err);
    setMfaChallengeResolver(resolver);
    setMfaChallengeCode('');
   } else if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
    addNotify('info', 'Popup sign-in was interrupted. Continuing with redirect sign-in...');
    await signInWithRedirect(auth, googleProvider);
   } else {
    addNotify('error', mapAuthError(err, authMode));
   }
  }
 };

 const handleResendVerificationEmail = async () => {
  if (!auth.currentUser || resendingVerification) return;
  setResendingVerification(true);
  try {
   await httpsCallable(getFunctions(undefined, 'asia-east2'), 'sendVerificationEmail')();
   setVerificationEmailSent(true);
   addNotify('success', `Verification email sent to ${auth.currentUser.email || 'your inbox'}.`);
  } catch (err: any) {
   const msg = (err?.message || '').includes('Too many') ? err.message : 'Could not send verification email. Please try again.';
   addNotify('error', msg);
  } finally {
   setResendingVerification(false);
  }
 };

 const handleCheckVerificationStatus = async () => {
  if (!auth.currentUser) return;
  try {
   await auth.currentUser.reload();
   const refreshedUser = auth.currentUser;
   setUser(refreshedUser);
   if (refreshedUser?.emailVerified) {
    addNotify('success', 'Email verified. Welcome to SoarOne.');
   } else {
    addNotify('info', 'Your email is not verified yet. Finish verification and try again.');
   }
  } catch (err: any) {
   addNotify('error', 'Could not refresh verification status. Please try again.');
  }
 };

 const handleRecoverySubmit = async () => {
  if (!recoveryEmail.trim()) return addNotify('error', 'Please enter your email address.');
  setRecoveryLoading(true);
  try {
   if (recoveryTab === 'password') {
    // Send password reset email
    await sendPasswordResetEmail(auth, recoveryEmail.trim());
    setRecoverySent(true);
    setRecoveryResult(`We sent a password reset link to ${recoveryEmail.trim()}. Check your inbox (and spam folder).`);
   } else {
    // Intentionally avoid revealing whether the email exists or which provider it uses.
    await fetchSignInMethodsForEmail(auth, recoveryEmail.trim());
    setRecoverySent(true);
    setRecoveryResult(`If an account exists for ${recoveryEmail.trim()}, use your normal sign-in method or reset your password from the login screen.`);
   }
  } catch (err: any) {
   if (err.code === 'auth/invalid-email') {
    addNotify('error', 'Please enter a valid email address.');
   } else {
    setRecoverySent(true);
    setRecoveryResult(`If an account exists for ${recoveryEmail.trim()}, use your normal sign-in method or reset your password from the login screen.`);
   }
  }
  setRecoveryLoading(false);
 };

 // Verify password reset code on mount (detection already done synchronously via useMemo)
 useEffect(() => {
  if (!resetMode || !resetOobCode) return;
  // Sign out any logged-in user so the reset screen shows cleanly
  signOut(auth).catch(() => {});
  // Verify the code and get the email
  verifyPasswordResetCode(auth, resetOobCode)
   .then((email) => setResetEmail(email))
   .catch(() => {
    addNotify('error', 'This password reset link has expired or is invalid. Please request a new one.');
    setResetMode(false);
    window.history.replaceState({}, '', window.location.pathname);
   });
 }, [resetMode, resetOobCode]);

 const handleResetPassword = async () => {
  if (!resetNewPassword || !resetConfirmPassword) {
   return addNotify('error', 'Please fill in both password fields.');
  }
  if (resetNewPassword !== resetConfirmPassword) {
   return addNotify('error', 'Passwords do not match.');
  }
  if (resetNewPassword.length < 8) {
   return addNotify('error', 'Password must be at least 8 characters.');
  }
  setResetLoading(true);
  try {
   await confirmPasswordReset(auth, resetOobCode, resetNewPassword);
   setResetSuccess(true);
   addNotify('success', 'Password updated successfully. You can now sign in.');
   // Clean URL parameters
   window.history.replaceState({}, '', window.location.pathname);
   // After 2 seconds, switch to login
   setTimeout(() => {
    setResetMode(false);
    setResetSuccess(false);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setAuthPage('login');
   }, 2000);
  } catch (err: any) {
   if (err.code === 'auth/expired-action-code') {
    addNotify('error', 'This reset link has expired. Please request a new one.');
   } else if (err.code === 'auth/weak-password') {
    addNotify('error', 'Password is too weak. Use at least 8 characters with letters and numbers.');
   } else {
    addNotify('error', err.message || 'Failed to reset password.');
   }
  }
  setResetLoading(false);
 };

 // --- Action Handlers ---
 const handleManualSync = async (realmId: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  setSyncingId(realmId);
  try {
   const payload: any = { realmId };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await httpsCallable(functions, 'manualDunningSync')(payload);
   addNotify('success', "Scanning complete.");
  } catch (err: any) {
   addNotify('error', err.message || "Sync failed.");
  } finally { setSyncingId(null); }
 };

 const handleDisconnect = async (realmId: string) => {
  if (!functions || isViewingMemberWorkspace) return;
  if (!window.confirm("Disconnect from QuickBooks? This revokes tokens and scrubs metadata.")) return;
  setSyncingId(realmId);
  try {
   await httpsCallable(functions, 'qboDisconnect')({ realmId });
   addNotify('success', "Identity unlinked.");
  } catch (err: any) {
   addNotify('error', err.message || "Revocation failed.");
  } finally { setSyncingId(null); }
 };

 const handleReconnect = async () => {
  if (!user || isViewingMemberWorkspace) return;
  if (!canAddConnection) {
   requireUpgrade(`More than ${effectivePlan.maxConnections} connection${effectivePlan.maxConnections > 1 ? 's' : ''}`);
   return;
  }
  if (!functions) return;
  try {
   const result = await httpsCallable<{}, { nonce: string }>(functions, 'qboGetAuthNonce')({});
   window.location.href = `${QBO_AUTH_INIT_URL}?nonce=${result.data.nonce}&uid=${user.uid}`;
  } catch (e: any) {
   addNotify('error', 'Failed to initiate QuickBooks connection: ' + e.message);
  }
 };

 // FIX: Tier editing uses draft state — copies full tier on Configure, saves on Commit
 const handleOnboardingAction = (actionKey: OnboardingStep['actionKey']) => {
  if (actionKey === 'quickbooks') {
   handleReconnect();
   return;
  }
  if (actionKey === 'settings') {
   setActiveTab('settings');
   return;
  }
  if (actionKey === 'connections') {
   setActiveTab('connections');
   // If there's exactly one active connection, trigger sync immediately
   const active = connections.find(c => c.status === 'active');
   if (active) handleManualSync(active.realmId);
   return;
  }
  setActiveTab('account');
 };

 type TemplateSafetyLevel = 'ready' | 'review' | 'blocked';
 type TemplateSafetyResult = { level: TemplateSafetyLevel; label: string; detail: string };

 function getTemplateSafety(tier: Partial<DunningTier>): TemplateSafetyResult {
  const subject = String(tier.subject || '').trim();
  const body = String(tier.body || '').trim();
  const combined = `${subject} ${body}`.toLowerCase();
  const violation = PROHIBITED_PHRASES.find(p => combined.includes(p));

  if (!subject || !body) {
   return { level: 'blocked', label: 'Blocked', detail: 'Subject and body are required before this template can send.' };
  }

  if (!(userProfile.senderAddress || '').trim() || !(userProfile.senderCity || '').trim()) {
   return { level: 'blocked', label: 'Blocked', detail: 'Add a physical mailing address in Account before reminders can send.' };
  }

  if (userProfile.useCustomSender && !(userProfile.senderEmail || '').trim()) {
   return { level: 'blocked', label: 'Blocked', detail: 'Custom sender is enabled, but no sender email is configured.' };
  }

  if (violation) {
   return { level: 'blocked', label: 'Blocked', detail: `Contains prohibited language: "${violation}".` };
  }

  if (tier.complianceReview?.compliant === false) {
   return { level: 'blocked', label: 'Blocked', detail: 'AI compliance review flagged this template. Revise it before sending.' };
  }

  if (canUseCompliance && !tier.complianceReview?.reviewedAt) {
   return { level: 'review', label: 'Review Recommended', detail: 'Run AI compliance review before enabling this template live.' };
  }

  return { level: 'ready', label: 'Ready to Send', detail: 'Core safeguards are satisfied for this template.' };
 }

 function renderTemplateSafetyBadge(safety: TemplateSafetyResult) {
  const badgeClass = safety.level === 'ready'
   ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
   : safety.level === 'review'
    ? 'bg-amber-50 text-amber-700 border-amber-100'
    : 'bg-rose-50 text-rose-700 border-rose-100';

  return <span className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wide border ${badgeClass}`}>{safety.label}</span>;
 }

 const handleSaveTier = async () => {
  if (!db || !user || !functions || !editingTierId || !editingDraft) return;

  // FDCPA: Client-side content guardrail
  const combined = `${editingDraft.subject || ''} ${editingDraft.body || ''}`.toLowerCase();
  const violation = PROHIBITED_PHRASES.find(p => combined.includes(p));
  if (violation) {
   addNotify('error', `Template contains "${violation}" — this language may violate consumer protection laws. Please revise.`);
   return;
  }

  setSavingSettings(true);
  try {
   const updateTierFn = httpsCallable(functions, 'updateDunningTier');
   await updateTierFn({ tierId: editingTierId, updates: { ...editingDraft, isDefault: false } });
   logAudit('template_updated', `Template "${editingDraft.name}" updated`, { tierId: editingTierId });
   setEditingTierId(null);
   setEditingDraft(null);
   setToneRewritePreview(null);
   setSuggestComplianceReview(false);
   setLastToneMode(null);
   addNotify('success', 'Logic committed.');
  } catch (err: any) { addNotify('error', err.message || "Commit failed."); } finally { setSavingSettings(false); }
 };

 const handleCheckCompliance = async () => {
  if (!functions || !editingTierId || !editingDraft) return;
  setComplianceReviewLoading(true);
  try {
   const reviewCallable = httpsCallable(aiFunctions, 'checkDunningCompliance');
   const result: any = await reviewCallable({
    tierId: editingTierId,
    tierName: editingDraft.name || '',
    subject: editingDraft.subject || '',
    body: editingDraft.body || '',
    daysPastDue: editingDraft.daysPastDue || 0
   });
   const review = result.data;
   setEditingDraft(prev => prev ? ({ ...prev, complianceReview: review }) : prev);
   setSuggestComplianceReview(false);
   addNotify(review.compliant ? 'success' : 'info', review.compliant ? 'AI compliance review passed.' : `AI compliance review flagged ${review.issues?.length || 0} issue(s).`);
  } catch (err: any) {
   addNotify('error', err.message || 'Compliance review failed.');
  } finally {
   setComplianceReviewLoading(false);
  }
 };

 const handleToneRewrite = async (toneMode: 'softer' | 'balanced' | 'firmer' | 'professional' | 'shorter') => {
  if (!functions || !editingTierId || !editingDraft) return;

  const combined = `${editingDraft.subject || ''} ${editingDraft.body || ''}`.toLowerCase();
  const violation = PROHIBITED_PHRASES.find(p => combined.includes(p));
  if (violation) {
   addNotify('error', `Template contains "${violation}" - remove prohibited language before requesting a tone rewrite.`);
   return;
  }

  setToneRewriteLoading(true);
  try {
   const rewriteCallable = httpsCallable(aiFunctions, 'rewriteDunningTemplateTone');
   const result: any = await rewriteCallable({
    tierId: editingTierId,
    tierName: editingDraft.name || '',
    subject: editingDraft.subject || '',
    body: editingDraft.body || '',
    daysPastDue: editingDraft.daysPastDue || 0,
    toneMode
   });
   setToneRewritePreview(result.data);
   setLastToneMode(toneMode);
   addNotify('success', `Tone rewrite ready: ${toneMode}. Review it before applying.`);
  } catch (err: any) {
   addNotify('error', err.message || 'Tone rewrite failed.');
  } finally {
   setToneRewriteLoading(false);
  }
 };

 const handleApplyToneRewrite = () => {
  if (!editingDraft || !toneRewritePreview) return;
  setEditingDraft({
   ...editingDraft,
   subject: toneRewritePreview.suggestedSubject || editingDraft.subject || '',
   body: toneRewritePreview.suggestedBody || editingDraft.body || ''
  });
  setToneRewritePreview(null);
  setSuggestComplianceReview(true);
  addNotify('success', 'Tone rewrite applied to the draft. A compliance review is recommended before saving.');
 };

 const handleDiscardToneRewrite = () => {
  setToneRewritePreview(null);
  setSuggestComplianceReview(false);
 };

 const handleApplyComplianceRewrite = () => {
  if (!editingDraft?.complianceReview) return;
  setEditingDraft({
   ...editingDraft,
   subject: editingDraft.complianceReview.suggestedSubject || editingDraft.subject || '',
   body: editingDraft.complianceReview.suggestedBody || editingDraft.body || ''
  });
  setSuggestComplianceReview(false);
  addNotify('success', 'Suggested rewrite applied to the draft. Review it, then save when ready.');
 };

 // --- Compliance Handlers ---
 const handleResolveDispute = async (disputeId: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  try {
   const payload: any = { disputeId, resolution: 'Resolved by account holder' };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await httpsCallable(functions, 'resolveDispute')(payload);
   logAudit('dispute_resolved', `Dispute ${disputeId} marked resolved`, { disputeId });
   addNotify('success', 'Dispute resolved. Automated emails will resume for this invoice.');
  } catch (err: any) { addNotify('error', err.message || 'Failed to resolve dispute.'); }
 };

 const handleRemoveOptOut = async (email: string) => {
  if (!db || isViewingMemberWorkspace) return;
  try {
   await deleteDoc(doc(db, ...PUBLIC_DATA_PATH, 'email_optouts', email));
   logAudit('optout_removed', `${email} removed from suppression list`, { email });
   addNotify('success', `${email} removed from suppression list.`);
  } catch (err: any) { addNotify('error', err.message || 'Failed to remove opt-out.'); }
 };

 const handleExportCustomerData = () => {
  if (!functions || !gdprEmail.trim()) return;
  requireReauth(async () => {
   setGdprLoading(true);
   try {
    const result = await httpsCallable(functions, 'exportCustomerData')({ email: gdprEmail.trim() });
    const data = (result as any).data;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-data-export-${gdprEmail.trim()}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addNotify('success', `Data export for ${gdprEmail} downloaded.`);
   } catch (err: any) { addNotify('error', err.message || 'Export failed.'); } finally { setGdprLoading(false); }
  });
 };

 const handleEraseCustomerData = () => {
  if (!functions || !gdprEmail.trim()) return;
  if (!window.confirm(`Permanently delete ALL data associated with ${gdprEmail.trim()}? This cannot be undone.`)) return;
  requireReauth(async () => {
   setGdprLoading(true);
   try {
    const result = await httpsCallable(functions, 'eraseCustomerData')({ email: gdprEmail.trim() });
    addNotify('success', `${(result as any).data.deleted} records deleted for ${gdprEmail}.`);
    setGdprEmail('');
   } catch (err: any) { addNotify('error', err.message || 'Erasure failed.'); } finally { setGdprLoading(false); }
  });
 };

 const handleAddTier = async () => {
  if (!db || !user) return;
  if (!canAddTemplate) {
   requireUpgrade(`More than ${effectivePlan.maxTemplates} templates`);
   return;
  }
  const newId = `tier_${Date.now()}`;
  const newTier: DunningTier = {
   id: newId,
   name: `New Template ${dunningTiers.length + 1}`,
   daysPastDue: 14,
   subject: 'Payment Reminder - Invoice #{{invoice_num}}',
   body: 'Hi {{customer_name}},\n\nThis is a reminder regarding invoice #{{invoice_num}} for ${{amount_due}}, which was due on {{due_date}}.\n\nPlease arrange payment at your earliest convenience.\n\nThank you.',
   enabled: true
  };
  setSavingSettings(true);
  try {
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'settings', newId), newTier);
   addNotify('success', 'New template created.');
   // Auto-open for editing
   logAudit('template_created', `Template "${newTier.name}" created`, { tierId: newId });
   setEditingTierId(newId);
   setEditingDraft(newTier);
   setToneRewritePreview(null);
   setSuggestComplianceReview(false);
   setLastToneMode(null);
  } catch (err: any) {
   addNotify('error', 'Failed to create template.');
  } finally { setSavingSettings(false); }
 };

 const handleDeleteTier = async (tierId: string, tierName: string) => {
  if (!db || !user) return;
  if (!window.confirm(`Delete "${tierName}"? This cannot be undone.`)) return;
  setSavingSettings(true);
  try {
   await deleteDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'settings', tierId));
   if (editingTierId === tierId) {
    setEditingTierId(null);
    setEditingDraft(null);
    setToneRewritePreview(null);
    setSuggestComplianceReview(false);
    setLastToneMode(null);
   }
   logAudit('template_deleted', `Template "${tierName}" deleted`, { tierId });
   addNotify('success', `"${tierName}" deleted.`);
  } catch (err: any) {
   addNotify('error', 'Delete failed.');
  } finally { setSavingSettings(false); }
 };

 const handleDuplicateTier = async (tier: DunningTier) => {
  if (!db || !user) return;
  if (!canAddTemplate) { requireUpgrade('duplicate template'); return; }
  const newId = `tier_${Date.now()}`;
  const copy: DunningTier = { ...tier, id: newId, name: `${tier.name} (copy)`, enabled: false };
  setSavingSettings(true);
  try {
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'settings', newId), copy);
   logAudit('template_created', `Template "${copy.name}" duplicated from "${tier.name}"`, { tierId: newId, sourceId: tier.id });
   addNotify('success', `"${copy.name}" created — disabled by default.`);
   setEditingTierId(newId);
   setEditingDraft(copy);
   setToneRewritePreview(null);
   setSuggestComplianceReview(false);
   setLastToneMode(null);
  } catch { addNotify('error', 'Duplicate failed.'); }
  finally { setSavingSettings(false); }
 };

 const renderWithSampleData = (text: string) => text
  .replace(/\{\{customer_name\}\}/g, 'Acme Corp')
  .replace(/\{\{invoice_num\}\}/g, 'INV-1042')
  .replace(/\{\{amount_due\}\}/g, '3,200.00')
  .replace(/\{\{due_date\}\}/g, 'March 31, 2026')
  .replace(/\{\{invoice_date\}\}/g, 'March 1, 2026')
  .replace(/\{\{company_name\}\}/g, userProfile.displayName || 'Your Company')
  .replace(/\{\{days_overdue\}\}/g, '14');

 const handlePurgeData = async () => {
  if (!functions) return;
  if (!window.confirm("CRITICAL: Wipe all account data? This cannot be undone.")) return;
  setSavingSettings(true);
  try {
   await httpsCallable(functions, 'purgeUserData')();
   addNotify('success', 'Identity scrubbed.');
   await signOut(auth);
  } catch (e) { addNotify('error', 'Purge failed.'); } finally { setSavingSettings(false); }
 };

 const handleUpdatePassword = () => {
  if (!auth.currentUser || !newPassword) return addNotify('error', "New password required.");
  if (newPassword.length < 6) return addNotify('error', "Password must be at least 6 characters.");
  requireReauth(async () => {
   try {
    await updatePassword(auth.currentUser!, newPassword);
    logAudit('password_changed', 'Password changed');
    setNewPassword('');
    addNotify('success', 'Password successfully updated.');
   } catch (err: any) { addNotify('error', `Update failed: ${err.message}.`); }
  });
 };

 const handleSignOutAllDevices = () => {
  requireReauth(async () => {
   setSignOutAllLoading(true);
   try {
    const revokeAll = httpsCallable(functions, 'revokeAllSessions');
    await revokeAll();
    logAudit('user_signout_all', 'Signed out all other devices');
    // Sign out this device too since our session token is now invalid
    await signOut(auth);
    addNotify('success', 'All sessions revoked. Please sign in again.');
   } catch (err: any) {
    addNotify('error', `Failed to sign out all devices: ${err.message}`);
   } finally {
    setSignOutAllLoading(false);
   }
  });
 };

 const logAudit = useCallback((action: string, details: string, metadata?: Record<string, any>) => {
  if (!functions) return;
  httpsCallable(functions, 'logClientEvent')({ action, details, metadata: metadata || null }).catch(() => {});
 }, [functions]);

 const handleAddNote = async () => {
  if (!noteModal || !noteInput.trim() || !functions) return;
  setNoteSaving(true);
  try {
   const fn = httpsCallable(functions, 'addCollectionNote');
   const payload: any = {
    realmId: noteModal.realmId, invoiceId: noteModal.invoiceId,
    note: noteInput.trim(), customerName: noteModal.customerName,
   };
   if (ptpInput) payload.ptpDate = ptpInput;
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   setNoteInput('');
   setPtpInput('');
  } catch (e: any) { addNotify('error', 'Failed to save note: ' + e.message); }
  setNoteSaving(false);
 };

 const handleDeleteNote = async (noteId: string) => {
  if (!functions) return;
  try {
   const fn = httpsCallable(functions, 'deleteCollectionNote');
   const payload: any = { noteId };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
  } catch (e: any) { addNotify('error', 'Failed to delete note: ' + e.message); }
 };

 const handleSnoozeInvoice = async () => {
  if (!snoozeModal || !functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  setSnoozeLoading(true);
  try {
   const fn = httpsCallable(functions, 'snoozeInvoice');
   const payload: any = { realmId: snoozeModal.realmId, invoiceId: snoozeModal.invoiceId, days: snoozeDays };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', `Invoice snoozed for ${snoozeDays} day${snoozeDays !== 1 ? 's' : ''}.`);
   setSnoozeModal(null);
   setSnoozeDays(7);
  } catch (err: any) {
   addNotify('error', `Snooze failed: ${err.message}`);
  } finally {
   setSnoozeLoading(false);
  }
 };

 const handleUnsnoozeInvoice = async (realmId: string, invoiceId: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  try {
   const fn = httpsCallable(functions, 'unsnoozeInvoice');
   const payload: any = { realmId, invoiceId };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', 'Snooze removed.');
  } catch (err: any) {
   addNotify('error', `Failed to remove snooze: ${err.message}`);
  }
 };

 const handlePauseCustomer = async (realmId: string, customerEmail: string, customerName: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  try {
   const fn = httpsCallable(functions, 'pauseCustomer');
   const payload: any = { realmId, customerEmail };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', `Dunning paused for ${customerName}.`);
  } catch (err: any) {
   addNotify('error', `Failed to pause customer: ${err.message}`);
  }
 };

 const handleResumeCustomer = async (realmId: string, customerEmail: string, customerName: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  try {
   const fn = httpsCallable(functions, 'resumeCustomer');
   const payload: any = { realmId, customerEmail };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', `Dunning resumed for ${customerName}.`);
  } catch (err: any) {
   addNotify('error', `Failed to resume customer: ${err.message}`);
  }
 };

 // Item 23: Open customer meta modal and pre-populate from existing data
 const openCustomerMetaModal = (realmId: string, customerEmail: string, customerName: string, existing?: CustomerMetaOverride) => {
  setCmNotes(existing?.notes ?? '');
  setCmTags((existing?.tags ?? []).join(', '));
  setCmEmailOverride(existing?.emailOverride ?? '');
  setCmExcluded(existing?.excluded ?? false);
  setCmActiveTab('details');
  setCustomerMetaModal({ realmId, customerEmail, customerName });
 };

 const handleSaveCustomerMeta = async () => {
  if (!customerMetaModal || !functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  setCmSaving(true);
  try {
   const fn = httpsCallable(functions, 'updateCustomerMeta');
   const tags = cmTags.split(',').map(t => t.trim()).filter(Boolean);
   const payload: any = { realmId: customerMetaModal.realmId, customerEmail: customerMetaModal.customerEmail, notes: cmNotes, tags, emailOverride: cmEmailOverride, excluded: cmExcluded };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', 'Customer details saved.');
   setCustomerMetaModal(null);
  } catch (err: any) {
   addNotify('error', `Failed to save: ${err.message}`);
  } finally {
   setCmSaving(false);
  }
 };

 // Item 24: Open invoice controls modal and pre-populate
 const openInvoiceCtrlModal = (invoiceId: string, realmId: string, customerName: string, docNumber: string, existing?: InvoiceControlsOverride) => {
  setIcDisabled(existing?.disabled ?? false);
  setIcMarkedCollected(existing?.markedCollected ?? false);
  setIcCcEmail(existing?.ccEmail ?? '');
  setIcCustomMessage(existing?.customMessage ?? '');
  setInvoiceCtrlModal({ invoiceId, realmId, customerName, docNumber });
 };

 const handleSaveInvoiceControls = async () => {
  if (!invoiceCtrlModal || !functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  setIcSaving(true);
  try {
   const fn = httpsCallable(functions, 'setInvoiceControls');
   const payload: any = { realmId: invoiceCtrlModal.realmId, invoiceId: invoiceCtrlModal.invoiceId, disabled: icDisabled, markedCollected: icMarkedCollected, ccEmail: icCcEmail, customMessage: icCustomMessage };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   await fn(payload);
   addNotify('success', 'Invoice controls saved.');
   setInvoiceCtrlModal(null);
  } catch (err: any) {
   addNotify('error', `Failed to save controls: ${err.message}`);
  } finally {
   setIcSaving(false);
  }
 };

 // --- Webhook Handlers ---
 const WEBHOOK_EVENT_OPTIONS = [
  { value: 'reminder_sent', label: 'Reminder Sent', desc: 'A dunning email was sent to a debtor' },
  { value: 'reminder_blocked', label: 'Reminder Blocked', desc: 'A reminder was suppressed (snoozed, paused, opt-out)' },
  { value: 'payment_received', label: 'Payment Received', desc: 'A payment was detected on an invoice' },
  { value: 'dispute_filed', label: 'Dispute Filed', desc: 'A customer filed a dispute on an invoice' },
  { value: 'sync_failed', label: 'Sync Failed', desc: 'A QuickBooks sync encountered an error' },
  { value: 'invoice_snoozed', label: 'Invoice Snoozed', desc: 'An invoice was snoozed' },
  { value: 'customer_paused', label: 'Customer Paused', desc: 'Reminders were paused for a customer' },
 ];

 const handleCreateWebhook = async () => {
  if (!functions) return;
  if (!/^https?:\/\/.+/.test(webhookForm.url)) {
   setWebhookCreateError('A valid URL is required (must start with http:// or https://).');
   return;
  }
  if (webhookForm.events.length === 0) {
   setWebhookCreateError('Select at least one event.');
   return;
  }
  setWebhookCreateLoading(true);
  setWebhookCreateError('');
  try {
   const fn = httpsCallable(functions, 'createWebhook');
   const res = await fn({ url: webhookForm.url, events: webhookForm.events, description: webhookForm.description });
   const { webhookId, secret } = (res.data as any);
   setWebhookNewSecret({ id: webhookId, secret });
   setWebhookForm({ url: '', description: '', events: [] });
   logAudit('webhook_created', `Webhook created for ${webhookForm.url}`, { webhookId, url: webhookForm.url, events: webhookForm.events });
   addNotify('success', 'Webhook endpoint created.');
  } catch (err: any) {
   setWebhookCreateError(err.message || 'Failed to create webhook.');
  } finally {
   setWebhookCreateLoading(false);
  }
 };

 const handleDeleteWebhook = async (webhookId: string) => {
  if (!functions) return;
  if (!window.confirm('Delete this webhook? Delivery logs will be removed.')) return;
  try {
   await httpsCallable(functions, 'deleteWebhook')({ webhookId });
   if (webhookExpandedId === webhookId) setWebhookExpandedId(null);
   logAudit('webhook_deleted', `Webhook ${webhookId} deleted`, { webhookId });
   addNotify('success', 'Webhook deleted.');
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to delete webhook.');
  }
 };

 const handleToggleWebhookActive = async (webhookId: string, currentActive: boolean) => {
  if (!functions) return;
  try {
   await httpsCallable(functions, 'updateWebhook')({ webhookId, active: !currentActive });
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to update webhook.');
  }
 };

 // --- Team Members / RBAC ---
 const handleSendTeamInvite = async () => {
  if (!functions) return;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(teamInviteEmail)) {
   setTeamInviteError('Enter a valid email address.');
   return;
  }
  setTeamInviteLoading(true);
  setTeamInviteError('');
  setTeamInviteSuccess('');
  try {
   await httpsCallable(functions, 'inviteTeamMember')({ email: teamInviteEmail, role: teamInviteRole });
   setTeamInviteSuccess(`Invite sent to ${teamInviteEmail}.`);
   setTeamInviteEmail('');
  } catch (err: any) {
   setTeamInviteError(err.message || 'Failed to send invite.');
  } finally {
   setTeamInviteLoading(false);
  }
 };

 const handleRevokeTeamMember = async (memberId: string, email: string) => {
  if (!functions) return;
  if (!window.confirm(`Remove ${email} from your workspace?`)) return;
  try {
   await httpsCallable(functions, 'revokeTeamMember')({ memberId });
   addNotify('success', `${email} removed.`);
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to remove member.');
  }
 };

 const handleUpdateTeamMemberRole = async (memberId: string, role: string) => {
  if (!functions) return;
  setTeamRoleUpdateLoading(memberId);
  try {
   await httpsCallable(functions, 'updateTeamMemberRole')({ memberId, role });
   addNotify('success', 'Role updated.');
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to update role.');
  } finally {
   setTeamRoleUpdateLoading(null);
  }
 };

 const handleUpdateMemberConnections = async (memberId: string, realmId: string, checked: boolean) => {
  if (!functions) return;
  const member = teamMembers.find(m => m.id === memberId);
  if (!member) return;
  const current = member.connectionIds ?? null;
  let updated: string[] | null;
  if (checked) {
   updated = current ? [...current, realmId] : [realmId];
  } else {
   const next = (current ?? connections.map(c => c.realmId)).filter(id => id !== realmId);
   updated = next.length === connections.length ? null : next;
  }
  setMemberConnectionsLoading(memberId);
  try {
   await httpsCallable(functions, 'updateMemberConnections')({ memberId, connectionIds: updated });
   addNotify('success', 'Connection access updated.');
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to update connection access.');
  } finally {
   setMemberConnectionsLoading(null);
  }
 };

 const handleSaveSlackUrl = async () => {
  if (!db || !user) return;
  const url = localSlackUrl.trim();
  if (url && !/^https?:\/\/.+/.test(url)) {
   addNotify('error', 'Enter a valid Slack webhook URL.');
   return;
  }
  setSlackUrlSaving(true);
  try {
   const update: any = { updatedAt: serverTimestamp() };
   if (url) { update.slackWebhookUrl = url; } else { update.slackWebhookUrl = null; }
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'), update, { merge: true });
   setUserProfile(p => ({ ...p, slackWebhookUrl: url || undefined }));
   addNotify('success', url ? 'Slack webhook saved.' : 'Slack webhook removed.');
  } catch {
   addNotify('error', 'Failed to save Slack webhook URL.');
  } finally {
   setSlackUrlSaving(false);
  }
 };

 const handleAcceptTeamInvite = async () => {
  if (!functions || !pendingInviteToken) return;
  setInviteAcceptLoading(true);
  setInviteAcceptError('');
  try {
   await httpsCallable(functions, 'acceptTeamInvite')({ token: pendingInviteToken });
   localStorage.removeItem('soarone_invite_token');
   setPendingInviteToken(null);
   setInviteAcceptInfo(null);
   setInviteAcceptDone(true);
   addNotify('success', `You've joined ${inviteAcceptInfo?.ownerName || 'the workspace'}.`);
  } catch (err: any) {
   setInviteAcceptError(err.message || 'Failed to accept invite.');
  } finally {
   setInviteAcceptLoading(false);
  }
 };

 const handleDeclineTeamInvite = () => {
  localStorage.removeItem('soarone_invite_token');
  setPendingInviteToken(null);
  setInviteAcceptInfo(null);
 };

 // --- SSO Config ---
 const handleSetSsoConfig = async (enabled: boolean) => {
  if (!functions) return;
  setSsoSaving(true);
  try {
   const domain = localSsoDomain.replace(/^@/, '').trim();
   await httpsCallable(functions, 'setSsoConfig')({ enabled, requiredDomain: domain });
   addNotify('success', enabled ? `SSO domain enforcement enabled for @${domain}` : 'SSO domain enforcement disabled.');
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to save SSO config.');
  } finally {
   setSsoSaving(false);
  }
 };

 // --- API Keys ---
 const handleCreateApiKey = async () => {
  if (!functions || !newApiKeyName.trim()) return;
  if (newApiKeyScopes.length === 0) { setApiKeyCreateError('Select at least one scope.'); return; }
  setApiKeyCreateLoading(true);
  setApiKeyCreateError('');
  try {
   const res = await httpsCallable(functions, 'createApiKey')({ name: newApiKeyName.trim(), scopes: newApiKeyScopes });
   const data = res.data as any;
   setJustCreatedKey({ id: data.keyId, key: data.key });
   setNewApiKeyName('');
   setNewApiKeyScopes(['invoices:read', 'logs:read', 'summary:read']);
   setShowApiKeyForm(false);
   setKeyCopied(false);
  } catch (err: any) {
   setApiKeyCreateError(err.message || 'Failed to create API key.');
  } finally {
   setApiKeyCreateLoading(false);
  }
 };

 const handleRevokeApiKey = async (keyId: string, name: string) => {
  if (!functions) return;
  if (!window.confirm(`Revoke "${name}"? Any integrations using this key will stop working immediately.`)) return;
  setApiKeyRevokeLoading(keyId);
  try {
   await httpsCallable(functions, 'revokeApiKey')({ keyId });
   if (justCreatedKey?.id === keyId) setJustCreatedKey(null);
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to revoke key.');
  } finally {
   setApiKeyRevokeLoading(null);
  }
 };

 const handleSwitchWorkspace = (ownerUid: string | null) => {
  setActiveWorkspaceOwnerId(ownerUid);
  setSelectedRealmId('all');
  setActiveTab('connections');
  // Reset all view state that could leak between workspaces
  setSelectedSupportThreadId(null);
  setSnoozeModal(null);
  setEditingTierId(null);
  setEditingDraft(null);
  setAuditSearch('');
  setInvoiceSearch('');
  setInvoiceStatusFilter('all');
 };

 const handleTestWebhook = async (webhookId: string) => {
  if (!functions) return;
  setWebhookTestLoading(webhookId);
  setWebhookTestResult(null);
  try {
   const res = await httpsCallable(functions, 'testWebhook')({ webhookId });
   const data = res.data as any;
   setWebhookTestResult({ id: webhookId, success: data.success, statusCode: data.statusCode, error: data.error });
  } catch (err: any) {
   setWebhookTestResult({ id: webhookId, success: false, statusCode: null, error: err.message || 'Test failed' });
  } finally {
   setWebhookTestLoading(null);
  }
 };

 const handleLoadDeliveries = async (webhookId: string) => {
  if (!functions) return;
  setWebhookDeliveriesLoading(webhookId);
  try {
   const res = await httpsCallable(functions, 'listWebhookDeliveries')({ webhookId });
   setWebhookDeliveries((res.data as any).deliveries as WebhookDelivery[]);
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to load deliveries.');
  } finally {
   setWebhookDeliveriesLoading(null);
  }
 };

 // --- 2FA Handlers ---
 const handleMfaEnrollStart = () => {
  requireReauth(async () => {
   if (!user) return;
   setMfaLoading(true);
   setMfaError('');
   try {
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    const qrUri = secret.generateQrCodeUrl(user.email || 'user', 'SoarOne AR');
    const dataUrl = await QRCode.toDataURL(qrUri, { width: 200, margin: 2 });
    setMfaTotpSecret(secret);
    setMfaQrDataUrl(dataUrl);
    setMfaVerifyCode('');
    setMfaError('');
    setMfaSetupStep('qrcode');
   } catch (err: any) {
    console.error('[2FA] enroll start error:', err.code, err.message);
    if (err.code === 'auth/requires-recent-login') {
     setMfaError('Session too old — please sign out and sign in again, then retry.');
    } else if (err.code === 'auth/email-not-verified') {
     setMfaError('Please verify your email address before enabling 2FA.');
    } else {
     setMfaError(`Could not start 2FA setup (${err.code || err.message}). Check the browser console for details.`);
    }
   } finally {
    setMfaLoading(false);
   }
  });
 };

 const handleMfaEnrollVerify = async () => {
  if (!mfaTotpSecret || !user) return;
  setMfaLoading(true);
  setMfaError('');
  try {
   const assertion = TotpMultiFactorGenerator.assertionForEnrollment(mfaTotpSecret, mfaVerifyCode.replace(/\s/g, ''));
   await multiFactor(user).enroll(assertion, 'Authenticator App');
   logAudit('2fa_enabled', '2FA (TOTP) enabled on account');
   setMfaSetupStep(null);
   setMfaTotpSecret(null);
   setMfaQrDataUrl('');
   setMfaVerifyCode('');
   addNotify('success', 'Two-factor authentication enabled.');
  } catch (err: any) {
   console.error('[2FA] enroll verify error:', err.code, err.message);
   if (err.code === 'auth/invalid-verification-code') {
    setMfaError('Incorrect code — check your authenticator app and try again.');
   } else {
    setMfaError(`Verification failed (${err.code || err.message}).`);
   }
  } finally {
   setMfaLoading(false);
  }
 };

 const handleMfaDisable = () => {
  requireReauth(async () => {
   if (!user) return;
   setMfaLoading(true);
   try {
    const factors = multiFactor(user).enrolledFactors;
    if (factors.length > 0) {
     await multiFactor(user).unenroll(factors[0]);
    }
    logAudit('2fa_disabled', '2FA (TOTP) disabled on account');
    addNotify('success', 'Two-factor authentication disabled.');
   } catch (err: any) {
    addNotify('error', `Failed to disable 2FA: ${err.message}`);
   } finally {
    setMfaLoading(false);
   }
  });
 };

 const handleMfaChallengeSubmit = async () => {
  if (!mfaChallengeResolver) return;
  setMfaChallengeLoading(true);
  try {
   const hint = mfaChallengeResolver.hints.find((h: any) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
   if (!hint) throw new Error('No TOTP factor found.');
   const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaChallengeCode.replace(/\s/g, ''));
   await mfaChallengeResolver.resolveSignIn(assertion);
   setMfaChallengeResolver(null);
   setMfaChallengeCode('');
  } catch (err: any) {
   addNotify('error', 'Invalid code — check your authenticator app.');
  } finally {
   setMfaChallengeLoading(false);
  }
 };

 const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  payment_received: true,
  dispute_filed: true,
  sync_failed: true,
  trial_expiring: true,
  subscription_payment_failed: true,
  weekly_digest: false,
 };

 const handleToggleNotificationPref = async (key: keyof NotificationPrefs) => {
  if (!db || !user) return;
  const currentPrefs: NotificationPrefs = { ...DEFAULT_NOTIFICATION_PREFS, ...userProfile.notificationPrefs };
  const updated = { ...currentPrefs, [key]: !currentPrefs[key] };
  setUserProfile(p => ({ ...p, notificationPrefs: updated }));
  try {
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'),
    { notificationPrefs: updated, updatedAt: serverTimestamp() },
    { merge: true }
   );
  } catch {
   // Revert on failure
   setUserProfile(p => ({ ...p, notificationPrefs: currentPrefs }));
   addNotify('error', 'Failed to save notification preference.');
  }
 };

 const handleUpdateProfile = async () => {
  if (!db || !user) return;
  setSavingSettings(true);
  try {
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'), {
    ...stripBillingFields(userProfile),
    photoURL: profilePicUrl,
    updatedAt: serverTimestamp()
   }, { merge: true });
   profileFormDirty.current = false;
   logAudit('profile_updated', 'Account profile updated');
   addNotify('success', 'Profile updated.');
  } catch (e) {
   addNotify('error', 'Update interrupted.');
  } finally {
   setSavingSettings(false);
  }
 };

 // FIX: Camera button now triggers file upload, converts to base64, and saves
 const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
   addNotify('error', 'Please upload a JPG, PNG, GIF, or WebP image.');
   return;
  }

  // Validate file size (max 500KB for Firestore storage)
  const MAX_SIZE = 500 * 1024;
  if (file.size > MAX_SIZE) {
   addNotify('error', 'Image must be under 500KB. Try a smaller or compressed image.');
   return;
  }

  try {
   // Resize and convert to base64
   const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
     const img = new Image();
     img.onload = () => {
      const canvas = document.createElement('canvas');
      const MAX_DIM = 256;
      let width = img.width;
      let height = img.height;

      // Scale down to max 256x256 to keep Firestore doc size reasonable
      if (width > MAX_DIM || height > MAX_DIM) {
       if (width > height) {
        height = Math.round((height * MAX_DIM) / width);
        width = MAX_DIM;
       } else {
        width = Math.round((width * MAX_DIM) / height);
        height = MAX_DIM;
       }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
     };
     img.onerror = () => reject(new Error('Failed to load image'));
     img.src = event.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
   });

   setProfilePicUrl(base64);

   // Auto-save to Firestore
   if (db && user) {
    await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'), {
     photoURL: base64,
     updatedAt: serverTimestamp()
    }, { merge: true });
    addNotify('success', 'Avatar updated.');
   }
  } catch (err: any) {
   addNotify('error', err.message || 'Image upload failed.');
  }

  // Reset file input so the same file can be re-selected
  if (fileInputRef.current) fileInputRef.current.value = '';
 };

 const [senderStatus, setSenderStatus] = useState<'unknown' | 'checking' | 'verified' | 'pending' | 'not_found'>('unknown');

 const handleCheckSenderStatus = async () => {
  if (!functions || !userProfile.senderEmail) return;
  setSenderStatus('checking');
  try {
   const checkFn = httpsCallable(functions, 'checkSenderStatus');
   const result: any = await checkFn({ email: userProfile.senderEmail });
   setSenderStatus(result.data.status || 'not_found');
  } catch {
   setSenderStatus('unknown');
  }
 };

 const handleVerifySender = async () => {
  if (!functions || !db || !user) return;

  // Validate email format
  const email = userProfile.senderEmail?.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
   addNotify('error', 'Please enter a valid email address.');
   return;
  }

  const senderZip = (userProfile.senderZip || '').trim();
  if (senderZip && senderZip.length > 10) {
   addNotify('error', 'ZIP / postal code must be 10 characters or fewer.');
   return;
  }

  setSavingSettings(true);
  try {
   // Auto-save profile first so the sender email persists
   await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'), {
    ...stripBillingFields(userProfile),
    senderEmail: email,
    photoURL: profilePicUrl,
    updatedAt: serverTimestamp()
   }, { merge: true });

   // Register sender with SendGrid
   const registerSenderFn = httpsCallable(functions, 'registerSenderIdentity');
   const result: any = await registerSenderFn({
    email,
    nickname: userProfile.displayName || "SoarOne",
    address: userProfile.senderAddress || "Unset",
    city: userProfile.senderCity || "Unset",
    zip: senderZip || "00000"
   });

   if (result.data.status === 'already_verified') {
    setSenderStatus('verified');
    logAudit('sender_updated', `Sender identity set to ${email} (already verified)`, { email });
    addNotify('success', `${email} is already verified and ready to use.`);
   } else {
    setSenderStatus('pending');
    logAudit('sender_updated', `Sender identity registration submitted for ${email}`, { email });
    addNotify('success', `Verification email sent to ${email}. Check your inbox and click the link.`);
   }
  } catch (err: any) {
   addNotify('error', err.message || 'Sender registration failed.');
  } finally {
   setSavingSettings(false);
  }
 };

 const handleSignOut = async () => {
  if (!auth) return;
  try {
   // FIX: Clear all user data immediately before signing out
   setConnections([]);
   setSupportThreads([]);
   setSupportMessages([]);
   setSupportHandoffs([]);
   setLogs([]);
   setInvoices([]);
   setAgingSnapshots([]);
   setCollectionNotes([]);
   setDunningOverrides([]);
   setAuditLog([]);
   setWebhooks([]);
   setWebhookDeliveries([]);
   setDunningTiers([]);
   setInsightsSegmentFilter('all');
   setUserProfile({ useCustomSender: false, notificationsEnabled: true });
   setProfilePicUrl('');
   setNewPassword('');
   setSelectedRealmId('all');
   setSelectedSupportThreadId(null);
   setSupportInput('');
   setSupportSending(false);
   setEditingTierId(null);
   setEditingDraft(null);
   setSenderStatus('unknown');
   initialLoadDone.current = false;
   setResendingVerification(false);
   setTosChecked(false);
   setPrivacyChecked(false);
   setTosLinkOpened(false);
   setPrivacyLinkOpened(false);

   await signOut(auth);
   addNotify('success', 'Signed out successfully.');
  } catch (err: any) { addNotify('error', err.message); }
 };

 const handleExportHistory = () => {
  if (!canExportCsv) { requireUpgrade('CSV Export'); return; }
  const headers = ['Date', 'Realm ID', 'Doc #', 'Customer', 'Tier', 'Status'];
  // CSV escape: wrap fields in quotes if they contain commas, quotes, or newlines
  const csvEscape = (val: string) => {
   if (val && (val.includes(',') || val.includes('"') || val.includes('\n'))) {
    return `"${val.replace(/"/g, '""')}"`;
   }
   return val || '';
  };
  const rows = filteredLogs.map(log => [
   formatTimestamp(getHistoryDateValue(log)),
   log.realmId,
   log.docNumber,
   log.customerName,
   log.templateSent,
   log.deliveryStatus
  ].map(csvEscape));
  const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `soarone_history_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  addNotify('success', 'History exported.');
 };

const handleSort = (key: string) => {
  setSortConfig(curr => ({ key, direction: curr?.key === key && curr.direction === 'asc' ? 'desc' : 'asc' }));
 };
 const getSortIndicator = (key: string) => {
  if (!sortConfig || sortConfig.key !== key) return '↕';
  return sortConfig.direction === 'asc' ? '↑' : '↓';
 };
const handleRetryFailedEmail = async (logId: string) => {
  if (!functions || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')) return;
  try {
   const payload: any = { logId };
   if (isViewingMemberWorkspace && activeWorkspaceOwnerId) payload.targetOwnerUid = activeWorkspaceOwnerId;
   const result: any = await httpsCallable(functions, 'retryFailedEmail')(payload);
   const retryStatus = String(result?.data?.status || '').toLowerCase();
   const retryDetail = result?.data?.errorDetail || '';
   if (retryStatus === 'queued') {
    addNotify('success', 'Retry queued successfully.');
   } else if (retryStatus === 'blocked') {
    addNotify('info', retryDetail || 'Retry was blocked again by current safeguards.');
   } else if (retryStatus === 'failed') {
    addNotify('error', retryDetail || 'Retry failed.');
   } else if (['sent', 'delivered', 'opened'].includes(retryStatus)) {
    addNotify('success', `Retry processed with status: ${retryStatus}.`);
   } else {
    addNotify('info', 'Retry completed, but the resulting status is still being determined.');
   }
  } catch (err: any) {
   addNotify('error', err.message || 'Retry failed.');
  }
 };

const handleSendSupportMessage = async (messageOverride?: string) => {
  const message = String(messageOverride ?? supportInput).trim();
  if (!message || !supportFunctions || !user || isViewingMemberWorkspace) return;
  setSupportSending(true);
  try {
    const supportChatFn = httpsCallable(supportFunctions, 'supportChat');
    const result: any = await supportChatFn({
     threadId: selectedSupportThreadId,
     message,
     mode: 'customer'
    });
    const resolvedThreadId = result?.data?.threadId || selectedSupportThreadId || `local_support_${Date.now()}`;
    const now = new Date();
    newConversationMode.current = false;
    setSelectedSupportThreadId(resolvedThreadId);
    setSupportThreads(prev => {
     const existing = prev.find(thread => thread.id === resolvedThreadId);
     if (existing) {
       return prev.map(thread => thread.id === resolvedThreadId ? {
        ...thread,
       linkedBy: user.uid,
       customerUid: user.uid,
       customerEmail: user.email || thread.customerEmail,
       customerDisplayName: userProfile.displayName || user.email || thread.customerDisplayName,
       lastMessagePreview: result?.data?.reply || message,
       lastHandoffId: result?.data?.handoffId || thread.lastHandoffId || null,
       lastHandoffSummary: result?.data?.summary || thread.lastHandoffSummary || null,
       lastTicketId: result?.data?.ticketId || thread.lastTicketId || null,
        status: result?.data?.status || (result?.data?.escalated ? 'escalated' : thread.status || 'open'),
        supportTier: thread.supportTier || effectiveSupportTier,
        targetResponseSlaHours: thread.targetResponseSlaHours || (effectiveSupportTier === 'white_glove' ? 4 : effectiveSupportTier === 'priority' ? 12 : 24),
        waitingOnCustomer: typeof result?.data?.waitingOnCustomer === 'boolean' ? result.data.waitingOnCustomer : thread.waitingOnCustomer,
       lastResponseState: result?.data?.lastResponseState || thread.lastResponseState || 'ready',
       firstResponseAt: thread.firstResponseAt || now,
       escalationAt: result?.data?.escalated ? (thread.escalationAt || now) : thread.escalationAt,
       updatedAt: now
      } : thread);
     }
     return [{
      id: resolvedThreadId,
      linkedBy: user.uid,
      customerUid: user.uid,
      customerEmail: user.email || '',
      customerDisplayName: userProfile.displayName || user.email || 'Customer',
      status: result?.data?.status || (result?.data?.escalated ? 'escalated' : 'open'),
      priority: 'normal',
      supportTier: effectiveSupportTier,
      targetResponseSlaHours: effectiveSupportTier === 'white_glove' ? 4 : effectiveSupportTier === 'priority' ? 12 : 24,
      issueType: 'unknown',
      subject: result?.data?.summary || 'Support request',
      lastMessagePreview: result?.data?.reply || message,
      lastHandoffId: result?.data?.handoffId || null,
      lastHandoffSummary: result?.data?.summary || null,
      lastTicketId: result?.data?.ticketId || null,
      firstResponseAt: now,
      escalationAt: result?.data?.escalated ? now : null,
      waitingOnCustomer: typeof result?.data?.waitingOnCustomer === 'boolean' ? result.data.waitingOnCustomer : false,
      lastResponseState: result?.data?.lastResponseState || 'ready',
      updatedAt: now,
      createdAt: now
     }, ...prev];
    });
    setSupportMessages(prev => {
     const next = [...prev];
     const latestThreadMessage = [...next]
      .filter(entry => entry.threadId === resolvedThreadId)
      .sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt))[0];
     const hasCustomerMessage = Boolean(
      latestThreadMessage &&
      latestThreadMessage.senderType === 'customer' &&
      latestThreadMessage.message === message &&
      String(latestThreadMessage.id || '').startsWith('local_customer_') &&
      Math.abs(now.getTime() - getTimestampMs(latestThreadMessage.createdAt)) < 10000
     );
     if (!hasCustomerMessage) {
      next.push({
       id: `local_customer_${Date.now()}`,
       threadId: resolvedThreadId,
       linkedBy: user.uid,
       senderType: 'customer',
       senderId: user.uid,
       message,
       messageFormat: 'plain',
       citations: [],
       createdAt: now
      });
     }
     const aiReply = String(result?.data?.reply || '').trim();
     const latestThreadAiMessage = [...next]
      .filter(entry => entry.threadId === resolvedThreadId && entry.senderType === 'ai')
      .sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt))[0];
     const hasAiMessage = Boolean(
      aiReply &&
      latestThreadAiMessage &&
      latestThreadAiMessage.message === aiReply &&
      String(latestThreadAiMessage.id || '').startsWith('local_ai_') &&
      Math.abs(now.getTime() - getTimestampMs(latestThreadAiMessage.createdAt)) < 10000
     );
     if (aiReply && !hasAiMessage) {
     next.push({
       id: `local_ai_${Date.now()}`,
       threadId: resolvedThreadId,
       linkedBy: user.uid,
       senderType: 'ai',
       senderId: 'support_orchestrator',
       message: aiReply,
       messageFormat: 'markdown',
       answerSource: result?.data?.answerSource || undefined,
       citations: Array.isArray(result?.data?.citations) ? result.data.citations : [],
       actions: Array.isArray(result?.data?.actions) ? result.data.actions : [],
       createdAt: now
      });
     }
     return next;
    });
    if (result?.data?.handoffId) {
     setSupportHandoffs(prev => {
      const exists = prev.some(handoff => handoff.id === result.data.handoffId);
      if (exists) return prev;
      return [{
       id: result.data.handoffId,
       threadId: resolvedThreadId,
       linkedBy: user.uid,
       summary: result?.data?.summary || 'Support escalation created.',
       riskReason: 'Escalated for human review.',
       recommendedNextStep: result?.data?.nextStep || 'Manual review required.',
       createdAt: now
      }, ...prev];
     });
    }
    if (result?.data?.escalated) {
     addNotify('info', 'This conversation was escalated for human review.');
    }
    if (!messageOverride) setSupportInput('');
  } catch (err: any) {
   const fallbackThreadId = selectedSupportThreadId || `local_support_${Date.now()}`;
   const now = new Date();
   const fallbackReply = 'I wasn\'t able to complete that check just now. Please try again in a moment — if the issue continues, our support team will be notified and follow up with you.';
   setSelectedSupportThreadId(fallbackThreadId);
   setSupportMessages(prev => ([
    ...prev,
    {
     id: `local_support_error_${Date.now()}`,
     threadId: fallbackThreadId,
     linkedBy: user.uid,
     senderType: 'ai',
     senderId: 'support_orchestrator',
     message: fallbackReply,
     messageFormat: 'markdown',
     answerSource: 'fallback',
     citations: [],
     createdAt: now
    }
   ]));
   setSupportThreads(prev => {
    const existing = prev.find(thread => thread.id === fallbackThreadId);
    if (existing) {
     return prev.map(thread => thread.id === fallbackThreadId ? {
      ...thread,
      lastMessagePreview: fallbackReply,
      lastResponseState: 'fallback',
      status: 'escalated',
      updatedAt: now
     } : thread);
    }
    return [{
     id: fallbackThreadId,
     linkedBy: user.uid,
     customerUid: user.uid,
     customerEmail: user.email || '',
     customerDisplayName: userProfile.displayName || user.email || 'Customer',
     status: 'escalated',
     priority: 'high',
     supportTier: effectiveSupportTier,
     targetResponseSlaHours: effectiveSupportTier === 'white_glove' ? 4 : effectiveSupportTier === 'priority' ? 12 : 24,
     issueType: 'unknown',
     subject: 'Support response needs retry',
     lastMessagePreview: fallbackReply,
     lastHandoffId: null,
     lastHandoffSummary: null,
     waitingOnCustomer: false,
     lastResponseState: 'fallback',
     updatedAt: now,
     createdAt: now
    }, ...prev];
   });
   addNotify('error', err.message || 'Support request failed.');
 } finally {
   setSupportSending(false);
  }
 };

const handleSupportSafeAction = useCallback(async (threadId: string, actionType: string) => {
  if (!supportFunctions || !user) return;
  try {
   const actionFn = httpsCallable(supportFunctions, 'supportExecuteSafeAction');
   const result: any = await actionFn({ threadId, actionType });
   const actionMessage = String(result?.data?.message || 'Support action completed.');
   const now = new Date();
   setSupportThreads(prev => prev.map(thread => thread.id === threadId ? {
    ...thread,
    lastMessagePreview: actionMessage,
    updatedAt: now
   } : thread));
   setSupportMessages(prev => {
    const exists = prev.some(message =>
     message.threadId === threadId &&
     message.senderType === 'system' &&
     message.message === actionMessage
    );
    if (exists) return prev;
    return [
     ...prev,
     {
      id: `local_support_action_${Date.now()}`,
      threadId,
      linkedBy: user.uid,
      senderType: 'system',
      senderId: 'support_safe_action',
      message: actionMessage,
      messageFormat: 'markdown',
      citations: [],
      actions: [],
      createdAt: now
     }
    ];
   });
   addNotify('success', actionMessage);
  } catch (err: any) {
   addNotify('error', err.message || 'Support action failed.');
  }
 }, [supportFunctions, user]);

 const handleSupportFeedback = useCallback(async (payload: { feedbackType: 'help_article' | 'support_message'; helpful: boolean; articleId?: string; threadId?: string; messageId?: string; reason?: string; }) => {
  if (!supportFunctions) return;
  try {
   const feedbackFn = httpsCallable(supportFunctions, 'submitSupportFeedback');
   await feedbackFn(payload);
  } catch (err: any) {
   addNotify('error', err.message || 'Failed to save feedback.');
  }
 }, [supportFunctions]);

 // --- Calendar Helpers ---
 const getDaysInMonth = (date: Date): (Date | null)[] => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (Date | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
  return days;
 };

 const getInvoicesForDate = (date: Date) => {
  return filteredInvoices.filter(inv => new Date(inv.dueDate).toDateString() === date.toDateString());
 };

 // FIX: Calendar navigation no longer mutates state directly
 const handlePrevMonth = () => {
  setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
 };

 const handleNextMonth = () => {
  setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
 };

 // --- Computed Data ---
 const filteredInvoices = useMemo(() => {
  const visibleRealmIds = isViewingMemberWorkspace && activeWorkspace?.connectionIds?.length
   ? activeWorkspace.connectionIds
   : null;
  let base = visibleRealmIds ? invoices.filter(inv => visibleRealmIds.includes(inv.realmId)) : invoices;
  if (selectedRealmId === 'all') return base;
  return base.filter(inv => inv.realmId === selectedRealmId);
 }, [invoices, selectedRealmId, isViewingMemberWorkspace, activeWorkspace]);

 const invoicesForTab = useMemo(() => {
  return filteredInvoices.map(inv => {
   const snoozeOverride = dunningOverrides.find((o): o is SnoozeOverride => o.type === 'snooze' && o.invoiceId === inv.invoiceId && o.realmId === inv.realmId);
   const pauseOverride = inv.customerEmail
    ? dunningOverrides.find((o): o is CustomerPauseOverride => o.type === 'customer_pause' && o.customerEmail?.toLowerCase() === inv.customerEmail?.toLowerCase() && o.realmId === inv.realmId && !!o.active)
    : undefined;
   const customerMeta = inv.customerEmail
    ? dunningOverrides.find((o): o is CustomerMetaOverride => o.type === 'customer_meta' && o.customerEmail?.toLowerCase() === inv.customerEmail?.toLowerCase() && o.realmId === inv.realmId)
    : undefined;
   const invoiceCtrl = dunningOverrides.find((o): o is InvoiceControlsOverride => o.type === 'invoice_controls' && o.invoiceId === inv.invoiceId && o.realmId === inv.realmId);
   const isSnoozed = snoozeOverride && snoozeOverride.snoozedUntil
    ? (typeof snoozeOverride.snoozedUntil.toDate === 'function'
       ? snoozeOverride.snoozedUntil.toDate()
       : new Date(snoozeOverride.snoozedUntil)) > new Date()
    : false;
   const isExcluded = customerMeta?.excluded === true;
   const isDisabled = invoiceCtrl?.disabled === true;
   const isCollected = invoiceCtrl?.markedCollected === true;
   return { ...inv, isSnoozed, isPaused: !!pauseOverride, snoozeOverride, pauseOverride, customerMeta, invoiceCtrl, isExcluded, isDisabled, isCollected };
  });
 }, [filteredInvoices, dunningOverrides]);

 const filteredInvoicesForTab = useMemo(() => {
  let result = [...invoicesForTab];
  if (invoiceStatusFilter === 'overdue') result = result.filter(i => i.status === 'overdue' && !i.isSnoozed && !i.isPaused);
  else if (invoiceStatusFilter === 'snoozed') result = result.filter(i => i.isSnoozed);
  else if (invoiceStatusFilter === 'paused') result = result.filter(i => i.isPaused);
  else if (invoiceStatusFilter === 'excluded') result = result.filter(i => i.isExcluded);
  else if (invoiceStatusFilter === 'collected') result = result.filter(i => i.isCollected);
  if (invoiceSearch.trim()) {
   const q = invoiceSearch.toLowerCase();
   result = result.filter(i =>
    (i.customerName || '').toLowerCase().includes(q) ||
    (i.docNumber || '').toLowerCase().includes(q) ||
    (i.customerEmail || '').toLowerCase().includes(q)
   );
  }
  return result.sort((a, b) => (b.balance || 0) - (a.balance || 0));
 }, [invoicesForTab, invoiceStatusFilter, invoiceSearch]);

 const ptpByInvoice = useMemo(() => {
  const now = new Date();
  const map: Record<string, { nextPtp: string | null; hasMissedPtp: boolean }> = {};
  for (const n of collectionNotes) {
   if (!n.ptpDate || !n.invoiceId) continue;
   const ptpDate = new Date(n.ptpDate);
   const isMissed = ptpDate < now;
   if (!map[n.invoiceId]) {
    map[n.invoiceId] = { nextPtp: isMissed ? null : n.ptpDate, hasMissedPtp: isMissed };
   } else {
    if (!isMissed && (!map[n.invoiceId].nextPtp || new Date(n.ptpDate) < new Date(map[n.invoiceId].nextPtp!))) {
     map[n.invoiceId].nextPtp = n.ptpDate;
    }
    if (isMissed) map[n.invoiceId].hasMissedPtp = true;
   }
  }
  return map;
 }, [collectionNotes]);

 const filteredLogs = useMemo(() => {
  let result = [...logs];
  if (selectedRealmId !== 'all') result = result.filter(log => log.realmId === selectedRealmId);
  if (historyStatusFilter !== 'all') result = result.filter(log => log.deliveryStatus?.toLowerCase() === historyStatusFilter.toLowerCase());
  if (historySearch.trim()) {
   const q = historySearch.toLowerCase();
   result = result.filter(log => (log.customerName || '').toLowerCase().includes(q) || (log.docNumber || '').toLowerCase().includes(q));
  }
  if (sortConfig) {
   result.sort((a, b) => {
    let aVal: any = a[sortConfig.key as keyof DunningLog];
    let bVal: any = b[sortConfig.key as keyof DunningLog];
    if (sortConfig.key === 'sentAt') { aVal = getTimestampMs(getHistoryDateValue(a)); bVal = getTimestampMs(getHistoryDateValue(b)); }
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
   });
  }
  return result;
 }, [logs, selectedRealmId, historyStatusFilter, historySearch, sortConfig]);

const totalBalance = useMemo(() => filteredInvoices.reduce((sum, inv) => sum + (inv.balance || 0), 0), [filteredInvoices]);
const totalOverdueBalance = useMemo(() => filteredInvoices.filter(i => i.status === 'overdue').reduce((sum, inv) => sum + (inv.balance || 0), 0), [filteredInvoices]);
const overdueShare = useMemo(() => totalBalance > 0 ? Math.round((totalOverdueBalance / totalBalance) * 100) : 0, [totalBalance, totalOverdueBalance]);

 const insightsCustomerTagMap = useMemo(() => {
  const map: Record<string, string[]> = {};
  for (const o of dunningOverrides) {
   if (o.type === 'customer_meta' && o.customerEmail && o.tags?.length) {
    map[o.customerEmail.toLowerCase()] = o.tags;
   }
  }
  return map;
 }, [dunningOverrides]);

 const insightsSegmentInvoices = useMemo(() =>
  insightsSegmentFilter === 'all'
   ? invoices
   : invoices.filter(i => {
      const email = (i.customerEmail || '').toLowerCase();
      return (insightsCustomerTagMap[email] || []).includes(insightsSegmentFilter);
     }),
  [invoices, insightsSegmentFilter, insightsCustomerTagMap]
 );

 const payerRows = useMemo(() => {
  const now = new Date();
  const custMap: Record<string, { name: string; invs: Invoice[]; engaged: boolean }> = {};
  for (const inv of insightsSegmentInvoices) {
   const key = inv.customerEmail || inv.customerName;
   if (!custMap[key]) custMap[key] = { name: inv.customerName, invs: [], engaged: false };
   custMap[key].invs.push(inv);
  }
  for (const l of logs) {
   if (['opened', 'clicked'].includes(l.deliveryStatus) && l.recipient && custMap[l.recipient]) {
    custMap[l.recipient].engaged = true;
   }
  }
  return Object.entries(custMap).map(([key, d]) => {
   const activeInvs = d.invs.filter(i => i.status !== 'paid');
   const totalBal = activeInvs.reduce((s, i) => s + (i.balance || 0), 0);
   const overdueInvs = activeInvs.filter(i => i.status === 'overdue');
   const maxDays = overdueInvs.reduce((mx, i) => {
    if (!i.dueDate) return mx;
    return Math.max(mx, Math.floor((now.getTime() - new Date(i.dueDate).getTime()) / 86400000));
   }, 0);
   const chronic90 = overdueInvs.filter(i => i.dueDate && Math.floor((now.getTime() - new Date(i.dueDate).getTime()) / 86400000) >= 90).length;
   let score: 'Good' | 'Slow' | 'At-Risk' | 'Chronic';
   if (chronic90 >= 2) score = 'Chronic';
   else if (maxDays >= 60 && !d.engaged) score = 'At-Risk';
   else if (maxDays >= 8) score = 'Slow';
   else score = 'Good';
   return { key, name: d.name, score, totalBal, maxDays, engaged: d.engaged };
  }).filter(r => r.totalBal > 0).sort((a, b) => {
   const order: Record<string, number> = { Chronic: 0, 'At-Risk': 1, Slow: 2, Good: 3 };
   return order[a.score] - order[b.score];
  });
 }, [insightsSegmentInvoices, logs]);

 const overdueDistribution = useMemo(() => {
  const bins = { 'Current': 0, '1-30 Days': 0, '31-60 Days': 0, '61-90+ Days': 0 };
  const today = new Date();
  filteredInvoices.forEach(inv => {
   const dueDate = new Date(inv.dueDate);
   const diff = Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
   if (diff <= 0) bins['Current'] += (inv.balance || 0);
   else if (diff <= 30) bins['1-30 Days'] += (inv.balance || 0);
   else if (diff <= 60) bins['31-60 Days'] += (inv.balance || 0);
   else bins['61-90+ Days'] += (inv.balance || 0);
  });
  return Object.entries(bins).map(([name, value]) => ({ name, value }));
 }, [filteredInvoices]);

 const dunningPerformance = useMemo(() => {
  return dunningTiers.map(tier => {
   const sent = logs.filter(l => l.templateSent === tier.name).length;
   const recovered = logs.filter(l => l.templateSent === tier.name && invoices.find(i => i.invoiceId === l.invoiceId)?.status === 'paid').length;
   return { name: tier.name, sent, recovered };
  });
 }, [logs, invoices, dunningTiers]);

 // --- Effects ---
 // FIX: Reset ALL user-specific state when auth changes (sign out or switch accounts)
 useEffect(() => {
  const unsub = onAuthStateChanged(auth, (u) => {
   // Always reset all user-scoped data on any auth change
   clearSessionTimers();
   setShowSessionExpiryWarning(false);
   setSessionCountdown(SESSION_COUNTDOWN_SECONDS);
   setConnections([]);
   setSupportThreads([]);
   setSupportMessages([]);
   setSupportHandoffs([]);
   setLogs([]);
   setInvoices([]);
   setAgingSnapshots([]);
   setCollectionNotes([]);
   setNoteModal(null);
   setInsightsSegmentFilter('all');
   setDunningOverrides([]);
   setAuditLog([]);
   setWebhooks([]);
   setWebhookDeliveries([]);
   setDunningTiers([]);
   setUserProfile({ useCustomSender: false, notificationsEnabled: true });
   setProfilePicUrl('');
   setNewPassword('');
   setSelectedRealmId('all');
   setSelectedSupportThreadId(null);
   setSupportInput('');
   setSupportSending(false);
   setEditingTierId(null);
	  setEditingDraft(null);
	  setActiveTab('connections');
	  setSenderStatus('unknown');
 	  setVerificationEmailSent(false);
	  initialLoadDone.current = false;

   setUser(u);
   if (!u) {
    currentSessionTokenRef.current = null;
    setLoading(false);
   }
   // Note: registerSession is called from the onSnapshot effect once user state settles
  });
  return () => unsub();
 }, [clearSessionTimers]);

 // Concurrent session detection: register session on login, then listen for token changes
 useEffect(() => {
  if (!user || !db) return;
  // Admin panel tabs must not register a new session — doing so would overwrite the
  // regular-app tab's token and cause a false "another device signed in" sign-out.
  // isAdmin covers both /#admin and /?admin=true entry points.
  if (isAdmin) return;
  currentSessionTokenRef.current = null;
  const sessionRef = doc(db, 'artifacts', currentAppId, 'users', user.uid, 'sessions', 'current');
  let unsubSession: (() => void) | null = null;
  let cancelled = false;

  // Register our session first so we know our own token before listening
  const registerFn = httpsCallable(functions, 'registerSession');
  // Log login event (fire-and-forget — must not block session registration)
  httpsCallable(functions, 'logClientEvent')({ action: 'user_login', details: `Signed in as ${user.email || user.uid}` }).catch(() => {});
  registerFn().then((res: any) => {
   if (cancelled) return;
   const myToken: string = res.data?.sessionToken || '';
   currentSessionTokenRef.current = myToken;

   // Now start listening — any subsequent token change means another device signed in
   unsubSession = onSnapshot(sessionRef, (snap) => {
    if (!snap.exists()) return;
    // Ignore local-cache fires — we only want server-confirmed data.
    // Without this, Firestore's in-memory cache from a previous login session
    // fires immediately with the old token, causing a false sign-out.
    if (snap.metadata.fromCache) return;
    const serverToken = snap.data()?.sessionToken;
    if (!serverToken || serverToken === currentSessionTokenRef.current) return;
    // Token changed — another device took over this account
    signOut(auth);
    addNotify('error', 'You have been signed out because your account was accessed on another device.');
   }, (err) => {
    console.error('[Session watch] onSnapshot error:', err.code, err.message);
   });
  }).catch((err) => {
   console.error('[Session watch] registerSession error:', err);
  });
  return () => {
   cancelled = true;
   unsubSession?.();
  };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid]);

 useEffect(() => {
  if (!user) return;
  const hash = window.location.hash.toLowerCase();
  if (hash === '#help') {
   setActiveTab('support');
   setSupportPanelMode('help');
  }
  if (hash === '#support') {
   setActiveTab('support');
   setSupportPanelMode('chat');
  }
 }, [user]);

 // Team invite: check localStorage for pending token after user is authenticated
 useEffect(() => {
  if (!user || !functions) return;
  const token = localStorage.getItem('soarone_invite_token');
  if (!token) return;
  setInviteInfoLoading(true);
  httpsCallable(functions, 'getTeamInviteInfo')({ token })
   .then((res: any) => {
    setInviteAcceptInfo(res.data as TeamInviteInfo);
    setPendingInviteToken(token);
   })
   .catch(() => {
    // Token invalid or expired — silently clear it
    localStorage.removeItem('soarone_invite_token');
   })
   .finally(() => setInviteInfoLoading(false));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid]);

 // Team members listener (owner: who I've invited)
 useEffect(() => {
  if (!user || !db) return;
  const unsub = onSnapshot(
   query(collection(db, 'artifacts', currentAppId, 'users', user.uid, 'team_members'), limit(50)),
   (s) => {
    // Filter out UID-keyed index docs (isIndexDoc:true) written by acceptTeamInvite
   // for Firestore security rule lookups — only real member records have email/role.
   const members: TeamMember[] = s.docs
    .filter(d => !d.data().isIndexDoc)
    .map(d => ({ id: d.id, ...d.data() } as TeamMember));
    members.sort((a, b) => {
     if (a.status === 'active' && b.status !== 'active') return -1;
     if (b.status === 'active' && a.status !== 'active') return 1;
     return (a.email || '').localeCompare(b.email || '');
    });
    setTeamMembers(members);
   },
   () => {}
  );
  return () => unsub();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid]);

 // Workspaces listener (member: workspaces I belong to)
 useEffect(() => {
  if (!user || !db) return;
  const unsub = onSnapshot(
   query(collection(db, 'artifacts', currentAppId, 'users', user.uid, 'workspaces'), limit(50)),
   (s) => {
    setMyWorkspaces(s.docs.map(d => ({ id: d.id, ...d.data() } as WorkspaceMembership)));
   },
   () => {}
  );
  return () => unsub();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid]);

 useEffect(() => {
  if (!user || !functions || activeWorkspaceOwnerId) return;
  httpsCallable(functions, 'syncWorkspaceAccessSummarySelf')().catch(() => {});
 }, [user?.uid, functions, activeWorkspaceOwnerId]);

 // Auto-exit member workspace if membership is revoked
 useEffect(() => {
  if (!activeWorkspaceOwnerId) return;
  const stillMember = myWorkspaces.some(w => w.ownerUid === activeWorkspaceOwnerId && w.status === 'active');
  if (!stillMember) {
   handleSwitchWorkspace(null);
   addNotify('info', 'Your access to that workspace has been removed.');
  }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [myWorkspaces, activeWorkspaceOwnerId]);

 useEffect(() => {
  if (!user) return;
  if (activeTab === 'support') {
   if (window.location.hash !== '#support') window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#support`);
   return;
  }
  if (window.location.hash === '#support' || window.location.hash === '#help') {
   window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }
 }, [activeTab, user]);

 useEffect(() => {
  if (!user) {
   clearSessionTimers();
   setShowSessionExpiryWarning(false);
   setSessionCountdown(SESSION_COUNTDOWN_SECONDS);
   return;
  }

  resetSessionTimers();

  const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
  const handleActivity = () => {
   const now = Date.now();
   if (now - activityThrottleRef.current < 1000) return;
   activityThrottleRef.current = now;
   if (showSessionExpiryWarning) return;
   resetSessionTimers();
  };

  const handleVisibilityChange = () => {
   if (document.visibilityState === 'visible' && !showSessionExpiryWarning) {
    resetSessionTimers();
   }
  };

  activityEvents.forEach(eventName => window.addEventListener(eventName, handleActivity, { passive: true }));
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
   activityEvents.forEach(eventName => window.removeEventListener(eventName, handleActivity));
   document.removeEventListener('visibilitychange', handleVisibilityChange);
   clearSessionTimers();
  };
 }, [user, showSessionExpiryWarning, clearSessionTimers, resetSessionTimers]);

 // Pause all Firestore listeners when the tab is hidden (prevents overnight read accumulation)
 useEffect(() => {
  const handleVisibilityChange = () => {
   setListenersPaused(document.visibilityState === 'hidden');
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  if (document.visibilityState === 'hidden') setListenersPaused(true);
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
 }, []);

 // Stable listeners — subscribe once on login/workspace change, never on tab switch
 useEffect(() => {
  if (!user || !db || listenersPaused) return;

  // Circuit breaker: if this effect re-runs more than 10 times per minute, something is looping
  const now = Date.now();
  if (now - listenerSubCountRef.current.windowStart > 60000) {
   listenerSubCountRef.current = { count: 0, windowStart: now };
  }
  listenerSubCountRef.current.count++;
  if (listenerSubCountRef.current.count > 10) {
   console.error('[SoarOne] Listener re-subscription rate exceeded threshold — possible render loop. Listeners paused.');
   setListenersPaused(true);
   return;
  }

  const canReadVerifiedSupportData = user.emailVerified || !user.providerData.some(provider => provider.providerId === 'password');
  if (!canReadVerifiedSupportData) {
   setSupportThreads([]);
   setSupportMessages([]);
   setSupportHandoffs([]);
   setLoading(false);
   return;
  }
  if (!initialLoadDone.current) setLoading(true);

  const ownerId = activeWorkspaceOwnerId ?? user.uid;

  const unsubC = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'qbo_connections'), where('linkedBy', '==', ownerId), limit(50)),
   (s) => setConnections(s.docs.map(d => ({ id: d.id, ...d.data() } as Connection))),
   (err) => { console.error('Connections listener error:', err); addNotify('error', 'Connection data sync interrupted.'); }
  );
  const unsubOverrides = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'dunning_overrides'), where('linkedBy', '==', ownerId), limit(500)),
   (s) => setDunningOverrides(s.docs.map(d => ({ id: d.id, ...d.data() } as unknown as DunningOverride))),
   (err) => { console.error('Overrides listener error:', err); }
  );
  const unsubWebhooks = onSnapshot(
   query(collection(db, 'artifacts', currentAppId, 'users', user.uid, 'webhooks'), limit(20)),
   (s) => {
    const hooks = s.docs.map(d => ({ id: d.id, ...d.data() } as Webhook));
    hooks.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setWebhooks(hooks);
   },
   (err) => { console.error('Webhooks listener error:', err); }
  );
  const unsubApiKeys = onSnapshot(
   query(collection(db, 'artifacts', currentAppId, 'users', user.uid, 'api_keys'), limit(50)),
   (s) => {
    const keys = s.docs.map(d => ({ id: d.id, ...d.data() } as ApiKey));
    keys.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setApiKeys(keys.filter(k => k.active));
   },
   (err) => { console.error('API keys listener error:', err); }
  );
  const unsubMaintenance = onSnapshot(
   doc(db, ...PUBLIC_DATA_PATH, 'system_status', 'maintenance'),
   (snapshot) => {
    if (!snapshot.exists()) { setMaintenanceNotice(null); return; }
    setMaintenanceNotice(snapshot.data() as MaintenanceNotice);
   },
   (err) => { console.error('Maintenance listener error:', err); setMaintenanceNotice(null); }
  );

  const unsubP = onSnapshot(
   doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'),
   (s) => {
    if (s.exists()) {
     const data = s.data() as UserProfile;
     setUserProfile(prev => profileFormDirty.current
      ? { ...data, useCustomSender: data.useCustomSender ?? false, displayName: prev.displayName, senderAddress: prev.senderAddress, senderCity: prev.senderCity, senderZip: prev.senderZip }
      : { ...data, useCustomSender: data.useCustomSender ?? false }
     );
     setProfilePicUrl(data.photoURL || '');
     if (
      !data.tosVersion || data.tosVersion !== CURRENT_TOS_VERSION ||
      !data.privacyVersion || data.privacyVersion !== CURRENT_PRIVACY_VERSION
     ) {
      setShowTosModal(true);
     }
    } else {
     setProfilePicUrl('');
     if (functions) {
      httpsCallable(functions, 'createUserProfile')({})
       .then(() => {
        // Profile listener will fire again once the doc is written — no action needed here
       })
       .catch((e) => console.error('Failed to create profile:', e));
     }
    }
   },
   (err) => { console.error('Profile listener error:', err); }
  );

  const setRef = query(collection(db, 'artifacts', currentAppId, 'users', ownerId, 'settings'), limit(20));
  const unsubS = onSnapshot(setRef, async (s) => {
   if (s.empty) {
    // AUTO-SEED: Create 3 default dunning tiers for new users (only when viewing own workspace)
    if (activeWorkspaceOwnerId) { setDunningTiers([]); setLoading(false); initialLoadDone.current = true; return; }
    const defaults = [
     {
      name: 'Friendly Reminder',
      daysPastDue: 7,
      subject: 'Friendly Reminder: Invoice #{{invoice_num}} is Past Due',
      body: 'Hi {{customer_name}},\n\nThis is a friendly reminder that invoice #{{invoice_num}} for ${{amount_due}} was due on {{due_date}}.\n\nPlease arrange payment at your earliest convenience. If you have already sent payment, please disregard this notice.\n\nThank you for your business!',
      enabled: true,
      isDefault: true
     },
     {
      name: 'Second Notice',
      daysPastDue: 30,
      subject: 'Second Notice: Invoice #{{invoice_num}} - Payment Required',
      body: 'Dear {{customer_name}},\n\nThis is a second notice regarding invoice #{{invoice_num}} for ${{amount_due}}, which was due on {{due_date}}.\n\nImmediate payment is required to keep your account in good standing. Please contact us if you are experiencing any difficulties with payment.\n\nRegards.',
      enabled: true,
      isDefault: true
     },
     {
      name: 'Final Notice',
      daysPastDue: 60,
      subject: 'Final Notice: Invoice #{{invoice_num}} - Immediate Action Required',
      body: 'Dear {{customer_name}},\n\nThis is a final notice regarding the outstanding balance of ${{amount_due}} on invoice #{{invoice_num}}, which was due on {{due_date}}.\n\nFailure to remit payment within the next 7 days may result in further collection action. Please contact us immediately to resolve this matter.\n\nRegards.',
      enabled: true,
      isDefault: true
     }
    ];
    try {
     const batch = writeBatch(db);
     for (const tier of defaults) {
      const tierId = tier.name.toLowerCase().replace(/\s+/g, '_');
      batch.set(doc(db, 'artifacts', currentAppId, 'users', ownerId, 'settings', tierId), tier);
     }
     await batch.commit();
    } catch (e) {
     console.error('Failed to seed default tiers:', e);
    }
   } else {
    setDunningTiers(s.docs.map(d => ({ id: d.id, ...d.data() } as DunningTier)));
   }
   setLoading(false);
   initialLoadDone.current = true;
  }, (err) => {
   console.error('Settings listener error:', err);
   setLoading(false);
   initialLoadDone.current = true;
  });

  return () => { unsubC(); unsubOverrides(); unsubWebhooks(); unsubApiKeys(); unsubMaintenance(); unsubP(); unsubS(); };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid, activeWorkspaceOwnerId, listenersPaused]);

 // Data listeners — stable subscriptions independent of active tab
 useEffect(() => {
  if (!user || !db || listenersPaused) return;
  const ownerId = activeWorkspaceOwnerId ?? user.uid;

  const unsubL = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'dunning_logs'), where('linkedBy', '==', ownerId), limit(500)),
   (s) => setLogs(s.docs.map(d => ({ id: d.id, ...d.data() } as DunningLog))),
   (err) => { console.error('Logs listener error:', err); }
  );
  const unsubI = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'qbo_invoices'), where('linkedBy', '==', ownerId), limit(1000)),
   (s) => setInvoices(s.docs.map(d => ({ id: d.id, ...d.data() } as Invoice))),
   (err) => { console.error('Invoices listener error:', err); }
  );
  const unsubAudit = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'ar_audit_log'), where('linkedBy', '==', ownerId), limit(250)),
   (s) => {
    const events = s.docs.map(d => ({ id: d.id, ...d.data() } as AuditEvent));
    events.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setAuditLog(events);
   },
   (err) => { console.error('Audit log listener error:', err); }
  );
  const canReadVerifiedSupportData = user.emailVerified || !user.providerData.some(provider => provider.providerId === 'password');
  let unsubST = () => {};
  let unsubSM = () => {};
  let unsubSH = () => {};
  if (canReadVerifiedSupportData) {
   unsubST = onSnapshot(
    query(collection(db, ...PUBLIC_DATA_PATH, 'support_threads'), where('linkedBy', '==', ownerId), limit(100)),
    (s) => setSupportThreads(s.docs.map(d => ({ id: d.id, ...d.data() } as SupportThread))),
    (err) => { console.error('Support threads listener error:', err); }
   );
   unsubSM = onSnapshot(
    query(collection(db, ...PUBLIC_DATA_PATH, 'support_messages'), where('linkedBy', '==', ownerId), limit(200)),
    (s) => setSupportMessages(s.docs.map(d => ({ id: d.id, ...d.data() } as SupportMessage))),
    (err) => { console.error('Support messages listener error:', err); }
   );
   unsubSH = onSnapshot(
    query(collection(db, ...PUBLIC_DATA_PATH, 'support_handoffs'), where('linkedBy', '==', ownerId), limit(100)),
    (s) => setSupportHandoffs(s.docs.map(d => ({ id: d.id, ...d.data() } as SupportHandoff))),
    (err) => { console.error('Support handoffs listener error:', err); }
   );
  }
  const unsubSnap = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'aging_snapshots'), where('linkedBy', '==', ownerId), limit(60)),
   (s) => {
    const snaps = s.docs.map(d => d.data() as AgingSnapshot);
    snaps.sort((a, b) => a.weekKey.localeCompare(b.weekKey));
    setAgingSnapshots(snaps);
   },
   (err) => { console.error('Aging snapshots listener error:', err); }
  );
  const unsubNotes = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'customer_notes'), where('linkedBy', '==', ownerId), limit(250)),
   (s) => {
    const notes = s.docs.map(d => ({ id: d.id, ...d.data() } as CollectionNote));
    notes.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    setCollectionNotes(notes);
   },
   (err) => { console.error('Notes listener error:', err); }
  );

  return () => { unsubL(); unsubI(); unsubAudit(); unsubST(); unsubSM(); unsubSH(); unsubSnap(); unsubNotes(); };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid, activeWorkspaceOwnerId, listenersPaused]);

 useEffect(() => {
  if (!filteredSupportThreads.length) {
   if (selectedSupportThreadId) setSelectedSupportThreadId(null);
   return;
  }
  if (newConversationMode.current) return;
  if (!selectedSupportThreadId || !filteredSupportThreads.some(thread => thread.id === selectedSupportThreadId)) {
   setSelectedSupportThreadId(filteredSupportThreads[0].id);
  }
 }, [filteredSupportThreads, selectedSupportThreadId]);

 // Stable string dep — prevents compliance listeners from re-subscribing on every
 // connection snapshot (which produces a new array ref even when realmIds haven't changed)
 const connectionRealmIds = useMemo(
  () => connections.map(c => c.realmId).sort().join(','),
  [connections]
 );

 // Compliance data listeners — re-subscribe only when realmIds actually change
 // Scoped to only the user's connected realmIds to prevent cross-tenant data leakage
 useEffect(() => {
  if (!user || !db || !connectionRealmIds || listenersPaused) {
   setDisputes([]);
   setOptOuts([]);
   return;
  }

  // Firestore 'in' operator supports max 30 values
  const realmIds = connectionRealmIds.split(',').filter(Boolean).slice(0, 30);

  const unsubDisputes = onSnapshot(
   query(collection(db, ...PUBLIC_DATA_PATH, 'invoice_disputes'),
         where('realmId', 'in', realmIds), limit(200)),
   (s) => setDisputes(s.docs.map(d => ({ id: d.id, ...d.data() }))),
   (err) => { console.error('Disputes listener error:', err); }
  );

  const unsubOptOuts = onSnapshot(
   query(
    collection(db, ...PUBLIC_DATA_PATH, 'email_optouts'),
    where('linkedBy', '==', activeWorkspaceOwnerId ?? user.uid),
    limit(500)
   ),
   (s) => setOptOuts(s.docs.map(d => ({ id: d.id, ...d.data() }))),
   (err) => { console.error('OptOuts listener error:', err); }
  );

  return () => { unsubDisputes(); unsubOptOuts(); };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user?.uid, connectionRealmIds, activeWorkspaceOwnerId, listenersPaused]);

 // --- Checkout success/cancel detection ---
 useEffect(() => {
  const clearCheckoutParam = () => {
   const url = new URL(window.location.href);
   url.searchParams.delete('checkout');
   const nextSearch = url.searchParams.toString();
   window.history.replaceState({}, '', `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`);
  };
  const params = new URLSearchParams(window.location.search);
  if (params.get('checkout') === 'plan_success') {
   addNotify('success', 'Subscription activated. Your plan has been updated.');
   clearCheckoutParam();
  } else if (params.get('checkout') === 'insights_success') {
   addNotify('success', 'AR Insights activated. Your dashboard will unlock as soon as billing sync completes.');
   clearCheckoutParam();
  } else if (params.get('checkout') === 'cancel') {
   addNotify('info', 'Checkout was canceled. No changes were made.');
   clearCheckoutParam();
  }
 }, []);

 // --- Email verification link return ---
 // Runs on mount and whenever user changes — handles the case where user wasn't
 // logged in at mount time and the param needs to be processed after auth resolves.
 useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const emailVerified = params.get('email_verified');
  if (!emailVerified) return;
  // Clear the param immediately so it doesn't re-trigger on subsequent renders
  window.history.replaceState({}, '', window.location.pathname);
  if (emailVerified === '1') {
   if (auth.currentUser) {
    auth.currentUser.reload().then(() => {
     if (auth.currentUser) setUser({ ...auth.currentUser } as any);
    });
   }
   addNotify('success', 'Email verified — your account is ready.');
  } else if (emailVerified === 'error') {
   const reason = params.get('reason');
   const msg = reason === 'expired'
    ? 'This verification link has expired or already been used. Request a new one below.'
    : 'The verification link is invalid. Please request a new one.';
   addNotify('error', msg);
  }
 }, [user]);

 // --- Pricing Modal Component ---
 const PricingModal = () => {
  if (!showPricing) return null;
  const plans = [
   { id: 'starter', name: 'Starter', price: 49, features: ['1 QuickBooks Connection', '3 Dunning Templates', 'System Email Sender', 'Real-Time Analytics', 'Automatic Invoice Sync'], highlight: false },
   { id: 'pro', name: 'Pro', price: 99, features: ['3 QuickBooks Connections', 'Unlimited Templates', 'Custom SMTP Gateway', 'Analytics + CSV Export', 'Compliance Dashboard', 'GDPR Tools', 'Dispute Management'], highlight: true },
   { id: 'cpa', name: 'CPA / Agency', price: 199, features: ['10 QuickBooks Connections', 'Everything in Pro', 'Multi-Client Dashboard', 'Per-Client Analytics', 'Priority Support', 'Dedicated Onboarding'], highlight: false },
  ];
  return (
   <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-6" onClick={() => setShowPricing(false)}>
    <div className={`${t.bgModal} rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-10`} onClick={e => e.stopPropagation()}>
     <div className="text-center mb-10">
      <h2 style={{fontFamily:"var(--font-display)"}} className="text-3xl font-bold text-slate-900 tracking-tight">Upgrade Your Plan</h2>
      <p className="text-sm text-slate-500 mt-2">Choose a plan that fits your business. Cancel anytime.</p>
      {isTrialActive && <p className="text-xs text-amber-600 font-semibold mt-3 bg-amber-50 inline-block px-4 py-1.5 rounded-full">Trial active — {Math.ceil((new Date(userProfile.trialEndsAt).getTime() - Date.now()) / 86400000)} days remaining</p>}
     </div>
     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {plans.map(p => {
       const isCurrent = currentPlan.id === p.id;
       const hasPaidSubscription = !!userProfile.paddleSubscriptionId && ['active', 'trialing', 'past_due', 'paused'].includes(userProfile.subscriptionStatus || '');
       return (
        <div key={p.id} className={`rounded-2xl p-8 border-2 transition-all relative ${p.highlight ? 'border-cyan-400 bg-cyan-50/30 shadow-lg shadow-cyan-100' : 'border-slate-100 bg-white'} ${isCurrent ? 'ring-2 ring-emerald-300' : ''}`}>
         {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider px-4 py-1 rounded-full" style={{backgroundColor: PRIMARY_BLUE, color:'#050508'}}>Most Popular</div>}
         {isCurrent && <div className="absolute -top-3 right-4 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-emerald-100 text-emerald-700">Current</div>}
         <h3 style={{fontFamily:"var(--font-display)"}} className="font-bold text-lg text-slate-900">{p.name}</h3>
         <div className="mt-3 mb-6">
          <span style={{fontFamily:"var(--font-display)"}} className="text-4xl font-extrabold text-slate-900">${p.price}</span>
          <span className="text-sm text-slate-400">/mo</span>
         </div>
         <ul className="space-y-3 mb-8">
          {p.features.map(f => (
           <li key={f} className="text-sm text-slate-600 flex items-start gap-2">
            <CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" />{f}
           </li>
          ))}
         </ul>
         {isCurrent ? (
          <button disabled className="w-full py-3 rounded-xl text-sm font-semibold bg-slate-100 text-slate-400 cursor-not-allowed">Current Plan</button>
         ) : (
          <button onClick={() => handleCheckout(p.id)} disabled={checkoutLoading === p.id}
           className={`w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all ${p.highlight ? 'text-slate-900 hover:shadow-lg' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
           style={p.highlight ? {backgroundColor: PRIMARY_BLUE} : {}}>
           {checkoutLoading === p.id ? 'Processing...' : hasPaidSubscription ? `Switch to ${p.name}` : 'Subscribe'}
          </button>
         )}
        </div>
       );
      })}
     </div>
     <div className="text-center mt-8">
      {/* Manage existing subscription */}
     {userProfile.paddleSubscriptionId && userProfile.subscriptionStatus === 'active' && (
       <button
        onClick={() => handleManageSubscription('manage')}
        disabled={checkoutLoading === 'manage'}
        className="text-sm font-semibold text-slate-500 hover:text-slate-800 underline transition-colors mb-4 block mx-auto"
       >
        {checkoutLoading === 'manage' ? 'Loading portal...' : 'Manage existing subscription →'}
       </button>
      )}
      {/* Pending cancellation notice */}
      {userProfile.paddleCancellationEffectiveDate && userProfile.subscriptionStatus === 'active' && (
       <p className="text-xs text-amber-600 font-semibold mb-3 bg-amber-50 inline-block px-4 py-1.5 rounded-full">
        Subscription cancels on {new Date(userProfile.paddleCancellationEffectiveDate).toLocaleDateString()}
       </p>
      )}
      <button onClick={() => setShowPricing(false)} className="block mx-auto mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors">Close</button>
     </div>
    </div>
   </div>
  );
 };

 // --- Admin Panel (secret route) ---
 if (isAdmin) return <AdminPanel />;

 // --- Password Reset Screen (MUST render first — before loading/auth) ---
 if (resetMode) return (
  <div className={`min-h-screen ${t.bg} flex items-center justify-center p-6 font-['DM_Sans',sans-serif] text-center`}>
   <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end pointer-events-none w-full sm:w-auto px-4 sm:px-0">
    {notifications.map(n => <Toast key={n.id} {...n} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} />)}
   </div>
   <div className="max-w-md w-full animate-in fade-in zoom-in-95 duration-500">
    <a href={MARKETING_SITE_URL} className="inline-block mb-8" aria-label="Go to SoarOne home">
     <img src="/brand-wordmark.png" alt="SoarOne" className="h-16 w-auto mx-auto" />
    </a>
    <div className={`${t.bgCard} p-10 rounded-2xl border ${t.border} shadow-none text-left mt-8`}>
     <div className="space-y-4">
      <div className="text-center mb-4">
       <Key size={24} style={{ color: PRIMARY_BLUE }} className="mx-auto mb-3" />
       <h2 className={`text-lg font-bold ${t.text}`}>Reset Your Password</h2>
       {resetEmail && <p className={`text-xs ${t.textMuted} mt-1`}>for {resetEmail}</p>}
      </div>
      {resetSuccess ? (
       <div className="text-center py-6">
        <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
        <p className={`text-sm font-medium ${t.text}`}>Password updated!</p>
        <p className={`text-xs ${t.textMuted} mt-1`}>Redirecting to sign in...</p>
       </div>
      ) : (
       <>
        <input
         type="password"
         placeholder="New password (min 8 characters)"
         value={resetNewPassword}
         onChange={(e) => setResetNewPassword(e.target.value)}
         className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
         autoFocus
        />
        <input
         type="password"
         placeholder="Confirm new password"
         value={resetConfirmPassword}
         onChange={(e) => setResetConfirmPassword(e.target.value)}
         className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
         onKeyDown={(e) => e.key === 'Enter' && handleResetPassword()}
        />
        <button
         onClick={handleResetPassword}
         disabled={resetLoading}
         style={{ backgroundColor: PRIMARY_BLUE }}
         className="w-full text-slate-900 font-semibold py-4 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none disabled:opacity-50"
        >
         {resetLoading ? 'Updating...' : 'Set New Password'}
        </button>
        <button
         onClick={() => { setResetMode(false); window.history.replaceState({}, '', window.location.pathname); }}
         className={`w-full text-xs font-normal ${t.textMuted} tracking-normal py-2`}
        >
         Back to Sign In
        </button>
       </>
      )}
     </div>
    </div>
   </div>
  </div>
 );

 // --- Debtor Self-Service Portal (unauthenticated, token-based) ---
 if (window.location.pathname === '/portal') return <DebtorPortalView />;

 // --- Loading Screen ---
 if (loading) return (
  <div className={`min-h-screen ${t.bgCard} flex flex-col items-center justify-center gap-6 font-['DM_Sans',sans-serif] text-center`}>
   <RefreshCw style={{ color: PRIMARY_BLUE }} className="animate-spin" size={48} />
   <p className={`text-xs font-medium ${t.textMuted} tracking-wide animate-pulse`}>Contextualizing Intelligence</p>
  </div>
 );

 const requiresEmailVerification = !!user && !user.emailVerified && user.providerData.some(provider => provider.providerId === 'password');

 if (requiresEmailVerification) return (
  <div className={`min-h-screen ${t.bg} flex items-center justify-center p-6 font-['DM_Sans',sans-serif] text-center`}>
   <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end pointer-events-none w-full sm:w-auto px-4 sm:px-0">
    {notifications.map(n => <Toast key={n.id} {...n} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} />)}
   </div>
   <div className="max-w-md w-full animate-in fade-in zoom-in-95 duration-500">
    <a href={MARKETING_SITE_URL} className="inline-block mb-8" aria-label="Go to SoarOne home">
     <img src="/brand-wordmark.png" alt="SoarOne" className="h-16 w-auto mx-auto" />
    </a>
    <div className={`${t.bgCard} p-10 rounded-2xl border ${t.border} shadow-none text-left mt-8`}>
     <div className="text-center mb-6">
      <MailIcon size={24} style={{ color: PRIMARY_BLUE }} className="mx-auto mb-3" />
      <h2 className={`text-lg font-bold ${t.text}`}>Verify your email</h2>
      <p className={`text-xs ${t.textMuted} mt-1`}>Check your inbox to finish setting up your account.</p>
     </div>
     <div className={`p-4 rounded-xl ${t.bgInput} border ${t.borderInput} text-xs ${t.textSec} leading-relaxed`}>
      We sent a verification email to <strong className={t.text}>{user?.email}</strong>. Confirm your email address before continuing into the app.
     </div>
     <div className="space-y-3 mt-6">
      <button
       onClick={handleCheckVerificationStatus}
       style={{ backgroundColor: PRIMARY_BLUE }}
       className="w-full text-slate-900 font-semibold py-4 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none"
      >
       I verified my email
      </button>
      <button
       onClick={handleResendVerificationEmail}
       disabled={resendingVerification}
       className={`w-full py-3.5 rounded-xl text-xs font-semibold border ${t.border} ${t.text} transition-all hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed`}
      >
       {resendingVerification ? 'Sending…' : verificationEmailSent ? 'Resend verification email' : 'Send verification email'}
      </button>
      <button
       onClick={() => signOut(auth)}
       className={`w-full text-xs font-normal ${t.textMuted} tracking-normal py-2 transition-colors`}
      >
       Sign out
      </button>
     </div>
    </div>
   </div>
  </div>
 );

 // --- Auth Screen ---
 if (!user) return (
  <div className={`min-h-screen ${t.bg} flex items-center justify-center p-6 font-['DM_Sans',sans-serif] text-center`}>
   {/* Toast notifications on auth screen */}
   <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end pointer-events-none w-full sm:w-auto px-4 sm:px-0">
    {notifications.map(n => <Toast key={n.id} {...n} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} />)}
   </div>
   <div className="max-w-md w-full animate-in fade-in zoom-in-95 duration-500">
    <a href={MARKETING_SITE_URL} className="inline-block mb-8" aria-label="Go to SoarOne home">
     <img src="/brand-wordmark.png" alt="SoarOne" className="h-16 w-auto mx-auto" />
    </a>

    {/* ===== MFA CHALLENGE ===== */}
    {mfaChallengeResolver ? (
     <div className={`${t.bgCard} p-10 rounded-2xl border ${t.border} shadow-none text-left mt-8`}>
      <div className="text-center mb-6">
       <ShieldCheck size={28} style={{ color: PRIMARY_BLUE }} className="mx-auto mb-3" />
       <h2 className={`text-lg font-bold ${t.text}`}>Two-Factor Authentication</h2>
       <p className={`text-xs ${t.textMuted} mt-1`}>Open your authenticator app and enter the 6-digit code.</p>
      </div>
      <div className="space-y-4">
       <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000 000"
        maxLength={7}
        value={mfaChallengeCode}
        onChange={(e) => setMfaChallengeCode(e.target.value.replace(/[^0-9\s]/g, ''))}
        onKeyDown={(e) => e.key === 'Enter' && handleMfaChallengeSubmit()}
        className={`w-full text-center text-2xl font-mono tracking-[0.4em] px-5 py-4 ${t.bgInput} border-2 ${t.borderInput} rounded-xl ${t.text} outline-none focus:ring-2 ${t.ring}`}
        autoFocus
       />
       <button
        onClick={handleMfaChallengeSubmit}
        disabled={mfaChallengeLoading || mfaChallengeCode.replace(/\s/g, '').length < 6}
        style={{ backgroundColor: PRIMARY_BLUE }}
        className="w-full text-slate-900 font-semibold py-4 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none disabled:opacity-50"
       >
        {mfaChallengeLoading ? 'Verifying…' : 'Verify Code'}
       </button>
       <button
        onClick={() => { setMfaChallengeResolver(null); setMfaChallengeCode(''); }}
        className={`w-full py-3 text-xs font-medium ${t.textMuted} hover:${t.textSec} transition-colors`}
       >
        Back to Sign In
       </button>
      </div>
     </div>
    ) : authPage === 'recovery' ? (
     <div className={`${t.bgCard} p-10 rounded-2xl border ${t.border} shadow-none text-left mt-8`}>
      <div className="text-center mb-6">
       <Key size={24} style={{ color: PRIMARY_BLUE }} className="mx-auto mb-3" />
       <h2 className={`text-lg font-bold ${t.text}`}>Account Recovery</h2>
       <p className={`text-xs ${t.textMuted} mt-1`}>Recover your password or find your account</p>
      </div>

      {/* Tabs */}
      <div className={`flex mb-6 rounded-xl ${t.bgInput} p-1`}>
       <button
        onClick={() => { setRecoveryTab('password'); setRecoverySent(false); setRecoveryResult(''); }}
        className={`flex-1 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${recoveryTab === 'password' ? 'bg-white text-slate-900 shadow-sm' : `${t.textMuted}`}`}
        style={recoveryTab === 'password' ? { backgroundColor: PRIMARY_BLUE, color: '#0F172A' } : {}}
       >
        Forgot Password
       </button>
       <button
        onClick={() => { setRecoveryTab('username'); setRecoverySent(false); setRecoveryResult(''); }}
        className={`flex-1 py-2.5 rounded-lg text-xs font-semibold tracking-wide transition-all ${recoveryTab === 'username' ? 'bg-white text-slate-900 shadow-sm' : `${t.textMuted}`}`}
        style={recoveryTab === 'username' ? { backgroundColor: PRIMARY_BLUE, color: '#0F172A' } : {}}
       >
        Find My Account
       </button>
      </div>

      {recoverySent ? (
       /* Success state */
       <div className="text-center py-4">
        <div className="mb-4">
         {recoveryTab === 'password' ? (
          <MailIcon size={32} style={{ color: PRIMARY_BLUE }} className="mx-auto" />
         ) : (
          <Search size={32} style={{ color: PRIMARY_BLUE }} className="mx-auto" />
         )}
        </div>
        <p className={`text-sm ${t.text} leading-relaxed mb-6`}>{recoveryResult}</p>
        {recoveryTab === 'password' && (
         <p className={`text-xs ${t.textMuted} mb-6`}>Didn't receive it? Check your spam folder or try again in a few minutes.</p>
        )}
        <div className="space-y-3">
         {recoveryTab === 'password' && (
          <button
           onClick={() => { setRecoverySent(false); setRecoveryResult(''); }}
           className={`w-full py-3 rounded-xl text-xs font-semibold border ${t.border} ${t.text} transition-all hover:shadow-sm`}
          >
           Send Again
          </button>
         )}
         <button
          onClick={() => { setAuthPage('login'); setRecoverySent(false); setRecoveryResult(''); }}
          style={{ backgroundColor: PRIMARY_BLUE }}
          className="w-full text-slate-900 font-semibold py-3 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none"
         >
          Back to Sign In
         </button>
        </div>
       </div>
      ) : (
       /* Input state */
       <div className="space-y-4">
        <div>
         <label className={`text-xs font-medium ${t.textSec} block mb-2`}>
          {recoveryTab === 'password' ? 'Enter your email to receive a reset link' : 'Enter your email to check your account'}
         </label>
         <input
          type="email"
          placeholder="you@example.com"
          value={recoveryEmail}
          onChange={(e) => setRecoveryEmail(e.target.value)}
          className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
          onKeyDown={(e) => e.key === 'Enter' && handleRecoverySubmit()}
          autoFocus
         />
        </div>
        <button
         onClick={handleRecoverySubmit}
         disabled={recoveryLoading}
         style={{ backgroundColor: PRIMARY_BLUE }}
         className="w-full text-slate-900 font-semibold py-4 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none disabled:opacity-50"
        >
         {recoveryLoading ? 'Processing...' : recoveryTab === 'password' ? 'Send Reset Link' : 'Look Up Account'}
        </button>
        <button
         onClick={() => setAuthPage('login')}
         className={`w-full text-xs font-normal ${t.textMuted} tracking-normal py-2 transition-colors`}
        >
         Back to Sign In
        </button>
       </div>
      )}
     </div>

    ) : (
     /* ===== LOGIN / REGISTER PAGE ===== */
     <div className={`${t.bgCard} p-10 rounded-2xl border ${t.border} shadow-none text-left mt-8`}>
      <div className="text-center mb-6">
       <h2 className={`text-lg font-bold ${t.text}`}>{authMode === 'register' ? 'Create your account' : 'Sign in to SoarOne'}</h2>
       <p className={`text-xs ${t.textMuted} mt-1`}>
        {authMode === 'register'
         ? 'Use your work email to create an account and verify it before continuing.'
         : 'Continue with email and password or use Google.'}
       </p>
      </div>
      <form className="space-y-4 mb-2" onSubmit={handleAuthSubmit}>
       <input
        type="email"
        placeholder="Email"
        value={authEmail}
        onChange={(e) => setAuthEmail(e.target.value)}
        className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
        required
       />
       <input
        type="password"
        placeholder="Password"
        value={authPassword}
        onChange={(e) => setAuthPassword(e.target.value)}
        className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
        required
       />
       {authMode === 'register' && (
        <>
         <input
          type="password"
          placeholder="Confirm Password"
          value={authConfirmPassword}
          onChange={(e) => setAuthConfirmPassword(e.target.value)}
          className={`w-full px-5 py-3.5 ${t.bgInput} border ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
          required
         />
         <p className={`text-[11px] ${t.textMuted} px-1`}>Use at least 8 characters. A mix of letters and numbers is recommended.</p>
         <label className={`flex items-start gap-3 px-1 ${tosLinkOpened ? 'cursor-pointer' : 'cursor-default'}`}>
          <input
           type="checkbox"
           checked={tosChecked}
           disabled={!tosLinkOpened}
           onChange={e => setTosChecked(e.target.checked)}
           className="mt-0.5 w-4 h-4 rounded accent-cyan-400 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          />
          <span className={`text-[11px] leading-relaxed ${tosLinkOpened ? t.textMuted : 'text-slate-400'}`}>
           I have read and agree to the{' '}
           <a href={TERMS_PAGE_URL} target="_blank" rel="noreferrer"
            onClick={() => setTosLinkOpened(true)}
            className="underline font-medium" style={{ color: PRIMARY_BLUE }}>Terms of Service</a>
           {!tosLinkOpened && <span className="ml-1 text-[10px] text-amber-500/80">— open to enable</span>}
          </span>
         </label>
         <label className={`flex items-start gap-3 px-1 ${privacyLinkOpened ? 'cursor-pointer' : 'cursor-default'}`}>
          <input
           type="checkbox"
           checked={privacyChecked}
           disabled={!privacyLinkOpened}
           onChange={e => setPrivacyChecked(e.target.checked)}
           className="mt-0.5 w-4 h-4 rounded accent-cyan-400 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          />
          <span className={`text-[11px] leading-relaxed ${privacyLinkOpened ? t.textMuted : 'text-slate-400'}`}>
           I have read and agree to the{' '}
           <a href={PRIVACY_PAGE_URL} target="_blank" rel="noreferrer"
            onClick={() => setPrivacyLinkOpened(true)}
            className="underline font-medium" style={{ color: PRIMARY_BLUE }}>Privacy Policy</a>
           {!privacyLinkOpened && <span className="ml-1 text-[10px] text-amber-500/80">— open to enable</span>}
          </span>
         </label>
        </>
       )}
       <button
        type="submit"
        disabled={authLoading}
        style={{ backgroundColor: PRIMARY_BLUE }}
        className="w-full text-slate-900 font-semibold py-4 rounded-xl tracking-wide text-xs active:scale-95 transition-all shadow-none disabled:opacity-50"
       >
        {authLoading ? 'Processing...' : authMode === 'register' ? 'Create Account' : 'Sign In'}
       </button>
      </form>
      <div className="flex justify-between px-1 mb-6">
       <button onClick={handleForgotPassword} className={`text-xs font-normal ${t.textMuted} tracking-normal transition-colors`}>Forgot Password?</button>
       <button
        onClick={() => {
         setAuthMode(authMode === 'login' ? 'register' : 'login');
         setAuthConfirmPassword('');
         setTosChecked(false);
         setTosLinkOpened(false);
         setPrivacyChecked(false);
         setPrivacyLinkOpened(false);
         setVerificationEmailSent(false);
        }}
        className={`text-xs font-normal ${t.textMuted} tracking-normal transition-colors`}
       >
        {authMode === 'login' ? 'Create Account' : 'Back to Login'}
      </button>
     </div>
      <button
       onClick={handleGoogleAuth}
       className="w-full bg-white border border-slate-100 font-medium py-4 rounded-xl flex items-center justify-center gap-3 text-sm hover:border-slate-300 transition-all active:scale-95 shadow-none"
      >
       <Globe size={18} style={{ color: PRIMARY_BLUE }} /> {authMode === 'register' ? 'Continue with Google' : 'Continue with Google'}
      </button>
      <p className="text-[11px] text-slate-400 text-center mt-4 leading-relaxed">
       By signing in you agree to our <a href={TERMS_PAGE_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Terms of Service</a> and <a href={PRIVACY_PAGE_URL} target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Privacy Policy</a>.
      </p>
     </div>
    )}
   </div>
  </div>
 );

 // --- Main App ---
 return (
  <div className={`min-h-screen ${t.bg} flex flex-col md:flex-row font-['DM_Sans',sans-serif] ${t.text} relative overflow-x-hidden`}>
   {/* Global Dark Mode Overrides */}
   {darkMode && <style>{`
    .dark-override, html.dark {
     color-scheme: dark;
    }
    html.dark .bg-white { background-color: #1A1F2E !important; }
    html.dark .bg-\\[\\#FBFBFE\\] { background-color: #0B0F1A !important; }
    html.dark .bg-slate-50 { background-color: #1E2538 !important; }
    html.dark .bg-slate-100 { background-color: #252D3F !important; }
    html.dark .border-slate-100 { border-color: #1F2937 !important; }
    html.dark .border-slate-50 { border-color: #1A2035 !important; }
    html.dark .border-slate-200 { border-color: #2D3748 !important; }
    html.dark .text-slate-900 { color: #F1F5F9 !important; }
    html.dark .text-slate-800 { color: #E2E8F0 !important; }
    html.dark .text-slate-700 { color: #CBD5E1 !important; }
    html.dark .text-slate-600 { color: #94A3B8 !important; }
    html.dark .text-slate-500 { color: #94A3B8 !important; }
    html.dark .text-slate-400 { color: #64748B !important; }
    html.dark .text-slate-300 { color: #475569 !important; }
    html.dark .hover\\:bg-slate-50:hover { background-color: #252D3F !important; }
    html.dark .hover\\:bg-slate-100:hover { background-color: #2D3748 !important; }
    html.dark .hover\\:text-slate-900:hover { color: #F1F5F9 !important; }
    html.dark .hover\\:text-slate-700:hover { color: #CBD5E1 !important; }
    html.dark .hover\\:border-slate-300:hover { border-color: #4A5568 !important; }
    html.dark .focus\\:ring-slate-100:focus { --tw-ring-color: #2D3748 !important; }
    html.dark .ring-slate-100 { --tw-ring-color: #2D3748 !important; }
    html.dark .divide-slate-100 > :not([hidden]) ~ :not([hidden]) { border-color: #1F2937 !important; }
    html.dark .bg-blue-50 { background-color: #1E2538 !important; }
    html.dark .bg-emerald-50 { background-color: #1E2538 !important; }
    html.dark .bg-amber-50 { background-color: #2D2A1E !important; }
    html.dark .bg-rose-50 { background-color: #2D1E1E !important; }
    html.dark .bg-cyan-50\\/30 { background-color: rgba(6,182,212,0.08) !important; }
    html.dark select option { background-color: #1A1F2E; color: #F1F5F9; }
    html.dark input::placeholder { color: #475569 !important; }
    html.dark textarea::placeholder { color: #475569 !important; }
    html.dark .shadow-sm { box-shadow: none !important; }
    html.dark .shadow { box-shadow: none !important; }
    html.dark ::-webkit-scrollbar-track { background: #111827; }
    html.dark ::-webkit-scrollbar-thumb { background: #2D3748; }
    html.dark .recharts-cartesian-grid line { stroke: #1F2937 !important; }
    html.dark .recharts-text { fill: #64748B !important; }
   `}</style>}
   <PricingModal />

   {/* Payment Status Banners */}
   {userProfile.subscriptionStatus === 'past_due' && (
    <div className="fixed top-0 left-0 right-0 z-[150] bg-amber-500 text-amber-950 text-center py-3 px-4 text-xs font-semibold shadow-lg">
     <AlertTriangle size={14} className="inline mr-2 -mt-0.5" />
     Your payment is past due. Please update your payment method to avoid service interruption.
     <button onClick={() => handleManageSubscription('payment')} className="ml-3 underline font-bold hover:text-amber-800 transition-colors">
      Update Payment →
     </button>
    </div>
   )}
   {userProfile.paddleCancellationEffectiveDate && userProfile.subscriptionStatus === 'active' && (
    <div className="fixed top-0 left-0 right-0 z-[150] bg-slate-800 text-white text-center py-3 px-4 text-xs font-semibold shadow-lg">
     Your {currentPlan.name} subscription will cancel on {new Date(userProfile.paddleCancellationEffectiveDate).toLocaleDateString()}.
     <button onClick={() => { setShowPricing(true); }} className="ml-3 underline font-bold" style={{ color: PRIMARY_BLUE }}>
      Reactivate
     </button>
    </div>
   )}
   {userProfile.subscriptionStatus === 'paused' && (
    <div className="fixed top-0 left-0 right-0 z-[150] bg-slate-700 text-white text-center py-3 px-4 text-xs font-semibold shadow-lg">
     Your subscription is paused. Features are limited to the Starter plan.
     <button onClick={() => handleManageSubscription('manage')} className="ml-3 underline font-bold" style={{ color: PRIMARY_BLUE }}>
      Resume Subscription →
     </button>
    </div>
   )}
   <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end pointer-events-none w-full sm:w-auto px-4 sm:px-0">
    {notifications.map(n => <Toast key={n.id} {...n} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} />)}
   </div>

   {/* Sidebar */}
   <nav className={`fixed inset-0 z-40 md:relative md:translate-x-0 transition-transform w-full md:w-72 ${t.bgSidebar} border-r ${t.border} p-8 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
    <div className="flex items-center justify-between mb-12">
     <a href={MARKETING_SITE_URL} className="flex items-center" aria-label="Go to SoarOne home">
      <img src="/brand-wordmark.png" alt="SoarOne" className="w-56 h-auto" />
     </a>
     <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 text-slate-400 hover:bg-slate-50 rounded-xl"><X size={20} /></button>
    </div>
    {/* Workspace Switcher — shown when user belongs to other workspaces */}
    {(myWorkspaces.filter(w => w.status === 'active').length > 0 || isViewingMemberWorkspace) && (
     <div className="mb-4">
      <p className={`text-[9px] font-bold uppercase tracking-widest ${t.textMuted} mb-2 px-1`}>Workspace</p>
      <div className="space-y-0.5">
       {/* Own workspace */}
       <button
        onClick={() => handleSwitchWorkspace(null)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-left ${!isViewingMemberWorkspace ? 'text-slate-900 font-semibold' : `${t.textMuted} ${t.bgHover} font-medium`}`}
        style={!isViewingMemberWorkspace ? { backgroundColor: PRIMARY_BLUE } : {}}
       >
        <div className={`w-5 h-5 rounded-lg flex items-center justify-center text-[9px] font-bold uppercase shrink-0 ${!isViewingMemberWorkspace ? 'bg-slate-900 text-white' : `${t.bgBadge} ${t.textSec} border ${t.border}`}`}>
         {(userProfile.displayName || user?.email || 'M').charAt(0).toUpperCase()}
        </div>
        <span className="text-xs truncate flex-1">My Workspace</span>
       </button>
       {/* Member workspaces */}
       {myWorkspaces.filter(w => w.status === 'active').map(ws => (
        <button
         key={ws.ownerUid}
         onClick={() => handleSwitchWorkspace(ws.ownerUid)}
         className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-left ${activeWorkspaceOwnerId === ws.ownerUid ? 'text-slate-900 font-semibold' : `${t.textMuted} ${t.bgHover} font-medium`}`}
         style={activeWorkspaceOwnerId === ws.ownerUid ? { backgroundColor: PRIMARY_BLUE } : {}}
        >
         <div className={`w-5 h-5 rounded-lg flex items-center justify-center text-[9px] font-bold uppercase shrink-0 ${activeWorkspaceOwnerId === ws.ownerUid ? 'bg-slate-900 text-white' : `${t.bgBadge} ${t.textSec} border ${t.border}`}`}>
          {(ws.ownerName || ws.ownerEmail || 'W').charAt(0).toUpperCase()}
         </div>
         <span className="text-xs truncate flex-1">{ws.ownerName || ws.ownerEmail}</span>
         <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${activeWorkspaceOwnerId === ws.ownerUid ? 'bg-slate-900 text-white' : `${t.bgBadge} ${t.textMuted} border ${t.border}`}`}>{ws.role}</span>
        </button>
       ))}
      </div>
      <div className={`mt-3 mb-1 border-t ${t.border}`} />
     </div>
    )}
    <div className="space-y-0.5 flex-1">
     {[
     { id: 'connections', label: 'Main', icon: Database, locked: false },
     { id: 'analytics', label: 'Analytics', icon: BarChart3, locked: false },
      { id: 'logs', label: 'History', icon: History, locked: false },
      { id: 'invoices', label: 'Invoices', icon: FileText, locked: false },
      { id: 'support', label: 'Support', icon: MessageSquare, locked: false },
      { id: 'settings', label: 'Templates', icon: Settings, locked: false },
      { id: 'compliance', label: 'Compliance', icon: Shield, locked: !canUseCompliance },
      { id: 'account', label: 'Account', icon: UserCog, locked: false },
     ].map((tab) => (
      <button key={tab.id} onClick={() => {
      if (tab.locked) { requireUpgrade(tab.label); return; }
       setActiveTab(tab.id);
       if (tab.id === 'support') setSupportPanelMode('chat');
       setIsSidebarOpen(false);
      }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${tab.locked ? `${t.textMuted} cursor-not-allowed` : activeTab === tab.id ? 'text-slate-900 font-semibold' : `${t.textMuted} ${t.bgHover} font-medium`}`} style={!tab.locked && activeTab === tab.id ? { backgroundColor: PRIMARY_BLUE } : {}}>
       <tab.icon size={16} />
       <span className="text-sm tracking-normal flex-1 text-left">{tab.label}</span>
       {tab.locked && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">Pro</span>}
       {!tab.locked && showWhatsNew && CHANGELOG[0].tabs.includes(tab.id) && <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0" />}
      </button>
     ))}
    </div>
    {/* Dark Mode Toggle */}
    <button
     onClick={toggleDarkMode}
     className={`mb-4 w-full flex items-center justify-between px-4 py-2.5 rounded-xl ${t.bgBadge} border ${t.border} transition-all ${t.bgHover}`}
    >
     <span className={`text-[10px] font-semibold uppercase tracking-wider ${t.textSec}`}>
      {darkMode ? 'Dark Mode' : 'Light Mode'}
     </span>
     {darkMode ? <Moon size={14} className="text-cyan-400" /> : <Sun size={14} className="text-amber-500" />}
    </button>

    {/* Setup progress widget — shown until all onboarding steps are done */}
    {showOnboardingChecklist && (
     <button
      onClick={() => { setActiveTab('connections'); setIsSidebarOpen(false); }}
      className={`mb-3 w-full px-4 py-3 ${t.bgBadge} rounded-xl border ${t.border} text-left transition-all ${t.bgHover} group`}
     >
      <div className="flex items-center justify-between mb-2">
       <span className={`text-[10px] font-semibold uppercase tracking-wider ${t.textSec} flex items-center gap-1.5`}>
        <Sparkles size={10} style={{ color: PRIMARY_BLUE }} />
        Setup
       </span>
       <span className="text-[10px] font-bold" style={{ color: PRIMARY_BLUE }}>{completedOnboardingSteps}/{onboardingSteps.length}</span>
      </div>
      <div className={`h-1.5 rounded-full bg-slate-200 overflow-hidden`}>
       <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${onboardingProgressPercent}%`, backgroundColor: PRIMARY_BLUE }}
       />
      </div>
      <p className={`text-[10px] ${t.textMuted} mt-2 group-hover:${t.textSec} transition-colors`}>
       {onboardingSteps.length - completedOnboardingSteps} step{onboardingSteps.length - completedOnboardingSteps !== 1 ? 's' : ''} remaining
      </p>
     </button>
    )}

    {/* Plan badge */}
    <div className={`mb-4 px-4 py-3 ${t.bgBadge} rounded-xl border ${t.border}`}>
     <div className="flex items-center justify-between">
      <div>
       <span className={`text-[10px] font-bold uppercase tracking-wider ${t.textSec}`}>{effectivePlan.name} Plan</span>
       {isTrialActive && <span className="ml-2 text-[10px] font-bold text-amber-600">{trialDaysLeft}d trial</span>}
      </div>
      <span className={`text-[10px] font-semibold ${t.textMuted}`}>${effectivePlan.price}/mo</span>
     </div>
     <div className={`flex gap-3 mt-1.5 text-[10px] ${t.textMuted}`}>
      <span>{connections.length}/{effectivePlan.maxConnections} connections</span>
      <span>{dunningTiers.length}/{effectivePlan.maxTemplates === 99 ? '∞' : effectivePlan.maxTemplates} templates</span>
     </div>
     <button onClick={() => setShowPricing(true)} style={{backgroundColor: PRIMARY_BLUE}} className="mt-3 w-full py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide text-slate-900 hover:shadow-md transition-all">
       {currentPlan.id === 'starter' ? 'Upgrade Plan' : 'View Plans'}
      </button>
    </div>
    <div className={`mt-auto pt-8 border-t ${t.border}`}>
     <div className="flex gap-3 mb-4">
      <a href={PRIVACY_PAGE_URL} target="_blank" rel="noreferrer" className={`text-[10px] ${t.textMuted} hover:${t.textSec} transition-colors`}>Privacy</a>
      <a href={TERMS_PAGE_URL} target="_blank" rel="noreferrer" className={`text-[10px] ${t.textMuted} hover:${t.textSec} transition-colors`}>Terms</a>
      <a href={STATUS_PAGE_URL} target="_blank" rel="noreferrer" className={`text-[10px] ${t.textMuted} hover:${t.textSec} transition-colors`}>Status</a>
      <button onClick={() => { setActiveTab('support'); setSupportPanelMode('help'); }} className={`text-[10px] ${t.textMuted} hover:${t.textSec} transition-colors`}>Help</button>
      <button onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }} className={`text-[10px] ${t.textMuted} hover:${t.textSec} transition-colors`}>Support</button>
      <button onClick={() => setShowWhatsNew(true)} className={`text-[10px] font-semibold transition-colors flex items-center gap-1`} style={{ color: PRIMARY_BLUE }}>
       <Sparkles size={9} /> What's New
      </button>
     </div>
     <div className="flex items-center justify-between">
     <div className="flex items-center gap-3 truncate max-w-[180px]">
      <div className={`w-10 h-10 rounded-xl ${t.bgBadge} ${t.text} flex items-center justify-center font-semibold text-xs uppercase border ${t.border} overflow-hidden shadow-none`}>
       {profilePicUrl ? <img src={profilePicUrl} className="w-full h-full object-cover" alt="avatar" /> : (user?.email?.charAt(0)?.toUpperCase() || '?')}
      </div>
      <span className="text-xs font-medium text-slate-400 truncate">{userProfile.displayName || user?.email || 'User'}</span>
     </div>
     <button onClick={handleSignOut} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><LogOut size={18} /></button>
     </div>
    </div>
   </nav>

   {/* Main Content */}
   <main className="flex-1 min-w-0 p-4 sm:p-8 md:p-12 overflow-y-auto overflow-x-hidden custom-scrollbar">
    {/* Workspace banner — shown when viewing a member workspace */}
    {isViewingMemberWorkspace && activeWorkspace && (() => {
      const roleCfg = memberWorkspaceRole === 'admin'
        ? { badge: 'bg-violet-100 border-violet-200 text-violet-700', dot: 'bg-violet-400', msg: 'Admin access — you can sync, snooze, pause, and resolve disputes on behalf of this workspace.' }
        : memberWorkspaceRole === 'viewer'
        ? { badge: 'bg-slate-100 border-slate-200 text-slate-600', dot: 'bg-slate-400', msg: 'Viewer access — read-only view of all AR data, history and reports.' }
        : { badge: 'bg-sky-100 border-sky-200 text-sky-700', dot: 'bg-sky-400', msg: 'Member access — view all AR data, history and reports.' };
      return (
       <div className="mb-6 flex items-center justify-between gap-4 px-5 py-4 rounded-2xl border border-cyan-200 bg-cyan-50">
        <div className="flex items-center gap-3 min-w-0">
         <div className="w-9 h-9 rounded-xl bg-cyan-100 border border-cyan-200 flex items-center justify-center text-sm font-bold uppercase text-cyan-700 shrink-0">
          {(activeWorkspace.ownerName || activeWorkspace.ownerEmail || 'W').charAt(0).toUpperCase()}
         </div>
         <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
           <p className="text-xs font-semibold text-cyan-900 truncate">
            <span className="font-bold">{activeWorkspace.ownerName || activeWorkspace.ownerEmail}</span>'s workspace
           </p>
           <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${roleCfg.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${roleCfg.dot}`} />
            {memberWorkspaceRole}
           </span>
          </div>
          <p className="text-[10px] text-cyan-600 mt-0.5 leading-relaxed">{roleCfg.msg}</p>
         </div>
        </div>
        <button
         onClick={() => handleSwitchWorkspace(null)}
         className="shrink-0 px-3 py-1.5 rounded-lg bg-white border border-cyan-200 text-[10px] font-semibold text-cyan-700 hover:bg-cyan-100 transition-all"
        >
         Exit
        </button>
       </div>
      );
     })()}
    <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
     <div className="flex items-center gap-4">
      <button onClick={() => setIsSidebarOpen(true)} className={`md:hidden p-3 ${t.bgCard} border ${t.border} rounded-2xl ${t.textMuted} shadow-none`}><Menu size={20} /></button>
      <div className="animate-in slide-in-from-left duration-500">
       <div style={{ color: PRIMARY_BLUE }} className="flex items-center gap-2 mb-1">
        <Sparkles size={12} />
        <span className="text-xs font-medium uppercase tracking-wider">
         Hello, {userProfile.displayName?.split(' ')[0] || 'Explorer'}
        </span>
       </div>
       <h2 className={`text-3xl font-bold ${t.text} tracking-tight capitalize`} style={{fontFamily:"var(--font-display)"}}>{activeTab === 'connections' ? 'Main' : activeTab === 'analytics' ? 'Analytics' : activeTab === 'compliance' ? 'Compliance' : activeTab === 'logs' ? 'History' : activeTab === 'invoices' ? 'Invoices' : activeTab === 'support' ? 'Support' : activeTab}</h2>
       {(activeTab === 'connections' || activeTab === 'logs' || activeTab === 'invoices') && (
        <div className="relative inline-block mt-2 group">
         <select value={selectedRealmId} onChange={(e) => setSelectedRealmId(e.target.value)} className={`appearance-none ${t.bgCard} border ${t.border} rounded-xl px-4 py-2 pr-10 text-xs font-medium uppercase tracking-wide ${t.textSec} outline-none focus:border-cyan-600 transition-all cursor-pointer shadow-none`}>
          <option value="all">Consolidated Portfolio</option>
          {visibleConnections.map(c => <option key={c.realmId} value={c.realmId}>{c.companyName || c.realmId}</option>)}
         </select>
         <ChevronDown size={14} className={`absolute right-3 top-1/2 -translate-y-1/2 ${t.textMuted} pointer-events-none`} />
        </div>
       )}
      </div>
     </div>
     <div className="flex flex-wrap items-center gap-3">
      {activeTab === 'support' && (
       <div className={`inline-flex items-center gap-1 rounded-2xl border ${t.border} ${t.bgInput} p-1`}>
        <button
         onClick={() => { setActiveTab('support'); setSupportPanelMode('help'); }}
         className={`px-4 py-3 rounded-xl text-[11px] font-semibold uppercase tracking-wide transition-all ${supportPanelMode === 'help' ? 'text-slate-900 shadow-sm' : t.textLink}`}
         style={supportPanelMode === 'help' ? { backgroundColor: PRIMARY_BLUE } : {}}
        >
         Help
        </button>
        <button
         onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }}
         className={`px-4 py-3 rounded-xl text-[11px] font-semibold uppercase tracking-wide inline-flex items-center gap-2 transition-all ${supportPanelMode === 'chat' ? 'text-slate-900 shadow-sm' : t.textLink}`}
         style={supportPanelMode === 'chat' ? { backgroundColor: PRIMARY_BLUE } : {}}
        >
         <LifeBuoy size={14} />
         Support
        </button>
       </div>
      )}
     {activeTab === 'connections' && (
      <div className="flex items-center gap-3">
       {!canAddConnection && <span className="text-[10px] font-semibold text-amber-600">Limit reached ({effectivePlan.maxConnections}/{effectivePlan.maxConnections})</span>}
       <button onClick={handleReconnect} style={{ backgroundColor: canAddConnection ? PRIMARY_BLUE : '#e2e8f0' }} className={`${canAddConnection ? 'hover:bg-slate-900 hover:text-white text-slate-900' : 'text-slate-400 cursor-not-allowed'} px-8 py-4 rounded-xl font-semibold text-xs tracking-wide shadow-none transition-all flex items-center gap-3`}>
        <img src="https://www.vectorlogo.zone/logos/intuit_quickbooks/intuit_quickbooks-icon.svg" className={`w-5 h-5 ${canAddConnection ? 'brightness-0' : 'opacity-30'}`} alt="QB" /> Integrate QuickBooks
       </button>
      </div>
     )}
     </div>
	    </header>

     {maintenanceBanner && (
      <div className={`${panelClass} p-5 sm:p-6 mb-8 overflow-hidden`}>
       <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
         <div className="flex flex-wrap items-center gap-3">
          <span className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wide border ${maintenanceBanner.badge.className}`}>
           {maintenanceBanner.badge.label}
          </span>
          {maintenanceBanner.retryAfterSeconds ? (
           <span className={`text-[11px] ${t.textMuted}`}>Retry-After: {maintenanceBanner.retryAfterSeconds}s</span>
          ) : null}
         </div>
         <h3 style={{fontFamily:"var(--font-display)"}} className={`mt-4 text-xl font-bold ${t.text} tracking-tight`}>
          {maintenanceBanner.title}
         </h3>
         <p className={`mt-2 text-sm ${t.textSec} leading-relaxed`}>
          {maintenanceBanner.message}
         </p>
         {maintenanceBanner.affected.length > 0 && (
          <p className={`mt-3 text-xs ${t.textMuted}`}>
           Affected areas: {maintenanceBanner.affected.join(', ')}
          </p>
         )}
         {maintenanceBanner.timeBits.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
           {maintenanceBanner.timeBits.map((bit) => (
            <span key={bit} className={`px-3 py-1.5 rounded-xl text-[11px] border ${t.borderInput} ${t.bgInput} ${t.textSec}`}>
             {bit}
            </span>
           ))}
          </div>
         )}
        </div>
        <div className="shrink-0">
         <button
          onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }}
          style={{ backgroundColor: PRIMARY_BLUE }}
          className="px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900"
         >
          Open Support
         </button>
        </div>
       </div>
      </div>
     )}

        {/* Onboarding checklist — shown until all steps complete */}
        {showOnboardingChecklist && (
         <div className={`${panelClass} p-5 sm:p-8 rounded-2xl border mb-8 relative overflow-hidden`}>
          <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: PRIMARY_BLUE }} />
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
           <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-50 border border-cyan-100">
             <Sparkles size={12} style={{ color: PRIMARY_BLUE }} />
             <p className="text-[10px] font-semibold uppercase tracking-[0.16em]" style={{ color: PRIMARY_BLUE }}>Getting Started</p>
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-xl sm:text-2xl font-bold text-slate-900 mt-4">Finish your setup checklist</h3>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">Complete the basics below to make sure reminders send with the right company details, compliance settings, and QuickBooks connection.</p>
           </div>
           <div className="shrink-0 min-w-[180px] px-4 py-4 rounded-2xl bg-slate-50 border border-slate-100">
            <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-400">
             <span>Progress</span>
             <span>{onboardingProgressPercent}%</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-200 overflow-hidden">
             <div className="h-full rounded-full transition-all duration-500" style={{ width: `${onboardingProgressPercent}%`, backgroundColor: PRIMARY_BLUE }} />
            </div>
            <p className="text-lg font-bold text-slate-900 mt-3">{completedOnboardingSteps}/{onboardingSteps.length} complete</p>
           </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
           {onboardingSteps.map((step) => {
            const StepIcon = step.id === 'profile' ? UserCog
             : step.id === 'quickbooks' ? Building
             : step.id === 'mailing' ? MapPin
             : step.id === 'sync' ? RefreshCw
             : FileText;
            return (
             <div key={step.id} className={`rounded-2xl border p-5 transition-all ${step.complete ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100 hover:border-slate-200'}`}>
              <div className="flex items-start gap-4">
               <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${step.complete ? 'bg-emerald-100 text-emerald-700' : 'bg-white border border-slate-200 text-slate-500'}`}>
                {step.complete ? <CheckCircle size={20} /> : <StepIcon size={20} />}
               </div>
               <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                 <div>
                  <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{step.description}</p>
                 </div>
                 <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full self-start shrink-0 ${step.complete ? 'bg-emerald-500/15 text-emerald-700' : 'bg-amber-500/15 text-amber-700'}`}>
                  {step.complete ? 'Done' : 'Pending'}
                 </span>
                </div>
                {!step.complete && (
                 <div className="mt-4">
                  <button
                   onClick={() => handleOnboardingAction(step.actionKey)}
                   className="px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide transition-all text-slate-900"
                   style={{ backgroundColor: PRIMARY_BLUE }}
                  >
                   {step.actionLabel}
                  </button>
                 </div>
                )}
               </div>
              </div>
             </div>
            );
           })}
          </div>
         </div>
        )}

        {/* Setup complete celebration — shown once all steps are done, until dismissed */}
        {showOnboardingComplete && (
         <div className="mb-8 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50 p-6 sm:p-10 relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-100 opacity-40 pointer-events-none" />
          <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-cyan-100 opacity-40 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-8">
           {/* Icon */}
           <div className="shrink-0 w-20 h-20 rounded-3xl bg-emerald-100 border border-emerald-200 flex items-center justify-center">
            <CheckCircle size={40} className="text-emerald-600" strokeWidth={1.5} />
           </div>
           {/* Text */}
           <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 mb-3">
             <Sparkles size={11} className="text-emerald-600" />
             <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Setup Complete</span>
            </div>
            <h3 style={{ fontFamily: 'var(--font-display)' }} className="text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
             You're all set!
            </h3>
            <p className="text-sm text-slate-500 mt-2 max-w-lg leading-relaxed">
             SoarOne is configured and ready. Automated reminders will run on your next dunning cycle — sit back and let it work.
            </p>
            {/* Stats row */}
            <div className="flex flex-wrap gap-3 mt-5">
             <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-emerald-100 shadow-sm">
              <Building size={14} className="text-emerald-600 shrink-0" />
              <span className="text-xs font-semibold text-slate-700">{connections.length} connection{connections.length !== 1 ? 's' : ''}</span>
             </div>
             <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-emerald-100 shadow-sm">
              <FileText size={14} className="text-emerald-600 shrink-0" />
              <span className="text-xs font-semibold text-slate-700">{dunningTiers.length} reminder template{dunningTiers.length !== 1 ? 's' : ''}</span>
             </div>
             <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-emerald-100 shadow-sm">
              <CheckCircle size={14} className="text-emerald-600 shrink-0" />
              <span className="text-xs font-semibold text-slate-700">First sync done</span>
             </div>
            </div>
           </div>
           {/* Actions */}
           <div className="shrink-0 flex flex-col items-start sm:items-end gap-3">
            <button
             onClick={() => { dismissOnboarding(); setActiveTab('connections'); }}
             className="px-6 py-3 rounded-xl text-sm font-semibold text-slate-900 transition-all hover:opacity-90 active:scale-95"
             style={{ backgroundColor: PRIMARY_BLUE }}
            >
             View Dashboard →
            </button>
            <button
             onClick={dismissOnboarding}
             className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
            >
             Dismiss this message
            </button>
           </div>
          </div>
         </div>
        )}

	    {/* Stats Cards */}
	    {activeTab !== 'account' && activeTab !== 'compliance' && activeTab !== 'settings' && activeTab !== 'logs' && activeTab !== 'invoices' && activeTab !== 'support' && (
     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      <div className={`${t.bgCard} p-10 rounded-2xl border border-slate-100 hover:border-slate-300 transition-colors shadow-none`}>
       <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Total AR Portfolio</p>
       <div className="flex justify-between items-center">
        <span className="text-2xl font-bold tracking-tight text-slate-900">${totalBalance.toLocaleString()}</span>
        <div className="p-3 bg-slate-50 text-slate-400 rounded-xl"><DollarSign size={24} /></div>
       </div>
      </div>
      <div className={`${t.bgCard} p-10 rounded-2xl border border-slate-100 hover:border-slate-300 transition-colors shadow-none`}>
       <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Overdue AR</p>
       <p className="text-[11px] text-slate-400 mb-3">Portion of the portfolio currently past due</p>
       <div className="flex justify-between items-center">
        <div>
         <span className="text-2xl font-bold tracking-tight text-slate-900">${totalOverdueBalance.toLocaleString()}</span>
         <p className="text-[11px] text-slate-400 mt-1">{overdueShare}% of total portfolio</p>
        </div>
        <div className="p-3 bg-slate-50 text-slate-900 rounded-xl"><Clock3 size={24} /></div>
       </div>
      </div>
      <div className={`${t.bgCard} p-10 rounded-2xl border border-slate-100 hover:border-emerald-100 transition-colors shadow-none`}>
       <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">Payable Nodes</p>
       <div className="flex justify-between items-center">
        <span className="text-2xl font-bold tracking-tight text-slate-900">{filteredInvoices.length} Items</span>
        <div className="p-3 bg-emerald-50 text-emerald-500 rounded-xl"><Activity size={24} /></div>
       </div>
      </div>
     </div>
    )}

    <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
     {/* CONNECTIONS TAB */}
     {activeTab === 'connections' && (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
       {visibleConnections.filter(c => selectedRealmId === 'all' || c.realmId === selectedRealmId).length === 0 && (
         <div className="col-span-full text-center py-20">
          <Database size={48} className="mx-auto mb-4 text-slate-200" />
          <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">No connections found. Link your QuickBooks account to get started.</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => { setActiveTab('support'); setSupportPanelMode('help'); }} className={`px-4 py-2 rounded-xl text-[11px] font-semibold uppercase tracking-wide border ${t.border} ${t.textLink}`}>Open Help Articles</button>
           <button onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }} style={{ backgroundColor: PRIMARY_BLUE }} className="px-4 py-2 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900">Ask Support</button>
          </div>
         </div>
        )}
       {visibleConnections.filter(c => selectedRealmId === 'all' || c.realmId === selectedRealmId).map((conn) => (
        <div key={conn.id} data-testid="connection-card" className={`bg-white p-10 rounded-2xl border transition-all flex flex-col min-h-[220px] shadow-none ${conn.status !== 'active' ? 'border-rose-200 bg-rose-50/10' : 'border-slate-100 hover:border-slate-300'}`}>
         <div className="flex justify-between items-start mb-10 relative z-10">
          <div className="flex items-start gap-4">
           <div className="w-14 h-14 rounded-2xl flex items-center justify-center border bg-slate-50 text-slate-700 border-slate-100"><Building size={28} /></div>
           <div>
            <h4 style={{fontFamily:"var(--font-display)"}} className="font-bold text-slate-900 text-xl tracking-tight leading-none mb-2">{conn.companyName || 'QuickBooks Realm'}</h4>
            <p className="text-xs font-semibold text-slate-400 uppercase">ID: {conn.realmId}</p>
           </div>
          </div>
          <StatusBadge status={conn.status} error={conn.syncError} />
         </div>

         {conn.status === 'disconnected' && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 shadow-none">
           <AlertCircle size={16} className="text-rose-600" />
           <p className="text-xs font-bold text-rose-700 uppercase tracking-tight">Identity Revoked: Re-authorization Required</p>
          </div>
         )}

         {conn.syncError && conn.status === 'active' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-3 shadow-none">
           <AlertTriangle size={16} className="text-amber-600" />
           <p className="text-xs font-bold text-amber-700 uppercase tracking-tight">Sync Alert: {conn.lastHandshakeError || 'Handshake was interrupted'}</p>
          </div>
         )}

         {conn.tokenStatus === 'refresh_failed' && conn.status === 'active' && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl shadow-none">
           <div className="flex items-center gap-3 mb-2">
            <AlertCircle size={16} className="text-rose-600 flex-shrink-0" />
            <p className="text-xs font-bold text-rose-700 uppercase tracking-tight">QuickBooks connection needs attention</p>
           </div>
           <p className="text-xs text-rose-600 ml-7">Token refresh failed{conn.tokenFailedAt ? ` on ${new Date(conn.tokenFailedAt).toLocaleDateString()}` : ''}. Automated reminders are paused until you reconnect.</p>
           <button onClick={handleReconnect} className="mt-3 ml-7 text-xs font-bold text-rose-700 underline underline-offset-2">Reconnect QuickBooks →</button>
          </div>
         )}

         {conn.lastCycleStatus === 'failed' && conn.status === 'active' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-3 shadow-none">
           <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
           <div>
            <p className="text-xs font-bold text-amber-700 uppercase tracking-tight">Last reminder cycle failed</p>
            <p className="text-xs text-amber-600 mt-1">{conn.lastCycleError || 'An error occurred during the last cycle.'}{conn.lastCycleAt ? ` (${new Date(conn.lastCycleAt).toLocaleDateString()})` : ''} You can trigger a manual sync to retry.</p>
           </div>
          </div>
         )}

         <div className="mt-auto pt-8 border-t border-slate-50 flex justify-between items-center relative z-10">
          <div className="flex flex-col gap-1">
           <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">Update: {conn.lastUpdated ? new Date(conn.lastUpdated).toLocaleTimeString() : 'N/A'}</span>
           {conn.lastCycleAt && (
            <span className={`text-xs font-semibold uppercase tracking-wide ${conn.lastCycleStatus === 'failed' ? 'text-amber-400' : 'text-slate-300'}`}>
             Last cycle: {new Date(conn.lastCycleAt).toLocaleDateString()} {conn.lastCycleStatus === 'success' ? '✓' : conn.lastCycleStatus === 'failed' ? '✗' : ''}
            </span>
           )}
          </div>
          <div className="flex gap-2">
           {conn.status !== 'active' ? (
            <button onClick={handleReconnect} style={{ backgroundColor: PRIMARY_BLUE }} className="flex items-center gap-2 px-6 py-2.5 text-slate-900 rounded-xl text-xs font-medium uppercase tracking-wide shadow-none animate-pulse">Reconnect</button>
           ) : (
            <>
             <button aria-label="Sync now" onClick={() => handleManualSync(conn.realmId)} style={{ backgroundColor: PRIMARY_BLUE }} disabled={syncingId === conn.realmId || (isViewingMemberWorkspace && memberWorkspaceRole !== 'admin')} className="px-4 py-2 text-slate-900 rounded-xl flex items-center justify-center transition-all shadow-none disabled:opacity-40 disabled:cursor-not-allowed"><RefreshCw size={16} className={syncingId === conn.realmId ? "animate-spin" : ""} /></button>
             {!isViewingMemberWorkspace && <button onClick={() => handleDisconnect(conn.realmId)} className="bg-rose-50 text-rose-600 px-5 py-2 rounded-xl text-xs font-medium uppercase tracking-wide hover:bg-rose-600 hover:text-white transition-all border border-rose-100 shadow-none">Disconnect</button>}
            </>
           )}
          </div>
         </div>
        </div>
       ))}
      </div>
     )}

     {/* ANALYTICS TAB */}
     {activeTab === 'analytics' && (
      <div className="space-y-8 animate-in fade-in duration-500">
       {/* Sub-tab toggle */}
       <div className={`inline-flex rounded-xl border ${t.border} ${t.bgInput} p-1 gap-1`}>
        {(['charts', 'insights', 'reports'] as const).map(st => (
         <button key={st} onClick={() => setAnalyticsSubTab(st)}
          className={`px-5 py-2 rounded-lg text-xs font-semibold transition-all ${analyticsSubTab === st ? 'bg-slate-900 text-white' : `${t.textMuted} hover:${t.text}`}`}>
          {st === 'charts' ? 'Charts' : st === 'insights' ? 'AR Insights' : 'Reports & Export'}
         </button>
        ))}
       </div>
       {analyticsSubTab === 'charts' && (
       <div className="space-y-8 animate-in fade-in duration-500">
       <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className={`lg:col-span-2 ${panelClass} p-10 relative overflow-hidden`}>
         <div className="absolute top-0 right-0 p-10 opacity-[0.02] -rotate-12 pointer-events-none"><Target size={200} /></div>
         <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3"><div className={`p-3 ${t.bgInput} ${t.text} rounded-xl border ${t.borderInput} shadow-none`}><CalendarIcon size={20} /></div><h3 style={{fontFamily:"var(--font-display)"}} className={`font-bold text-sm tracking-tight ${t.text}`}>Collection Calendar</h3></div>
          <div className={`flex items-center gap-2 ${t.bgInput} px-4 py-2 rounded-2xl border ${t.borderInput}`}>
           {/* FIX: Calendar navigation no longer mutates state */}
           <button onClick={handlePrevMonth} className={`${t.textSec} hover:${t.textLink} transition-colors`}><ChevronLeft size={16} /></button>
           <span className={`min-w-[120px] text-center font-semibold text-xs uppercase tracking-wide ${t.text}`}>{viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
           <button onClick={handleNextMonth} className={`${t.textSec} hover:${t.textLink} transition-colors`}><ChevronRight size={16} /></button>
          </div>
         </div>
         <div className={`grid grid-cols-7 gap-px ${t.bgInput} rounded-3xl overflow-hidden border ${t.borderInput} relative z-10 shadow-none`}>
          {/* FIX: Unique keys for day headers (T appears twice) */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, idx) => <div key={idx} className={`${t.bgCard} p-4 text-center text-xs font-semibold ${t.textMuted} uppercase tracking-wide`}>{d.charAt(0)}</div>)}
          {getDaysInMonth(viewDate).map((date: Date | null, i: number) => {
           if (!date) return <div key={`empty-${i}`} className={`${t.bgCard} min-h-[80px]`} />;
           const invs = getInvoicesForDate(date);
           const isSel = selectedDate.toDateString() === date.toDateString();
           return (
            <button key={`day-${i}`} onClick={() => setSelectedDate(date)} className={`${t.bgCard} min-h-[80px] p-2 flex flex-col items-center justify-between border-t ${t.border} transition-all ${isSel ? 'z-10 scale-[0.98]' : t.bgHover}`} style={isSel ? { boxShadow: `inset 0 0 0 2px ${PRIMARY_BLUE}` } : {}}>
             <span className={`text-xs font-semibold ${isSel ? 'text-cyan-500' : t.textSec}`}>{date.getDate()}</span>
             <div className="flex gap-1 mb-1">
              {invs.some(iv => iv.status === 'overdue') && <div className="w-2 h-2 rounded-full bg-rose-400 shadow-none" />}
              {invs.some(iv => iv.status === 'paid') && <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-none" />}
             </div>
            </button>
           );
          })}
         </div>
        </div>
       <div className={`${panelClass} p-10 hover:border-slate-300 transition-colors flex flex-col relative overflow-hidden`}>
        <h3 className={`font-semibold text-xs uppercase tracking-wide mb-8 border-b ${t.border} pb-6 flex items-center gap-2`} style={{ color: PRIMARY_BLUE }}><ArrowRight size={14} /> Agenda: {selectedDate.toLocaleDateString()}</h3>
        <div className="space-y-4 flex-1 overflow-y-auto pr-2">
         {getInvoicesForDate(selectedDate).length === 0 ? (
          <div className="py-20 text-center">
           <p className={`text-xs font-semibold ${t.textMuted} uppercase tracking-wide leading-relaxed`}>No invoices scheduled for this date.</p>
          </div>
         ) : getInvoicesForDate(selectedDate).map(inv => (
          <div key={inv.id} className={`p-6 rounded-2xl border transition-all ${inv.status === 'overdue' ? 'bg-rose-50 border-rose-100' : `${t.bgInput} ${t.borderInput}`}`}>
           <p className={`text-xs font-semibold truncate ${t.text} mb-2`}>{inv.customerName}</p>
           <div className="flex justify-between items-center">
            <span className={`text-[10px] font-semibold uppercase ${t.textMuted}`}>Invoice #{inv.docNumber}</span>
            <span className={`text-xl font-semibold tracking-tight ${inv.status === 'overdue' ? 'text-rose-600' : t.text}`}>${inv.balance.toLocaleString()}</span>
           </div>
          </div>
         ))}
        </div>
       </div>
       </div>
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className={`${panelClass} p-12 hover:border-slate-300 transition-colors`}>
         <h3 className={`font-semibold text-xs uppercase tracking-wide ${t.text} mb-10`}>Aging Portfolio Distribution</h3>
         <div className="w-full min-w-0">
          <ResponsiveContainer width="100%" height={320} minWidth={0}>
           <PieChart>
            <Pie data={overdueDistribution} innerRadius={80} outerRadius={110} paddingAngle={5} dataKey="value" stroke="none">
             {overdueDistribution.map((entry, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: '24px', border: 'none' }} />
            <Legend verticalAlign="bottom" height={36} />
           </PieChart>
          </ResponsiveContainer>
         </div>
        </div>
        <div className={`${panelClass} p-12 hover:border-slate-300 transition-colors`}>
         <h3 className={`font-semibold text-xs uppercase tracking-wide ${t.text} mb-10`}>Recovery Performance</h3>
         <div className="w-full min-w-0">
          <ResponsiveContainer width="100%" height={320} minWidth={0}>
           <BarChart data={dunningPerformance}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 'bold', fill: '#94a3b8' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 'bold', fill: '#94a3b8' }} />
            <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '1.5rem', border: 'none' }} />
            <Bar dataKey="sent" name="Attempts" fill="#cbd5e1" radius={[8, 8, 0, 0]} barSize={32} />
            <Bar dataKey="recovered" name="Payments" fill={PRIMARY_BLUE} radius={[8, 8, 0, 0]} barSize={32} />
           </BarChart>
          </ResponsiveContainer>
         </div>
        </div>
       </div>
       </div>
       )}
      </div>
     )}

     {/* HISTORY TAB */}
     {activeTab === 'logs' && (
      <>
      <div className="space-y-6">
       <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Queued</p>
         <p className="mt-2 text-2xl font-bold text-amber-900">{historyStatusSummary.queued}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-100 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Sent</p>
         <p className="mt-2 text-2xl font-bold text-slate-900">{historyStatusSummary.sent}</p>
        </div>
        <div className="rounded-2xl border border-cyan-100 bg-cyan-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-cyan-700">Delivered</p>
         <p className="mt-2 text-2xl font-bold text-cyan-900">{historyStatusSummary.delivered}</p>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Opened</p>
         <p className="mt-2 text-2xl font-bold text-emerald-900">{historyStatusSummary.opened}</p>
        </div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Failed</p>
         <p className="mt-2 text-2xl font-bold text-rose-900">{historyStatusSummary.failed}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50/70 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800">Blocked</p>
         <p className="mt-2 text-2xl font-bold text-amber-900">{historyStatusSummary.blocked}</p>
        </div>
       </div>

       <div className="flex flex-col md:flex-row gap-4 bg-white p-8 rounded-2xl border border-slate-100 shadow-none">
        <div className="relative flex-1">
         <Search size={20} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" />
         <input type="text" placeholder="Search by customer or invoice..." className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-xl font-normal text-sm outline-none focus:ring-2 focus:ring-slate-100" value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
        </div>
        <div className="flex gap-4">
         <div className="relative group">
          <Filter size={18} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
          <select value={historyStatusFilter} onChange={(e) => setHistoryStatusFilter(e.target.value)} className="appearance-none bg-slate-50 border-none rounded-[1.5rem] pl-14 pr-10 py-4 text-xs font-medium uppercase tracking-wide text-slate-500 cursor-pointer">
           <option value="all">Global Status</option>
           <option value="queued">Queued</option>
           <option value="sent">Sent</option>
           <option value="delivered">Delivered</option>
           <option value="opened">Opened</option>
           <option value="failed">Failed</option>
           <option value="blocked">Blocked</option>
           <option value="opted_out">Opted Out</option>
           <option value="disputed">Disputed</option>
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none group-hover:text-slate-900" />
         </div>
         <button onClick={handleExportHistory} style={{ backgroundColor: canExportCsv ? PRIMARY_BLUE : '#e2e8f0' }} className={`flex items-center gap-3 ${canExportCsv ? 'text-slate-900' : 'text-slate-400'} px-10 py-4 rounded-xl font-semibold text-xs shadow-none transition-all`}><Download size={18} /> Export CSV {!canExportCsv && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/50 text-slate-500">Pro</span>}</button>
        </div>
       </div>
       <div className={`${t.bgCard} rounded-2xl border overflow-hidden animate-in fade-in duration-500 overflow-x-auto border-slate-100 shadow-none`}>
        {filteredLogs.length === 0 ? (
          <div className="text-center py-20 shadow-none">
           <Info size={40} className="mx-auto mb-4 text-slate-200" />
           <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">No history found</p>
           <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => { setActiveTab('support'); setSupportPanelMode('help'); }} className="px-4 py-2 rounded-xl text-[11px] font-semibold uppercase tracking-wide border border-slate-200 text-slate-600">Help Articles</button>
            <button onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }} style={{ backgroundColor: PRIMARY_BLUE }} className="px-4 py-2 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900">Ask Support</button>
           </div>
          </div>
         ) : (
         <>
         <table className="w-full text-left min-w-[1100px] shadow-none">
          <thead>
           <tr className="bg-slate-50/50 border-b border-slate-100">
            <th className="px-10 py-8 text-xs font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-900" onClick={() => handleSort('customerName')}>
             <span className="inline-flex items-center gap-2">Customer <span className="text-slate-300">{getSortIndicator('customerName')}</span></span>
            </th>
            <th className="px-10 py-8 text-xs font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-900" onClick={() => handleSort('templateSent')}>
             <span className="inline-flex items-center gap-2">Dunning Stage <span className="text-slate-300">{getSortIndicator('templateSent')}</span></span>
            </th>
            <th className="px-10 py-8 text-xs font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-900" onClick={() => handleSort('deliveryStatus')}>
             <span className="inline-flex items-center gap-2">Status <span className="text-slate-300">{getSortIndicator('deliveryStatus')}</span></span>
            </th>
            <th className="px-10 py-8 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-900" onClick={() => handleSort('sentAt')}>
             <span className="inline-flex items-center gap-2 justify-end">Date <span className="text-slate-300">{getSortIndicator('sentAt')}</span></span>
            </th>
            <th className="px-10 py-8 text-right text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
           </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
           {filteredLogs.slice(0, 100).map(log => (
            <tr key={log.id} className="hover:bg-slate-50 transition-colors group text-xs font-normal">
             <td className="px-10 py-8">
              <div className="space-y-2">
               <p className="font-semibold text-slate-900 text-sm leading-none">{log.customerName}</p>
               <p className="text-xs font-normal text-slate-400 tracking-normal opacity-80">Invoice #{log.docNumber}</p>
              </div>
             </td>
             <td className="px-10 py-8">
              <span className="inline-flex min-w-[168px] justify-center whitespace-nowrap px-5 py-2.5 bg-slate-50 text-slate-900 rounded-lg text-xs font-medium uppercase tracking-wide border border-slate-100">
               {log.templateSent}
              </span>
             </td>
             <td className="px-10 py-8">
              <div className="space-y-2">
               <DeliveryBadge status={log.deliveryStatus} />
               {['failed', 'blocked'].includes((log.deliveryStatus || '').toLowerCase()) && log.errorDetail && (
                <p className={`text-[11px] max-w-[260px] leading-relaxed ${log.deliveryStatus === 'failed' ? 'text-rose-500' : 'text-amber-700'}`}>{log.errorDetail}</p>
               )}
               {(log.deliveryStatus || '').toLowerCase() === 'queued' && (
                <p className="text-[11px] text-slate-500 max-w-[260px] leading-relaxed">Accepted by SoarOne and waiting for SendGrid event confirmation.</p>
               )}
              </div>
             </td>
            <td className="px-10 py-8 text-right">
             <div className="space-y-1">
              <p className="text-slate-400 group-hover:text-slate-900 transition-colors font-semibold">{formatTimestamp(getHistoryDateValue(log))}</p>
              {(log.deliveryStatus || '').toLowerCase() === 'queued' && <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Awaiting provider event</p>}
             </div>
             </td>
             <td className="px-10 py-8 text-right">
              <div className="flex justify-end gap-2">
               <button onClick={() => setSelectedHistoryLog(log)} className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all">
                Details
               </button>
               {['failed', 'blocked'].includes((log.deliveryStatus || '').toLowerCase()) ? (
                 <button onClick={() => handleRetryFailedEmail(log.id)} className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide border border-amber-300 text-amber-700 hover:bg-amber-50 transition-all">
                  Retry
                 </button>
               ) : null}
              </div>
             </td>
            </tr>
           ))}
          </tbody>
         </table>
         {filteredLogs.length > 100 && (
          <p className={`text-xs ${t.textMuted} text-center py-3 border-t ${t.border}`}>Showing 100 of {filteredLogs.length} — use filters to narrow results</p>
         )}
         </>
        )}
       </div>
      </div>

      {selectedHistoryLog && (
       <div className="fixed inset-0 z-[1100] bg-slate-900/35 backdrop-blur-sm flex justify-end p-4 sm:p-6" onClick={() => setSelectedHistoryLog(null)}>
        <div className={`${t.bgCard} h-full w-full max-w-2xl rounded-3xl border ${t.border} shadow-2xl overflow-y-auto`} onClick={(event) => event.stopPropagation()}>
         <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 sm:px-8 py-6 border-b bg-white/90 backdrop-blur-sm border-slate-100">
          <div>
           <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Reminder Evidence</p>
           <h3 style={{fontFamily:"var(--font-display)"}} className="mt-2 text-2xl font-bold text-slate-900 tracking-tight">{selectedHistoryLog.customerName}</h3>
           <p className="mt-1 text-sm text-slate-500">Invoice #{selectedHistoryLog.docNumber} · {selectedHistoryLog.templateSent}</p>
          </div>
          <button onClick={() => setSelectedHistoryLog(null)} className="p-3 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all">
           <X size={18} />
          </button>
         </div>

         <div className="px-6 sm:px-8 py-6 space-y-6">
          <div className="flex flex-wrap items-center gap-3">
           <DeliveryBadge status={selectedHistoryLog.deliveryStatus} />
           {(selectedHistoryLog.deliveryStatus || '').toLowerCase() === 'blocked' && (
            <span className="px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
             {getBlockedReasonLabel(selectedHistoryLog.blockedReasonCode)}
            </span>
           )}
          </div>

          <div className="rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
           <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Status Meaning</p>
           <p className="mt-2 text-sm leading-relaxed text-slate-700">{getHistoryStatusNarrative(selectedHistoryLog)}</p>
           {(selectedHistoryLog.deliveryStatus || '').toLowerCase() === 'blocked' && (
            <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
             <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">What To Do Next</p>
             <p className="mt-1 text-sm leading-relaxed text-amber-900">{getBlockedReasonNextStep(selectedHistoryLog.blockedReasonCode)}</p>
            </div>
           )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Customer</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{selectedHistoryLog.customerName}</p>
            <p className="mt-1 text-xs text-slate-500">{selectedHistoryLog.recipient || 'No email stored'}</p>
           </div>
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Send Context</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{selectedHistoryLog.templateSent}</p>
            <p className="mt-1 text-xs text-slate-500">Sender: {selectedHistoryLog.senderUsed || 'System sender'}</p>
           </div>
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Amount Due</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{formatCurrency(selectedHistoryLog.invoiceAmount)}</p>
            <p className="mt-1 text-xs text-slate-500">Recorded on {formatTimestamp(selectedHistoryLog.createdAt || getHistoryDateValue(selectedHistoryLog))}</p>
           </div>
          </div>

          <div className={`rounded-2xl border ${t.borderInput} bg-slate-50 p-5`}>
           <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-4">Timeline</p>
           <div className="space-y-3">
            {[
             { label: 'Created', value: selectedHistoryLog.createdAt },
             { label: 'Queued', value: selectedHistoryLog.queuedAt },
             { label: 'Sent', value: selectedHistoryLog.sentAt },
             { label: 'Delivered', value: selectedHistoryLog.deliveredAt },
             { label: 'Opened', value: selectedHistoryLog.openedAt },
             { label: 'Blocked', value: selectedHistoryLog.blockedAt },
             { label: 'Failed', value: selectedHistoryLog.failedAt }
            ].filter(item => getTimestampMs(item.value)).map(item => (
             <div key={item.label} className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-700">{item.label}</p>
              <p className="text-sm text-slate-500 text-right">{formatTimestamp(item.value)}</p>
             </div>
            ))}
            {![
             selectedHistoryLog.createdAt,
             selectedHistoryLog.queuedAt,
             selectedHistoryLog.sentAt,
             selectedHistoryLog.deliveredAt,
             selectedHistoryLog.openedAt,
             selectedHistoryLog.blockedAt,
             selectedHistoryLog.failedAt
            ].some(getTimestampMs) && <p className="text-sm text-slate-500">No timeline events recorded yet.</p>}
           </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Safeguards</p>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
             <div className="flex items-center justify-between gap-4"><span>First contact notice</span><span className="font-semibold">{selectedHistoryLog.isFirstContact ? 'Yes' : 'No'}</span></div>
             <div className="flex items-center justify-between gap-4"><span>Payment link included</span><span className="font-semibold">{selectedHistoryLog.includedPaymentLink ? 'Yes' : 'No'}</span></div>
             <div className="flex items-center justify-between gap-4"><span>Invoice attachment included</span><span className="font-semibold">{selectedHistoryLog.includedInvoiceAttachment ? 'Yes' : 'No'}</span></div>
            </div>
           </div>
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Provider Evidence</p>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
             <div className="flex items-center justify-between gap-4"><span>Last provider event</span><span className="font-semibold text-right">{selectedHistoryLog.lastEventType || 'Not yet received'}</span></div>
             <div className="flex items-center justify-between gap-4"><span>Payment host</span><span className="font-semibold text-right">{selectedHistoryLog.paymentLinkHost || 'None'}</span></div>
             <div className="flex items-center justify-between gap-4"><span>PDF mode</span><span className="font-semibold text-right">{getPdfModeLabel(selectedHistoryLog.invoicePdfMode)}</span></div>
            </div>
           </div>
          </div>

          {selectedHistoryLog.safeguardSnapshot ? (
           <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Safeguard Snapshot</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
             {Object.entries(selectedHistoryLog.safeguardSnapshot)
              .filter(([, value]) => Boolean(value))
              .map(([key, value]) => (
               <div key={key} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                <p className="mt-1 text-sm text-slate-700 leading-relaxed">{getSafeguardSnapshotLabel(key, String(value))}</p>
               </div>
             ))}
            </div>
           </div>
          ) : (
           <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Safeguard Snapshot</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">Safeguard snapshot unavailable for older history rows or non-send event rows.</p>
           </div>
          )}

          {(selectedHistoryLog.errorDetail || selectedHistoryLog.blockedReasonCode || selectedHistoryLog.sgMessageId || selectedHistoryLog.attachmentFilename) && (
           <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5 space-y-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Operational Detail</p>
            {selectedHistoryLog.blockedReasonCode && (
             <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Blocked Reason</p>
              <p className="mt-1 text-sm text-slate-700">{getBlockedReasonLabel(selectedHistoryLog.blockedReasonCode)}</p>
             </div>
            )}
            {selectedHistoryLog.errorDetail && (
             <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">System Note</p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{selectedHistoryLog.errorDetail}</p>
             </div>
            )}
            {selectedHistoryLog.sgMessageId && (
             <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Provider Message ID</p>
              <p className="mt-1 text-sm text-slate-700 break-all">{selectedHistoryLog.sgMessageId}</p>
             </div>
            )}
            {selectedHistoryLog.attachmentFilename && (
             <div>
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Attachment</p>
              <p className="mt-1 text-sm text-slate-700">{selectedHistoryLog.attachmentFilename}</p>
             </div>
            )}
           </div>
          )}
         </div>
        </div>
       </div>
      )}
      </>
     )}

     {/* INVOICES TAB */}
     {activeTab === 'invoices' && (
      <div className="space-y-6">
       {/* Summary cards */}
       <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-slate-100 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Total Invoices</p>
         <p className="mt-2 text-2xl font-bold text-slate-900">{invoicesForTab.length}</p>
        </div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Overdue</p>
         <p className="mt-2 text-2xl font-bold text-rose-900">{invoicesForTab.filter(i => i.status === 'overdue' && !i.isSnoozed && !i.isPaused).length}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Snoozed</p>
         <p className="mt-2 text-2xl font-bold text-amber-900">{invoicesForTab.filter(i => i.isSnoozed).length}</p>
        </div>
        <div className="rounded-2xl border border-violet-100 bg-violet-50 px-5 py-4">
         <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">Paused</p>
         <p className="mt-2 text-2xl font-bold text-violet-900">{invoicesForTab.filter(i => i.isPaused).length}</p>
        </div>
       </div>

       {/* Search + filter */}
       <div className="flex flex-col md:flex-row gap-4 bg-white p-8 rounded-2xl border border-slate-100 shadow-none">
        <div className="relative flex-1">
         <Search size={20} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" />
         <input type="text" placeholder="Search by customer, email, or invoice #..." className="w-full pl-14 pr-6 py-4 bg-slate-50 border-none rounded-xl font-normal text-sm outline-none focus:ring-2 focus:ring-slate-100" value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} />
        </div>
        <div className="relative group">
         <Filter size={18} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
         <select value={invoiceStatusFilter} onChange={(e) => setInvoiceStatusFilter(e.target.value as any)} className="appearance-none bg-slate-50 border-none rounded-[1.5rem] pl-14 pr-10 py-4 text-xs font-medium uppercase tracking-wide text-slate-500 cursor-pointer">
          <option value="all">All Invoices</option>
          <option value="overdue">Overdue Only</option>
          <option value="snoozed">Snoozed</option>
          <option value="paused">Customer Paused</option>
          <option value="excluded">Excluded Customers</option>
          <option value="collected">Marked Collected</option>
         </select>
         <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none group-hover:text-slate-900" />
        </div>
       </div>

       {/* Table */}
       <div className={`${t.bgCard} rounded-2xl border overflow-hidden animate-in fade-in duration-500 overflow-x-auto border-slate-100 shadow-none`}>
        {filteredInvoicesForTab.length === 0 ? (
         <div className="text-center py-20">
          <Info size={40} className="mx-auto mb-4 text-slate-200" />
          <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">No invoices found</p>
         </div>
        ) : (
         <table className="w-full text-left min-w-[900px] shadow-none">
          <thead>
           <tr className="bg-slate-50/50 border-b border-slate-100">
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Customer</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Invoice #</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Balance</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Due Date</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Days Past Due</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide">Status</th>
            <th className="px-8 py-6 text-xs font-semibold text-slate-400 uppercase tracking-wide text-right">Actions</th>
           </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
           {filteredInvoicesForTab.map(inv => {
            const snoozeUntilDate = inv.snoozeOverride?.snoozedUntil
             ? (typeof inv.snoozeOverride.snoozedUntil.toDate === 'function'
                ? inv.snoozeOverride.snoozedUntil.toDate()
                : new Date(inv.snoozeOverride.snoozedUntil))
             : null;
            return (
             <tr key={inv.id} className="hover:bg-slate-50/60 transition-colors">
              <td className="px-8 py-5">
               <button
                className="text-sm font-semibold text-slate-800 hover:text-sky-600 text-left transition-colors"
                onClick={() => inv.customerEmail && openCustomerMetaModal(inv.realmId, inv.customerEmail, inv.customerName, inv.customerMeta)}
                title="View customer details"
               >
                {inv.customerName || '—'}
               </button>
               {inv.customerEmail && <p className="text-xs text-slate-400 mt-0.5">{inv.customerMeta?.emailOverride ? <><span className="line-through">{inv.customerEmail}</span> → {inv.customerMeta.emailOverride}</> : inv.customerEmail}</p>}
               {inv.customerMeta?.tags && inv.customerMeta.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                 {inv.customerMeta.tags.slice(0, 3).map((tag, ti) => (
                  <span key={ti} className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sky-50 text-sky-600 border border-sky-100">{tag}</span>
                 ))}
                </div>
               )}
              </td>
              <td className="px-8 py-5 text-sm text-slate-600">{inv.docNumber || inv.invoiceId || '—'}</td>
              <td className="px-8 py-5 text-sm font-semibold text-slate-800">${(inv.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td className="px-8 py-5 text-sm text-slate-600">{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</td>
              <td className="px-8 py-5 text-sm font-semibold">
               {inv.dueDate && inv.status === 'overdue'
                ? (() => { const d = Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86400000); return <span className="text-rose-600">{d} {d === 1 ? 'day' : 'days'}</span>; })()
                : <span className="text-slate-300">—</span>}
              </td>
              <td className="px-8 py-5">
               <div className="flex flex-wrap gap-1.5">
                {inv.isCollected && (
                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-100 text-emerald-700">
                  <CheckCircle2 size={10} /> Collected
                 </span>
                )}
                {inv.isDisabled && !inv.isCollected && (
                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500">
                  Dunning Off
                 </span>
                )}
                {inv.isExcluded && !inv.isCollected && !inv.isDisabled && (
                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-orange-100 text-orange-700">
                  Excluded
                 </span>
                )}
                {!inv.isCollected && !inv.isDisabled && !inv.isExcluded && (
                 <>
                  {inv.status === 'overdue' && !inv.isSnoozed && !inv.isPaused && (
                   <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700">Overdue</span>
                  )}
                  {inv.isSnoozed && snoozeUntilDate && (
                   <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700">
                    <Clock size={10} /> Snoozed until {snoozeUntilDate.toLocaleDateString()}
                   </span>
                  )}
                  {inv.isPaused && (
                   <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700">
                    <PauseCircle size={10} /> Paused
                   </span>
                  )}
                  {!inv.isSnoozed && !inv.isPaused && inv.status !== 'overdue' && (
                   <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500">{inv.status || 'Unknown'}</span>
                  )}
                 </>
                )}
                {ptpByInvoice[inv.invoiceId]?.hasMissedPtp && inv.status !== 'paid' && (
                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-rose-100 text-rose-700">
                  <Clock size={10} /> PTP Missed
                 </span>
                )}
                {ptpByInvoice[inv.invoiceId]?.nextPtp && inv.status !== 'paid' && (
                 <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700">
                  <Clock size={10} /> PTP {new Date(ptpByInvoice[inv.invoiceId].nextPtp!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                 </span>
                )}
               </div>
              </td>
              <td className="px-8 py-5">
               <div className="flex items-center justify-end gap-2">
                {(!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && (inv.isSnoozed ? (
                 <button
                  onClick={() => handleUnsnoozeInvoice(inv.realmId, inv.invoiceId)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors"
                 >
                  Remove Snooze
                 </button>
                ) : (
                 <button
                  onClick={() => setSnoozeModal({ invoiceId: inv.invoiceId, realmId: inv.realmId, customerName: inv.customerName })}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                 >
                  Snooze
                 </button>
                ))}
                {(!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && inv.customerEmail && (
                 inv.isPaused ? (
                  <button
                   onClick={() => handleResumeCustomer(inv.realmId, inv.customerEmail!, inv.customerName)}
                   className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-violet-50 hover:bg-violet-100 text-violet-700 transition-colors"
                  >
                   Resume
                  </button>
                 ) : (
                  <button
                   onClick={() => handlePauseCustomer(inv.realmId, inv.customerEmail!, inv.customerName)}
                   className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                  >
                   Pause
                  </button>
                 )
                )}
                {(!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && (
                 <button
                  onClick={() => openInvoiceCtrlModal(inv.invoiceId, inv.realmId, inv.customerName, inv.docNumber || inv.invoiceId, inv.invoiceCtrl)}
                  className="px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                  title="Invoice controls"
                 >
                  Controls
                 </button>
                )}
                <button
                 onClick={() => setNoteModal({ invoiceId: inv.invoiceId, realmId: inv.realmId, customerName: inv.customerName, docNumber: inv.docNumber || inv.invoiceId })}
                 className={`relative px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-colors ${collectionNotes.some(n => n.invoiceId === inv.invoiceId) ? 'bg-sky-50 hover:bg-sky-100 text-sky-600' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                 title="Collections notes"
                >
                 <MessageSquare size={13} className="inline -mt-0.5" />
                 {collectionNotes.some(n => n.invoiceId === inv.invoiceId) && (
                  <span className="ml-1">{collectionNotes.filter(n => n.invoiceId === inv.invoiceId).length}</span>
                 )}
                </button>
               </div>
              </td>
             </tr>
            );
           })}
          </tbody>
         </table>
        )}
       </div>
      </div>
     )}

      {activeTab === 'support' && supportPanelMode === 'chat' && (
       <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-8">
       <div className={`${panelClass} p-8 flex flex-col gap-6`}>
       <div>
        <div className="flex items-center justify-between gap-3">
          <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Support Threads</h3>
           <p className={`text-xs ${t.textMuted} mt-1`}>We answer using your live account data.</p>
          </div>
          <button onClick={() => { newConversationMode.current = true; setSelectedSupportThreadId(null); setSupportInput(''); }} style={{ backgroundColor: PRIMARY_BLUE }} className="shrink-0 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide text-slate-900">
           + New
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
         <input
          type="text"
          placeholder="Search threads..."
          value={supportSearch}
          onChange={(e) => setSupportSearch(e.target.value)}
          className={`w-full ${t.bgInput} border ${t.borderInput} rounded-xl px-4 py-3 text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
         />
         <select value={supportStatusFilter} onChange={(e) => setSupportStatusFilter(e.target.value)} className={`w-full ${t.bgInput} border ${t.borderInput} rounded-xl px-4 py-3 text-xs ${t.text}`}>
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="escalated">Escalated</option>
          <option value="resolved">Resolved</option>
          <option value="pending">Pending</option>
         </select>
        </div>
       </div>
        <div className="flex-1 space-y-2 max-h-[50vh] xl:max-h-[calc(100vh-340px)] overflow-y-auto pr-1">
         {filteredSupportThreads.length === 0 && (
           <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5 text-sm ${t.textSec}`}>
            <p>No support threads yet. Start a conversation and the first thread will be created automatically.</p>
            <p className="mt-3 text-xs">Use the top actions to browse help articles or start a support conversation. If AI cannot resolve the issue, it will escalate it for human follow-up.</p>
           </div>
          )}
         {filteredSupportThreads.map(thread => {
          const isSelected = selectedSupportThread?.id === thread.id;
          const statusDotColor = thread.status === 'escalated' ? '#f59e0b' : thread.status === 'resolved' ? '#10b981' : thread.status === 'pending' ? '#94a3b8' : '#3b82f6';
          return (
          <button
           key={thread.id}
           onClick={() => { newConversationMode.current = false; setSelectedSupportThreadId(thread.id); }}
           className={`w-full text-left rounded-2xl border p-5 transition-all ${isSelected ? `${t.text} border-transparent` : `${t.borderInput} ${t.bgInput} hover:border-slate-300`}`}
           style={isSelected ? { backgroundColor: PRIMARY_BLUE } : {}}
          >
           <div className="flex items-start gap-3">
            <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: isSelected ? 'rgba(0,0,0,0.35)' : statusDotColor }} />
            <div className="min-w-0 flex-1">
             <p className={`text-sm font-semibold leading-snug truncate ${isSelected ? 'text-slate-900' : t.text}`}>{thread.subject || 'Support request'}</p>
             <p className={`mt-1.5 text-xs leading-relaxed line-clamp-2 ${isSelected ? 'text-slate-900/75' : t.textSec}`}>{thread.lastMessagePreview || 'No messages yet.'}</p>
             <p className={`mt-2 text-[11px] ${isSelected ? 'text-slate-900/55' : t.textMuted}`}>{formatTimestamp(thread.updatedAt || thread.createdAt)}</p>
            </div>
           </div>
          </button>
          );
         })}
        </div>
        <div className={`pt-5 border-t ${t.borderInput}`}>
         <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
           <span className={`text-[10px] font-bold uppercase tracking-wide ${t.textMuted}`}>Your tier</span>
           <span className="px-2 py-1 rounded-full bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wide">
            {SUPPORT_TIER_LABELS[effectiveSupportTier] || 'Standard'}
           </span>
          </div>
          <a href={`mailto:${SUPPORT_EMAIL}`} className={`text-[11px] ${t.textLink} underline underline-offset-2`}>Email us</a>
         </div>
         <p className={`mt-2 text-[11px] ${t.textMuted} leading-relaxed`}>{effectiveSupportSlaLabel}.</p>
        </div>
       </div>

        <div className={`${panelClass} p-8 flex flex-col min-h-[60vh] xl:min-h-[70vh] overflow-x-hidden`}>
         <div className={`pb-6 border-b ${t.borderInput}`}>
          <div className="flex items-start justify-between gap-4">
           <div className="min-w-0 flex-1">
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-2xl font-bold ${t.text} tracking-tight leading-tight`}>{selectedSupportThread?.subject || 'New Support Conversation'}</h3>
            {selectedSupportThread ? (
             <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${t.borderInput} ${t.bgInput} ${t.textSec}`}>
               {SUPPORT_ISSUE_LABELS[String(selectedSupportThread.issueType || 'unknown').toLowerCase()] || 'General'}
              </span>
              <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${t.borderInput} ${t.bgInput} ${t.textSec}`}>
               {SUPPORT_PRIORITY_LABELS[String(selectedSupportThread.priority || 'normal').toLowerCase()] || 'Normal'}
              </span>
              <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${t.borderInput} ${t.bgInput} ${t.textSec}`}>
               {String(selectedSupportThread.status || 'open')}
              </span>
              {selectedSupportThread.targetResponseSlaHours ? (
               <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${t.borderInput} ${t.bgInput} ${t.textMuted}`}>
                Human follow-up within {selectedSupportThread.targetResponseSlaHours}h
               </span>
              ) : null}
             </div>
            ) : (
             <p className={`mt-2 text-sm ${t.textSec} leading-relaxed`}>Ask about your reminders, QuickBooks connection, sender setup, or account status.</p>
            )}
            {selectedSupportThread?.lastTicketId ? (
             <p className={`mt-3 text-[11px] ${t.textMuted}`}>Ticket reference: <span className="font-semibold">#{String(selectedSupportThread.lastTicketId).slice(0, 8).toUpperCase()}</span> — our team has been notified.</p>
            ) : null}
           </div>
           {supportPanelMode === 'chat' && selectedSupportStatusBadge && (
            <span className={`px-3 py-2 rounded-xl text-[10px] font-bold uppercase tracking-wide border shrink-0 ${selectedSupportStatusBadge.className}`}>{selectedSupportStatusBadge.label}</span>
           )}
          </div>
          {supportPanelMode === 'chat' && selectedSupportHandoff && (
           <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
            <p className="font-semibold">Escalation summary</p>
            <p className="mt-2 leading-relaxed">{selectedSupportHandoff.summary}</p>
            <p className="mt-3 text-xs text-amber-800">Recommended next step: {selectedSupportHandoff.recommendedNextStep}</p>
          </div>
         )}
        </div>

        {supportPanelMode === 'chat' && (
         <>
        <div className="flex-1 py-6 space-y-6 overflow-y-auto overflow-x-hidden">
         {selectedSupportMessages.length === 0 && (
          <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-6 text-sm ${t.textSec} leading-relaxed`}>
           Ask a specific support question and SoarOne will answer using your live account context.
          </div>
         )}
         {selectedSupportMessages.map(message => (
          <div key={message.id} className={`max-w-3xl ${message.senderType === 'customer' ? 'ml-auto' : ''}`}>
           <div className={`rounded-2xl border p-5 overflow-hidden ${message.senderType === 'customer' ? 'bg-slate-900 text-white border-slate-900' : `${t.bgInput} ${t.borderInput} ${t.text}`}`}>
           <p className="text-xs font-bold uppercase tracking-wide opacity-70 mb-2">{message.senderType === 'customer' ? 'You' : message.senderType === 'ai' ? 'SoarOne Support AI' : 'Support'}</p>
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.message}</p>
            {message.senderType === 'ai' && (
             <div className="mt-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
               <p className={`text-[11px] ${t.textMuted}`}>Was this support answer helpful?</p>
               {message.answerSource === 'fallback' && (
                <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide border border-amber-200 bg-amber-50 text-amber-700">
                 Needs Review
                </span>
               )}
              </div>
              <div className="flex items-center gap-2">
               <button
                onClick={() => {
                 setSupportMessageFeedback(prev => ({ ...prev, [message.id]: 'yes' }));
                 handleSupportFeedback({
                  feedbackType: 'support_message',
                  helpful: true,
                  threadId: message.threadId,
                  messageId: message.id
                 });
                }}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${supportMessageFeedback[message.id] === 'yes' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : `${t.border} ${t.textLink}`}`}
               >
                Yes
               </button>
               <button
                onClick={() => {
                 setSupportMessageFeedback(prev => ({ ...prev, [message.id]: 'no' }));
                 handleSupportFeedback({
                  feedbackType: 'support_message',
                  helpful: false,
                  threadId: message.threadId,
                  messageId: message.id
                 });
                }}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${supportMessageFeedback[message.id] === 'no' ? 'border-rose-300 bg-rose-50 text-rose-700' : `${t.border} ${t.textLink}`}`}
               >
                No
               </button>
              </div>
             </div>
            )}
            {message.actions && message.actions.length > 0 && (
             <div className="mt-4 flex flex-wrap gap-2">
              {message.actions.map((action, index) => {
               const isSafeAction = ['manual_sync', 'retry_failed_email', 'resend_sender_verification'].includes(action.type);
               return (
                <button
                 key={`${message.id}_action_${index}`}
                 onClick={() => {
                  if (isSafeAction) {
                   handleSupportSafeAction(message.threadId, action.type);
                   return;
                  }
                  if (action.type === 'open_help') {
                   setSupportPanelMode('help');
                   return;
                  }
                  if (action.type === 'retry_support') {
                   const lastCustomerMessage = [...supportMessages]
                    .filter(entry => entry.threadId === message.threadId && entry.senderType === 'customer')
                    .sort((a, b) => getTimestampMs(b.createdAt) - getTimestampMs(a.createdAt))[0];
                   if (!lastCustomerMessage?.message) {
                    addNotify('info', 'There is no earlier customer message to retry yet.');
                    return;
                   }
                   handleSendSupportMessage(lastCustomerMessage.message);
                   return;
                  }
                 }}
                 className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${t.border} ${t.textLink}`}
                >
                 {action.label}
                </button>
               );
              })}
             </div>
            )}
            {message.citations && message.citations.length > 0 && (
             <div className={`mt-4 rounded-xl border ${message.senderType === 'customer' ? 'border-white/20 bg-white/10' : `${t.border} ${t.bgCard}`} p-3 overflow-hidden`}>
              <p className="text-[10px] font-bold uppercase tracking-wide opacity-70 mb-2">Citations</p>
              <div className="space-y-2">
               {message.citations.map((citation, index) => (
                <div key={`${message.id}_${index}`} className="text-xs break-words">
                 <span className="font-semibold">{citation.label}:</span> {citation.value}
                </div>
               ))}
              </div>
             </div>
            )}
           </div>
           <p className={`mt-2 text-[11px] ${t.textMuted}`}>{formatTimestamp(message.createdAt)}</p>
          </div>
         ))}
        </div>

        <div className={`pt-6 border-t ${t.borderInput} sticky bottom-0 ${t.bgCard}`}>
          <textarea
           value={supportInput}
           onChange={(e) => setSupportInput(e.target.value)}
           onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !supportSending && supportInput.trim()) { e.preventDefault(); handleSendSupportMessage(); } }}
           placeholder="Ask a support question..."
           rows={3}
           className={`w-full ${t.bgInput} border ${t.borderInput} rounded-2xl px-5 py-4 text-sm ${t.text} outline-none focus:ring-2 ${t.ring} resize-none`}
          />
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
           <button onClick={() => { handleSendSupportMessage(); }} disabled={supportSending || !supportInput.trim() || isViewingMemberWorkspace} style={{ backgroundColor: PRIMARY_BLUE }} className="w-full sm:w-auto px-6 py-3 rounded-xl text-slate-900 text-xs font-semibold tracking-wide disabled:opacity-50 order-first sm:order-last shrink-0">
            {supportSending ? 'Checking your account...' : 'Send'}
           </button>
           <p className={`text-[11px] ${t.textMuted} leading-relaxed`}>{supportSending ? 'This usually takes under 10 seconds.' : 'Answers use your live account data. Risky or unclear requests are escalated to a human. ⌘↵ to send.'}</p>
          </div>
        </div>
         </>
        )}
       </div>
       </div>
      )}

      {activeTab === 'support' && supportPanelMode === 'help' && (
       <div className="space-y-6 animate-in fade-in duration-500">
        <div className={`${panelClass} p-8`}>
         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-2xl font-bold ${t.text} tracking-tight`}>Help Center</h3>
           <p className={`text-sm ${t.textSec} mt-1`}>Guides for setup, reminders, QuickBooks sync, sender configuration, and compliance.</p>
          </div>
          <button onClick={() => setSupportPanelMode('chat')} style={{ backgroundColor: PRIMARY_BLUE }} className="shrink-0 px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900">
           Ask Support
          </button>
         </div>
        </div>
        <div className="relative">
         <Search size={16} className={`absolute left-4 top-1/2 -translate-y-1/2 ${t.textMuted}`} />
         <input
          type="text"
          placeholder="Search help articles..."
          value={helpSearch}
          onChange={(e) => setHelpSearch(e.target.value)}
          className={`w-full pl-10 pr-4 py-4 ${t.bgInput} border ${t.borderInput} rounded-2xl text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
         />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
         {filteredHelpArticles.length === 0 && (
          <div className={`lg:col-span-2 rounded-2xl border ${t.borderInput} ${t.bgInput} p-8`}>
           <p className={`text-sm ${t.textSec}`}>No help articles matched your search.</p>
           <button onClick={() => { setActiveTab('support'); setSupportPanelMode('chat'); }} style={{ backgroundColor: PRIMARY_BLUE }} className="mt-4 px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900">Ask Support Instead</button>
          </div>
         )}
         {filteredHelpArticles.map(article => (
          <article key={article.id} className={`${panelClass} p-8`}>
           <button
            onClick={() => setExpandedHelpArticles(prev => ({ ...prev, [article.id]: !prev[article.id] }))}
            className="w-full text-left"
           >
            <div className="flex items-start justify-between gap-3">
             <div>
              <h4 className={`text-lg font-bold ${t.text}`}>{article.title}</h4>
              <p className={`mt-2 text-sm ${t.textSec}`}>{article.summary}</p>
             </div>
             <ChevronDown
              size={18}
              className={`mt-1 shrink-0 transition-transform ${t.textMuted} ${expandedHelpArticles[article.id] ? 'rotate-180' : ''}`}
             />
            </div>
           </button>
           {expandedHelpArticles[article.id] && (
            <>
             <div className="mt-4">
              <p className={`text-[10px] font-bold uppercase tracking-wide ${t.textMuted}`}>Key Points</p>
             </div>
             <ul className="mt-3 space-y-2">
              {article.bullets.map((bullet, idx) => (
               <li key={`${article.id}_${idx}`} className={`text-sm ${t.text} leading-relaxed flex items-start gap-2`}>
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color: PRIMARY_BLUE }} />
                <span>{bullet}</span>
               </li>
              ))}
             </ul>
             {article.howTo && article.howTo.length > 0 && (
              <div className={`mt-5 rounded-2xl border ${t.borderInput} ${t.bgInput} p-5`}>
               <p className={`text-[10px] font-bold uppercase tracking-wide ${t.textMuted}`}>How To</p>
               <ol className="mt-3 space-y-3">
                {article.howTo.map((step, idx) => (
                 <li key={`${article.id}_howto_${idx}`} className={`text-sm ${t.text} leading-relaxed flex items-start gap-3`}>
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 text-slate-900" style={{ backgroundColor: PRIMARY_BLUE }}>
                   {idx + 1}
                  </span>
                  <span>{step}</span>
                 </li>
                ))}
               </ol>
              </div>
             )}
             {article.details && article.details.length > 0 && (
              <div className="mt-5 space-y-3">
               <p className={`text-[10px] font-bold uppercase tracking-wide ${t.textMuted}`}>More Detail</p>
               {article.details.map((detail, idx) => (
                <p key={`${article.id}_detail_${idx}`} className={`text-sm ${t.textSec} leading-relaxed`}>
                 {detail}
                </p>
               ))}
              </div>
             )}
             {article.faqs && article.faqs.length > 0 && (
              <div className="mt-5 space-y-3">
               <p className={`text-[10px] font-bold uppercase tracking-wide ${t.textMuted}`}>FAQ</p>
               <div className="space-y-3">
                {article.faqs.map((faq, idx) => (
                 <div key={`${article.id}_faq_${idx}`} className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-4`}>
                  <p className={`text-sm font-semibold ${t.text}`}>{faq.question}</p>
                  <p className={`mt-2 text-sm ${t.textSec} leading-relaxed`}>{faq.answer}</p>
                 </div>
                ))}
               </div>
              </div>
             )}
             <div className={`mt-5 pt-4 border-t ${t.borderInput} flex items-center justify-between gap-3`}>
              <p className={`text-xs ${t.textMuted}`}>Was this article helpful?</p>
              <div className="flex items-center gap-2">
               <button onClick={() => { setHelpFeedback(prev => ({ ...prev, [article.id]: 'yes' })); handleSupportFeedback({ feedbackType: 'help_article', helpful: true, articleId: article.id }); }} className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${helpFeedback[article.id] === 'yes' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : `${t.border} ${t.textLink}`}`}>Yes</button>
               <button onClick={() => { setHelpFeedback(prev => ({ ...prev, [article.id]: 'no' })); handleSupportFeedback({ feedbackType: 'help_article', helpful: false, articleId: article.id }); }} className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide border ${helpFeedback[article.id] === 'no' ? 'border-rose-300 bg-rose-50 text-rose-700' : `${t.border} ${t.textLink}`}`}>No</button>
              </div>
             </div>
            </>
           )}
          </article>
         ))}
        </div>
        <div className={`${panelClass} p-6`}>
         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
           <h4 className={`text-base font-bold ${t.text}`}>Didn't find what you need?</h4>
           <p className={`text-sm ${t.textSec} mt-1`}>Our support team can help with anything not covered here.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
           <button onClick={() => { setSupportPanelMode('chat'); }} style={{ backgroundColor: PRIMARY_BLUE }} className="px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide text-slate-900">
            Ask Support
           </button>
           <a href={`mailto:${SUPPORT_EMAIL}`} className={`px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide border ${t.border} ${t.textLink}`}>
            Email Us
           </a>
           <a href={STATUS_PAGE_URL} target="_blank" rel="noreferrer" className={`px-4 py-2.5 rounded-xl text-[11px] font-semibold uppercase tracking-wide border ${t.border} ${t.textMuted} hover:${t.textSec} transition-colors`}>
            System Status
           </a>
          </div>
         </div>
        </div>
       </div>
      )}

      {activeTab === 'settings' && (
       <div className="max-w-4xl space-y-10 animate-in fade-in duration-500">
       {isViewingMemberWorkspace && (
        <div className="flex items-center gap-3 px-5 py-3 rounded-2xl border border-amber-200 bg-amber-50">
         <AlertTriangle size={15} className="text-amber-500 shrink-0" />
         <p className="text-xs text-amber-800">You are viewing <span className="font-semibold">{activeWorkspace?.ownerName || activeWorkspace?.ownerEmail}</span>'s templates. Editing is disabled — only the workspace owner can modify templates.</p>
        </div>
       )}
       <div className={`${panelClass} p-5 sm:p-8 lg:p-12 relative overflow-hidden text-left`}>
        <div style={{backgroundColor: PRIMARY_BLUE}} className="absolute top-0 left-0 w-2 h-full shadow-none"></div>
        <div className="flex flex-col gap-4 sm:gap-6 sm:flex-row sm:items-start sm:justify-between mb-8 sm:mb-12 shadow-none">
         <div><h3 className="text-2xl font-semibold uppercase tracking-wide text-slate-900 font-semibold">Automation core</h3><p className="text-xs font-semibold text-slate-400 tracking-wide mt-2 font-semibold">Dunning sequence orchestration</p></div>
         <div className="flex w-full flex-col gap-3 sm:w-auto sm:items-end">
          <div style={{backgroundColor: PRIMARY_BLUE}} className="px-4 sm:px-6 py-3 rounded-xl text-slate-900 text-xs font-medium uppercase tracking-wide border border-white shadow-none font-semibold text-center">{dunningTiers.filter(t => t.enabled).length} / {dunningTiers.length} Active</div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
           <span className="text-[10px] text-slate-400">{dunningTiers.length}/{effectivePlan.maxTemplates === 99 ? '∞' : effectivePlan.maxTemplates}</span>
           {!isViewingMemberWorkspace && (
            <button
             onClick={() => setShowTemplateLibrary(v => !v)}
             className="w-full sm:w-auto px-5 py-3 bg-white border-2 border-slate-100 rounded-xl text-xs font-medium uppercase tracking-wide text-slate-500 hover:border-slate-300 hover:text-slate-900 active:scale-95 transition-all shadow-none"
            >
             {showTemplateLibrary ? 'Hide Library' : 'Browse Library'}
            </button>
           )}
           <button onClick={handleAddTier} disabled={savingSettings || !canAddTemplate || isViewingMemberWorkspace} className={`w-full sm:w-auto px-6 py-3 ${canAddTemplate && !isViewingMemberWorkspace ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-200 cursor-not-allowed'} text-white rounded-xl text-xs font-medium uppercase tracking-wide active:scale-95 transition-all shadow-none`}>+ Add Template</button>
          </div>
         </div>
        </div>

        <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-3">
         <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Ready to Send</p>
          <p className="mt-2 text-2xl font-bold text-emerald-900">{templateSafetySummary.ready}</p>
          <p className="mt-1 text-xs text-emerald-800">Templates with core safeguards satisfied.</p>
         </div>
         <div className="rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Review Recommended</p>
          <p className="mt-2 text-2xl font-bold text-amber-900">{templateSafetySummary.review}</p>
          <p className="mt-1 text-xs text-amber-800">Templates that should run AI review before going live.</p>
         </div>
         <div className="rounded-2xl border border-rose-100 bg-rose-50 px-5 py-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Blocked</p>
          <p className="mt-2 text-2xl font-bold text-rose-900">{templateSafetySummary.blocked}</p>
          <p className="mt-1 text-xs text-rose-800">Templates that cannot safely send in the current state.</p>
         </div>
        </div>
        
        {showTemplateLibrary && (
         <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden animate-in slide-in-from-top-2 duration-300">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
           <div>
            <p className="text-sm font-bold text-slate-900">Starter Template Library</p>
            <p className="text-xs text-slate-400 mt-0.5">Apply a pre-written template to get started quickly. Each one is fully editable after creation.</p>
           </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200">
           {[
            {
             name: 'Friendly Reminder', days: 7, tone: 'Warm',
             badge: 'bg-emerald-100 border-emerald-200 text-emerald-700',
             description: 'A soft first touch. Assumes the invoice was simply overlooked and keeps the tone positive.',
             subject: 'Friendly Reminder: Invoice #{{invoice_num}} is due',
             body: 'Hi {{customer_name}},\n\nI hope this message finds you well. This is a friendly reminder that invoice #{{invoice_num}} for ${{amount_due}}, due on {{due_date}}, is currently outstanding.\n\nIf you\'ve already arranged payment, please disregard this note. Otherwise, we\'d appreciate your attention at your earliest convenience.\n\nFeel free to reach out if you have any questions — we\'re always happy to help.\n\nThank you for your business.',
            },
            {
             name: 'Firm Notice', days: 30, tone: 'Direct',
             badge: 'bg-amber-100 border-amber-200 text-amber-700',
             description: 'Clear and professional. States the overdue status plainly and requests prompt payment.',
             subject: 'Payment Required: Invoice #{{invoice_num}} — {{days_overdue}} Days Overdue',
             body: 'Hi {{customer_name}},\n\nThis is a follow-up regarding invoice #{{invoice_num}} for ${{amount_due}}, originally due on {{due_date}}. The account is now {{days_overdue}} days past due.\n\nPlease arrange payment at your earliest convenience. If you are experiencing difficulties or have already sent payment, please contact us immediately.\n\nThank you for your prompt attention.',
            },
            {
             name: 'Final Notice', days: 60, tone: 'Urgent',
             badge: 'bg-rose-100 border-rose-200 text-rose-700',
             description: 'A final escalation. Urgent tone, states consequences, and demands immediate action or contact.',
             subject: 'Final Notice: Invoice #{{invoice_num}} — Immediate Action Required',
             body: 'Hi {{customer_name}},\n\nDespite our previous correspondence, invoice #{{invoice_num}} for ${{amount_due}} remains unpaid. This account is now {{days_overdue}} days overdue.\n\nThis is a final notice. We urge you to make payment immediately or contact us to make arrangements before further recovery steps are taken.\n\nPayment details:\n• Invoice: #{{invoice_num}}\n• Amount due: ${{amount_due}}\n• Original due date: {{due_date}}\n\nThis is a communication from {{company_name}}.',
            },
           ].map(starter => (
            <div key={starter.name} className="p-6 flex flex-col gap-4">
             <div className="flex items-start justify-between gap-3">
              <div>
               <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide ${starter.badge}`}>
                {starter.tone}
               </span>
               <p className="font-bold text-slate-900 mt-2">{starter.name}</p>
               <p className="text-xs text-slate-400 mt-1">Send {starter.days} days past due</p>
              </div>
             </div>
             <p className="text-xs text-slate-500 leading-relaxed flex-1">{starter.description}</p>
             <button
              disabled={savingSettings || !canAddTemplate || isViewingMemberWorkspace}
              onClick={async () => {
               if (!db || !user || !canAddTemplate) { requireUpgrade('template library'); return; }
               const newId = `tier_${Date.now()}`;
               const tier: DunningTier = { id: newId, name: starter.name, daysPastDue: starter.days, subject: starter.subject, body: starter.body, enabled: false };
               setSavingSettings(true);
               try {
                await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'settings', newId), tier);
                logAudit('template_created', `Template "${starter.name}" applied from library`, { tierId: newId });
                addNotify('success', `"${starter.name}" added — review and enable it when ready.`);
                setEditingTierId(newId);
                setEditingDraft(tier);
                setShowTemplateLibrary(false);
               } catch { addNotify('error', 'Failed to apply template.'); }
               finally { setSavingSettings(false); }
              }}
              className="w-full py-2.5 rounded-xl text-xs font-semibold text-slate-900 transition-all active:scale-95 disabled:opacity-40"
              style={{ backgroundColor: PRIMARY_BLUE }}
             >
              Use this template
             </button>
            </div>
           ))}
          </div>
         </div>
        )}

        <div className="space-y-6 shadow-none">
         {[...dunningTiers].sort((a,b) => a.daysPastDue - b.daysPastDue).map((tier) => (
         <div key={tier.id} className={`p-4 sm:p-6 lg:p-10 bg-slate-50 rounded-2xl border-2 transition-all shadow-none ${editingTierId === tier.id ? 'bg-white border-slate-200' : 'border-slate-50 hover:border-slate-200'} ${!tier.enabled ? 'opacity-40' : ''}`} style={editingTierId === tier.id ? {borderColor: PRIMARY_BLUE} : {}}>
           {(() => {
            const safety = getTemplateSafety(tier);
            return (
            <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-center shadow-none">
             <div className="flex items-start gap-4 sm:gap-6 lg:gap-8 shadow-none min-w-0">
              <div className={`p-3 sm:p-4 lg:p-5 rounded-xl border shadow-none shrink-0 ${editingTierId === tier.id ? 'text-slate-900 border-slate-900' : 'bg-white text-slate-400 border-slate-100'}`} style={editingTierId === tier.id ? {backgroundColor: PRIMARY_BLUE} : {}}><MailIcon size={22} className="sm:w-7 sm:h-7" /></div>
              <div className="shadow-none">
               <p style={{fontFamily:"var(--font-display)"}} className="font-bold text-lg sm:text-xl text-slate-900 mb-1 break-words">{tier.name}</p>
               <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide font-semibold">Send</span>
                <span style={{color: PRIMARY_BLUE}} className="text-lg font-semibold">{Math.abs(tier.daysPastDue)}</span>
                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide font-semibold">days {tier.daysPastDue < 0 ? 'before' : 'after'} due date</span>
               </div>
               <div className="mt-3 flex flex-wrap items-center gap-3">
                {renderTemplateSafetyBadge(safety)}
                <p className="text-[11px] text-slate-500 leading-relaxed">{safety.detail}</p>
               </div>
              </div>
             </div>
             <div className="flex w-full flex-col sm:flex-row sm:flex-wrap xl:w-auto items-stretch sm:items-center gap-3">
              <button
               disabled={isViewingMemberWorkspace}
               onClick={async () => {
                if (!db || !user || isViewingMemberWorkspace) return;
                const nextEnabled = !tier.enabled;
                const safety = getTemplateSafety(tier);
                if (nextEnabled && safety.level === 'blocked') {
                 addNotify('error', safety.detail);
                 return;
                }
                if (nextEnabled && safety.level === 'review') {
                 addNotify('info', 'AI review is still recommended before you enable this template live.');
                }
                try {
                 await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'settings', tier.id), { enabled: nextEnabled }, { merge: true });
                 addNotify('success', `${tier.name} ${nextEnabled ? 'enabled' : 'disabled'}.`);
                } catch (err) { addNotify('error', 'Toggle failed.'); }
               }}
               className={`p-1 rounded-full transition-all shadow-none ${tier.enabled ? 'bg-slate-900' : 'bg-slate-200'}`}
               title={tier.enabled ? 'Disable this tier' : 'Enable this tier'}
              >
               {tier.enabled ? <ToggleRight className="text-white" size={28} /> : <ToggleLeft className="text-slate-500" size={28} />}
              </button>
              {!isViewingMemberWorkspace && <button onClick={() => {
                if (editingTierId === tier.id) {
                  setEditingTierId(null);
                  setEditingDraft(null);
                  setToneRewritePreview(null);
                  setSuggestComplianceReview(false);
                  setLastToneMode(null);
                } else {
                  setEditingTierId(tier.id);
                  setEditingDraft({...tier});
                  setToneRewritePreview(null);
                  setSuggestComplianceReview(false);
                  setLastToneMode(null);
                }
              }} className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-white border-2 border-slate-100 rounded-xl text-xs font-medium uppercase tracking-wide text-slate-400 hover:text-slate-900 hover:border-slate-300 transition-all active:scale-95 font-semibold shadow-none">
                {editingTierId === tier.id ? 'Discard' : 'Configure'}
              </button>}
              {!isViewingMemberWorkspace && editingTierId !== tier.id && (
               <button
                onClick={() => handleDuplicateTier(tier)}
                disabled={savingSettings || !canAddTemplate}
                title="Duplicate this template"
                className="px-4 py-3 sm:py-2 bg-white border-2 border-slate-100 rounded-xl text-xs font-medium uppercase tracking-wide text-slate-400 hover:text-slate-900 hover:border-slate-300 transition-all active:scale-95 shadow-none disabled:opacity-40"
               >
                Duplicate
               </button>
              )}
             </div>
            </div>
            );
           })()}
            {editingTierId === tier.id && editingDraft && (
             <div className="mt-6 sm:mt-10 pt-6 sm:pt-10 border-t border-slate-100 space-y-6 sm:space-y-8 animate-in slide-in-from-top-4 duration-300 shadow-none">
              {(() => {
               const safety = getTemplateSafety(editingDraft);
               const panelClass = safety.level === 'ready'
                ? 'bg-emerald-50 border-emerald-100'
                : safety.level === 'review'
                 ? 'bg-amber-50 border-amber-100'
                 : 'bg-rose-50 border-rose-100';
               return (
                <div className={`rounded-2xl border p-5 ${panelClass}`}>
                 <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                   <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Send Safety</p>
                   <p className="text-sm text-slate-700 mt-1">{safety.detail}</p>
                  </div>
                  {renderTemplateSafetyBadge(safety)}
                 </div>
                </div>
               );
              })()}
              {/* Days to send - prominent */}
              <div className="p-5 sm:p-8 rounded-2xl border-2 border-slate-100 bg-white shadow-none">
               <label className="block text-xs font-semibold uppercase text-slate-400 tracking-wide font-semibold mb-4">When to send this email</label>
               <div className="flex flex-col items-center text-center gap-4 sm:flex-row sm:items-center sm:text-left sm:gap-6">
                <input type="number" className="w-full max-w-[10rem] sm:w-32 bg-slate-50 border-2 border-slate-100 focus:ring-2 focus:ring-cyan-100 rounded-xl px-6 py-4 text-2xl font-semibold text-center outline-none transition-all shadow-none" value={editingDraft.daysPastDue} onChange={(e) => setEditingDraft({...editingDraft, daysPastDue: parseInt(e.target.value) || 0})} />
                <div>
                 <p className="text-sm font-semibold text-slate-900">days {(editingDraft.daysPastDue ?? 0) < 0 ? 'before' : 'after'} invoice due date</p>
                 <p className="text-xs font-medium text-slate-400 tracking-normal mt-1">Use negative numbers (e.g. -3) to send before the due date</p>
                </div>
               </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 shadow-none">
               <div className="space-y-2 shadow-none">
                <label className="block text-xs font-medium uppercase text-slate-400 ml-4 tracking-wide font-semibold">Template Name</label>
                <input type="text" className="w-full bg-slate-50 border-2 border-slate-50 focus:ring-2 focus:ring-slate-100 rounded-xl px-6 py-4 font-normal text-sm outline-none transition-all shadow-none" value={editingDraft.name || ''} onChange={(e) => setEditingDraft({...editingDraft, name: e.target.value})} />
               </div>
               <div className="space-y-2 shadow-none">
                <label className="block text-xs font-medium uppercase text-slate-400 ml-4 tracking-wide font-semibold">Email Subject Line</label>
                <input type="text" className="w-full bg-slate-50 border-2 border-slate-50 focus:ring-2 focus:ring-slate-100 rounded-xl px-6 py-4 font-normal text-sm outline-none transition-all shadow-none" value={editingDraft.subject || ''} onChange={(e) => setEditingDraft({...editingDraft, subject: e.target.value})} />
               </div>
              </div>
              <div className="space-y-2 shadow-none">
               <label className="block text-xs font-medium uppercase text-slate-400 ml-4 tracking-wide font-semibold">Email Body</label>
               <textarea rows={8} className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-5 sm:px-8 py-5 sm:py-6 font-normal text-sm resize-none outline-none focus:ring-2 focus:ring-slate-100 transition-all shadow-none" value={editingDraft.body || ''} onChange={(e) => setEditingDraft({...editingDraft, body: e.target.value})} />
               <div className="mt-4 space-y-3">
                <div className="p-4 sm:p-5 bg-slate-50 rounded-2xl flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center border border-slate-100 shadow-none">
                 <ZapIcon size={18} style={{ color: PRIMARY_BLUE }} className="animate-pulse shrink-0" />
                 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide break-words flex-1">
                  Variable Tags: {'{{customer_name}}, {{invoice_num}}, {{amount_due}}, {{due_date}}'}
                 </p>
                 <button
                  onClick={() => setShowMergePreview(v => !v)}
                  className="shrink-0 px-4 py-2 rounded-xl text-xs font-semibold border border-slate-200 bg-white text-slate-600 hover:border-slate-300 transition-all"
                 >
                  {showMergePreview ? 'Hide Preview' : 'Preview Email'}
                 </button>
                </div>
                {showMergePreview && (
                 <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
                   <Eye size={13} className="text-slate-400" />
                   <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Preview with sample data</span>
                  </div>
                  <div className="p-5 sm:p-7 space-y-4">
                   <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Subject</p>
                    <p className="text-sm font-semibold text-slate-900">{renderWithSampleData(editingDraft.subject || '')}</p>
                   </div>
                   <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Body</p>
                    <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl px-5 py-4 border border-slate-100">
                     {renderWithSampleData(editingDraft.body || '')}
                    </div>
                   </div>
                   <p className="text-[10px] text-slate-400">Sample data: Acme Corp · INV-1042 · $3,200.00 · due March 31, 2026</p>
                  </div>
                 </div>
                )}
               </div>
              </div>
              <div className="p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
               <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div>
                 <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI Tone Assist</p>
                 <p className="text-xs text-slate-400 mt-1">Refine the draft without changing the underlying facts. Use this to soften, firm up, or tighten the template before running compliance review.</p>
                </div>
               </div>
               <div className="flex flex-wrap gap-3">
               {[
                 ['softer', 'Softer'],
                 ['balanced', 'Balanced'],
                 ['firmer', 'Firmer'],
                 ['professional', 'Professional'],
                 ['shorter', 'Shorter']
                ].map(([value, label]) => (
                 <button
                  key={value}
                  onClick={() => handleToneRewrite(value as 'softer' | 'balanced' | 'firmer' | 'professional' | 'shorter')}
                  disabled={toneRewriteLoading}
                  className={`px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide border transition-all disabled:opacity-50 ${
                   lastToneMode === value
                    ? 'text-slate-900 border-cyan-200 bg-cyan-50'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                 >
                  {toneRewriteLoading ? 'Working...' : label}
                 </button>
                ))}
               </div>
               {toneRewritePreview && (
                <div className="rounded-2xl border border-cyan-100 bg-cyan-50/50 p-4 sm:p-5 space-y-4">
                 <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <div>
                   <p className="text-sm font-semibold text-slate-800">{lastToneMode ? `${toneModeLabels[lastToneMode]} rewrite ready` : 'Tone rewrite preview'}</p>
                   <p className="text-[11px] text-slate-500 mt-1">{toneRewritePreview.toneSummary}</p>
                  </div>
                  <p className="text-[11px] text-slate-500">{lastToneMode ? `${toneModeLabels[lastToneMode]} mode - ` : ''}{toneRewritePreview.model} - {new Date(toneRewritePreview.rewrittenAt).toLocaleString()}</p>
                 </div>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-slate-100 p-4">
                   <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Suggested Subject</p>
                   <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{toneRewritePreview.suggestedSubject}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-100 p-4">
                   <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Implementation Note</p>
                   <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{toneRewritePreview.notes}</p>
                  </div>
                 </div>
                 <div className="bg-white rounded-xl border border-slate-100 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Suggested Body</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{toneRewritePreview.suggestedBody}</p>
                 </div>
	               <div className="flex flex-col sm:flex-row gap-3">
                 <button onClick={handleApplyToneRewrite} className="px-5 py-3 rounded-xl text-xs font-semibold tracking-wide text-slate-900 transition-all" style={{ backgroundColor: PRIMARY_BLUE }}>
                  Apply Rewrite
                 </button>
                 <button onClick={handleDiscardToneRewrite} className="px-5 py-3 rounded-xl text-xs font-semibold tracking-wide border border-slate-200 bg-white text-slate-700 hover:border-slate-300 transition-all">
                   Discard
                  </button>
                 </div>
	                 <div className="pt-1 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 text-[11px] text-slate-500">
                  <span>{toneRewritePreview.usage.totalTokens.toLocaleString()} tokens used</span>
                  <span>Estimated cost: ${Number(toneRewritePreview.usage.estimatedCostUsd || 0).toFixed(4)}</span>
                 </div>
                </div>
               )}
              {suggestComplianceReview && !toneRewritePreview && (
	               <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                 <p className="text-sm font-semibold text-slate-800">Recommended next step</p>
                 <p className="text-[11px] text-slate-500 mt-1">Run AI compliance review on the rewritten draft before saving it live.</p>
                </div>
                <button
                 onClick={handleCheckCompliance}
                 disabled={complianceReviewLoading}
                 className="px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide text-slate-900 transition-all disabled:opacity-50"
                 style={{ backgroundColor: PRIMARY_BLUE }}
                >
                 {complianceReviewLoading ? 'Reviewing...' : 'Run Compliance Review'}
                </button>
               </div>
              )}
              </div>
              <div className="p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
               <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div>
                 <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI Compliance Review</p>
                 <p className="text-xs text-slate-400 mt-1">Run a semantic FDCPA review on tone, threats, misleading claims, and coercive language before you save or enable this template.</p>
                </div>
                <div className="flex w-full sm:w-auto flex-col sm:flex-row gap-3">
                 <button onClick={handleCheckCompliance} disabled={complianceReviewLoading} className="px-5 py-3 rounded-xl text-xs font-semibold tracking-wide text-slate-900 active:scale-95 transition-all disabled:opacity-50" style={{ backgroundColor: PRIMARY_BLUE }}>
                  {complianceReviewLoading ? 'Reviewing...' : 'Check Compliance'}
                 </button>
                 {editingDraft.complianceReview?.suggestedBody && (
                  <button onClick={handleApplyComplianceRewrite} className="px-5 py-3 bg-white border border-slate-200 rounded-xl text-xs font-semibold tracking-wide text-slate-700 hover:border-slate-300 transition-all">
                   Apply Suggested Rewrite
                  </button>
                 )}
                </div>
               </div>
               {editingDraft.complianceReview && (
                <div className={`rounded-2xl border p-5 ${editingDraft.complianceReview.compliant ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
                 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-3">
                  <div className="flex items-center gap-3">
                   <div className={`w-2.5 h-2.5 rounded-full ${editingDraft.complianceReview.compliant ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                   <p className="text-sm font-semibold text-slate-800">
                    {editingDraft.complianceReview.compliant ? 'Passed AI semantic review' : `Flagged as ${editingDraft.complianceReview.riskLevel} risk`}
                   </p>
                  </div>
                  <p className="text-[11px] text-slate-500">
                   {editingDraft.complianceReview.model || 'ai'} {editingDraft.complianceReview.reviewedAt ? `· ${new Date(editingDraft.complianceReview.reviewedAt).toLocaleString()}` : ''}
                  </p>
                 </div>
                 {editingDraft.complianceReview.explanation && (
                  <p className="text-xs text-slate-600 leading-relaxed mb-3">{editingDraft.complianceReview.explanation}</p>
                 )}
                 {!!editingDraft.complianceReview.issues?.length && (
                  <div className="space-y-2">
                   {editingDraft.complianceReview.issues.map((issue, idx) => (
                    <div key={idx} className="bg-white/70 border border-white rounded-xl px-4 py-3">
                     <p className="text-xs font-semibold text-slate-700">{issue.category} · {issue.severity} risk{issue.phrase ? ` · "${issue.phrase}"` : ''}</p>
                     <p className="text-[11px] text-slate-500 mt-1">{issue.explanation}</p>
                    </div>
                   ))}
                  </div>
                 )}
                </div>
               )}
              </div>
              {/* PDF Attachment Toggle */}
              <div className="p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100">
               <div className="flex items-start sm:items-center justify-between gap-4">
                <div>
                 <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Invoice PDF Attachment</p>
                 <p className="text-xs text-slate-400 mt-1">When enabled, a PDF copy of the invoice is attached to this reminder email. Disable for shorter, lightweight emails.</p>
                </div>
                <button
                 onClick={() => setEditingDraft({ ...editingDraft, attachPdf: !(editingDraft.attachPdf !== false) })}
                 className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide border transition-all"
                 style={editingDraft.attachPdf !== false ? { backgroundColor: PRIMARY_BLUE, borderColor: PRIMARY_BLUE, color: '#0f172a' } : { background: 'white', borderColor: '#e2e8f0', color: '#64748b' }}
                >
                 {editingDraft.attachPdf !== false ? <><ToggleRight size={16} /> PDF On</> : <><ToggleLeft size={16} /> PDF Off</>}
                </button>
               </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
               <button onClick={handleSaveTier} disabled={savingSettings} style={{backgroundColor: PRIMARY_BLUE}} className="flex-1 text-slate-900 py-5 rounded-xl text-xs font-semibold tracking-wide active:scale-95 transition-all flex items-center justify-center gap-4 shadow-none"><Save size={20} /> {savingSettings ? 'Saving...' : 'Commit Logic Changes'}</button>
               <button onClick={() => handleDeleteTier(tier.id, tier.name)} disabled={savingSettings} className="px-8 py-5 bg-rose-50 text-rose-600 rounded-xl text-xs font-semibold uppercase tracking-wide hover:bg-rose-600 hover:text-white active:scale-95 transition-all border border-rose-100 shadow-none flex items-center gap-3"><Trash2 size={18} /> Delete</button>
              </div>
             </div>
            )}
          </div>
         ))}
        </div>
       </div>
      </div>
     )}


     {/* INSIGHTS (Analytics sub-tab) */}
     {activeTab === 'analytics' && analyticsSubTab === 'insights' && (() => {
      const now = new Date();
      const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
      const cardClass = `${t.bgCard} rounded-2xl border ${t.border} p-6`;

      const customerTagMap = insightsCustomerTagMap;
      const allTags = Array.from(new Set(Object.values(customerTagMap).flat())).sort();
      const segmentInvoices = insightsSegmentInvoices;

      // --- DSO (segment-scoped) ---
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000);
      const arBalance = segmentInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.balance || 0), 0);
      const creditSales30 = segmentInvoices
       .filter(i => i.invoiceDate && new Date(i.invoiceDate) >= thirtyDaysAgo)
       .reduce((s, i) => s + (i.totalAmount || 0), 0);
      const dso = creditSales30 > 0 ? Math.round((arBalance / creditSales30) * 30) : null;
      const dsoLabel = dso === null ? null : dso <= 30 ? '✓ Excellent — collecting faster than industry average' : dso <= 45 ? '◎ Within typical range for professional services' : '△ Above average — consider tightening follow-up cadence';

      // --- CEI — Collection Effectiveness Index (global AR, not segmented) ---
      // Formula: (BeginAR + CreditSales − EndAR) / (BeginAR + CreditSales − EndCurrentAR) × 100
      // All AR values come from snapshots. Credit sales are filtered to the exact snapshot date
      // range so the AR window and sales window are always aligned.
      // Delta is only shown when two full non-overlapping windows of the same length exist.
      const snapsSorted = [...agingSnapshots].sort((a, b) => a.weekKey.localeCompare(b.weekKey));
      let cei: number | null = null;
      let ceiLabel = '';
      let ceiDelta: number | null = null;
      const snapTotal = (s: AgingSnapshot) => s.current + s.days1_30 + s.days31_60 + s.days61_90 + s.days90plus;

      // Convert ISO week key "YYYY-WXX" → Monday date of that week
      const weekKeyToMonday = (key: string): Date => {
       const [yearStr, weekStr] = key.split('-W');
       const year = parseInt(yearStr, 10);
       const week = parseInt(weekStr, 10);
       const jan4 = new Date(year, 0, 4);
       const jan4Day = jan4.getDay() || 7; // Sun=0 → treat as 7 so Mon=1
       const week1Mon = new Date(jan4.getTime() - (jan4Day - 1) * 86400000);
       return new Date(week1Mon.getTime() + (week - 1) * 7 * 86400000);
      };

      // Credit sales strictly within [fromDate, toDate)
      const creditSalesInRange = (fromDate: Date, toDate: Date): number =>
       invoices
        .filter(i => {
         if (!i.invoiceDate) return false;
         const d = getParsedDate(i.invoiceDate);
         return !!d && d >= fromDate && d < toDate;
        })
        .reduce((s, i) => s + (i.totalAmount || 0), 0);
      const snapshotBoundaryDate = (snapshot: AgingSnapshot): Date => {
       const snapshotDate = getParsedDate(snapshot.snapshotDate);
       return snapshotDate || weekKeyToMonday(snapshot.weekKey);
      };

      if (snapsSorted.length >= 2) {
       const n = snapsSorted.length;
       const periodLen = Math.min(4, n - 1); // up to 4 weeks back, never zero

       // Current period
       const beginSnap = snapsSorted[n - 1 - periodLen];
       const latestSnap = snapsSorted[n - 1];
       const beginDate = snapshotBoundaryDate(beginSnap);
       const endDate = snapshotBoundaryDate(latestSnap);

       const beginningAR = snapTotal(beginSnap);
       const endingAR = snapTotal(latestSnap);
       const endingCurrentAR = latestSnap.current; // "current" bucket = not-yet-overdue at period end
       const creditSales = beginDate && endDate ? creditSalesInRange(beginDate, endDate) : 0;

       const denom = beginningAR + creditSales - endingCurrentAR;
       if (denom > 0) {
        cei = Math.max(0, Math.min(100, Math.round(((beginningAR + creditSales - endingAR) / denom) * 100)));
       }

       // Delta: two full non-overlapping periods of the same length.
       // Requires n >= 2 * periodLen + 1 so neither window is degenerate or shared.
       if (cei !== null && n >= 2 * periodLen + 1) {
        const prevEndSnap = snapsSorted[n - 1 - periodLen];       // same as current beginSnap
        const prevBeginSnap = snapsSorted[n - 1 - periodLen * 2]; // start of previous period
        const prevBeginDate = snapshotBoundaryDate(prevBeginSnap);
        const prevEndDate = snapshotBoundaryDate(prevEndSnap);

        const prevBeginAR = snapTotal(prevBeginSnap);
        const prevEndAR = snapTotal(prevEndSnap);
        const prevCurrentAR = prevEndSnap.current;
        const prevCreditSales = prevBeginDate && prevEndDate ? creditSalesInRange(prevBeginDate, prevEndDate) : 0;

        const prevDenom = prevBeginAR + prevCreditSales - prevCurrentAR;
        if (prevDenom > 0) {
         const prevCei = Math.max(0, Math.min(100, Math.round(((prevBeginAR + prevCreditSales - prevEndAR) / prevDenom) * 100)));
         ceiDelta = cei - prevCei;
        }
       }
      }
      ceiLabel = cei === null ? '' : cei >= 80 ? 'Strong — collecting most of what was collectible' : cei >= 60 ? 'Average — room to tighten follow-up cadence' : 'Below average — collections need immediate attention';
      const ceiColor = cei === null ? t.text : cei >= 80 ? 'text-emerald-600' : cei >= 60 ? 'text-amber-600' : 'text-rose-600';

      // --- Cash Collection Forecast (segment-scoped) ---
      const engagedEmails = new Set<string>(
       logs.filter(l => ['opened', 'clicked'].includes(l.deliveryStatus)).map(l => l.recipient || '')
      );
      const fcastHigh = segmentInvoices.filter(i => i.status === 'open').reduce((s, i) => s + (i.balance || 0), 0);
      let fcastMedium = 0, fcastAtRisk = 0;
      for (const inv of segmentInvoices.filter(i => i.status === 'overdue')) {
       const daysOver = inv.dueDate ? Math.floor((now.getTime() - new Date(inv.dueDate).getTime()) / 86400000) : 999;
       const engaged = inv.customerEmail ? engagedEmails.has(inv.customerEmail) : false;
       if (daysOver <= 30 && engaged) fcastMedium += inv.balance || 0;
       else fcastAtRisk += inv.balance || 0;
      }
      const fcastTotal = fcastHigh + fcastMedium + fcastAtRisk;

      const scoreStyle = (s: string) => s === 'Chronic' ? 'bg-rose-100 text-rose-800 border-rose-200' : s === 'At-Risk' ? 'bg-orange-100 text-orange-800 border-orange-200' : s === 'Slow' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-emerald-100 text-emerald-800 border-emerald-200';

      // --- AR Aging Trend (global — snapshots don't have per-tag breakdown) ---
      const trendData = agingSnapshots.slice(-13).map(s => ({
       week: s.weekKey.replace(/\d{4}-W(\d+)/, 'W$1'),
       'Current': s.current,
       '1-30d': s.days1_30,
       '31-60d': s.days31_60,
       '61-90d': s.days61_90,
       '90+d': s.days90plus,
      }));

      // --- Segment summary (all invoices, one row per tag) ---
      const segmentSummary = allTags.map(tag => {
       const tagInvs = invoices.filter(i => {
        const email = (i.customerEmail || '').toLowerCase();
        return (customerTagMap[email] || []).includes(tag);
       });
       const outstanding = tagInvs.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.balance || 0), 0);
       const cs = tagInvs.filter(i => i.invoiceDate && new Date(i.invoiceDate) >= thirtyDaysAgo).reduce((s, i) => s + (i.totalAmount || 0), 0);
       const tagDso = cs > 0 ? Math.round((outstanding / cs) * 30) : null;
       const customers = new Set(tagInvs.filter(i => i.status !== 'paid').map(i => i.customerEmail || i.customerName)).size;
       return { tag, outstanding, dso: tagDso, customers };
      }).filter(s => s.outstanding > 0 || s.customers > 0).sort((a, b) => b.outstanding - a.outstanding);

      // --- Insights access gate ---
      if (!hasInsightsAccess) {
       return (
        <div className="max-w-5xl animate-in fade-in duration-700">
         {/* Blurred preview behind the gate */}
         <div className="relative">
          <div className="blur-sm pointer-events-none select-none opacity-60 space-y-6">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
             { label: 'Days Sales Outstanding', sub: 'AR balance vs. last 30 days of credit sales' },
             { label: 'Collection Effectiveness', sub: '% of collectible AR actually collected' },
             { label: 'Cash Collection Forecast', sub: 'Outstanding AR by collection confidence' },
            ].map(c => (
             <div key={c.label} className={`${cardClass} space-y-3`}>
              <div>
               <p className={`text-xs font-bold uppercase tracking-wide ${t.textMuted}`}>{c.label}</p>
              </div>
              <div className="space-y-2.5 pt-1">
               <div className="h-11 rounded-xl bg-slate-100 border border-slate-200" />
               <div className="h-4 w-3/4 rounded-full bg-slate-100" />
               {c.label === 'Cash Collection Forecast' && (
                <>
                 <div className="h-10 rounded-xl bg-emerald-50 border border-emerald-100" />
                 <div className="h-10 rounded-xl bg-amber-50 border border-amber-100" />
                 <div className="h-10 rounded-xl bg-rose-50 border border-rose-100" />
                </>
               )}
              </div>
              <p className={`text-xs ${t.textMuted}`}>{c.sub}</p>
             </div>
            ))}
           </div>
          </div>

          {/* Gate overlay */}
          <div className="absolute inset-0 flex items-start justify-center pt-8 md:pt-12">
           <div className={`${t.bgCard} rounded-2xl border ${t.border} shadow-2xl p-8 max-w-md w-full mx-4 text-center`}>
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-slate-100 flex items-center justify-center">
             <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
             </svg>
            </div>
            <h2 style={{fontFamily:'var(--font-display)'}} className={`text-xl font-bold ${t.text} tracking-tight mb-2`}>Unlock AR Insights</h2>
            <p className={`text-sm ${t.textMuted} mb-2`}>
             Intelligence your QuickBooks account can't give you - DSO tracking, collection effectiveness, cash forecasting, and segment analysis in one place.
            </p>
            <p className={`text-[11px] ${t.textMuted} mb-6`}>
             Preview cards stay hidden until billing activates so financial metrics never show placeholder values.
            </p>
            <ul className={`text-left text-sm ${t.textMuted} space-y-2.5 mb-7`}>
             {[
              'DSO + CEI dashboard — know your collection health at a glance',
              'Segment filtering — see AR performance by customer type or tag',
              'Promise-to-Pay tracker — log commitments, get alerted on misses',
             ].map(feat => (
              <li key={feat} className="flex items-start gap-2.5">
               <span className="text-emerald-500 font-bold mt-0.5 shrink-0">✓</span>
               <span>{feat}</span>
              </li>
             ))}
            </ul>
            <button
             onClick={handleInsightsCheckout}
             disabled={insightsCheckoutLoading}
             style={{backgroundColor: '#22c55e'}}
             className="w-full py-4 rounded-xl text-sm font-bold text-white tracking-wide active:scale-95 transition-all disabled:opacity-60 shadow-none"
            >
             {insightsCheckoutLoading ? 'Loading...' : 'Add AR Insights →'}
            </button>
            <p className={`text-[11px] ${t.textMuted} mt-3`}>
             {isTrialActive ? `Free during your trial — ${trialDaysLeft} days left` : 'Cancel anytime from your billing portal'}
            </p>
           </div>
          </div>
         </div>
        </div>
       );
      }

      return (
       <div className="max-w-5xl space-y-8 animate-in fade-in duration-700">

        {/* Segment filter bar */}
        {allTags.length > 0 && (
         <div className={`${t.bgCard} rounded-2xl border ${t.border} px-5 py-4`}>
          <div className="flex items-center gap-3 flex-wrap">
           <p className={`text-[10px] font-bold uppercase tracking-wider ${t.textMuted} shrink-0`}>Filter by segment</p>
           {(['all', ...allTags] as string[]).map(tag => (
            <button
             key={tag}
             onClick={() => setInsightsSegmentFilter(tag)}
             className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold tracking-wide border transition-all ${insightsSegmentFilter === tag ? 'bg-slate-900 text-white border-slate-900' : `${t.bgInput} ${t.textMuted} ${t.border} hover:border-slate-400`}`}
            >
             {tag === 'all' ? 'All customers' : tag}
            </button>
           ))}
          </div>
          {insightsSegmentFilter !== 'all' && (
           <p className={`text-[11px] ${t.textMuted} mt-3`}>
            Showing <span className="font-semibold">{segmentInvoices.filter(i => i.status !== 'paid').length}</span> open invoices for <span className="font-semibold">{insightsSegmentFilter}</span> · CEI and Aging Trend always reflect full AR
           </p>
          )}
         </div>
        )}

        {/* Row 1: DSO | CEI | Cash Forecast */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
         {/* DSO */}
         <div className={cardClass}>
          <div className="flex items-center gap-3 mb-5">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><TrendingUp size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>Days Sales Outstanding</h3>
            <p className={`text-xs ${t.textMuted}`}>How fast you collect after invoicing</p>
           </div>
          </div>
          {dso !== null ? (
           <div>
            <p className={`text-5xl font-bold tracking-tight ${t.text}`}>{dso}<span className={`text-lg font-semibold ${t.textMuted} ml-2`}>days</span></p>
            <p className={`text-xs ${t.textMuted} mt-3`}>AR balance vs. last 30 days of credit sales</p>
            <p className="text-xs text-slate-500 mt-2 font-medium">{dsoLabel}</p>
           </div>
          ) : (
           <p className={`text-sm ${t.textMuted} py-4`}>Not enough invoice data yet — sync more invoices to calculate.</p>
          )}
         </div>

         {/* CEI */}
         <div className={cardClass}>
          <div className="flex items-center gap-3 mb-5">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><BarChart3 size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>Collection Effectiveness</h3>
            <p className={`text-xs ${t.textMuted}`}>% of collectible AR actually collected</p>
           </div>
          </div>
          {cei !== null ? (
           <div>
            <div className="flex items-end gap-3">
             <p className={`text-5xl font-bold tracking-tight ${ceiColor}`}>{cei}<span className="text-lg font-semibold ml-0.5">%</span></p>
             {ceiDelta !== null && (
              <span className={`mb-1 text-xs font-semibold px-2 py-1 rounded-lg ${ceiDelta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
               {ceiDelta >= 0 ? '↑' : '↓'} {Math.abs(ceiDelta)}pts
              </span>
             )}
            </div>
            <p className={`text-xs ${t.textMuted} mt-3`}>You collected {cei}% of what was collectible this period</p>
            <p className="text-xs text-slate-500 mt-2 font-medium">{ceiLabel}</p>
           </div>
          ) : (
           <p className={`text-sm ${t.textMuted} py-4`}>Needs 2+ weekly snapshots — check back next week.</p>
          )}
         </div>

         {/* Cash Collection Forecast */}
         <div className={cardClass}>
          <div className="flex items-center gap-3 mb-5">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><CalendarIcon size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>Cash Collection Forecast</h3>
            <p className={`text-xs ${t.textMuted}`}>Outstanding AR by collection confidence</p>
           </div>
          </div>
          {fcastTotal > 0 ? (
           <div className="space-y-2.5">
            {[
             { label: 'High confidence', amount: fcastHigh, style: 'bg-emerald-50 border-emerald-200 text-emerald-700', sub: 'Not yet due' },
             { label: 'Medium', amount: fcastMedium, style: 'bg-amber-50 border-amber-200 text-amber-700', sub: '1-30d overdue, reminder opened' },
             { label: 'At risk', amount: fcastAtRisk, style: 'bg-rose-50 border-rose-200 text-rose-700', sub: '31+ days or no engagement' },
            ].map(b => (
             <div key={b.label} className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${b.style}`}>
              <div>
               <p className="text-[10px] font-bold uppercase tracking-wide">{b.label}</p>
               <p className="text-[10px] opacity-70 mt-0.5">{b.sub}</p>
              </div>
              <p className="text-base font-bold tracking-tight">{fmt(b.amount)}</p>
             </div>
            ))}
           </div>
          ) : (
           <p className={`text-sm ${t.textMuted} py-4`}>No outstanding invoices to forecast.</p>
          )}
         </div>
        </div>

        {/* AR Aging Trend */}
        <div className={cardClass}>
         <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><TrendingUp size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>AR Aging Trend</h3>
            <p className={`text-xs ${t.textMuted}`}>Weekly snapshots of aging buckets — a rising 90+ line signals a collections problem</p>
           </div>
          </div>
          {insightsSegmentFilter !== 'all' && (
           <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-lg bg-slate-100 ${t.textMuted} shrink-0`}>Full AR</span>
          )}
         </div>
         {trendData.length >= 2 ? (
          <div className="w-full min-w-0">
           <ResponsiveContainer width="100%" height={260} minWidth={0}>
            <LineChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
             <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
             <XAxis dataKey="week" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }} />
             <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 600 }} tickFormatter={(v: number) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
             <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', fontSize: 12 }} formatter={(v: any) => [fmt(Number(v)), '']} />
             <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
             <Line type="monotone" dataKey="Current" stroke="#10b981" strokeWidth={2} dot={false} />
             <Line type="monotone" dataKey="1-30d" stroke="#f59e0b" strokeWidth={2} dot={false} />
             <Line type="monotone" dataKey="31-60d" stroke="#f97316" strokeWidth={2} dot={false} />
             <Line type="monotone" dataKey="61-90d" stroke="#ef4444" strokeWidth={2} dot={false} />
             <Line type="monotone" dataKey="90+d" stroke="#991b1b" strokeWidth={2.5} dot={false} />
            </LineChart>
           </ResponsiveContainer>
          </div>
         ) : (
          <div className="py-12 text-center">
           <p className={`text-sm font-semibold ${t.textMuted}`}>Collecting trend data…</p>
           <p className={`text-xs ${t.textMuted} mt-1`}>First snapshot runs Sunday. Check back next week to see your aging trend line.</p>
          </div>
         )}
        </div>

        {/* Customer Payer Score */}
        <div className={cardClass}>
         <div className="flex items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><Users size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>Customer Payer Score</h3>
            <p className={`text-xs ${t.textMuted}`}>Rule-based classification from invoice and reminder history</p>
           </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
           {(['Good', 'Slow', 'At-Risk', 'Chronic'] as const).map(s => (
            <span key={s} className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${scoreStyle(s)}`}>{s}</span>
           ))}
          </div>
         </div>
         {payerRows.length > 0 ? (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
           <table className="w-full text-xs">
            <thead>
             <tr className={`${t.bgInput} border-b ${t.border}`}>
              {['Customer', 'Score', 'Outstanding', 'Days Overdue', 'Reminder Opened'].map(h => (
               <th key={h} className={`px-4 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>{h}</th>
              ))}
             </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
             {payerRows.map(r => (
              <tr key={r.key} className="hover:bg-slate-50/60 transition-colors">
               <td className={`px-4 py-3 font-medium ${t.text}`}>{r.name}</td>
               <td className="px-4 py-3">
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${scoreStyle(r.score)}`}>{r.score}</span>
               </td>
               <td className={`px-4 py-3 font-semibold ${r.totalBal > 0 ? 'text-rose-600' : t.textMuted}`}>{fmt(r.totalBal)}</td>
               <td className={`px-4 py-3 ${r.maxDays > 60 ? 'text-rose-600 font-semibold' : r.maxDays > 30 ? 'text-orange-500' : t.textMuted}`}>{r.maxDays > 0 ? `${r.maxDays}d` : '—'}</td>
               <td className="px-4 py-3">
                {r.engaged ? <span className="text-emerald-600 font-semibold">✓ Yes</span> : <span className={t.textMuted}>No</span>}
               </td>
              </tr>
             ))}
            </tbody>
           </table>
          </div>
         ) : (
          <p className={`text-sm ${t.textMuted} text-center py-8`}>{insightsSegmentFilter !== 'all' ? `No outstanding invoices for segment "${insightsSegmentFilter}".` : 'No customers with outstanding invoices yet.'}</p>
         )}
        </div>

        {/* Broken Promises */}
        {(() => {
         const brokenPromises = collectionNotes
          .filter(n => n.ptpDate && new Date(n.ptpDate) < now)
          .map(n => {
           const inv = invoices.find(i => i.invoiceId === n.invoiceId);
           if (!inv || inv.status === 'paid') return null;
           if (insightsSegmentFilter !== 'all') {
            const email = (inv.customerEmail || '').toLowerCase();
            if (!(customerTagMap[email] || []).includes(insightsSegmentFilter)) return null;
           }
           const daysMissed = Math.floor((now.getTime() - new Date(n.ptpDate!).getTime()) / 86400000);
           return { note: n, inv, daysMissed };
          })
          .filter(Boolean)
          .sort((a, b) => b!.daysMissed - a!.daysMissed) as { note: CollectionNote; inv: Invoice; daysMissed: number }[];

         if (brokenPromises.length === 0) return null;
         return (
          <div className={cardClass}>
           <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-rose-50 rounded-xl text-rose-500"><Clock size={18} /></div>
            <div>
             <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>Broken Promises</h3>
             <p className={`text-xs ${t.textMuted}`}>{brokenPromises.length} invoice{brokenPromises.length !== 1 ? 's' : ''} past their Promise-to-Pay date and still unpaid</p>
            </div>
           </div>
           <div className="overflow-x-auto rounded-xl border border-rose-100">
            <table className="w-full text-xs">
             <thead>
              <tr className="bg-rose-50 border-b border-rose-100">
               {['Customer', 'Invoice #', 'Balance', 'PTP Date', 'Days Missed', 'Note'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-rose-600 uppercase tracking-wide text-[10px]">{h}</th>
               ))}
              </tr>
             </thead>
             <tbody className="divide-y divide-rose-50">
              {brokenPromises.map(({ note: n, inv, daysMissed }) => (
               <tr key={n.id} className="hover:bg-rose-50/40 transition-colors">
                <td className={`px-4 py-3 font-medium ${t.text}`}>{inv.customerName}</td>
                <td className={`px-4 py-3 ${t.textMuted}`}>{inv.docNumber || inv.invoiceId}</td>
                <td className="px-4 py-3 font-semibold text-rose-600">{fmt(inv.balance)}</td>
                <td className={`px-4 py-3 ${t.textMuted}`}>{new Date(n.ptpDate!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                <td className="px-4 py-3 font-bold text-rose-700">{daysMissed}d ago</td>
                <td className={`px-4 py-3 ${t.textMuted} max-w-[200px] truncate`} title={n.note}>{n.note}</td>
               </tr>
              ))}
             </tbody>
            </table>
           </div>
          </div>
         );
        })()}

        {/* Segment Summary — visible only in All view when tags exist */}
        {insightsSegmentFilter === 'all' && segmentSummary.length > 0 && (
         <div className={cardClass}>
          <div className="flex items-center gap-3 mb-5">
           <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500"><Users size={18} /></div>
           <div>
            <h3 style={{fontFamily:"var(--font-display)"}} className={`text-base font-bold ${t.text} tracking-tight`}>AR by Customer Segment</h3>
            <p className={`text-xs ${t.textMuted}`}>Outstanding AR and DSO broken down by customer tag — click a segment above to drill in</p>
           </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-100">
           <table className="w-full text-xs">
            <thead>
             <tr className={`${t.bgInput} border-b ${t.border}`}>
             {['Segment', 'Customers', 'Outstanding AR', 'DSO'].map(h => (
              <th key={h} className={`px-4 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>{h}</th>
             ))}
             </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
             {segmentSummary.map(row => (
              <tr
               key={row.tag}
               className="hover:bg-slate-50/60 transition-colors cursor-pointer"
               onClick={() => setInsightsSegmentFilter(row.tag)}
              >
               <td className={`px-4 py-3 font-semibold ${t.text}`}>
                <span className="inline-flex items-center gap-1.5">
                 <span className="w-2 h-2 rounded-full bg-slate-400 inline-block" />
                 {row.tag}
                </span>
               </td>
               <td className={`px-4 py-3 ${t.textMuted}`}>{row.customers}</td>
               <td className={`px-4 py-3 font-semibold ${row.outstanding > 0 ? 'text-rose-600' : t.textMuted}`}>{fmt(row.outstanding)}</td>
               <td className={`px-4 py-3 ${row.dso !== null && row.dso > 45 ? 'text-rose-600 font-semibold' : row.dso !== null && row.dso > 30 ? 'text-amber-600' : t.textMuted}`}>
                {row.dso !== null ? `${row.dso}d` : '—'}
               </td>
              </tr>
             ))}
            </tbody>
           </table>
          </div>
         </div>
        )}
       </div>
      );
     })()}

     {/* REPORTS (Analytics sub-tab) */}
     {activeTab === 'analytics' && analyticsSubTab === 'reports' && (() => {
      const now = new Date();
      const periodMs = reportPeriod > 0 ? reportPeriod * 24 * 60 * 60 * 1000 : Infinity;
      const periodStart = reportPeriod > 0 ? new Date(now.getTime() - periodMs) : new Date(0);

      const toDate = (v: any): Date | null => {
       if (!v) return null;
       if (v?.toDate) return v.toDate();
       if (v?.seconds) return new Date(v.seconds * 1000);
       const d = new Date(v);
       return isNaN(d.getTime()) ? null : d;
      };

      const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
      const pct = (n: number, d: number) => d === 0 ? '—' : `${Math.round((n / d) * 100)}%`;

      // --- Period-filtered logs ---
      const periodLogs = logs.filter(l => {
       const d = toDate(l.createdAt);
       return d ? d >= periodStart : false;
      });

      const sentLogs = periodLogs.filter(l => !['blocked', 'opted_out', 'dispute_hold', 'frequency_cap', 'dedup'].includes(l.deliveryStatus));
      const deliveredLogs = periodLogs.filter(l => ['delivered', 'opened', 'clicked'].includes(l.deliveryStatus));
      const openedLogs = periodLogs.filter(l => ['opened', 'clicked'].includes(l.deliveryStatus));

      // Paid invoices (period: paid invoices whose lastSynced is in period — proxy for when payment was detected)
      const paidInvoices = invoices.filter(i => i.status === 'paid');
      const overdueInvoices = invoices.filter(i => i.status === 'overdue');
      const openInvoices = invoices.filter(i => i.status === 'open');
      const totalOutstanding = overdueInvoices.reduce((s, i) => s + (i.balance || 0), 0);
      const totalPaid = paidInvoices.reduce((s, i) => s + (i.totalAmount || 0), 0);
      const recoveryRate = paidInvoices.length + overdueInvoices.length > 0
       ? paidInvoices.length / (paidInvoices.length + overdueInvoices.length)
       : 0;

      // --- Aging buckets ---
      const agingBuckets = { current: [] as Invoice[], d30: [] as Invoice[], d60: [] as Invoice[], d90: [] as Invoice[], d90plus: [] as Invoice[] };
      for (const inv of invoices.filter(i => i.status !== 'paid')) {
       const due = inv.dueDate ? new Date(inv.dueDate) : null;
       if (!due) continue;
       const days = Math.floor((now.getTime() - due.getTime()) / 86400000);
       if (days <= 0) agingBuckets.current.push(inv);
       else if (days <= 30) agingBuckets.d30.push(inv);
       else if (days <= 60) agingBuckets.d60.push(inv);
       else if (days <= 90) agingBuckets.d90.push(inv);
       else agingBuckets.d90plus.push(inv);
      }
      const agingSum = (arr: Invoice[]) => arr.reduce((s, i) => s + (i.balance || 0), 0);

      // --- Per-connection stats ---
      const connMap: Record<string, { name: string; sent: number; opened: number; paid: number; outstanding: number }> = {};
      for (const c of connections) {
       connMap[c.realmId] = { name: c.companyName || c.realmId, sent: 0, opened: 0, paid: 0, outstanding: 0 };
      }
      for (const l of sentLogs) {
       if (!connMap[l.realmId]) connMap[l.realmId] = { name: l.realmId, sent: 0, opened: 0, paid: 0, outstanding: 0 };
       connMap[l.realmId].sent++;
       if (['opened', 'clicked'].includes(l.deliveryStatus)) connMap[l.realmId].opened++;
      }
      for (const i of invoices) {
       if (!connMap[i.realmId]) connMap[i.realmId] = { name: i.realmId, sent: 0, opened: 0, paid: 0, outstanding: 0 };
       if (i.status === 'paid') connMap[i.realmId].paid++;
       else connMap[i.realmId].outstanding += i.balance || 0;
      }
      const connRows = Object.entries(connMap).filter(([, v]) => v.sent > 0 || v.paid > 0 || v.outstanding > 0);

      // --- Tier performance ---
      const tierMap: Record<string, { sent: number; opened: number }> = {};
      for (const l of sentLogs) {
       const tier = l.templateSent || 'Unknown';
       if (!tierMap[tier]) tierMap[tier] = { sent: 0, opened: 0 };
       tierMap[tier].sent++;
       if (['opened', 'clicked'].includes(l.deliveryStatus)) tierMap[tier].opened++;
      }
      const tierRows = Object.entries(tierMap).sort((a, b) => b[1].sent - a[1].sent);

      // --- CSV export helpers ---
      const exportCsv = (filename: string, headers: string[], rows: (string | number)[][][]) => {
       const lines = [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell[0]).replace(/"/g, '""')}"`).join(','))];
       const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
       const url = URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url; a.download = filename; a.click();
       URL.revokeObjectURL(url);
      };

      const exportAgingCsv = () => exportCsv(`aging-report-${now.toISOString().slice(0,10)}.csv`,
       ['Customer', 'Invoice #', 'Realm', 'Due Date', 'Balance', 'Aging Bucket'],
       [...agingBuckets.current, ...agingBuckets.d30, ...agingBuckets.d60, ...agingBuckets.d90, ...agingBuckets.d90plus].map(i => {
        const due = i.dueDate ? new Date(i.dueDate) : null;
        const days = due ? Math.floor((now.getTime() - due.getTime()) / 86400000) : 0;
        const bucket = days <= 0 ? 'Current' : days <= 30 ? '1-30 days' : days <= 60 ? '31-60 days' : days <= 90 ? '61-90 days' : '90+ days';
        return [[i.customerName], [i.docNumber || i.invoiceId], [connMap[i.realmId]?.name || i.realmId], [i.dueDate || ''], [i.balance], [bucket]];
       })
      );

      const exportConnCsv = () => exportCsv(`connection-report-${now.toISOString().slice(0,10)}.csv`,
       ['Connection', 'Reminders Sent', 'Opened', 'Open Rate', 'Invoices Paid', 'Outstanding Balance'],
       connRows.map(([, v]) => [[v.name], [v.sent], [v.opened], [v.sent > 0 ? `${Math.round(v.opened/v.sent*100)}%` : '0%'], [v.paid], [v.outstanding.toFixed(2)]])
      );

      const cardClass = `${t.bgCard} rounded-2xl border ${t.border} p-6`;
      const sectionHead = (title: string, sub: string, icon: React.ReactNode, onExport?: () => void) => (
       <div className="flex items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
         <div className="p-2.5 bg-slate-50 rounded-xl text-slate-500">{icon}</div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-lg font-bold ${t.text} tracking-tight`}>{title}</h3>
          <p className={`text-xs ${t.textMuted}`}>{sub}</p>
         </div>
        </div>
        {onExport && (
         <button onClick={onExport} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-slate-900 text-white rounded-xl hover:bg-slate-800 active:scale-95 transition-all">
          <Download size={13} /> Export CSV
         </button>
        )}
       </div>
      );

      return (
       <div className="max-w-5xl space-y-10 animate-in fade-in duration-700">
        {/* Period selector */}
        <div className="flex items-center gap-3 flex-wrap">
         <span className={`text-xs font-semibold ${t.textMuted} uppercase tracking-wide`}>Period:</span>
         {([30, 60, 90, 365, 0] as const).map(p => (
          <button key={p} onClick={() => setReportPeriod(p)}
           className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all border ${reportPeriod === p ? 'bg-slate-900 text-white border-slate-900' : `${t.bgInput} ${t.textSec} ${t.border} hover:border-slate-400`}`}>
           {p === 0 ? 'All time' : `Last ${p} days`}
          </button>
         ))}
        </div>

        {/* Recovery Overview */}
        <div className={`${cardClass}`}>
         {sectionHead('AR Recovery Overview', `Reminders sent in the last ${reportPeriod > 0 ? `${reportPeriod} days` : 'all time'}`, <TrendingUp size={18} />)}
         <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
           { label: 'Reminders Sent', value: sentLogs.length.toLocaleString(), sub: `${periodLogs.length} total incl. blocked` },
           { label: 'Delivered', value: deliveredLogs.length.toLocaleString(), sub: pct(deliveredLogs.length, sentLogs.length) + ' delivery rate' },
           { label: 'Opened / Clicked', value: openedLogs.length.toLocaleString(), sub: pct(openedLogs.length, deliveredLogs.length) + ' open rate' },
           { label: 'Recovery Rate', value: pct(paidInvoices.length, paidInvoices.length + overdueInvoices.length), sub: `${paidInvoices.length} paid, ${overdueInvoices.length} overdue` },
          ].map(s => (
           <div key={s.label} className={`p-4 rounded-xl ${t.bgInput} border ${t.border}`}>
            <p className={`text-[10px] uppercase tracking-wide font-semibold ${t.textMuted} mb-1`}>{s.label}</p>
            <p className={`text-2xl font-bold ${t.text} tracking-tight`}>{s.value}</p>
            <p className={`text-[11px] ${t.textMuted} mt-0.5`}>{s.sub}</p>
           </div>
          ))}
         </div>
         <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          {[
           { label: 'Total Outstanding', value: fmt(totalOutstanding), sub: `${overdueInvoices.length + openInvoices.length} open invoices` },
           { label: 'Total Collected (paid)', value: fmt(totalPaid), sub: `${paidInvoices.length} invoices paid` },
           { label: 'Avg Outstanding / Invoice', value: overdueInvoices.length > 0 ? fmt(totalOutstanding / overdueInvoices.length) : '—', sub: 'overdue invoices only' },
          ].map(s => (
           <div key={s.label} className={`p-4 rounded-xl ${t.bgInput} border ${t.border}`}>
            <p className={`text-[10px] uppercase tracking-wide font-semibold ${t.textMuted} mb-1`}>{s.label}</p>
            <p className={`text-2xl font-bold ${t.text} tracking-tight`}>{s.value}</p>
            <p className={`text-[11px] ${t.textMuted} mt-0.5`}>{s.sub}</p>
           </div>
          ))}
         </div>
        </div>

        {/* Aging Report */}
        <div className={`${cardClass}`}>
         {sectionHead('Invoice Aging', 'Outstanding balances by how long they\'ve been overdue', <CalendarIcon size={18} />, exportAgingCsv)}
         <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
           { label: 'Current', count: agingBuckets.current.length, amount: agingSum(agingBuckets.current), color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
           { label: '1–30 days', count: agingBuckets.d30.length, amount: agingSum(agingBuckets.d30), color: 'bg-amber-50 border-amber-200 text-amber-700' },
           { label: '31–60 days', count: agingBuckets.d60.length, amount: agingSum(agingBuckets.d60), color: 'bg-orange-50 border-orange-200 text-orange-700' },
           { label: '61–90 days', count: agingBuckets.d90.length, amount: agingSum(agingBuckets.d90), color: 'bg-rose-50 border-rose-200 text-rose-700' },
           { label: '90+ days', count: agingBuckets.d90plus.length, amount: agingSum(agingBuckets.d90plus), color: 'bg-rose-100 border-rose-300 text-rose-800' },
          ].map(b => (
           <div key={b.label} className={`p-4 rounded-xl border ${b.color}`}>
            <p className="text-[10px] uppercase tracking-wide font-bold mb-1">{b.label}</p>
            <p className="text-xl font-bold tracking-tight">{fmt(b.amount)}</p>
            <p className="text-[11px] mt-0.5 opacity-80">{b.count} invoice{b.count !== 1 ? 's' : ''}</p>
           </div>
          ))}
         </div>
         {/* Top overdue invoices */}
         {[...agingBuckets.d30, ...agingBuckets.d60, ...agingBuckets.d90, ...agingBuckets.d90plus]
          .sort((a, b) => (b.balance || 0) - (a.balance || 0))
          .slice(0, 10)
          .length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-100">
           <table className="w-full text-xs">
            <thead>
             <tr className={`${t.bgInput} border-b ${t.border}`}>
              {['Customer', 'Invoice #', 'Connection', 'Due Date', 'Days Past Due', 'Balance'].map(h => (
               <th key={h} className={`px-4 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>{h}</th>
              ))}
             </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
             {[...agingBuckets.d30, ...agingBuckets.d60, ...agingBuckets.d90, ...agingBuckets.d90plus]
              .sort((a, b) => (b.balance || 0) - (a.balance || 0))
              .slice(0, 10)
              .map(inv => {
               const due = inv.dueDate ? new Date(inv.dueDate) : null;
               const days = due ? Math.floor((now.getTime() - due.getTime()) / 86400000) : 0;
               const severity = days > 90 ? 'text-rose-700 font-bold' : days > 60 ? 'text-rose-600' : days > 30 ? 'text-orange-600' : 'text-amber-600';
               return (
                <tr key={inv.id} className="hover:bg-slate-50/60 transition-colors">
                 <td className={`px-4 py-3 font-medium ${t.text}`}>{inv.customerName}</td>
                 <td className={`px-4 py-3 ${t.textMuted}`}>{inv.docNumber || inv.invoiceId}</td>
                 <td className={`px-4 py-3 ${t.textMuted}`}>{connMap[inv.realmId]?.name || inv.realmId}</td>
                 <td className={`px-4 py-3 ${t.textMuted}`}>{inv.dueDate}</td>
                 <td className={`px-4 py-3 ${severity}`}>{days} days</td>
                 <td className={`px-4 py-3 font-semibold ${t.text}`}>{fmt(inv.balance)}</td>
                </tr>
               );
              })}
            </tbody>
           </table>
          </div>
         )}
         {agingBuckets.d30.length + agingBuckets.d60.length + agingBuckets.d90.length + agingBuckets.d90plus.length === 0 && (
          <p className={`text-sm ${t.textMuted} text-center py-8`}>No overdue invoices — great work!</p>
         )}
        </div>

        {/* Per-Connection Report */}
        {connRows.length > 0 && (
         <div className={`${cardClass}`}>
          {sectionHead('Per-Connection Performance', 'Reminders and recovery broken down by QuickBooks company', <Database size={18} />, exportConnCsv)}
          <div className="overflow-x-auto rounded-xl border border-slate-100">
           <table className="w-full text-xs">
            <thead>
             <tr className={`${t.bgInput} border-b ${t.border}`}>
              {['Connection', 'Sent', 'Opened', 'Open Rate', 'Invoices Paid', 'Outstanding'].map(h => (
               <th key={h} className={`px-4 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>{h}</th>
              ))}
             </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
             {connRows.map(([realm, v]) => (
              <tr key={realm} className="hover:bg-slate-50/60 transition-colors">
               <td className={`px-4 py-3 font-semibold ${t.text}`}>{v.name}</td>
               <td className={`px-4 py-3 ${t.textMuted}`}>{v.sent}</td>
               <td className={`px-4 py-3 ${t.textMuted}`}>{v.opened}</td>
               <td className={`px-4 py-3 font-medium ${t.text}`}>{pct(v.opened, v.sent)}</td>
               <td className={`px-4 py-3 ${t.textMuted}`}>{v.paid}</td>
               <td className={`px-4 py-3 font-semibold ${v.outstanding > 0 ? 'text-rose-600' : t.textMuted}`}>{fmt(v.outstanding)}</td>
              </tr>
             ))}
            </tbody>
           </table>
          </div>
         </div>
        )}

        {/* Dunning Tier Performance */}
        {tierRows.length > 0 && (
         <div className={`${cardClass}`}>
          {sectionHead('Dunning Tier Performance', 'Engagement rates per template over the selected period', <BarChart3 size={18} />)}
          <div className="overflow-x-auto rounded-xl border border-slate-100">
           <table className="w-full text-xs">
            <thead>
             <tr className={`${t.bgInput} border-b ${t.border}`}>
              {['Template / Tier', 'Sent', 'Opened / Clicked', 'Open Rate', 'Engagement'].map(h => (
               <th key={h} className={`px-4 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>{h}</th>
              ))}
             </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
             {tierRows.map(([tier, v]) => {
              const rate = v.sent > 0 ? v.opened / v.sent : 0;
              return (
               <tr key={tier} className="hover:bg-slate-50/60 transition-colors">
                <td className={`px-4 py-3 font-semibold ${t.text}`}>{tier}</td>
                <td className={`px-4 py-3 ${t.textMuted}`}>{v.sent}</td>
                <td className={`px-4 py-3 ${t.textMuted}`}>{v.opened}</td>
                <td className={`px-4 py-3 font-medium ${t.text}`}>{pct(v.opened, v.sent)}</td>
                <td className="px-4 py-3 w-40">
                 <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                   <div className="h-full bg-sky-400 rounded-full" style={{ width: `${Math.round(rate * 100)}%` }} />
                  </div>
                  <span className={`text-[11px] ${t.textMuted} w-8 text-right`}>{Math.round(rate * 100)}%</span>
                 </div>
                </td>
               </tr>
              );
             })}
            </tbody>
           </table>
          </div>
         </div>
        )}

        {logs.length === 0 && invoices.length === 0 && (
         <div className={`${cardClass} text-center py-16`}>
          <TrendingUp size={40} className={`mx-auto mb-4 ${t.textMuted} opacity-30`} />
          <p className={`text-sm font-semibold ${t.textMuted}`}>No data yet</p>
          <p className={`text-xs ${t.textMuted} mt-1`}>Run your first dunning sync to start seeing reports here.</p>
         </div>
        )}
       </div>
      );
     })()}

     {/* COMPLIANCE TAB */}
     {activeTab === 'compliance' && (
      <div className="max-w-4xl space-y-10 animate-in fade-in duration-700 text-left shadow-none">

       {/* Active Disputes */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-amber-50 text-amber-600 rounded-xl"><FileText size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Invoice Disputes</h3>
          <p className={`text-xs ${t.textMuted}`}>Automated emails are paused while a dispute is active</p>
         </div>
        </div>
        {disputes.length === 0 ? (
         <p className={`text-sm ${t.textMuted} py-6 text-center`}>No disputes filed. Disputes are created when a customer clicks the dispute link in a dunning email.</p>
        ) : (
         <div className="space-y-3">
          {disputes.map((d: any) => (
           <div key={d.id} className={`flex items-center justify-between p-5 rounded-xl border ${d.status === 'resolved' ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
            <div className="flex-1">
             <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${d.status === 'resolved' ? 'bg-emerald-200 text-emerald-800' : 'bg-amber-200 text-amber-800'}`}>
               {d.status === 'resolved' ? 'Resolved' : 'Pending Review'}
              </span>
              <span className="text-xs text-slate-400">Realm: {d.realmId}</span>
             </div>
             <p className="text-sm font-semibold text-slate-900">Invoice: {d.invoiceId}</p>
             <p className="text-xs text-slate-500">{d.email} &middot; Filed {d.disputedAt?.toDate ? d.disputedAt.toDate().toLocaleDateString() : 'recently'}</p>
            </div>
            {d.status !== 'resolved' && (!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && (
             <button onClick={() => handleResolveDispute(d.id)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 active:scale-95 transition-all flex items-center gap-2 shadow-none">
              <CheckCircle2 size={14} /> Resolve
             </button>
            )}
           </div>
          ))}
         </div>
        )}
       </div>

       {/* Email Suppressions (Opt-Outs) */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-rose-50 text-rose-500 rounded-xl"><UserX size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Email Suppressions</h3>
          <p className="text-xs text-slate-400">Recipients who have unsubscribed — no automated emails will be sent to these addresses</p>
         </div>
        </div>
        {optOuts.length === 0 ? (
         <p className={`text-sm ${t.textMuted} py-6 text-center`}>No unsubscribes recorded yet.</p>
        ) : (
         <div className="space-y-2">
          {optOuts.map((o: any) => (
           <div key={o.id} className={`flex items-center justify-between p-4 ${t.bgInput} border ${t.borderInput} rounded-xl`}>
            <div>
             <p className={`text-sm font-semibold ${t.text}`}>{o.email}</p>
             <p className={`text-xs ${t.textMuted}`}>
              {o.source === 'gdpr_erasure' ? 'GDPR erasure' : 'Unsubscribed'} &middot;
              {o.optedOutAt?.toDate ? ` ${o.optedOutAt.toDate().toLocaleDateString()}` : ' recently'} &middot;
              Realm: {o.realmId || 'all'}
             </p>
            </div>
            {!isViewingMemberWorkspace && <button onClick={() => handleRemoveOptOut(o.email)} className="px-4 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition-all shadow-none">Remove</button>}
           </div>
          ))}
         </div>
        )}
       </div>

       {/* Audit Log */}
       {(() => {
        const AUDIT_CATEGORIES: { label: string; key: string; actions: string[] }[] = [
         { label: 'All', key: 'all', actions: [] },
         { label: 'Auth', key: 'auth', actions: ['user_login', 'password_changed', '2fa_enabled', '2fa_disabled', 'user_signout_all'] },
         { label: 'Templates', key: 'templates', actions: ['template_created', 'template_updated', 'template_deleted'] },
         { label: 'Dunning', key: 'dunning', actions: ['invoice_snoozed', 'invoice_unsnoozed', 'customer_paused', 'customer_resumed', 'dispute_resolved'] },
         { label: 'Account', key: 'account', actions: ['profile_updated', 'sender_updated', 'webhook_created', 'webhook_deleted', 'optout_removed'] },
         { label: 'Data', key: 'data', actions: ['gdpr_export', 'gdpr_erasure', 'qbo_disconnected'] },
        ];
        const [auditCat, setAuditCat] = [auditSearch.startsWith('__cat:') ? auditSearch.slice(6) : 'all', (c: string) => setAuditSearch(c === 'all' ? '' : `__cat:${c}`)];
        const searchTerm = auditSearch.startsWith('__cat:') ? '' : auditSearch;
        const activeCat = AUDIT_CATEGORIES.find(c => c.key === auditCat) || AUDIT_CATEGORIES[0];
        const filtered = auditLog.filter(e => {
         const matchesCat = activeCat.key === 'all' || activeCat.actions.includes(e.action);
         const matchesSearch = !searchTerm.trim() || e.details.toLowerCase().includes(searchTerm.toLowerCase()) || e.action.toLowerCase().includes(searchTerm.toLowerCase()) || e.actor.toLowerCase().includes(searchTerm.toLowerCase());
         return matchesCat && matchesSearch;
        });
        const ACTION_COLOR: Record<string, string> = {
         user_login: 'bg-emerald-50 text-emerald-700',
         password_changed: 'bg-amber-50 text-amber-700',
         '2fa_enabled': 'bg-cyan-50 text-cyan-700',
         '2fa_disabled': 'bg-rose-50 text-rose-700',
         user_signout_all: 'bg-amber-50 text-amber-700',
         template_created: 'bg-violet-50 text-violet-700',
         template_updated: 'bg-violet-50 text-violet-600',
         template_deleted: 'bg-rose-50 text-rose-700',
         invoice_snoozed: 'bg-amber-50 text-amber-700',
         invoice_unsnoozed: 'bg-slate-50 text-slate-600',
         customer_paused: 'bg-orange-50 text-orange-700',
         customer_resumed: 'bg-emerald-50 text-emerald-700',
         dispute_resolved: 'bg-emerald-50 text-emerald-700',
         profile_updated: 'bg-sky-50 text-sky-700',
         sender_updated: 'bg-sky-50 text-sky-600',
         webhook_created: 'bg-violet-50 text-violet-700',
         webhook_deleted: 'bg-rose-50 text-rose-600',
         optout_removed: 'bg-amber-50 text-amber-700',
         gdpr_export: 'bg-blue-50 text-blue-700',
         gdpr_erasure: 'bg-rose-50 text-rose-700',
         qbo_disconnected: 'bg-orange-50 text-orange-700',
        };
        const exportCsv = () => {
         const rows = [['Timestamp', 'Actor', 'Action', 'Details'].join(','), ...filtered.map(e => [
          e.createdAt?.toDate ? e.createdAt.toDate().toISOString() : '',
          `"${e.actor}"`, `"${e.action}"`, `"${e.details.replace(/"/g, '""')}"`
         ].join(','))];
         const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
         const url = URL.createObjectURL(blob);
         const a = document.createElement('a'); a.href = url; a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`; a.click();
         URL.revokeObjectURL(url);
        };
        return (
         <div className={`${panelClass} p-10`}>
          <div className="flex items-center justify-between gap-4 mb-6">
           <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-50 text-slate-500 rounded-xl"><FileText size={20} /></div>
            <div>
             <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Audit Log</h3>
             <p className={`text-xs ${t.textMuted}`}>All account actions — settings changes, logins, exports, dunning controls</p>
            </div>
           </div>
           <button onClick={exportCsv} className={`shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold tracking-wide ${t.bgInput} border ${t.borderInput} ${t.textSec}`}>
            <Download size={14} /> Export CSV
           </button>
          </div>
          {/* Category filter */}
          <div className="flex flex-wrap gap-1.5 mb-4">
           {AUDIT_CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => setAuditCat(cat.key)}
             className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border ${auditCat === cat.key ? 'bg-slate-900 text-white border-slate-900' : `${t.bgInput} ${t.textMuted} ${t.border} hover:border-slate-400`}`}>
             {cat.label}
             {cat.key !== 'all' && <span className="ml-1.5 opacity-60">{auditLog.filter(e => cat.actions.includes(e.action)).length}</span>}
            </button>
           ))}
          </div>
          {/* Search */}
          <div className="relative mb-4">
           <Search size={16} className={`absolute left-4 top-1/2 -translate-y-1/2 ${t.textMuted}`} />
           <input type="text" placeholder="Search by action, detail, or actor…"
            value={searchTerm}
            onChange={e => setAuditSearch(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 ${t.bgInput} border ${t.borderInput} rounded-xl text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`} />
          </div>
          <div className={`rounded-2xl border ${t.border} overflow-hidden`}>
           {auditLog.length === 0 ? (
            <div className="text-center py-12">
             <FileText size={32} className="mx-auto mb-3 text-slate-200" />
             <p className="text-xs font-semibold uppercase text-slate-400 tracking-wide">No audit events yet</p>
             <p className={`text-xs ${t.textMuted} mt-1`}>Actions will appear here as you use the app</p>
            </div>
           ) : filtered.length === 0 ? (
            <div className="text-center py-10"><p className="text-xs text-slate-400">No matching events</p></div>
           ) : (
            <div className="overflow-x-auto">
             <table className="w-full text-left text-sm min-w-[700px]">
              <thead><tr className={`border-b ${t.border} ${t.bgInput}`}>
               <th className="px-5 py-3.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">Time</th>
               <th className="px-5 py-3.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Actor</th>
               <th className="px-5 py-3.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Action</th>
               <th className="px-5 py-3.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Details</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
               {filtered.slice(0, 250).map(e => {
                const ts = e.createdAt?.toDate ? e.createdAt.toDate() : e.createdAt ? new Date(e.createdAt) : null;
                const colorClass = ACTION_COLOR[e.action] || 'bg-slate-100 text-slate-600';
                return (
                 <tr key={e.id} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3.5 whitespace-nowrap">
                   <p className={`text-xs font-medium ${t.text}`}>{ts ? ts.toLocaleDateString() : '—'}</p>
                   <p className={`text-[10px] ${t.textMuted}`}>{ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </td>
                  <td className={`px-5 py-3.5 text-xs ${t.textMuted} max-w-[160px] truncate`} title={e.actor}>{e.actor || '—'}</td>
                  <td className="px-5 py-3.5">
                   <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${colorClass}`}>{e.action.replace(/_/g, ' ')}</span>
                  </td>
                  <td className={`px-5 py-3.5 text-xs ${t.textSec} max-w-xs`}>{e.details}</td>
                 </tr>
                );
               })}
              </tbody>
             </table>
            </div>
           )}
          </div>
          {filtered.length > 250 && <p className={`text-xs ${t.textMuted} mt-3 text-right`}>Showing 250 most recent matching events of {filtered.length} total</p>}
          {auditLog.length > 0 && <p className={`text-xs ${t.textMuted} mt-2 text-right`}>{auditLog.length} total events</p>}
         </div>
        );
       })()}

       {/* Outbound Webhooks */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-violet-50 text-violet-500 rounded-xl"><Zap size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Outbound Webhooks</h3>
          <p className={`text-xs ${t.textMuted}`}>Get notified in real-time when events happen in your account — send to any HTTPS endpoint</p>
         </div>
        </div>

        {/* New secret banner */}
        {webhookNewSecret && (
         <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start justify-between gap-4">
           <div className="min-w-0">
            <p className="text-xs font-bold text-amber-800 mb-1">Save your webhook secret — it won't be shown again</p>
            <code className="text-xs text-amber-900 font-mono break-all select-all">{webhookNewSecret.secret}</code>
            <p className="text-[11px] text-amber-700 mt-2">Use this to verify the <span className="font-mono">X-SoarOne-Signature</span> header on incoming requests.</p>
           </div>
           <button onClick={() => setWebhookNewSecret(null)} className="text-amber-600 hover:text-amber-800 flex-shrink-0"><X size={16} /></button>
          </div>
         </div>
        )}

        {/* Test result banner */}
        {webhookTestResult && (
         <div className={`mb-6 p-4 rounded-xl border text-xs flex items-center justify-between gap-4 ${webhookTestResult.success ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
          <span>
           {webhookTestResult.success
            ? `Test delivered — HTTP ${webhookTestResult.statusCode}`
            : `Test failed${webhookTestResult.statusCode ? ` (HTTP ${webhookTestResult.statusCode})` : ''}: ${webhookTestResult.error || 'Unknown error'}`}
          </span>
          <button onClick={() => setWebhookTestResult(null)}><X size={14} /></button>
         </div>
        )}

        {/* Existing webhooks */}
        {webhooks.length > 0 && (
         <div className="mb-8 space-y-3">
          {webhooks.map(hook => (
           <div key={hook.id} className={`border ${t.border} rounded-xl overflow-hidden`}>
            <div className="flex items-center gap-4 px-5 py-4">
             <div className={`w-2 h-2 rounded-full flex-shrink-0 ${hook.active ? 'bg-emerald-400' : 'bg-slate-300'}`} />
             <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${t.text} truncate`}>{hook.url}</p>
              {hook.description && <p className={`text-xs ${t.textMuted} truncate`}>{hook.description}</p>}
              <div className="flex flex-wrap gap-1 mt-1">
               {hook.events.map(ev => (
                <span key={ev} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">{ev.replace(/_/g, ' ')}</span>
               ))}
              </div>
             </div>
             <div className="flex items-center gap-2 flex-shrink-0">
              <button
               onClick={() => handleToggleWebhookActive(hook.id, hook.active)}
               className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${hook.active ? 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'}`}
              >
               {hook.active ? 'Disable' : 'Enable'}
              </button>
              <button
               onClick={() => handleTestWebhook(hook.id)}
               disabled={webhookTestLoading === hook.id}
               className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
               {webhookTestLoading === hook.id ? 'Sending…' : 'Test'}
              </button>
              <button
               onClick={() => {
                if (webhookExpandedId === hook.id) {
                 setWebhookExpandedId(null);
                } else {
                 setWebhookExpandedId(hook.id);
                 handleLoadDeliveries(hook.id);
                }
               }}
               className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${webhookExpandedId === hook.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}
              >
               {webhookExpandedId === hook.id ? 'Hide logs' : 'Logs'}
              </button>
              <button onClick={() => handleDeleteWebhook(hook.id)} className="p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 size={14} /></button>
             </div>
            </div>
            {/* Delivery log */}
            {webhookExpandedId === hook.id && (
             <div className={`border-t ${t.border} bg-slate-50/50`}>
              {webhookDeliveriesLoading === hook.id ? (
               <div className={`px-6 py-4 text-xs ${t.textMuted}`}>Loading deliveries…</div>
              ) : webhookDeliveries.filter(d => d.webhookId === hook.id).length === 0 ? (
               <div className={`px-6 py-4 text-xs ${t.textMuted}`}>No deliveries recorded yet. Send a test to see results here.</div>
              ) : (
               <div className="overflow-x-auto">
                <table className="w-full text-xs">
                 <thead>
                  <tr className={`${t.bgCard} border-b ${t.border}`}>
                   <th className={`px-5 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>Time</th>
                   <th className={`px-5 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>Event</th>
                   <th className={`px-5 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>Status</th>
                   <th className={`px-5 py-3 text-left font-semibold ${t.textMuted} uppercase tracking-wide text-[10px]`}>Detail</th>
                  </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100">
                  {webhookDeliveries.filter(d => d.webhookId === hook.id).map(d => {
                   const ts = d.deliveredAt?.seconds ? new Date(d.deliveredAt.seconds * 1000) : null;
                   return (
                    <tr key={d.id}>
                     <td className={`px-5 py-3 whitespace-nowrap ${t.textMuted}`}>{ts ? ts.toLocaleString() : '—'}</td>
                     <td className="px-5 py-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">{d.event}</span></td>
                     <td className="px-5 py-3">
                      {d.success
                       ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700">HTTP {d.statusCode}</span>
                       : <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700">{d.statusCode ? `HTTP ${d.statusCode}` : 'Failed'}</span>}
                     </td>
                     <td className={`px-5 py-3 ${t.textMuted} max-w-xs truncate`}>{d.error || '—'}</td>
                    </tr>
                   );
                  })}
                 </tbody>
                </table>
               </div>
              )}
             </div>
            )}
           </div>
          ))}
         </div>
        )}

        {/* Create form */}
        {webhooks.length < 5 ? (
         <div className={`p-6 border-2 border-dashed ${t.border} rounded-xl`}>
          <p className={`text-xs font-semibold ${t.textSec} mb-4`}>Add Endpoint</p>
          <div className="space-y-3">
           <input
            type="url"
            placeholder="https://your-server.com/webhook"
            value={webhookForm.url}
            onChange={e => setWebhookForm(f => ({ ...f, url: e.target.value }))}
            className={`w-full ${t.bgInput} border-2 ${t.borderInput} rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ${t.ring} ${t.text} transition-all`}
           />
           <input
            type="text"
            placeholder="Description (optional)"
            value={webhookForm.description}
            onChange={e => setWebhookForm(f => ({ ...f, description: e.target.value }))}
            className={`w-full ${t.bgInput} border-2 ${t.borderInput} rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ${t.ring} ${t.text} transition-all`}
           />
           <div>
            <p className={`text-xs font-semibold ${t.textSec} mb-2`}>Events to subscribe to:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
             {WEBHOOK_EVENT_OPTIONS.map(ev => (
              <label key={ev.value} className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer border transition-colors ${webhookForm.events.includes(ev.value) ? 'bg-violet-50 border-violet-200' : `${t.bgInput} ${t.border}`}`}>
               <input
                type="checkbox"
                checked={webhookForm.events.includes(ev.value)}
                onChange={e => setWebhookForm(f => ({
                 ...f,
                 events: e.target.checked ? [...f.events, ev.value] : f.events.filter(x => x !== ev.value)
                }))}
                className="mt-0.5 accent-violet-600"
               />
               <div>
                <p className={`text-xs font-semibold ${t.text}`}>{ev.label}</p>
                <p className={`text-[11px] ${t.textMuted}`}>{ev.desc}</p>
               </div>
              </label>
             ))}
            </div>
           </div>
           {webhookCreateError && <p className="text-xs text-rose-600">{webhookCreateError}</p>}
           <button
            onClick={handleCreateWebhook}
            disabled={webhookCreateLoading || !webhookForm.url.trim() || webhookForm.events.length === 0}
            className="px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-slate-800 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2"
           >
            {webhookCreateLoading ? 'Creating…' : <><Plus size={14} /> Add Webhook</>}
           </button>
          </div>
         </div>
        ) : (
         <p className={`text-xs ${t.textMuted} text-center py-4`}>Maximum of 5 webhook endpoints reached. Delete one to add another.</p>
        )}

        {/* Signature verification */}
        <div className={`mt-6 p-4 bg-slate-50 rounded-xl border ${t.border}`}>
         <p className={`text-xs font-semibold ${t.text} mb-2`}>Verifying signatures</p>
         <p className={`text-[11px] ${t.textMuted} leading-relaxed`}>
          Each request includes a <span className="font-mono bg-slate-100 px-1 rounded">X-SoarOne-Signature: sha256=&lt;hmac&gt;</span> header.
          Compute <span className="font-mono bg-slate-100 px-1 rounded">HMAC-SHA256(secret, raw_body)</span> and compare to verify the payload is authentic.
          Your endpoint must respond with HTTP 2xx within 10 seconds.
         </p>
        </div>

        {/* Payload reference */}
        {(() => {
         const PAYLOAD_EXAMPLES = [
          { event: 'reminder_sent', desc: 'A dunning email was sent to a customer.', payload: { event: 'reminder_sent', createdAt: '2026-04-28T04:00:00.000Z', data: { invoiceId: 'inv_abc123', docNumber: '1042', recipient: 'customer@example.com', realmId: '1234567890', companyName: 'Acme Corp', balance: 1500.00, dueDate: '2026-03-01', tierName: 'Friendly Reminder', tierDays: 7 } } },
          { event: 'reminder_blocked', desc: 'Suppressed by a rule (opt-out, snooze, VIP, pause, dedup).', payload: { event: 'reminder_blocked', createdAt: '2026-04-28T04:00:00.000Z', data: { invoiceId: 'inv_abc123', docNumber: '1042', recipient: 'customer@example.com', realmId: '1234567890', reason: 'snoozed' } } },
          { event: 'payment_received', desc: 'Invoice balance reached zero (synced from QuickBooks).', payload: { event: 'payment_received', createdAt: '2026-04-28T04:00:00.000Z', data: { invoiceId: 'inv_abc123', docNumber: '1042', recipient: 'customer@example.com', realmId: '1234567890', companyName: 'Acme Corp', amountPaid: 1500.00 } } },
          { event: 'dispute_filed', desc: 'Customer clicked the dispute link in a dunning email.', payload: { event: 'dispute_filed', createdAt: '2026-04-28T04:00:00.000Z', data: { invoiceId: 'inv_abc123', docNumber: '1042', email: 'customer@example.com', realmId: '1234567890', reason: 'Already paid on 2026-04-10', disputeId: 'disp_xyz789' } } },
          { event: 'sync_failed', desc: 'QuickBooks sync or token refresh failed for a connection.', payload: { event: 'sync_failed', createdAt: '2026-04-28T04:00:00.000Z', data: { realmId: '1234567890', companyName: 'Acme Corp', error: 'Token refresh failed: invalid_grant' } } },
         ];
         const isOpen = webhookPayloadOpen !== null;
         return (
          <div className={`mt-4 rounded-xl border ${t.border} overflow-hidden`}>
           <button
            onClick={() => setWebhookPayloadOpen(v => v !== null ? null : '')}
            className={`w-full flex items-center justify-between px-5 py-3 ${t.bgInput} text-left`}
           >
            <span className={`text-xs font-semibold ${t.text}`}>Payload reference</span>
            <ChevronDown size={14} className={`${t.textMuted} transition-transform ${isOpen ? 'rotate-180' : ''}`} />
           </button>
           {isOpen && (
            <div className="divide-y divide-slate-100">
             {PAYLOAD_EXAMPLES.map(ex => (
              <div key={ex.event}>
               <button
                onClick={() => setWebhookPayloadOpen(v => v === ex.event ? '' : ex.event)}
                className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-50 transition-colors"
               >
                <div>
                 <span className="font-mono text-xs font-semibold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">{ex.event}</span>
                 <span className={`text-[11px] ${t.textMuted} ml-3`}>{ex.desc}</span>
                </div>
                <ChevronDown size={12} className={`${t.textMuted} transition-transform shrink-0 ml-2 ${webhookPayloadOpen === ex.event ? 'rotate-180' : ''}`} />
               </button>
               {webhookPayloadOpen === ex.event && (
                <pre className="px-5 pb-4 text-[11px] leading-relaxed overflow-x-auto bg-slate-900 text-emerald-300 font-mono">
                 {JSON.stringify(ex.payload, null, 2)}
                </pre>
               )}
              </div>
             ))}
            </div>
           )}
          </div>
         );
        })()}
       </div>

       {/* GDPR: Customer Data Tools */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-blue-50 text-blue-500 rounded-xl"><Shield size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Customer Data Rights (GDPR)</h3>
          <p className={`text-xs ${t.textMuted}`}>Export or erase all data associated with a customer email address</p>
         </div>
        </div>
        <div className="flex gap-3 mb-4">
         <input
          type="email"
          placeholder="customer@email.com"
          value={gdprEmail}
          onChange={(e) => setGdprEmail(e.target.value)}
          className={`flex-1 ${t.bgInput} border-2 ${t.borderInput} rounded-xl px-5 py-4 text-sm font-normal outline-none focus:ring-2 ${t.ring} ${t.text} transition-all shadow-none`}
         />
        </div>
        <div className="flex gap-3">
         <button
          onClick={handleExportCustomerData}
          disabled={gdprLoading || !gdprEmail.trim()}
          className="flex-1 px-6 py-4 bg-slate-900 text-white rounded-xl text-xs font-semibold tracking-wide hover:bg-slate-800 active:scale-95 transition-all flex items-center justify-center gap-3 shadow-none disabled:opacity-50"
         >
          <Download size={16} /> Export Data (SAR)
         </button>
         <button
          onClick={handleEraseCustomerData}
          disabled={gdprLoading || !gdprEmail.trim()}
          className="flex-1 px-6 py-4 bg-rose-50 text-rose-600 border border-rose-100 rounded-xl text-xs font-semibold tracking-wide hover:bg-rose-600 hover:text-white active:scale-95 transition-all flex items-center justify-center gap-3 shadow-none disabled:opacity-50"
         >
          <Trash2 size={16} /> Erase All Data
         </button>
        </div>
        <p className={`text-[11px] ${t.textMuted} mt-4 leading-relaxed`}>
         <strong>Export</strong> downloads a JSON file containing all dunning logs, invoices, disputes, and opt-out records for this email.
         <strong> Erase</strong> permanently deletes all records and adds the email to the suppression list to prevent future contact. This cannot be undone.
        </p>
       </div>

       {/* Compliance Summary */}
       <div className={`${t.bgCard} p-10 rounded-2xl border border-slate-100 shadow-none`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-emerald-50 text-emerald-500 rounded-xl"><ShieldCheck size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Active Safeguards</h3>
          <p className={`text-xs ${t.textMuted}`}>Automated compliance protections running on every email</p>
         </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
         {[
          { label: 'CAN-SPAM: Unsubscribe Link', desc: 'Every email includes a one-click unsubscribe link in the footer', active: true },
          { label: 'CAN-SPAM: Physical Address', desc: 'Sender mailing address included in every email footer', active: !!(userProfile.senderAddress && userProfile.senderCity) },
          { label: 'CAN-SPAM: List-Unsubscribe Header', desc: 'RFC 8058 compliant header for Gmail/Yahoo one-click unsubscribe', active: true },
          { label: 'CAN-SPAM: Opt-Out Suppression', desc: `${optOuts.length} suppressed recipient(s) — emails auto-blocked`, active: true },
          { label: 'FDCPA: Frequency Cap', desc: 'Max 7 emails per recipient per 30 days per realm', active: true },
          { label: 'FDCPA: Dispute Hold', desc: `${disputes.filter((d: any) => d.status !== 'resolved').length} active dispute(s) — emails paused`, active: true },
          { label: 'FDCPA: Content Guardrails', desc: 'Templates validated against prohibited threatening language', active: true },
          { label: 'FDCPA: AI Semantic Review', desc: reviewedTemplateCount === 0 ? 'No templates reviewed yet - run AI compliance review from the template editor' : `${passingTemplateCount}/${reviewedTemplateCount} reviewed template(s) currently passing semantic review${flaggedTemplateCount ? ` - ${flaggedTemplateCount} flagged` : ''}`, active: reviewedTemplateCount > 0 && flaggedTemplateCount === 0 },
          { label: 'FDCPA: First-Contact Notice', desc: 'Dispute rights and validation notice on first email per invoice', active: true },
          { label: 'GDPR: Data Retention', desc: 'Automated cleanup of dunning logs older than 12 months', active: true },
          { label: 'GDPR: Data Rights', desc: 'Customer data export and erasure tools available above', active: true },
         ].map((item, i) => (
          <div key={i} className={`p-4 rounded-xl border ${item.active ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
           <div className="flex items-center gap-2 mb-1">
            <div className={`w-2 h-2 rounded-full ${item.active ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
            <span className={`text-xs font-semibold ${t.textLink}`}>{item.label}</span>
           </div>
           <p className={`text-[11px] ${t.textSec} ml-4`}>{item.desc}</p>
          </div>
         ))}
        </div>
        {!(userProfile.senderAddress && userProfile.senderCity) && (
         <div className="mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
          <p className="text-xs text-amber-700 font-semibold">⚠ Missing physical mailing address — this is required by CAN-SPAM. Please configure it in Account.</p>
         </div>
        )}
       </div>
      </div>
     )}

     {/* ACCOUNT TAB */}
     {activeTab === 'account' && (
      <div className="max-w-4xl space-y-10 animate-in fade-in duration-700 text-left shadow-none">
       <div className={`${t.bgCard} p-12 rounded-2xl border border-slate-100 relative overflow-hidden text-left shadow-none`}>
        <div style={{ backgroundColor: PRIMARY_BLUE }} className="absolute top-0 left-0 w-2 h-full shadow-none"></div>
        <div className="flex flex-col md:flex-row items-center gap-12 mb-12 relative z-10 shadow-none">
         <div className="relative shadow-none">
          <div className={`w-40 h-40 rounded-2xl ${t.bgInput} border-4 ${t.border} flex items-center justify-center ${t.textMuted} overflow-hidden shadow-none`}>
           {profilePicUrl ? <img src={profilePicUrl} className="w-full h-full object-cover" alt="" /> : <UserCog size={64} className="opacity-20 shadow-none" />}
          </div>
          <input
           ref={fileInputRef}
           type="file"
           accept="image/jpeg,image/png,image/gif,image/webp"
           className="hidden"
           onChange={handleImageUpload}
          />
          <button
           onClick={() => fileInputRef.current?.click()}
           className="absolute bottom-2 right-2 p-5 bg-slate-900 text-white rounded-xl shadow-none hover:bg-slate-800 active:scale-90 transition-all"
           title="Upload avatar image"
          >
           <Camera size={20} />
          </button>
         </div>
         <div className="text-center md:text-left flex-1 shadow-none">
          <div className="flex items-center justify-center md:justify-start gap-4 mb-3 shadow-none">
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-2xl font-bold ${t.text} tracking-tight`}>{userProfile.displayName || "Root Identity"}</h3>
           <ShieldCheck size={24} className="text-emerald-500 shadow-none" />
          </div>
          <p className={`${t.textMuted} font-normal text-sm tracking-normal mb-6`}>{user?.email}</p>
          <div className="flex justify-center md:justify-start gap-4 shadow-none">
           <span className={`px-5 py-2 ${t.bgInput} ${t.textLink} rounded-lg text-xs font-semibold uppercase border ${t.borderInput} tracking-wide shadow-none`}>Validated Node</span>
           <span className={`px-5 py-2 ${t.bgInput} ${t.textMuted} rounded-lg text-xs font-semibold uppercase border ${t.borderInput} shadow-none`}>UID: {user?.uid.substring(0, 10)}...</span>
          </div>
         </div>
        </div>

        <div className={`border-t ${t.borderInput} pt-12 relative z-10 shadow-none`}>
         <div className="flex items-start justify-between gap-6 mb-8 flex-col md:flex-row shadow-none">
          <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-2xl font-bold ${t.text} tracking-tight`}>Profile & Business Details</h3>
           <p className={`text-xs ${t.textMuted} mt-1`}>Keep your account identity and required mailing address together so reminders, compliance, and billing records stay consistent.</p>
          </div>
          <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.borderInput} min-w-[180px]`}>
           <p className={`text-[10px] uppercase tracking-wide ${t.textMuted} mb-2`}>Mailing Address</p>
           <p className={`text-lg font-bold ${t.text}`}>{userProfile.senderAddress && userProfile.senderCity ? 'Configured' : 'Required'}</p>
          </div>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 gap-12 shadow-none">
          <div className="space-y-2 shadow-none">
           <label className={subtleLabelClass}>Display Name</label>
           <input type="text" className={inputClass} value={userProfile.displayName || ''} onChange={(e) => { profileFormDirty.current = true; setUserProfile({ ...userProfile, displayName: e.target.value }); }} />
          </div>
          <div className="space-y-2 shadow-none">
           <label className={subtleLabelClass}>Avatar URL (or use camera to upload)</label>
           <div className="flex gap-3">
            <input type="text" className={inputClass} value={profilePicUrl && !profilePicUrl.startsWith('data:') ? profilePicUrl : ''} placeholder="https://example.com/photo.jpg" onChange={(e) => setProfilePicUrl(e.target.value)} />
            {profilePicUrl && (
             <button
              onClick={async () => {
               setProfilePicUrl('');
               if (db && user) {
                await setDoc(doc(db, 'artifacts', currentAppId, 'users', user.uid, 'profile', 'data'), {
                 photoURL: '',
                 updatedAt: serverTimestamp()
                }, { merge: true });
                addNotify('info', 'Avatar removed.');
               }
              }}
              className="px-4 py-2 bg-rose-50 text-rose-500 rounded-xl border border-rose-100 hover:bg-rose-500 hover:text-white transition-all text-xs font-medium uppercase tracking-wide whitespace-nowrap"
             >
              Remove
             </button>
            )}
           </div>
           {profilePicUrl && profilePicUrl.startsWith('data:') && (
            <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide ml-4">Custom image uploaded</p>
           )}
          </div>
         </div>

         <div className={`border-t ${t.borderInput} mt-12 pt-10 shadow-none`}>
          <div className="flex items-start justify-between gap-6 mb-8 flex-col md:flex-row">
           <div>
            <h4 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Mailing Address</h4>
            <p className={`text-xs ${t.textMuted} mt-1`}>Required by CAN-SPAM on all commercial payment reminders, even if you are not using Pro SMTP.</p>
           </div>
           <div className={`p-4 rounded-2xl ${t.bgInput} border ${t.borderInput} min-w-[160px]`}>
            <p className={`text-[10px] uppercase tracking-wide ${t.textMuted} mb-2`}>Status</p>
            <p className={`text-lg font-bold ${t.text}`}>{userProfile.senderAddress && userProfile.senderCity ? 'Configured' : 'Required'}</p>
           </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
           <div className="md:col-span-3 space-y-2 shadow-none">
            <label className={subtleLabelClass}>Physical Mailing Address <span className="text-rose-500">(Required by CAN-SPAM)</span></label>
            <input type="text" className={inputClass} placeholder="123 Business Ave" value={userProfile.senderAddress || ''} onChange={(e) => { profileFormDirty.current = true; setUserProfile({ ...userProfile, senderAddress: e.target.value }); }} />
           </div>
           <div className="space-y-2 shadow-none md:col-span-2">
            <label className={subtleLabelClass}>City</label>
            <input type="text" className={inputClass} placeholder="City" value={userProfile.senderCity || ''} onChange={(e) => { profileFormDirty.current = true; setUserProfile({ ...userProfile, senderCity: e.target.value }); }} />
           </div>
           <div className="space-y-2 shadow-none">
            <label className={subtleLabelClass}>ZIP / Postal Code</label>
            <input type="text" maxLength={10} className={inputClass} placeholder="00000" value={userProfile.senderZip || ''} onChange={(e) => { profileFormDirty.current = true; setUserProfile({ ...userProfile, senderZip: e.target.value }); }} />
           </div>
          </div>
         </div>
        </div>

        <div className="mt-12 flex justify-end">
         <button onClick={handleUpdateProfile} disabled={savingSettings} style={{ backgroundColor: PRIMARY_BLUE }} className="w-full md:w-auto md:min-w-[220px] text-slate-900 px-8 py-5 rounded-xl text-xs font-semibold tracking-wide active:scale-95 transition-all flex items-center justify-center gap-4 shadow-none disabled:opacity-50"><Save size={20} /> {savingSettings ? 'Saving...' : 'Save Profile & Address'}</button>
        </div>
       </div>

       {/* Password Management */}
       <div className={`${panelClass} p-12 relative overflow-hidden text-left`}>
        <div style={{ backgroundColor: PRIMARY_BLUE }} className="absolute top-0 left-0 w-2 h-full shadow-none"></div>
        <div className="flex items-center gap-6 mb-10 shadow-none">
         <div className={`p-4 ${t.bgInput} ${t.text} rounded-xl border ${t.borderInput} shadow-none`}><Key size={28} /></div>
         <div className="shadow-none"><h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight shadow-none`}>Password Management</h3><p className={`text-xs font-normal ${t.textMuted} tracking-normal shadow-none`}>Update your account access key</p></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-6 max-w-2xl shadow-none">
         <div className="relative flex-1 shadow-none">
          <Key size={18} className={`absolute left-6 top-1/2 -translate-y-1/2 ${t.textMuted} shadow-none`} />
          <input type="password" title="New Password" placeholder="Enter New Password..." className={`w-full pl-14 pr-8 py-5 ${t.bgInput} border-2 ${t.borderInput} rounded-xl font-normal text-sm ${t.text} outline-none transition-all shadow-none`} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
         </div>
         <button onClick={handleUpdatePassword} className="bg-slate-900 hover:bg-slate-800 text-white px-12 py-5 rounded-xl font-semibold text-xs tracking-wide active:scale-95 transition-all shadow-none">Update Password</button>
        </div>
        <div className="mt-6 pt-6 border-t border-slate-100">
         <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
           <p className={`text-sm font-semibold ${t.text}`}>Sign out all other devices</p>
           <p className={`text-xs ${t.textMuted} mt-1`}>Immediately revoke access on all other active sessions. You will also be signed out of this session.</p>
          </div>
          <button onClick={handleSignOutAllDevices} disabled={signOutAllLoading} className="whitespace-nowrap px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold tracking-wide active:scale-95 transition-all shadow-none disabled:opacity-50 flex items-center gap-2">
           <LogOut size={14} />
           {signOutAllLoading ? 'Revoking...' : 'Sign Out All Devices'}
          </button>
         </div>
        </div>
       </div>

       {/* Two-Factor Authentication */}
       <div className={`${panelClass} p-12 relative overflow-hidden text-left`}>
        <div style={{ backgroundColor: PRIMARY_BLUE }} className="absolute top-0 left-0 w-2 h-full shadow-none"></div>
        <div className="flex items-center gap-6 mb-8">
         <div className={`p-4 ${t.bgInput} ${t.text} rounded-xl border ${t.borderInput}`}><ShieldCheck size={28} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Two-Factor Authentication</h3>
          <p className={`text-xs font-normal ${t.textMuted} tracking-normal mt-0.5`}>Add an extra layer of security with an authenticator app (Google Authenticator, Authy, etc.)</p>
         </div>
        </div>

        {mfaSetupStep === null && (
         <>
          {multiFactor(user!).enrolledFactors.length > 0 ? (
           <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl bg-emerald-50 border border-emerald-100">
            <div className="flex items-center gap-3">
             <CheckCircle size={18} className="text-emerald-600 shrink-0" />
             <div>
              <p className="text-sm font-semibold text-emerald-800">2FA is enabled</p>
              <p className="text-xs text-emerald-600 mt-0.5">Your account is protected with an authenticator app.</p>
             </div>
            </div>
            <button
             onClick={handleMfaDisable}
             disabled={mfaLoading}
             className="whitespace-nowrap px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide bg-white border border-emerald-200 text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
            >
             {mfaLoading ? 'Working…' : 'Disable 2FA'}
            </button>
           </div>
          ) : (
           <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-2xl bg-amber-50 border border-amber-100">
            <div className="flex items-center gap-3">
             <AlertTriangle size={18} className="text-amber-600 shrink-0" />
             <div>
              <p className="text-sm font-semibold text-amber-800">2FA is not enabled</p>
              <p className="text-xs text-amber-600 mt-0.5">Protect your account by setting up two-factor authentication.</p>
             </div>
            </div>
            <button
             onClick={handleMfaEnrollStart}
             disabled={mfaLoading}
             style={{ backgroundColor: PRIMARY_BLUE }}
             className="whitespace-nowrap px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50 transition-all active:scale-95"
            >
             {mfaLoading ? 'Loading…' : 'Enable 2FA'}
            </button>
           </div>
          )}
         </>
        )}

        {mfaSetupStep === 'qrcode' && (
         <div className="space-y-6">
          <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.borderInput} text-center`}>
           <p className={`text-xs font-semibold uppercase tracking-wide ${t.textMuted} mb-4`}>Step 1 — Scan with your authenticator app</p>
           {mfaQrDataUrl && <img src={mfaQrDataUrl} alt="2FA QR Code" className="mx-auto rounded-xl" style={{ width: 180, height: 180 }} />}
           <p className={`text-xs ${t.textMuted} mt-3`}>Or manually enter the setup key in your app:</p>
           <p className={`text-xs font-mono font-bold ${t.text} mt-1 break-all`}>{mfaTotpSecret?.secretKey}</p>
          </div>
          <button
           onClick={() => setMfaSetupStep('verify')}
           style={{ backgroundColor: PRIMARY_BLUE }}
           className="w-full py-4 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 active:scale-95 transition-all"
          >
           I've scanned it — Next
          </button>
          <button onClick={() => { setMfaSetupStep(null); setMfaTotpSecret(null); setMfaQrDataUrl(''); }} className={`w-full py-3 text-xs font-medium ${t.textMuted}`}>Cancel</button>
         </div>
        )}

        {mfaSetupStep === 'verify' && (
         <div className="space-y-5">
          <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
           <p className={`text-xs font-semibold uppercase tracking-wide ${t.textMuted} mb-3`}>Step 2 — Enter the 6-digit code from your app</p>
           <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000 000"
            maxLength={7}
            value={mfaVerifyCode}
            onChange={(e) => setMfaVerifyCode(e.target.value.replace(/[^0-9\s]/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleMfaEnrollVerify()}
            className={`w-full text-center text-2xl font-mono tracking-[0.4em] px-5 py-4 bg-white border-2 ${t.borderInput} rounded-xl ${t.text} outline-none focus:ring-2 ${t.ring}`}
            autoFocus
           />
           {mfaError && <p className="text-xs text-rose-600 mt-2">{mfaError}</p>}
          </div>
          <button
           onClick={handleMfaEnrollVerify}
           disabled={mfaLoading || mfaVerifyCode.replace(/\s/g, '').length < 6}
           style={{ backgroundColor: PRIMARY_BLUE }}
           className="w-full py-4 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50 active:scale-95 transition-all"
          >
           {mfaLoading ? 'Verifying…' : 'Verify & Enable 2FA'}
          </button>
          <button onClick={() => setMfaSetupStep('qrcode')} className={`w-full py-3 text-xs font-medium ${t.textMuted}`}>Back</button>
         </div>
        )}
       </div>

       {/* Team Members */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center justify-between gap-4 mb-6">
         <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-50 text-violet-500 rounded-xl"><Users size={20} /></div>
          <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Team Members</h3>
           <p className={`text-xs ${t.textMuted}`}>
            {canUseTeam ? `Invite colleagues to your workspace — ${effectivePlan.maxTeamMembers} seat${effectivePlan.maxTeamMembers !== 1 ? 's' : ''} on your plan` : 'Upgrade to Pro or CPA to invite team members'}
           </p>
          </div>
         </div>
         {!canUseTeam && (
          <button onClick={() => requireUpgrade('Team Members')} className="px-4 py-2 text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-700 rounded-xl">Upgrade</button>
         )}
        </div>

        {canUseTeam ? (
         <>
          {/* Current members */}
          {teamMembers.length > 0 && (
           <div className="mb-6 space-y-3">
            {teamMembers.map(member => (
             <div key={member.id} className={`p-4 rounded-xl border ${t.border} ${t.bgInput}`}>
              <div className="flex items-center gap-4">
               <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                 <p className={`text-sm font-semibold ${t.text} truncate`}>{member.displayName || member.email}</p>
                 {member.displayName && <p className={`text-xs ${t.textMuted} truncate`}>{member.email}</p>}
                 <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${member.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {member.status === 'active' ? 'Active' : 'Pending invite'}
                 </span>
                 {member.connectionIds && member.connectionIds.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-50 text-violet-700">
                   {member.connectionIds.length} connection{member.connectionIds.length !== 1 ? 's' : ''}
                  </span>
                 )}
                </div>
               </div>
               <select
                value={member.role}
                onChange={e => handleUpdateTeamMemberRole(member.id, e.target.value)}
                disabled={teamRoleUpdateLoading === member.id || member.status === 'pending'}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${t.border} ${t.bgCard} ${t.text} outline-none disabled:opacity-50`}
               >
                <option value="admin">Admin</option>
                <option value="member">Member</option>
                <option value="viewer">Viewer</option>
               </select>
               <button
                onClick={() => handleRevokeTeamMember(member.id, member.email)}
                className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                title={member.status === 'pending' ? 'Cancel invite' : 'Remove member'}
               >
                <Trash2 size={14} />
               </button>
              </div>
              {/* Per-connection access restriction — only shown for active members with connections */}
              {member.status === 'active' && connections.length > 1 && (
               <div className="mt-3 pt-3 border-t border-slate-100">
                <p className={`text-[11px] font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>Connection access</p>
                <div className="flex flex-wrap gap-2">
                 {connections.map(conn => {
                  const hasAccess = !member.connectionIds || member.connectionIds.includes(conn.realmId);
                  return (
                   <label key={conn.realmId} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-medium cursor-pointer transition-colors ${hasAccess ? 'bg-sky-50 border-sky-200 text-sky-700' : `${t.bgCard} ${t.border} ${t.textMuted}`} ${memberConnectionsLoading === member.id ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input
                     type="checkbox"
                     checked={hasAccess}
                     onChange={e => handleUpdateMemberConnections(member.id, conn.realmId, e.target.checked)}
                     className="sr-only"
                    />
                    <span className={`w-3 h-3 rounded border flex items-center justify-center ${hasAccess ? 'bg-sky-500 border-sky-500' : t.border}`}>
                     {hasAccess && <CheckCircle2 size={8} className="text-white" />}
                    </span>
                    {conn.companyName || conn.realmId}
                   </label>
                  );
                 })}
                </div>
                <p className={`text-[10px] ${t.textMuted} mt-1.5`}>All checked = access to all connections</p>
               </div>
              )}
             </div>
            ))}
           </div>
          )}

          {/* Invite form */}
          {teamMembers.length < effectivePlan.maxTeamMembers && (
           <div className={`p-5 rounded-xl border ${t.border} ${t.bgInput}`}>
            <p className={`text-xs font-semibold ${t.text} mb-3`}>Invite a new member</p>
            <div className="flex flex-col sm:flex-row gap-3">
             <input
              type="email"
              placeholder="colleague@example.com"
              value={teamInviteEmail}
              onChange={e => { setTeamInviteEmail(e.target.value); setTeamInviteError(''); setTeamInviteSuccess(''); }}
              className={`flex-1 px-4 py-2.5 ${t.bgCard} border ${t.border} rounded-xl text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
             />
             <select
              value={teamInviteRole}
              onChange={e => setTeamInviteRole(e.target.value as 'admin' | 'member' | 'viewer')}
              className={`px-3 py-2.5 ${t.bgCard} border ${t.border} rounded-xl text-sm font-medium ${t.text} outline-none`}
             >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
             </select>
             <button
              onClick={handleSendTeamInvite}
              disabled={teamInviteLoading || !teamInviteEmail}
              style={{ backgroundColor: PRIMARY_BLUE }}
              className="px-5 py-2.5 text-slate-900 rounded-xl text-xs font-bold uppercase tracking-wide active:scale-95 transition-all disabled:opacity-50 whitespace-nowrap"
             >
              {teamInviteLoading ? 'Sending…' : 'Send Invite'}
             </button>
            </div>
            {teamInviteError && <p className="text-xs text-rose-600 mt-2">{teamInviteError}</p>}
            {teamInviteSuccess && <p className="text-xs text-emerald-600 mt-2">{teamInviteSuccess}</p>}
           </div>
          )}

          {/* Role guide */}
          <div className={`mt-4 p-4 rounded-xl ${t.bgInput} border ${t.border}`}>
           <p className={`text-[11px] font-semibold ${t.textMuted} mb-3 uppercase tracking-wide`}>Role permissions</p>
           <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
            {[
             { role: 'Admin', badge: 'bg-violet-100 border-violet-200 text-violet-700', dot: 'bg-violet-400', perms: ['View all AR data, history, reports', 'Full edit access (sync, templates, settings)', 'Cannot change billing or remove owner'] },
             { role: 'Member', badge: 'bg-sky-100 border-sky-200 text-sky-700', dot: 'bg-sky-400', perms: ['View all AR data, history, reports', 'Trigger manual syncs', 'Cannot edit templates or settings'] },
             { role: 'Viewer', badge: 'bg-slate-100 border-slate-200 text-slate-600', dot: 'bg-slate-400', perms: ['View AR data, history, reports', 'Read-only — no actions', 'Cannot trigger syncs or edits'] },
            ].map(r => (
             <div key={r.role} className={`p-3 rounded-lg ${t.bgCard} border ${t.border}`}>
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wide mb-2 ${r.badge}`}>
               <span className={`w-1.5 h-1.5 rounded-full ${r.dot}`} />
               {r.role}
              </span>
              <ul className={`space-y-1 ${t.textMuted} leading-relaxed`}>
               {r.perms.map(p => <li key={p} className="flex items-start gap-1"><span className="mt-0.5 shrink-0">·</span>{p}</li>)}
              </ul>
             </div>
            ))}
           </div>
           <p className={`text-[10px] ${t.textMuted} mt-3`}>Admins can sync, snooze, pause, and resolve disputes on behalf of your workspace. Use connection access controls above to restrict member visibility to specific QuickBooks connections.</p>
          </div>
         </>
        ) : (
         <div className={`text-center py-10 rounded-xl border-2 border-dashed ${t.border}`}>
          <Users size={32} className={`mx-auto mb-3 ${t.textMuted} opacity-30`} />
          <p className={`text-sm font-semibold ${t.textMuted}`}>Team features on Pro & CPA</p>
          <p className={`text-xs ${t.textMuted} mt-1`}>Upgrade to invite colleagues and manage workspace access.</p>
         </div>
        )}
       </div>

       {/* Notification Preferences */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-sky-50 text-sky-500 rounded-xl"><Bell size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Notifications</h3>
          <p className={`text-xs ${t.textMuted}`}>Email and Slack alerts for account events</p>
         </div>
        </div>
        {(() => {
         const prefs: NotificationPrefs = { ...DEFAULT_NOTIFICATION_PREFS, ...userProfile.notificationPrefs };
         const NOTIF_OPTIONS: { key: keyof NotificationPrefs; label: string; desc: string }[] = [
          { key: 'payment_received', label: 'Payment Received', desc: 'An invoice balance reaches zero — customer has paid' },
          { key: 'dispute_filed', label: 'Dispute Filed', desc: 'A customer clicks the dispute link in a dunning email' },
          { key: 'sync_failed', label: 'Sync / Connection Failed', desc: 'QuickBooks token refresh fails and reminders are paused' },
          { key: 'trial_expiring', label: 'Trial Expiring', desc: 'Your trial ends in 3 days' },
          { key: 'subscription_payment_failed', label: 'Subscription Payment Failed', desc: 'Billing issue detected — action may be required' },
          { key: 'weekly_digest', label: 'Weekly Digest', desc: 'Monday morning summary: reminders sent, delivery rate, disputes filed' },
         ];
         return (
          <div className="space-y-3">
           <p className={`text-[11px] font-semibold ${t.textMuted} uppercase tracking-wide mb-1`}>Email alerts — sent to {user?.email}</p>
           {NOTIF_OPTIONS.map(opt => (
            <div key={opt.key} className={`flex items-center justify-between gap-4 p-4 rounded-xl border ${t.border} hover:bg-slate-50/50 transition-colors`}>
             <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${t.text}`}>{opt.label}</p>
              <p className={`text-xs ${t.textMuted} mt-0.5`}>{opt.desc}</p>
             </div>
             <button
              onClick={() => handleToggleNotificationPref(opt.key)}
              className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${prefs[opt.key] ? 'bg-sky-500' : 'bg-slate-200'}`}
              role="switch"
              aria-checked={prefs[opt.key]}
             >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${prefs[opt.key] ? 'translate-x-6' : 'translate-x-1'}`} />
             </button>
            </div>
           ))}
           {/* Slack webhook */}
           <div className={`mt-6 p-5 rounded-xl border ${t.border} ${t.bgInput}`}>
            <div className="flex items-center gap-2 mb-3">
             <ZapIcon size={14} className="text-violet-500" />
             <p className={`text-xs font-semibold ${t.text}`}>Slack Notifications</p>
            </div>
            <p className={`text-xs ${t.textMuted} mb-3`}>Post alerts to a Slack channel. Paste your Slack incoming webhook URL below — the same events you have enabled above will also fire to Slack.</p>
            <div className="flex gap-2">
             <input
              type="url"
              placeholder="https://hooks.slack.com/services/..."
              value={localSlackUrl}
              onChange={e => setLocalSlackUrl(e.target.value)}
              className={`flex-1 px-4 py-2.5 ${t.bgCard} border ${t.border} rounded-xl text-sm ${t.text} outline-none focus:ring-2 ${t.ring} font-mono`}
             />
             <button
              onClick={handleSaveSlackUrl}
              disabled={slackUrlSaving || localSlackUrl === (userProfile.slackWebhookUrl ?? '')}
              className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-wide active:scale-95 transition-all disabled:opacity-50 whitespace-nowrap"
             >
              {slackUrlSaving ? 'Saving…' : 'Save'}
             </button>
            </div>
            {userProfile.slackWebhookUrl && (
             <p className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle2 size={11} /> Slack notifications active</p>
            )}
            <p className={`text-[10px] ${t.textMuted} mt-2`}>
             Create an incoming webhook at <span className="font-mono">api.slack.com/apps</span> → Your App → Incoming Webhooks.
            </p>
           </div>
          </div>
         );
        })()}
       </div>

       {/* SSO / Google Workspace (Item 19) */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-3 mb-6">
         <div className="p-3 bg-blue-50 text-blue-500 rounded-xl"><ShieldCheck size={20} /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>SSO / Google Workspace</h3>
          <p className={`text-xs ${t.textMuted}`}>Require team members to sign in with a specific Google Workspace domain</p>
         </div>
        </div>
        {!canUseSmtp && (
         <div className={`p-4 rounded-xl border ${t.border} bg-amber-50 text-amber-800 text-xs mb-6`}>
          SSO domain enforcement requires Pro or CPA plan.
         </div>
        )}
        <div className={`space-y-5 ${!canUseSmtp ? 'opacity-40 pointer-events-none' : ''}`}>
         <div className={`flex items-center justify-between p-5 rounded-xl border ${t.border} ${t.bgInput}`}>
          <div>
           <p className={`text-sm font-semibold ${t.text}`}>Enforce Google Workspace domain</p>
           <p className={`text-xs ${t.textMuted} mt-0.5`}>Team members must sign in with a Google account from your domain</p>
          </div>
          <button
           onClick={() => { setLocalSsoEnabled(v => !v); }}
           className={`p-1 rounded-full transition-all ${localSsoEnabled ? 'bg-slate-900' : 'bg-slate-200'}`}
          >
           {localSsoEnabled ? <ToggleRight className="text-white" size={32} /> : <ToggleLeft className="text-slate-500" size={32} />}
          </button>
         </div>
         {localSsoEnabled && (
          <div>
           <label className={`${subtleLabelClass} mb-1 block`}>Required Domain</label>
           <div className="flex gap-3 items-center">
            <div className="relative flex-1 max-w-xs">
             <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">@</span>
             <input
              value={localSsoDomain}
              onChange={e => setLocalSsoDomain(e.target.value.replace(/^@/, ''))}
              placeholder="company.com"
              className={`${inputClass} pl-8 text-sm`}
             />
            </div>
           </div>
           <p className={`text-xs ${t.textMuted} mt-2`}>Members will see an error if they try to accept an invite using a different email domain.</p>
          </div>
         )}
         <button
          onClick={() => handleSetSsoConfig(localSsoEnabled)}
          disabled={ssoSaving || (localSsoEnabled && !localSsoDomain.trim())}
          style={{ backgroundColor: PRIMARY_BLUE }}
          className="px-5 py-2.5 rounded-xl text-slate-900 text-xs font-semibold active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2"
         >
          <Save size={13} /> {ssoSaving ? 'Saving…' : 'Save SSO Settings'}
         </button>
         <p className={`text-xs ${t.textMuted} pt-1`}>SAML 2.0 / enterprise IdP support available on request — contact support.</p>
        </div>
       </div>

       {/* API Keys */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center justify-between gap-4 mb-6">
         <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-50 text-violet-500 rounded-xl"><Key size={20} /></div>
          <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>API Keys</h3>
           <p className={`text-xs ${t.textMuted}`}>Integrate SoarOne into your own tools and workflows</p>
          </div>
         </div>
         {!showApiKeyForm && (
          <button
           onClick={() => { setShowApiKeyForm(true); setApiKeyCreateError(''); }}
           className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-all"
          >
           <Plus size={14} /> New Key
          </button>
         )}
        </div>

        {/* New key reveal — shown once after creation */}
        {justCreatedKey && (
         <div className="mb-6 p-5 rounded-2xl border-2 border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-2 mb-3">
           <CheckCircle size={16} className="text-emerald-600 shrink-0" />
           <p className="text-sm font-bold text-emerald-800">API key created — copy it now. It won't be shown again.</p>
          </div>
          <div className="flex items-center gap-3 bg-white rounded-xl border border-emerald-200 px-4 py-3">
           <code className="flex-1 text-xs font-mono text-slate-800 break-all select-all">{justCreatedKey.key}</code>
           <button
            onClick={() => { navigator.clipboard.writeText(justCreatedKey.key); setKeyCopied(true); }}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-bold hover:bg-emerald-700 transition-all"
           >
            {keyCopied ? 'Copied!' : 'Copy'}
           </button>
          </div>
          <button onClick={() => setJustCreatedKey(null)} className="mt-3 text-[10px] text-emerald-700 underline">Done — I've saved my key</button>
         </div>
        )}

        {/* Create form */}
        {showApiKeyForm && (
         <div className="mb-6 p-5 rounded-2xl border border-slate-200 bg-slate-50 space-y-4">
          <p className="text-sm font-semibold text-slate-800">New API Key</p>
          <div>
           <label className={`${subtleLabelClass} mb-1 block`}>Key Name</label>
           <input
            value={newApiKeyName}
            onChange={e => setNewApiKeyName(e.target.value)}
            placeholder="e.g. My Integration, Zapier Webhook"
            className={`${inputClass} text-sm`}
           />
          </div>
          <div>
           <label className={`${subtleLabelClass} mb-2 block`}>Scopes</label>
           <div className="flex flex-wrap gap-2">
            {(['invoices:read', 'logs:read', 'summary:read', 'sync:write'] as const).map(scope => (
             <label key={scope} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-all text-xs font-medium ${newApiKeyScopes.includes(scope) ? 'bg-violet-50 border-violet-300 text-violet-800' : `${t.bgInput} ${t.border} ${t.textMuted}`}`}>
              <input
               type="checkbox"
               className="rounded"
               checked={newApiKeyScopes.includes(scope)}
               onChange={e => setNewApiKeyScopes(prev => e.target.checked ? [...prev, scope] : prev.filter(s => s !== scope))}
              />
              {scope}
             </label>
            ))}
           </div>
          </div>
          {apiKeyCreateError && <p className="text-xs text-rose-600">{apiKeyCreateError}</p>}
          <div className="flex gap-3">
           <button
            onClick={handleCreateApiKey}
            disabled={apiKeyCreateLoading || !newApiKeyName.trim()}
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 disabled:opacity-50 transition-all"
           >
            {apiKeyCreateLoading ? 'Creating…' : 'Create Key'}
           </button>
           <button onClick={() => { setShowApiKeyForm(false); setApiKeyCreateError(''); }} className={`px-5 py-2.5 rounded-xl border ${t.border} ${t.textMuted} text-xs font-medium hover:${t.bgHover} transition-all`}>Cancel</button>
          </div>
         </div>
        )}

        {/* Keys table */}
        {apiKeys.length === 0 && !showApiKeyForm && (
         <div className={`text-center py-10 ${t.textMuted} text-sm`}>
          <Key size={32} className="mx-auto mb-3 opacity-20" />
          <p>No API keys yet. Create one to start integrating.</p>
         </div>
        )}
        {apiKeys.length > 0 && (
         <div className="space-y-3">
          {apiKeys.map(k => (
           <div key={k.id} className={`flex items-center gap-4 px-5 py-4 rounded-2xl border ${t.border} ${t.bgInput}`}>
            <div className="flex-1 min-w-0">
             <p className={`text-sm font-semibold ${t.text} truncate`}>{k.name}</p>
             <p className="text-[11px] font-mono text-slate-400 mt-0.5">{k.keyPrefix}</p>
             <div className="flex flex-wrap gap-1 mt-1.5">
              {k.scopes.map(s => (
               <span key={s} className="px-2 py-0.5 rounded-lg bg-violet-50 border border-violet-100 text-violet-700 text-[10px] font-semibold">{s}</span>
              ))}
             </div>
            </div>
            <div className="text-right text-[10px] text-slate-400 shrink-0 hidden sm:block">
             <p>Created {k.createdAt?.toDate ? k.createdAt.toDate().toLocaleDateString() : '—'}</p>
             <p className="mt-0.5">Last used {k.lastUsedAt?.toDate ? k.lastUsedAt.toDate().toLocaleDateString() : 'never'}</p>
             <p className="mt-0.5">{k.callCount ?? 0} calls</p>
            </div>
            <button
             onClick={() => handleRevokeApiKey(k.id, k.name)}
             disabled={apiKeyRevokeLoading === k.id}
             className="shrink-0 px-3 py-1.5 rounded-xl border border-rose-200 text-rose-600 text-[10px] font-semibold hover:bg-rose-50 disabled:opacity-50 transition-all"
            >
             {apiKeyRevokeLoading === k.id ? 'Revoking…' : 'Revoke'}
            </button>
           </div>
          ))}
         </div>
        )}

        {/* Quick reference */}
        <div className="mt-6 pt-6 border-t border-slate-100">
         <button onClick={() => setShowApiKeyDocs(v => !v)} className={`text-xs font-semibold ${t.textLink} flex items-center gap-1`}>
          {showApiKeyDocs ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Quick API Reference
         </button>
         {showApiKeyDocs && (
          <div className="mt-4 space-y-3 text-xs">
           <div className={`p-4 rounded-xl ${t.bgInput} border ${t.border} font-mono`}>
            <p className={`${t.textMuted} mb-1`}>Base URL</p>
            <p className={`${t.text} select-all`}>https://app.soar-one.com/api/v1</p>
           </div>
           <div className={`p-4 rounded-xl ${t.bgInput} border ${t.border} font-mono`}>
            <p className={`${t.textMuted} mb-1`}>Authentication</p>
            <p className={`${t.text} select-all`}>Authorization: Bearer sk_live_...</p>
           </div>
           <table className="w-full text-[11px]">
            <thead><tr className={`${t.textMuted} text-left`}><th className="pb-2 pr-4">Method</th><th className="pb-2 pr-4">Endpoint</th><th className="pb-2">Scope</th></tr></thead>
            <tbody className={t.text}>
             <tr><td className="pr-4 py-1 font-mono text-emerald-600">GET</td><td className="pr-4 py-1 font-mono">/v1/invoices</td><td className="py-1">invoices:read</td></tr>
             <tr><td className="pr-4 py-1 font-mono text-emerald-600">GET</td><td className="pr-4 py-1 font-mono">/v1/invoices/:id</td><td className="py-1">invoices:read</td></tr>
             <tr><td className="pr-4 py-1 font-mono text-emerald-600">GET</td><td className="pr-4 py-1 font-mono">/v1/logs</td><td className="py-1">logs:read</td></tr>
             <tr><td className="pr-4 py-1 font-mono text-emerald-600">GET</td><td className="pr-4 py-1 font-mono">/v1/summary</td><td className="py-1">summary:read</td></tr>
             <tr><td className="pr-4 py-1 font-mono text-blue-600">POST</td><td className="pr-4 py-1 font-mono">/v1/sync</td><td className="py-1">sync:write</td></tr>
            </tbody>
           </table>
           <p className={t.textMuted}>Optional query param: <code className={t.text}>?limit=N</code> (max 500)</p>
           <p className={t.textMuted}>POST /v1/sync accepts optional body: <code className={t.text}>{`{"realmId":"..."}`}</code> to sync a specific connection</p>
          </div>
         )}
        </div>
       </div>

       {/* Sender Identity */}
       <div className={`${panelClass} p-10`}>
        <div className="flex items-center gap-6 mb-8">
         <div className="p-4 rounded-xl border border-slate-100 shadow-none" style={{ backgroundColor: PRIMARY_BLUE }}><MailIcon size={24} className="text-slate-900" /></div>
         <div>
          <h3 style={{fontFamily:"var(--font-display)"}} className={`text-xl font-bold ${t.text} tracking-tight`}>Sender Identity</h3>
          <p className={`text-xs ${t.textMuted} mt-1`}>Configure a custom sender address for outbound dunning emails</p>
         </div>
        </div>
        <div className="p-8 bg-slate-50 rounded-2xl border border-slate-100 mb-8 shadow-none">
         <div className="flex items-center justify-between mb-6 shadow-none">
          <div className="shadow-none">
           <label className="text-xs font-semibold uppercase text-slate-900 tracking-wide shadow-none">Custom SMTP Gateway</label>
           <p className="text-xs font-normal text-slate-400 mt-1 shadow-none">Override the system default sender address</p>
           {!canUseSmtp && <p className="text-[10px] font-semibold text-amber-600 mt-1">Requires Pro plan or higher</p>}
          </div>
          <button onClick={() => {
           if (!canUseSmtp) { requireUpgrade('Custom SMTP'); return; }
           setUserProfile({ ...userProfile, useCustomSender: !userProfile.useCustomSender });
          }} className={`p-1 rounded-full transition-all shadow-none ${userProfile.useCustomSender && canUseSmtp ? 'bg-slate-900' : 'bg-slate-200'}`}>
           {userProfile.useCustomSender && canUseSmtp ? <ToggleRight className="text-white" size={36} /> : <ToggleLeft className="text-slate-500" size={36} />}
          </button>
         </div>
         <div className={`space-y-4 transition-all duration-500 ${userProfile.useCustomSender ? 'opacity-100' : 'opacity-20 pointer-events-none'}`}>
          <div className="relative">
           <Globe size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
           <input type="email" className={`w-full pl-12 pr-32 py-4 ${t.bgCard} border ${t.border} rounded-xl text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`} placeholder="sender@yourdomain.com" value={userProfile.senderEmail || ''} onChange={(e) => { setUserProfile({ ...userProfile, senderEmail: e.target.value }); setSenderStatus('unknown'); }} />
           <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <button onClick={handleCheckSenderStatus} disabled={!userProfile.senderEmail || senderStatus === 'checking'} className="p-2 text-slate-400 hover:text-slate-700 rounded-lg transition-all" title="Check status">
             {senderStatus === 'checking' ? <RefreshCw size={14} className="animate-spin" /> : <Eye size={14} />}
            </button>
            <button onClick={handleVerifySender} disabled={savingSettings} className="p-2.5 text-slate-900 rounded-xl active:scale-95 transition-all" style={{ backgroundColor: PRIMARY_BLUE }} title="Register sender"><Send size={14} /></button>
           </div>
          </div>
          {senderStatus === 'verified' && <p className="text-xs font-semibold text-emerald-500 flex items-center gap-1.5"><CheckCircle size={13} /> Verified — ready to send</p>}
          {senderStatus === 'pending' && <p className="text-xs font-semibold text-amber-500 flex items-center gap-1.5"><AlertCircle size={13} /> Pending — check inbox for verification email</p>}
          {senderStatus === 'not_found' && <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><AlertCircle size={13} /> Not registered — click send to register</p>}
         </div>
         {!userProfile.useCustomSender && <p className="mt-3 text-xs text-slate-400">Emails dispatch via the system sender when custom SMTP is off.</p>}
        </div>
        <button onClick={handleUpdateProfile} disabled={savingSettings} style={{ backgroundColor: PRIMARY_BLUE }} className="px-6 py-3 text-slate-900 rounded-xl text-xs font-semibold tracking-wide active:scale-95 transition-all flex items-center gap-2 shadow-none disabled:opacity-50"><Save size={14} /> Save Sender Settings</button>
       </div>

       <div className={`${t.bgCard} p-10 rounded-2xl border border-slate-100 shadow-none`}>
        <div className="flex items-start justify-between gap-6 mb-8">
         <div>
           <h3 style={{fontFamily:"var(--font-display)"}} className={`text-2xl font-bold ${t.text} tracking-tight`}>Billing & Subscription</h3>
          <p className={`text-xs ${t.textMuted} mt-1`}>Manage your plan, recurring billing, payment method, and cancellation settings.</p>
         </div>
         <button onClick={() => setShowPricing(true)} style={{ backgroundColor: PRIMARY_BLUE }} className="px-5 py-3 rounded-xl text-slate-900 text-xs font-bold uppercase tracking-wide shadow-none">
          Change Plan
         </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
         <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
          <p className={`text-[10px] uppercase tracking-wide ${t.textMuted} mb-2`}>Current Plan</p>
          <p className={`text-lg font-bold ${t.text}`}>{effectivePlan.name}</p>
          <p className={`text-xs ${t.textSec} mt-1`}>
           {userProfile.paddleSubscriptionId ? `$${currentPlan.price}/month recurring` : isTrialActive ? 'Trial access' : 'No active paid subscription'}
          </p>
         </div>
         <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
          <p className={`text-[10px] uppercase tracking-wide ${t.textMuted} mb-2`}>Billing Status</p>
          <p className={`text-lg font-bold ${t.text}`}>{billingPortalData?.status || userProfile.subscriptionStatus || (isTrialActive ? 'trialing' : 'inactive')}</p>
          <p className={`text-xs ${t.textSec} mt-1`}>
           {billingPortalData?.nextBilledAt
            ? `Renews on ${new Date(billingPortalData.nextBilledAt).toLocaleDateString()}`
            : userProfile.paddleCancellationEffectiveDate
             ? `Cancels on ${new Date(userProfile.paddleCancellationEffectiveDate).toLocaleDateString()}`
             : 'Monthly recurring billing'}
          </p>
         </div>
        </div>

        {isTrialActive && (
         <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-100 text-amber-800 text-sm">
          Trial active. You have {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining before paid billing applies.
         </div>
        )}

        <div className="flex flex-wrap gap-3 mb-6">
         <button onClick={() => setShowPricing(true)} className="px-5 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-wide shadow-none">
          {userProfile.paddleSubscriptionId ? 'Upgrade or Downgrade' : 'Choose a Plan'}
         </button>
         <button onClick={() => handleManageSubscription('payment')} disabled={checkoutLoading === 'payment' || !userProfile.paddleSubscriptionId} className={`px-5 py-3 ${t.bgInput} border ${t.borderInput} ${t.textLink} rounded-xl text-xs font-bold uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed shadow-none`}>
          {checkoutLoading === 'payment' ? 'Loading...' : 'Update Payment Method'}
         </button>
         <button onClick={() => handleManageSubscription('manage')} disabled={checkoutLoading === 'manage' || !userProfile.paddleSubscriptionId} className={`px-5 py-3 ${t.bgInput} border ${t.borderInput} ${t.textLink} rounded-xl text-xs font-bold uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed shadow-none`}>
          {checkoutLoading === 'manage' ? 'Loading...' : 'Open Billing Portal'}
         </button>
         <button onClick={() => handleManageSubscription('cancel')} disabled={checkoutLoading === 'cancel' || !userProfile.paddleSubscriptionId} className="px-5 py-3 bg-rose-50 border border-rose-100 text-rose-600 rounded-xl text-xs font-bold uppercase tracking-wide disabled:opacity-40 disabled:cursor-not-allowed shadow-none">
          {checkoutLoading === 'cancel' ? 'Loading...' : 'Cancel Subscription'}
         </button>
        </div>

        <div className={`text-[11px] ${t.textSec} leading-relaxed space-y-2`}>
         <p>Recurring billing is active for paid subscriptions. Customers can update payment method or cancel from the billing portal.</p>
         <p>Plan changes are available now. Annual billing is not yet configured because the app currently has monthly Paddle price IDs only.</p>
        </div>
       </div>

       <div className="p-10 rounded-2xl border border-rose-100 bg-rose-50/40 shadow-none">
        <div className="flex items-start justify-between gap-6 flex-col md:flex-row md:items-center">
         <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4 shadow-none">
           <Trash2 size={24} className="text-rose-500 shadow-none" />
           <h3 style={{fontFamily:"var(--font-display)"}} className="text-2xl font-bold text-rose-900 tracking-tight">Delete All Account Data</h3>
          </div>
          <p className="text-sm text-rose-700 leading-relaxed">
           Permanently remove your connected QuickBooks data, templates, and activity history from SoarOne. This action cannot be undone.
          </p>
         </div>
         <button
          onClick={handlePurgeData}
          disabled={savingSettings}
          className="bg-rose-500 hover:bg-rose-600 text-white px-6 py-4 rounded-xl font-semibold text-xs uppercase tracking-wide active:scale-95 transition-all whitespace-nowrap shadow-none disabled:opacity-50"
         >
          Delete Everything
         </button>
        </div>
       </div>
      </div>
     )}
   </div>
  </main>

   {/* Team Invite Accept Modal */}
   {inviteAcceptInfo && pendingInviteToken && (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1400] flex items-center justify-center p-6">
     <div className={`${t.bgCard} w-full max-w-md rounded-3xl border ${t.border} shadow-none overflow-hidden`}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-center gap-4 mb-6">
        <div className={`p-3 rounded-2xl bg-violet-50 flex-shrink-0`}><UserPlus size={22} className="text-violet-600" /></div>
        <div>
         <h3 style={{fontFamily:"var(--font-display)"}} className={`text-lg font-bold ${t.text} tracking-tight`}>Workspace Invitation</h3>
         <p className={`text-xs ${t.textMuted} mt-0.5`}>You've been invited to join a workspace</p>
        </div>
       </div>
       <div className={`p-5 rounded-2xl ${t.bgInput} border ${t.border} mb-6`}>
        <div className="flex items-center justify-between gap-4 mb-3">
         <div>
          <p className={`text-xs ${t.textMuted} uppercase tracking-wide font-semibold mb-0.5`}>Workspace owner</p>
          <p className={`text-sm font-bold ${t.text}`}>{inviteAcceptInfo.ownerName}</p>
          <p className={`text-xs ${t.textMuted}`}>{inviteAcceptInfo.ownerEmail}</p>
         </div>
         <div>
          <p className={`text-xs ${t.textMuted} uppercase tracking-wide font-semibold mb-0.5`}>Your role</p>
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-violet-50 text-violet-700 capitalize">{inviteAcceptInfo.role}</span>
         </div>
        </div>
        <p className={`text-xs ${t.textMuted}`}>Invited to: <span className={`font-medium ${t.text}`}>{inviteAcceptInfo.targetEmail}</span></p>
       </div>
       {inviteAcceptError && <p className="text-xs text-rose-600 mb-4">{inviteAcceptError}</p>}
       <div className="flex gap-3">
        <button
         onClick={handleAcceptTeamInvite}
         disabled={inviteAcceptLoading}
         style={{ backgroundColor: PRIMARY_BLUE }}
         className="flex-1 py-3 text-slate-900 rounded-xl text-sm font-bold active:scale-95 transition-all disabled:opacity-50"
        >
         {inviteAcceptLoading ? 'Accepting…' : 'Accept Invitation'}
        </button>
        <button
         onClick={handleDeclineTeamInvite}
         className={`px-5 py-3 ${t.bgInput} border ${t.border} ${t.textMuted} rounded-xl text-sm font-semibold`}
        >
         Decline
        </button>
       </div>
      </div>
     </div>
    </div>
   )}

   {/* Legal Documents Re-accept Modal */}
   {showTosModal && (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1300] flex items-center justify-center p-6">
     <div className={`${t.bgCard} w-full max-w-lg rounded-3xl border ${t.border} shadow-none overflow-hidden`}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput} flex-shrink-0`}>
         <FileText size={22} style={{ color: PRIMARY_BLUE }} />
        </div>
        <div>
         <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-xl font-bold ${t.text} tracking-tight`}>
          Review our policies
         </h3>
         <p className={`text-sm ${t.textSec} mt-1 leading-relaxed`}>
          Please read and accept both documents to continue using SoarOne.
         </p>
        </div>
       </div>
       <div className="space-y-3 mb-5">
        <label className={`flex items-start gap-3 p-4 rounded-2xl border ${t.borderInput} ${t.bgInput} ${modalTosOpened ? 'cursor-pointer' : 'cursor-default'}`}>
         <input
          type="checkbox"
          checked={modalTosChecked}
          disabled={!modalTosOpened}
          onChange={e => setModalTosChecked(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-cyan-400 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
         />
         <span className={`text-sm leading-relaxed ${modalTosOpened ? t.text : t.textMuted}`}>
          I have read and agree to the{' '}
          <a href={TERMS_PAGE_URL} target="_blank" rel="noreferrer"
           onClick={() => setModalTosOpened(true)}
           className="underline font-semibold" style={{ color: PRIMARY_BLUE }}>Terms of Service</a>
          {!modalTosOpened && <span className="ml-1 text-[11px] text-amber-500/90">— open to enable</span>}
         </span>
        </label>
        <label className={`flex items-start gap-3 p-4 rounded-2xl border ${t.borderInput} ${t.bgInput} ${modalPrivacyOpened ? 'cursor-pointer' : 'cursor-default'}`}>
         <input
          type="checkbox"
          checked={modalPrivacyChecked}
          disabled={!modalPrivacyOpened}
          onChange={e => setModalPrivacyChecked(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded accent-cyan-400 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
         />
         <span className={`text-sm leading-relaxed ${modalPrivacyOpened ? t.text : t.textMuted}`}>
          I have read and agree to the{' '}
          <a href={PRIVACY_PAGE_URL} target="_blank" rel="noreferrer"
           onClick={() => setModalPrivacyOpened(true)}
           className="underline font-semibold" style={{ color: PRIMARY_BLUE }}>Privacy Policy</a>
          {!modalPrivacyOpened && <span className="ml-1 text-[11px] text-amber-500/90">— open to enable</span>}
         </span>
        </label>
       </div>
       <button
        disabled={!(modalTosChecked && modalPrivacyChecked) || modalAccepting}
        onClick={async () => {
         setModalAccepting(true);
         try {
          await httpsCallable(functions, 'acceptTos')();
          setShowTosModal(false);
         } catch {
          addNotify('error', 'Could not record your acceptance. Please try again.');
         } finally { setModalAccepting(false); }
        }}
        style={{ backgroundColor: (modalTosChecked && modalPrivacyChecked) ? PRIMARY_BLUE : undefined }}
        className={`w-full py-4 rounded-xl font-bold text-sm tracking-wide transition-all ${(modalTosChecked && modalPrivacyChecked) ? 'text-slate-900' : `${t.bgInput} ${t.textMuted} cursor-not-allowed`}`}
       >
        {modalAccepting ? 'Saving…' : 'Accept both and continue'}
       </button>
       <p className={`text-[10px] ${t.textMuted} text-center mt-3`}>
        You must accept both documents to continue. Questions? <a href="mailto:support@soar-one.com" className="underline">support@soar-one.com</a>
       </p>
      </div>
     </div>
    </div>
   )}

   {/* Re-authentication Modal */}
   {snoozeModal && (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1200] flex items-center justify-center p-6" onClick={() => { setSnoozeModal(null); setSnoozeDays(7); }}>
     <div className={`${t.bgCard} w-full max-w-sm rounded-3xl border ${t.border} shadow-none overflow-hidden`} onClick={e => e.stopPropagation()}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
         <Clock size={22} style={{ color: PRIMARY_BLUE }} />
        </div>
        <div>
         <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-xl font-bold ${t.text} tracking-tight`}>Snooze Invoice</h3>
         <p className={`text-sm ${t.textSec} mt-1`}>{snoozeModal.customerName}</p>
        </div>
       </div>
       <p className={`text-xs ${t.textMuted} mb-3 uppercase tracking-wide font-semibold`}>Pause dunning for</p>
       <div className="flex gap-2 flex-wrap mb-6">
        {[3, 7, 14, 30].map(d => (
         <button
          key={d}
          onClick={() => setSnoozeDays(d)}
          className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wide border transition-all ${snoozeDays === d ? 'text-slate-900 border-transparent' : `${t.border} ${t.textSec} hover:border-slate-400`}`}
          style={snoozeDays === d ? { backgroundColor: PRIMARY_BLUE } : {}}
         >
          {d} days
         </button>
        ))}
       </div>
       <div className="flex items-center gap-2 mb-6">
        <input
         type="number"
         min={1}
         max={180}
         value={snoozeDays}
         onChange={e => setSnoozeDays(Math.max(1, Math.min(180, parseInt(e.target.value) || 1)))}
         className={`w-24 ${t.bgInput} border ${t.borderInput} rounded-xl px-4 py-3 text-sm ${t.text} outline-none focus:ring-2 ${t.ring}`}
        />
        <span className={`text-sm ${t.textSec}`}>custom days</span>
       </div>
       <div className="flex gap-3">
        <button
         onClick={() => { setSnoozeModal(null); setSnoozeDays(7); }}
         className={`flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide ${t.bgInput} ${t.textSec} border ${t.borderInput}`}
        >
         Cancel
        </button>
        <button
         onClick={handleSnoozeInvoice}
         disabled={snoozeLoading}
         className="flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50"
         style={{ backgroundColor: PRIMARY_BLUE }}
        >
         {snoozeLoading ? 'Saving…' : 'Snooze'}
        </button>
       </div>
      </div>
     </div>
    </div>
   )}

   {/* Collections Notes Modal */}
   {noteModal && (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1200] flex items-center justify-center p-4" onClick={() => { setNoteModal(null); setNoteInput(''); setPtpInput(''); }}>
     <div className={`${t.bgCard} w-full max-w-lg rounded-3xl border ${t.border} shadow-none overflow-hidden flex flex-col max-h-[85vh]`} onClick={e => e.stopPropagation()}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full shrink-0" />
      <div className="p-6 border-b border-slate-100 shrink-0">
       <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
         <div className={`p-2.5 rounded-xl ${t.bgInput} border ${t.borderInput}`}>
          <MessageSquare size={18} style={{ color: PRIMARY_BLUE }} />
         </div>
         <div>
          <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-lg font-bold ${t.text} tracking-tight`}>Collections Notes</h3>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>Invoice #{noteModal.docNumber} · {noteModal.customerName}</p>
         </div>
        </div>
        <button onClick={() => { setNoteModal(null); setNoteInput(''); setPtpInput(''); }} className={`${t.textMuted} hover:${t.text} transition-colors`}><X size={18} /></button>
       </div>
      </div>
      {/* Existing notes */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3 min-h-0">
       {collectionNotes.filter(n => n.invoiceId === noteModal.invoiceId).length === 0 ? (
        <p className={`text-sm ${t.textMuted} text-center py-8`}>No notes yet. Add the first one below.</p>
       ) : collectionNotes.filter(n => n.invoiceId === noteModal.invoiceId).map(n => {
        const ts = n.createdAt?.toDate ? n.createdAt.toDate() : n.createdAt ? new Date(n.createdAt.seconds * 1000) : null;
        const ptpPast = n.ptpDate && new Date(n.ptpDate) < new Date();
        return (
         <div key={n.id} className={`p-4 rounded-2xl border ${t.bgInput} ${t.borderInput} relative`}>
          <div className="flex items-start justify-between gap-2 mb-2">
           <div className="flex items-center gap-2 flex-wrap">
            {n.ptpDate && (
             <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${ptpPast ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-violet-100 text-violet-700 border-violet-200'}`}>
              {ptpPast ? '⚠ PTP Missed' : '✓ PTP'} {new Date(n.ptpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
             </span>
            )}
            <span className={`text-[10px] ${t.textMuted}`}>{n.createdBy?.split('@')[0]} · {ts ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</span>
           </div>
           {(!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && (
            <button onClick={() => handleDeleteNote(n.id)} className="text-slate-300 hover:text-rose-400 transition-colors shrink-0" title="Delete note">
             <Trash2 size={13} />
            </button>
           )}
          </div>
          <p className={`text-sm ${t.text} whitespace-pre-wrap leading-relaxed`}>{n.note}</p>
         </div>
        );
       })}
      </div>
      {/* Add note form */}
      {(!isViewingMemberWorkspace || memberWorkspaceRole === 'admin') && (
       <div className="p-6 border-t border-slate-100 shrink-0 space-y-3">
        <textarea
         rows={3}
         placeholder="Add a collections note… (e.g. 'Spoke with John, says payment coming Friday')"
         value={noteInput}
         onChange={e => setNoteInput(e.target.value)}
         className={`w-full ${t.bgInput} border ${t.borderInput} rounded-xl px-4 py-3 text-sm ${t.text} outline-none focus:ring-2 ${t.ring} resize-none`}
        />
        <div className="flex items-center gap-3">
         <div className="flex items-center gap-2 flex-1">
          <label className={`text-xs font-semibold ${t.textMuted} whitespace-nowrap`}>Promise-to-Pay:</label>
          <input
           type="date"
           value={ptpInput}
           onChange={e => setPtpInput(e.target.value)}
           className={`flex-1 ${t.bgInput} border ${t.borderInput} rounded-xl px-3 py-2 text-xs ${t.text} outline-none focus:ring-2 ${t.ring}`}
          />
          {ptpInput && <button onClick={() => setPtpInput('')} className={`${t.textMuted} hover:${t.text}`}><X size={13} /></button>}
         </div>
         <button
          onClick={handleAddNote}
          disabled={noteSaving || !noteInput.trim()}
          className="px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50 shrink-0"
          style={{ backgroundColor: PRIMARY_BLUE }}
         >
          {noteSaving ? 'Saving…' : 'Save Note'}
         </button>
        </div>
       </div>
      )}
     </div>
    </div>
   )}

   {/* ITEM 23: Customer Detail Modal */}
   {customerMetaModal && (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1200] flex items-center justify-center p-6" onClick={() => setCustomerMetaModal(null)}>
     <div className={`${t.bgCard} w-full max-w-lg rounded-3xl border ${t.border} shadow-none overflow-hidden`} onClick={e => e.stopPropagation()}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
         <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
          <Users size={22} style={{ color: PRIMARY_BLUE }} />
         </div>
         <div>
          <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-xl font-bold ${t.text} tracking-tight`}>{customerMetaModal.customerName}</h3>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>{customerMetaModal.customerEmail}</p>
         </div>
        </div>
        <button onClick={() => setCustomerMetaModal(null)} className={`p-2 rounded-xl ${t.bgInput} ${t.textSec} hover:${t.text}`}><X size={16} /></button>
       </div>
       {/* Tabs */}
       <div className="flex gap-1 mb-6 bg-slate-100/80 rounded-xl p-1">
        {(['details', 'history'] as const).map(tab => (
         <button key={tab} onClick={() => setCmActiveTab(tab)}
          className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide rounded-lg transition-all ${cmActiveTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
          {tab === 'details' ? 'Details' : 'History'}
         </button>
        ))}
       </div>
       {cmActiveTab === 'details' ? (
        <div className="space-y-4">
         <div>
          <label className={`block text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>Internal Notes</label>
          <textarea rows={3} value={cmNotes} onChange={e => setCmNotes(e.target.value)} placeholder="Add private notes about this customer…" maxLength={2000}
           className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm outline-none resize-none focus:ring-2`} style={{ '--tw-ring-color': PRIMARY_BLUE } as any} />
          <p className={`text-[10px] ${t.textMuted} text-right mt-1`}>{cmNotes.length}/2000</p>
         </div>
         <div>
          <label className={`block text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>Tags <span className={`normal-case ${t.textMuted}`}>(comma-separated)</span></label>
          <input type="text" value={cmTags} onChange={e => setCmTags(e.target.value)} placeholder="VIP, slow payer, disputed frequently"
           className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm outline-none focus:ring-2`} style={{ '--tw-ring-color': PRIMARY_BLUE } as any} />
         </div>
         <div>
          <label className={`block text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>Email Override <span className={`normal-case ${t.textMuted}`}>(use instead of QB email)</span></label>
          <input type="email" value={cmEmailOverride} onChange={e => setCmEmailOverride(e.target.value)} placeholder="billing@theircorp.com"
           className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm outline-none focus:ring-2`} style={{ '--tw-ring-color': PRIMARY_BLUE } as any} />
         </div>
         <div className={`flex items-center justify-between p-4 rounded-2xl border ${t.borderInput} ${t.bgInput}`}>
          <div>
           <p className={`text-sm font-semibold ${t.text}`}>Exclude from dunning</p>
           <p className={`text-xs ${t.textMuted} mt-0.5`}>Never send automated reminders to this customer</p>
          </div>
          <button onClick={() => setCmExcluded(v => !v)} className={`p-1 rounded-full transition-all ${cmExcluded ? 'bg-rose-600' : 'bg-slate-200'}`}>
           {cmExcluded ? <ToggleRight className="text-white" size={28} /> : <ToggleLeft className="text-slate-500" size={28} />}
          </button>
         </div>
         <div className="flex gap-3 pt-2">
          <button onClick={() => setCustomerMetaModal(null)} className={`flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide ${t.bgInput} ${t.textSec} border ${t.borderInput}`}>Cancel</button>
          <button onClick={handleSaveCustomerMeta} disabled={cmSaving} className="flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50" style={{ backgroundColor: PRIMARY_BLUE }}>
           {cmSaving ? 'Saving…' : 'Save'}
          </button>
         </div>
        </div>
       ) : (
        <div>
         <p className={`text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-3`}>Recent sends to {customerMetaModal.customerEmail}</p>
         {(() => {
          const customerLogs = logs.filter(l => l.recipient?.toLowerCase() === customerMetaModal.customerEmail.toLowerCase()).slice(0, 15);
          if (customerLogs.length === 0) return <p className={`text-sm ${t.textMuted} text-center py-8`}>No reminders sent yet.</p>;
          return (
           <div className="space-y-2 max-h-64 overflow-y-auto">
            {customerLogs.map(l => (
             <div key={l.id} className={`flex items-center justify-between p-3 rounded-xl border ${t.borderInput} ${t.bgInput}`}>
              <div>
               <p className={`text-xs font-semibold ${t.text}`}>#{l.docNumber} — {l.templateSent}</p>
               <p className={`text-[10px] ${t.textMuted} mt-0.5`}>{getHistoryDateValue(l) ? new Date(typeof getHistoryDateValue(l) === 'object' && 'seconds' in (getHistoryDateValue(l) as any) ? (getHistoryDateValue(l) as any).seconds * 1000 : getHistoryDateValue(l) as any).toLocaleDateString() : '—'}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase ${l.deliveryStatus === 'opened' ? 'bg-emerald-100 text-emerald-700' : l.deliveryStatus === 'delivered' ? 'bg-sky-100 text-sky-700' : l.deliveryStatus === 'failed' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'}`}>
               {l.deliveryStatus}
              </span>
             </div>
            ))}
           </div>
          );
         })()}
        </div>
       )}
      </div>
     </div>
    </div>
   )}

   {/* ITEM 24: Invoice Controls Modal */}
   {invoiceCtrlModal && (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1200] flex items-center justify-center p-6" onClick={() => setInvoiceCtrlModal(null)}>
     <div className={`${t.bgCard} w-full max-w-lg rounded-3xl border ${t.border} shadow-none overflow-hidden`} onClick={e => e.stopPropagation()}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
         <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
          <FileText size={22} style={{ color: PRIMARY_BLUE }} />
         </div>
         <div>
          <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-xl font-bold ${t.text} tracking-tight`}>Invoice Controls</h3>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>#{invoiceCtrlModal.docNumber} — {invoiceCtrlModal.customerName}</p>
         </div>
        </div>
        <button onClick={() => setInvoiceCtrlModal(null)} className={`p-2 rounded-xl ${t.bgInput} ${t.textSec}`}><X size={16} /></button>
       </div>
       <div className="space-y-4">
        <div>
         <label className={`block text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>CC Email <span className={`normal-case ${t.textMuted}`}>(copied on every reminder)</span></label>
         <input type="email" value={icCcEmail} onChange={e => setIcCcEmail(e.target.value)} placeholder="manager@yourdomain.com"
          className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm outline-none focus:ring-2`} style={{ '--tw-ring-color': PRIMARY_BLUE } as any} />
        </div>
        <div>
         <label className={`block text-xs font-semibold ${t.textMuted} uppercase tracking-wide mb-2`}>Custom Note <span className={`normal-case ${t.textMuted}`}>(appended to every reminder for this invoice)</span></label>
         <textarea rows={3} value={icCustomMessage} onChange={e => setIcCustomMessage(e.target.value)} placeholder="Add a personal note that appears in every reminder sent for this invoice…" maxLength={1000}
          className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm outline-none resize-none focus:ring-2`} style={{ '--tw-ring-color': PRIMARY_BLUE } as any} />
         <p className={`text-[10px] ${t.textMuted} text-right mt-1`}>{icCustomMessage.length}/1000</p>
        </div>
        <div className={`flex items-center justify-between p-4 rounded-2xl border ${t.borderInput} ${t.bgInput}`}>
         <div>
          <p className={`text-sm font-semibold ${t.text}`}>Disable dunning for this invoice</p>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>No reminders will be sent until re-enabled</p>
         </div>
         <button onClick={() => setIcDisabled(v => !v)} className={`p-1 rounded-full transition-all ${icDisabled ? 'bg-slate-700' : 'bg-slate-200'}`}>
          {icDisabled ? <ToggleRight className="text-white" size={28} /> : <ToggleLeft className="text-slate-500" size={28} />}
         </button>
        </div>
        <div className={`flex items-center justify-between p-4 rounded-2xl border ${icMarkedCollected ? 'border-emerald-200 bg-emerald-50/50' : `${t.borderInput} ${t.bgInput}`}`}>
         <div>
          <p className={`text-sm font-semibold ${icMarkedCollected ? 'text-emerald-700' : t.text}`}>Mark as collected</p>
          <p className={`text-xs ${t.textMuted} mt-0.5`}>Suppresses all future reminders without filing a dispute</p>
         </div>
         <button onClick={() => setIcMarkedCollected(v => !v)} className={`p-1 rounded-full transition-all ${icMarkedCollected ? 'bg-emerald-600' : 'bg-slate-200'}`}>
          {icMarkedCollected ? <ToggleRight className="text-white" size={28} /> : <ToggleLeft className="text-slate-500" size={28} />}
         </button>
        </div>
        <div className="flex gap-3 pt-2">
         <button onClick={() => setInvoiceCtrlModal(null)} className={`flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide ${t.bgInput} ${t.textSec} border ${t.borderInput}`}>Cancel</button>
         <button onClick={handleSaveInvoiceControls} disabled={icSaving} className="flex-1 px-4 py-3 rounded-xl text-xs font-bold uppercase tracking-wide text-slate-900 disabled:opacity-50" style={{ backgroundColor: PRIMARY_BLUE }}>
          {icSaving ? 'Saving…' : 'Save Controls'}
         </button>
        </div>
       </div>
      </div>
     </div>
    </div>
   )}

   {reauthModalOpen && (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1200] flex items-center justify-center p-6" onClick={() => setReauthModalOpen(false)}>
     <div className={`${t.bgCard} w-full max-w-md rounded-3xl border ${t.border} shadow-none overflow-hidden`} onClick={e => e.stopPropagation()}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start gap-4 mb-6">
        <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
         <Shield size={22} style={{ color: PRIMARY_BLUE }} />
        </div>
        <div>
         <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-xl font-bold ${t.text} tracking-tight`}>Confirm your identity</h3>
         <p className={`text-sm ${t.textSec} mt-1 leading-relaxed`}>For your security, re-enter your password to continue.</p>
        </div>
       </div>
       <input
        type="password"
        value={reauthPassword}
        onChange={e => setReauthPassword(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleReauthSubmit(); }}
        placeholder="Your current password"
        autoFocus
        className={`w-full px-4 py-3 rounded-xl border ${t.borderInput} ${t.bgInput} ${t.text} text-sm mb-3 outline-none focus:ring-2`}
        style={{ '--tw-ring-color': PRIMARY_BLUE } as any}
       />
       {reauthError && <p className="text-xs text-rose-600 font-semibold mb-4">{reauthError}</p>}
       <div className="flex gap-3 mt-2">
        <button onClick={() => setReauthModalOpen(false)} className={`flex-1 px-4 py-3 rounded-xl border ${t.border} ${t.textSec} text-sm font-semibold hover:${t.bgInput} transition-colors`}>Cancel</button>
        <button onClick={handleReauthSubmit} disabled={reauthLoading || !reauthPassword} style={{ backgroundColor: PRIMARY_BLUE }} className="flex-1 px-4 py-3 rounded-xl text-slate-900 text-sm font-semibold disabled:opacity-50 transition-opacity">
         {reauthLoading ? 'Verifying…' : 'Confirm'}
        </button>
       </div>
      </div>
     </div>
    </div>
   )}

   {showSessionExpiryWarning && (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1100] flex items-center justify-center p-6">
     <div className={`${t.bgCard} w-full max-w-md rounded-3xl border ${t.border} shadow-none overflow-hidden`}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="h-1.5 w-full" />
      <div className="p-8">
       <div className="flex items-start gap-4 mb-5">
        <div className={`p-3 rounded-2xl ${t.bgInput} border ${t.borderInput}`}>
         <Clock3 size={22} style={{ color: PRIMARY_BLUE }} />
        </div>
        <div>
         <h3 style={{ fontFamily: "var(--font-display)" }} className={`text-2xl font-bold ${t.text} tracking-tight`}>
          Session Expiring Soon
         </h3>
         <p className={`text-sm ${t.textSec} mt-1 leading-relaxed`}>
          For security, we will sign you out after 30 minutes of inactivity.
         </p>
        </div>
       </div>

       <div className={`rounded-2xl border ${t.borderInput} ${t.bgInput} p-5 mb-6`}>
        <p className={`text-[11px] uppercase tracking-wide ${t.textMuted} mb-2`}>Time Remaining</p>
        <p className={`text-3xl font-bold ${t.text}`}>
         {Math.floor(sessionCountdown / 60)}:{String(sessionCountdown % 60).padStart(2, '0')}
        </p>
        <p className={`text-xs ${t.textSec} mt-2`}>
         Select <strong className={t.text}>Stay Signed In</strong> to continue your session.
        </p>
       </div>

       <div className="flex flex-col sm:flex-row gap-3">
        <button
         onClick={handleExtendSession}
         style={{ backgroundColor: PRIMARY_BLUE }}
         className="flex-1 text-slate-900 font-semibold py-4 rounded-xl text-xs tracking-wide active:scale-95 transition-all"
        >
         Stay Signed In
        </button>
        <button
         onClick={() => handleIdleLogout()}
         className={`flex-1 py-4 rounded-xl text-xs font-semibold border ${t.border} ${t.text} transition-all`}
        >
         Sign Out Now
        </button>
       </div>
      </div>
     </div>
    </div>
   )}

   {/* Item 26: What's New modal */}
   {showWhatsNew && (
    <WhatsNewModal onClose={() => {
     localStorage.setItem('soarone_seen_version', APP_VERSION);
     setShowWhatsNew(false);
    }} />
   )}

   {/* Synchronizing Overlay */}
   {syncingId && (
    <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-xl z-[1000] flex items-center justify-center p-8 animate-in fade-in duration-300 shadow-none">
     <div className={`${t.bgCard} p-14 rounded-3xl border border-slate-100 flex flex-col items-center text-center max-w-sm relative overflow-hidden shadow-none`}>
      <div style={{ backgroundColor: PRIMARY_BLUE }} className="absolute top-0 left-0 w-full h-1.5 animate-pulse shadow-none"></div>
      <div className="relative mb-12 shadow-none">
       <RefreshCw size={80} style={{ color: PRIMARY_BLUE }} className="animate-spin opacity-20 shadow-none" />
       <div className="absolute inset-0 flex items-center justify-center shadow-none"><Activity size={40} style={{ color: PRIMARY_BLUE }} className="animate-pulse shadow-none" /></div>
      </div>
      <h4 className="text-3xl font-semibold text-slate-900 mb-2 tracking-tight leading-none shadow-none">Reconciling</h4>
      <p className="text-xs font-normal text-slate-400 tracking-normal leading-relaxed px-4 shadow-none">Synchronizing node logic with the production core.</p>
     </div>
    </div>
   )}
  </div>
 );
}
